import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { FaHome, FaSearch, FaEdit, FaPaperPlane, FaCopy, FaDownload, FaFile, FaTimes, FaPlus, FaTrash, FaUser, FaSignOutAlt, FaSignInAlt, FaBell, FaGasPump, FaOilCan, FaTruck, FaIndustry, FaFire, FaFlask, FaShip, FaBox, FaCubes, FaArrowRight, FaEye, FaEyeSlash, FaBars, FaLock } from 'react-icons/fa';
import { toEthiopianDateStr, formatEthDateEn, convertDbDateToDisplay, ethDateStrToGregStr } from './ethiopianDate';
import EthiopianDatePicker from './EthiopianDatePicker';
import watermarkImage from './front_building.png';
import { DraftContext, useFormDraft, clearDraft, getDraftMeta } from './draft';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
axios.defaults.headers.common['X-Portal'] = 'customer';
const API_ORIGIN = API_URL.replace(/\/api\/?$/, '');
axios.defaults.withCredentials = true;
axios.interceptors.request.use((config) => {
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    const h = config.headers;
    if (h) {
      if (typeof h.delete === 'function') h.delete('Content-Type');
      delete h['Content-Type'];
      delete h['content-type'];
    }
  }
  return config;
});
const fileUrl = (p) => {
  if (!p) return p;
  let clean = String(p).replace(/\\/g, '/');
  if (!clean.startsWith('/') && !/^https?:\/\//i.test(clean)) clean = '/' + clean;
  return `${API_ORIGIN}${clean}`;
};
const CLASSIFICATION_CODES = [
  { code: '61921', amharic: 'የተሽከርካሪ ነዳጅና ቅባት ጅምላ ንግድ', english: 'Wholesale trade of automotive fuel and lubricants' },
  { code: '62714', amharic: 'የተሽከርካሪ ነዳጅ እና ቅባት ችርቻሮ ንግድ (በማደያ)', english: 'Retail trade in vehicle fuel and lubricants (at gas stations)' },
  { code: '62811', amharic: 'የኪሮሲን ነዳጅና ቅባት ችርቻሮ ንግድ(ከማደያ ውጪ)', english: 'Retail trade of kerosene fuel and lubricants (outside gas stations)' },
  { code: '63114', amharic: 'የነዳጅ ማደያ መሳሪያዎች እና የባዮጋዝ ማብሊያ ተከላ/ግንባታ እና ጥገና', english: 'Installation/construction and maintenance of gas station equipment and biogas plants' },
  { code: '65412', amharic: 'ፔትሮሊየም እና የፔትሮልየም ውጤቶችና ተዛማጅ ምርቶች አስመጪነት', english: 'Import of petroleum, petroleum products and related products' },
  { code: '65413', amharic: 'የተፈጥሮ እና ሰው ሰራሽ ጋዝ አስመጪነት', english: 'Import of natural and man-made gas' },
  { code: '66412', amharic: 'ፔትሮሊየም እና የፔትሮልየም ውጤቶችና ተዛማጅ ምርቶች ላኪነት', english: 'Export of petroleum, petroleum products and related products' },
  { code: '66413', amharic: 'የተፈጥሮና ሰው ሰራሽ ጋዝ ላኪነት', english: 'Natural and man-made gas export' },
  { code: '71115', amharic: 'የፈሳሽ ጭነት ትራንስፖርት አገልግሎት', english: 'Liquid cargo transportation service' },
  { code: '41200', amharic: 'ጋዝ ማምረት፣ እንፋሎት እና የጋዝ ምርቶችን በመስመር ማከፋፈል', english: 'Gas production, steam and gas product distribution on pipe' }
];

function AuthModal({ onClose, onLoginSuccess, initialMode = false }) {
  const [isRegister, setIsRegister] = useState(initialMode);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [formData, setFormData] = useState({ username: '', password: '', confirmPassword: '', fullName: '', email: '', phone: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isRegister && formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      if (isRegister) {
        if (!formData.email) {
          toast.error('Email is required for registration');
          setLoading(false);
          return;
        }
        const { confirmPassword, ...registerData } = formData;
        const response = await axios.post(`${API_URL}/auth/register`, registerData);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        onLoginSuccess(response.data.user);
        toast.success('Registration successful!');
      } else {
        const response = await axios.post(`${API_URL}/auth/login`, { username: formData.username, password: formData.password, source: 'customer' });
        localStorage.setItem('user', JSON.stringify(response.data.user));
        onLoginSuccess(response.data.user);
        toast.success('Login successful!');
      }
      onClose();
    } catch (error) {
      console.error('Auth error:', error.response?.data || error.message);
      const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Failed. Please try again.';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email: forgotEmail });
      toast.success('Password reset link sent to your email');
      setResetSuccess(true);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  const activeTab = isForgotPassword ? 'forgot' : (isRegister ? 'register' : 'login');
  const switchTab = (tab) => {
    setIsForgotPassword(tab === 'forgot');
    setIsRegister(tab === 'register');
    setResetSuccess(false);
    setForgotEmail('');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg p-4 md:p-8 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex mb-4 border border-gray-300 rounded overflow-hidden">
          <button type="button" onClick={() => switchTab('login')} className={`flex-1 py-2 text-sm font-medium ${activeTab === 'login' ? 'bg-[#006400] text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
            Login
          </button>
          <button type="button" onClick={() => switchTab('register')} className={`flex-1 py-2 text-sm font-medium border-l border-gray-300 ${activeTab === 'register' ? 'bg-[#006400] text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
            Register
          </button>
          <button type="button" onClick={() => switchTab('forgot')} className={`flex-1 py-2 text-sm font-medium border-l border-gray-300 ${activeTab === 'forgot' ? 'bg-[#006400] text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
            Forgot Password
          </button>
        </div>
        <h2 className="text-lg md:text-2xl font-bold text-center mb-4 md:mb-6 text-[#006400]">
          {isForgotPassword ? 'Reset Password' : (isRegister ? 'Register' : 'Sign In')} <span className="hidden sm:inline">- Petroleum and Energy Authority Online Service</span>
        </h2>
        
        {isForgotPassword ? (
          resetSuccess ? (
            <div className="text-center">
              <p className="text-green-500 mb-4">Check your email for password reset instructions.</p>
              <button onClick={() => { setIsForgotPassword(false); setResetSuccess(false); setForgotEmail(''); }} className="text-[#006400] hover:underline">
                Back to Login
              </button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Email Address</label>
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#4CAF50] text-white py-2 rounded hover:bg-green-400 disabled:opacity-50"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
              <button type="button" onClick={() => setIsForgotPassword(false)} className="w-full mt-4 border py-2 rounded hover:bg-gray-100">
                Back to Login
              </button>
            </form>
          )
        ) : (
          <form onSubmit={handleSubmit}>
            {isRegister && (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Full Name *</label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]"
                    required={isRegister}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]"
                    required={isRegister}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]"
                  />
                </div>
              </>
            )}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Username</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 pr-10 focus:outline-none focus:border-[#006400]"
                  required
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>
            {isRegister && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Confirm Password</label>
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]"
                  required={isRegister}
                />
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#4CAF50] text-white py-2 rounded hover:bg-green-400 disabled:opacity-50"
            >
              {loading ? (isRegister ? 'Registering...' : 'Signing in...') : (isRegister ? 'Register' : 'Sign In')}
            </button>
          </form>
        )}
        
        <button
          type="button"
          onClick={() => { if (onClose) onClose(); }}
          className="w-full mt-4 border py-2 rounded hover:bg-gray-100"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function NotificationBell({ user }) {
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [viewedNotifications, setViewedNotifications] = useState(true);

  useEffect(() => {
    if (!user || !user.id) return;
    
    const checkNotifications = async () => {
      try {
        const lastChecked = localStorage.getItem('lastCheckedNotifications') || '';
        
        const [directFuel, gsTransfer, diversion, operator, newFuelCompany, fuelRenewal, fuelTransfer] = await Promise.all([
          axios.get(`${API_URL}/direct-fuel-beneficiary?user_id=${user.id}`),
          axios.get(`${API_URL}/gas-station-transfer?user_id=${user.id}`),
          axios.get(`${API_URL}/diversion-license?user_id=${user.id}`),
          axios.get(`${API_URL}/operator-change-license?user_id=${user.id}`),
          axios.get(`${API_URL}/new-fuel-company?user_id=${user.id}`),
          axios.get(`${API_URL}/fuel-company-renewal?user_id=${user.id}`),
          axios.get(`${API_URL}/fuel-company-transfer?user_id=${user.id}`)
        ]);

        const allApps = [
          ...directFuel.data.map(a => ({ ...a, serviceName: 'Service for direct fuel beneficiary renewal' })),
          ...gsTransfer.data.map(a => ({ ...a, serviceName: 'Service for existing gas station transfer from on company to the other' })),
          ...diversion.data.map(a => ({ ...a, serviceName: 'Service for diversion permit' })),
          ...operator.data.map(a => ({ ...a, serviceName: 'Service for operator/dealer change permit' })),
          ...newFuelCompany.data.map(a => ({ ...a, serviceName: 'New Fuel Company' })),
          ...fuelRenewal.data.map(a => ({ ...a, serviceName: 'Fuel Company Renewal' })),
          ...fuelTransfer.data.map(a => ({ ...a, serviceName: 'Fuel Company Transfer' }))
        ];

        const notifiedIds = JSON.parse(localStorage.getItem('notifiedAppIds') || '[]');
        const newNotifications = allApps.filter(app => 
          (app.status === 'approved' || app.status === 'rejected' || app.status === 'returned') &&
          !notifiedIds.includes(app.id)
        );

        if (newNotifications.length > 0) {
          newNotifications.forEach(app => {
            if (app.status === 'returned') {
              toast.info(`Your ${app.serviceName} application (${app.reference_no}) has been returned for correction. Please check My Applications for details.`, {
                position: "top-right",
                autoClose: 10000,
                closeOnClick: true,
              });
            } else {
              const statusText = app.status === 'approved' ? 'Approved' : 'Rejected';
              toast.success(`Your ${app.serviceName} application (${app.reference_no}) has been ${statusText}!`, {
                position: "top-right",
                autoClose: 10000,
                closeOnClick: true,
              });
            }
          });
          const allIds = [...new Set([...notifiedIds, ...newNotifications.map(a => a.id)])];
          localStorage.setItem('notifiedAppIds', JSON.stringify(allIds));
        }

        setNotifications(newNotifications);
        if (newNotifications.length > 0) setViewedNotifications(false);
      } catch (error) {
        console.error('Failed to check notifications:', error);
      }
    };

    checkNotifications();
    const interval = setInterval(checkNotifications, 60000);
    return () => clearInterval(interval);
  }, [user]);

  if (!user) return null;

  return (
    <div className="relative">
      <button 
        onClick={() => {
          setShowNotifications(!showNotifications);
          if (!showNotifications) setViewedNotifications(true);
        }}
        className="relative p-2 hover:bg-orange-600 rounded-full transition"
      >
        <FaBell className="text-xl" />
        {notifications.length > 0 && !viewedNotifications && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {notifications.length}
          </span>
        )}
      </button>
      
      {showNotifications && (
        <div className="absolute right-0 top-12 bg-white text-gray-800 rounded-lg shadow-lg w-80 max-w-[calc(100vw-2rem)] max-h-96 overflow-y-auto z-50">
          <div className="p-4 border-b bg-orange-50">
            <h3 className="font-bold">Notifications</h3>
          </div>
          {notifications.length === 0 ? (
            <div className="p-4 text-center text-gray-500">No new notifications</div>
          ) : (
            <div className="divide-y">
              {notifications.map((notif, index) => (
                <div key={index} className="p-3 hover:bg-gray-50">
                  <p className="font-medium text-sm">{notif.serviceName}</p>
                  <p className="text-xs text-gray-500">Ref: {notif.reference_no}</p>
                  <span className={`inline-block px-2 py-1 rounded text-xs mt-1 ${notif.status === 'approved' ? 'bg-green-100 text-green-800' : notif.status === 'returned' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>
                    {notif.status === 'approved' ? 'Approved' : notif.status === 'returned' ? 'Returned for Correction' : 'Rejected'}
                  </span>
                </div>
              ))}
        </div>
      )}

    </div>
      )}
    </div>
  );
}

function Header({ user, onLoginClick, onLogout, onMyApplicationsClick, onSignupClick, onNavigate, onOpenPasswordChange }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const navItems = [
    { label: 'Home', action: 'home' }, { label: 'About us', action: 'about' },
    { label: 'Services', action: 'services' }, { label: 'Complaints', action: 'complaints' },
    { label: 'Feedback', action: 'feedback' }, { label: 'Check Status', action: 'search' },
    { label: 'Requirements', action: 'requirements' }, { label: 'Manual', action: 'manual' }
  ];
  return (
    <header className="bg-gradient-to-r from-[#FF6B00] to-[#cc5500] text-white shadow-lg">
      <div className="container mx-auto px-4 md:px-6 py-2">
        <div className="flex justify-between items-center gap-2">
          <div className="flex items-center gap-2 md:gap-3 min-w-0">
            <img src="/Logo1.png" alt="PEA Logo" className="h-12 md:h-20 flex-shrink-0" />
            <h1 className="text-sm md:text-lg lg:text-2xl font-bold leading-tight truncate">የነዳጅና ኢነርጂ ባለሥልጣን<br className="hidden md:inline"/> Petroleum and Energy Authority</h1>
          </div>
          <div className="flex items-center gap-1 md:gap-3">
            {user ? (
              <>
                <NotificationBell user={user} />
                <span className="hidden sm:flex flex-col items-start bg-[#cc5500] px-2 md:px-3 py-1 rounded-lg text-xs md:text-sm max-w-[160px] md:max-w-none">
                  <span className="flex items-center gap-2 max-w-full">
                    <FaUser className="flex-shrink-0" /> <span className="truncate">{user.username || user.fullname}</span>
                  </span>
                  {user.fullname && (
                    <span className="text-[10px] md:text-xs opacity-90 max-w-full truncate">{user.fullname}</span>
                  )}
                </span>
                <button onClick={onOpenPasswordChange} className="flex items-center gap-1 bg-[#4CAF50] hover:bg-green-500 px-2 md:px-4 py-1 md:py-2 rounded text-xs md:text-sm font-medium transition">
                  <FaLock /> <span className="hidden md:inline">Change Password</span>
                </button>
                <button onClick={onLogout} className="flex items-center gap-1 bg-[#cc5500] hover:bg-[#b04800] px-2 md:px-4 py-1 md:py-2 rounded text-xs md:text-sm font-medium transition">
                  <FaSignOutAlt /> <span className="hidden sm:inline">Logout</span>
                </button>
              </>
            ) : (
              <>
                <button onClick={onLoginClick} className="flex items-center gap-1 bg-white text-orange-600 px-2 md:px-4 py-1 md:py-2 rounded text-xs md:text-sm font-medium hover:bg-gray-100 transition">
                  <FaSignInAlt /> <span>Sign In</span>
                </button>
                <button onClick={onSignupClick} className="hidden sm:flex items-center gap-1 bg-[#4CAF50] hover:bg-green-400 text-white px-2 md:px-4 py-1 md:py-2 rounded text-xs md:text-sm font-medium transition">
                  Sign Up
                </button>
              </>
            )}
            <button onClick={() => setNavOpen(!navOpen)} className="md:hidden p-2 hover:bg-[#cc5500] rounded">
              {navOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>
        <nav className={`${navOpen ? 'flex' : 'hidden'} md:flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-6 mt-3 pt-3 border-t border-orange-400`}>
          {navItems.map(item => (
            <button key={item.action} onClick={() => { onNavigate(item.action); setNavOpen(false); }} className="hover:underline hover:text-yellow-200 transition text-sm md:text-base w-full md:w-auto text-left">{item.label}</button>
          ))}
          {user && (
            <button onClick={() => { onMyApplicationsClick(); setNavOpen(false); }} className="hover:underline hover:text-yellow-200 transition text-sm md:text-base w-full md:w-auto text-left">My Applications</button>
          )}
        </nav>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-green-500 text-white py-3 md:py-4 px-4 md:px-6 mt-auto">
      <div className="container mx-auto text-center">
        <p className="text-xs md:text-sm">&copy; 2026 የነዳጅና ኢነርጂ ባለሥልጣን የኦንላይን አገልግሎት - Petroleum and Energy Authority Online Service. All rights reserved.</p>
      </div>
    </footer>
  );
}

function ChangePasswordModal({ onPasswordChanged, onLogout, onCancel }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    if (newPassword.length < 4) {
      setError('New password must be at least 4 characters');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/change-password`,
        { currentPassword, newPassword }
      );
      const savedUser = JSON.parse(localStorage.getItem('user'));
      savedUser.must_change_password = false;
      localStorage.setItem('user', JSON.stringify(savedUser));
      toast.success('Password changed successfully!');
      onPasswordChanged();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg p-4 md:p-8 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-[#006400]">Change Your Password</h2>
          {onCancel && (
            <button onClick={onCancel} className="text-gray-500 hover:text-gray-800 text-2xl leading-none" aria-label="Close"><FaTimes /></button>
          )}
        </div>
        {!onCancel && (
          <p className="text-gray-600 mb-4 text-sm md:text-base text-center">You must change your password before continuing. This is required for first login or after a password reset.</p>
        )}
        {error && <p className="text-red-500 text-sm mb-4 text-center">{error}</p>}
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Current Password</label>
            <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="w-full border rounded px-3 py-2" required />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">New Password</label>
            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-full border rounded px-3 py-2" required minLength={4} />
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium mb-1">Confirm New Password</label>
            <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full border rounded px-3 py-2" required />
          </div>
          <button type="submit" disabled={loading} className="w-full bg-[#006400] text-white py-2 rounded hover:bg-green-700 disabled:opacity-50">
            {loading ? 'Changing...' : 'Change Password'}
          </button>
        </form>
        <div className="mt-4 text-center">
          {onCancel ? (
            <button onClick={onCancel} className="text-sm text-gray-500 hover:text-gray-800">
              Cancel
            </button>
          ) : (
            <button onClick={onLogout} className="text-sm text-gray-500 hover:text-red-500 underline">
              Logout
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function DirectFuelForm({ onSubmit, onCancel, initialData = null, isEdit = false, title: formTitle = null }) {
  useFormDraft(() => [formData, setFormData], initialData);
  const [formData, setFormData] = useState({
    organization_name: '',
    project_name: '',
    location: '',
    manager_name: '',
    nationality: '',
    fan_national_id: '',
    region: '',
    subcity: '',
    woreda: '',
    hno: '',
    phone_no: '',
    email: '',
    po_box: '',
    product_type: '',
    product_amount: '',
    no_of_machines: '',
    type_of_machine: '',
    fuel_type: '',
    amount_of_fuel: '',
    monthly_fuel_need: '',
    project_end_date: '',
    supplier_name: '',
    fuel_distributer: '',
    fuel_distributor_letter_no: '',
    fuel_distributor_letter_date: '',
    support_organization_name: '',
    support_org_letter_no: '',
    support_org_letter_date: '',
    id_photo: null,
    application_letter: null,
    support_letter: null,
    aggrement_letter: null,
    remark: '',
    existing_application_letter: '',
    existing_support_letter: '',
    existing_aggrement_letter: '',
    existing_id_photo: '',
    request_date: toEthiopianDateStr(new Date())
  });
  const [idPhotoPreview, setIdPhotoPreview] = useState('');

  const [additionalAttachments, setAdditionalAttachments] = useState([]);

  useEffect(() => {
    if (!formData.id_photo) {
      setIdPhotoPreview('');
      return undefined;
    }

    const previewUrl = URL.createObjectURL(formData.id_photo);
    setIdPhotoPreview(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [formData.id_photo]);

  const handleAddAttachment = () => {
    if (additionalAttachments.length >= 10) {
      toast.warn('You can only add up to 10 attachments');
      return;
    }
    setAdditionalAttachments([...additionalAttachments, { id: Date.now(), name: '', file: null }]);
  };

  const handleAdditionalNameChange = (id, e) => {
    const newAttachments = additionalAttachments.map(att => 
      att.id === id ? { ...att, name: e.target.value } : att
    );
    setAdditionalAttachments(newAttachments);
  };

  const handleAdditionalFileChangeDirect = (id, e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    const newAttachments = additionalAttachments.map(att => 
      att.id === id ? { ...att, file: selectedFile } : att
    );
    setAdditionalAttachments(newAttachments);
  };

  const handleRemoveAttachment = (id) => {
    setAdditionalAttachments(additionalAttachments.filter(att => att.id !== id));
  };

  useEffect(() => {
    if (initialData) {
      setFormData({
        organization_name: initialData.organization_name || '',
        project_name: initialData.project_name || '',
        location: initialData.location || '',
        manager_name: initialData.manager_name || '',
        nationality: initialData.nationality || '',
        fan_national_id: initialData.fan_national_id || '',
        region: initialData.region || '',
        subcity: initialData.subcity || '',
        woreda: initialData.woreda || '',
        hno: initialData.hno || '',
        phone_no: initialData.phone_no || '',
        email: initialData.email || '',
        po_box: initialData.po_box || '',
        product_type: initialData.product_type || '',
        product_amount: initialData.product_amount || '',
        no_of_machines: initialData.no_of_machines || '',
        type_of_machine: initialData.type_of_machine || '',
        fuel_type: initialData.fuel_type || '',
        amount_of_fuel: initialData.amount_of_fuel || '',
        monthly_fuel_need: initialData.monthly_fuel_need || '',
        project_end_date: initialData.project_end_date ? convertDbDateToDisplay(initialData.project_end_date) : '',
        supplier_name: initialData.supplier_name || '',
        fuel_distributer: initialData.fuel_distributer || '',
        fuel_distributor_letter_no: initialData.fuel_distributor_letter_no || '',
        fuel_distributor_letter_date: initialData.fuel_distributor_letter_date ? convertDbDateToDisplay(initialData.fuel_distributor_letter_date) : '',
        support_organization_name: initialData.support_organization_name || '',
        support_org_letter_no: initialData.support_org_letter_no || '',
        support_org_letter_date: initialData.support_org_letter_date ? convertDbDateToDisplay(initialData.support_org_letter_date) : '',
        id_photo: null,
        application_letter: null,
        support_letter: null,
        aggrement_letter: null,
        remark: initialData.remark || '',
        existing_application_letter: initialData.application_letter || '',
        existing_support_letter: initialData.support_letter || '',
        existing_aggrement_letter: initialData.aggrement_letter || '',
        existing_id_photo: initialData.id_photo || '',
        request_date: initialData.request_date ? convertDbDateToDisplay(initialData.request_date) : toEthiopianDateStr(new Date())
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e, field) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid file.');
      e.target.value = '';
      return;
    }
    if (selectedFile && field === 'id_photo' && !['image/jpeg', 'image/png'].includes(selectedFile.type)) {
      toast.error('ID photo must be a JPG or PNG image.');
      e.target.value = '';
      return;
    }
    setFormData({ ...formData, [field]: selectedFile });
  };

  const handleAdditionalFileChange = (e, index) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    const newAttachments = [...additionalAttachments];
    newAttachments[index].file = selectedFile;
    setAdditionalAttachments(newAttachments);
  };

  const handleAdditionalFileNameChange = (e, index) => {
    const newAttachments = [...additionalAttachments];
    newAttachments[index].name = e.target.value;
    setAdditionalAttachments(newAttachments);
  };

  const addAdditionalAttachment = () => {
    if (additionalAttachments.length >= 10) {
      toast.warn('You can only add up to 10 attachments');
      return;
    }
    setAdditionalAttachments([...additionalAttachments, { name: '', file: null }]);
  };

  const removeAdditionalAttachment = (index) => {
    const newAttachments = additionalAttachments.filter((_, i) => i !== index);
    setAdditionalAttachments(newAttachments);
  };

  const DATE_FIELDS = ['request_date', 'project_end_date', 'fuel_distributor_letter_date', 'support_org_letter_date'];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isEdit && !formData.id_photo) {
      toast.error('Please upload an ID photo before submitting.');
      return;
    }
    const formDataToSend = new FormData();
    
    Object.keys(formData).forEach(key => {
      if (formData[key] !== null && key !== 'existing_application_letter' && 
          key !== 'existing_support_letter' && key !== 'existing_aggrement_letter' &&
        key !== 'existing_id_photo' && !DATE_FIELDS.includes(key)) {
        if (formData[key] instanceof File) {
          if (formData[key]) {
            formDataToSend.append(key, formData[key]);
          }
        } else {
          formDataToSend.append(key, formData[key]);
        }
      }
    });
    
    DATE_FIELDS.forEach(key => {
      if (formData[key]) formDataToSend.append(key, ethDateStrToGregStr(formData[key]));
    });
    
    if (isEdit && initialData) {
      formDataToSend.append('existing_application_letter', formData.existing_application_letter);
      formDataToSend.append('existing_support_letter', formData.existing_support_letter);
      formDataToSend.append('aggrement_letter', formData.existing_aggrement_letter);
      formDataToSend.append('existing_id_photo', formData.existing_id_photo || '');
      formDataToSend.append('status', initialData.status || 'pending');
      formDataToSend.append('response_reason', initialData.response_reason || '');
      formDataToSend.append('rejection_reason', initialData.rejection_reason || '');
    }
    
    additionalAttachments.forEach((att, index) => {
      if (att.file) {
        formDataToSend.append(`additional_attachment_${index}_name`, att.name || '');
        formDataToSend.append(`additional_attachment_${index}`, att.file);
      }
    });
    formDataToSend.append('additional_attachments_count', additionalAttachments.filter(a => a.file).length);
    
    onSubmit(formDataToSend, isEdit ? initialData.id : null);
  };

  const inputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]";
  const fileInputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400] bg-white";

  const renderFileInput = (label, field, existingValue) => (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {existingValue && (
        <div className="flex items-center gap-2 mb-2 text-sm text-green-500">
          <FaFile /> Currently uploaded: 
          <a href={fileUrl(existingValue)} target="_blank" rel="noopener noreferrer" className="underline">
            View file
          </a>
        </div>
      )}
      <input type="file" onChange={(e) => handleFileChange(e, field)} className={fileInputClass} accept=".pdf" />
      <p className="text-xs text-gray-500 mt-1">PDF (max 10MB)</p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-[#006400]">
          {isEdit ? 'Edit Service Request' : (formTitle || 'የነዳጅ ቀጥታ ተጠቃሚ ተቋማት ትስስር ፈቃድ አገልግሎት / Service for direct fuel beneficiary')}
        </h2>
        {isEdit && (
          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm">Editing Request</span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-1">የመታወቂያ ፎቶ / ID Photo (3 x 4 inch)</label>
          <div className="relative w-[3in] h-[4in] border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 flex items-center justify-center overflow-hidden">
            {(idPhotoPreview || formData.existing_id_photo) ? (
              <img
                src={idPhotoPreview || fileUrl(formData.existing_id_photo)}
                alt="ID card"
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <label htmlFor="direct-fuel-id-photo" className="pointer-events-none flex flex-col items-center justify-center text-center text-xs text-gray-500 px-3">
                <span className="text-base font-medium text-gray-600">Upload</span>
                <span>3 × 4 inch</span>
              </label>
            )}
            <input
              type="file"
              name="id_photo"
              accept=".jpg,.jpeg,.png,image/jpeg,image/png"
              onChange={(e) => handleFileChange(e, 'id_photo')}
              className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
              id="direct-fuel-id-photo"
            />
            {(idPhotoPreview || formData.existing_id_photo) && (
              <label htmlFor="direct-fuel-id-photo" className="pointer-events-none absolute inset-x-0 bottom-0 bg-black/60 px-2 py-1 text-center text-xs text-white">
                {formData.id_photo ? formData.id_photo.name : 'Click to replace photo'}
              </label>
            )}
          </div>
          {isEdit && formData.existing_id_photo && (
            <p className="text-sm text-gray-600 mt-2">Current: <a href={fileUrl(formData.existing_id_photo)} target="_blank" rel="noopener noreferrer" className="text-green-600 underline">{formData.existing_id_photo.split('/').pop()}</a></p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">የድርጅቱ ሥም / Organization Name *</label>
          <input name="organization_name" value={formData.organization_name} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">ፐሮጀክት/ኢንዱስትሪ ሥም / Project or Industry Name *</label>
          <input name="project_name" value={formData.project_name} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">አድራሻ / Location *</label>
          <input name="location" value={formData.location} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">የስራ አስኪያጅ ስም / Manager Name *</label>
          <input name="manager_name" value={formData.manager_name} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Nationality (ዜግነት) *</label>
          <input name="nationality" value={formData.nationality} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">የፋይዳ ተለዋጭ ቁጥር (FAN) National ID *</label>
          <input name="fan_national_id" value={formData.fan_national_id} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Region (ክልል) *</label>
          <input name="region" value={formData.region} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">ክፍለ ከተማ / Subcity *</label>
          <input name="subcity" value={formData.subcity} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">ወረዳ/ቀበሌ / Wereda or Kebele *</label>
          <input name="woreda" value={formData.woreda} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">የቤት ቁጥር / House No. (HNO) *</label>
          <input name="hno" value={formData.hno} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">ስልክ ቁጥር / Phone No *</label>
          <input name="phone_no" value={formData.phone_no} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">ኢሜይል / Email *</label>
          <input type="email" name="email" value={formData.email} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">ፖስታ ሳጥን / PO Box</label>
          <input name="po_box" value={formData.po_box} onChange={handleChange} className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">የምርት አይነት / Product or Work Type *</label>
          <input name="product_type" value={formData.product_type} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">የምርት መጠን / Product Amount (Work performed by %)</label>
          <input type="number" name="product_amount" value={formData.product_amount} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">የማሽን ብዛት / No. of Machines *</label>
          <input type="number" name="no_of_machines" value={formData.no_of_machines} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">የማሽን አይነት / Type of Machine *</label>
          <input name="type_of_machine" value={formData.type_of_machine} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">የነዳጅ አይነት / Fuel Type *</label>
          <input name="fuel_type" value={formData.fuel_type} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">የነዳጅ መጠን / Amount of Fuel *</label>
          <input name="amount_of_fuel" value={formData.amount_of_fuel} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">ወርሃዊ የነዳጅ ፍጆታ / Monthly Fuel Consumption *</label>
          <input type="number" name="monthly_fuel_need" value={formData.monthly_fuel_need} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">ፐሮጀክቱ የሚያልቅበት ጊዜ / Project End Date *</label>
          <EthiopianDatePicker 
            name="project_end_date"
            value={formData.project_end_date} 
            onChange={handleChange}
            className={inputClass} 
            required 
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">አቅራቢ ድርጅት / Supplier Name *</label>
          <input name="supplier_name" value={formData.supplier_name} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">የነዳጅ አከፋፋይ ድርጅት / Fuel Distributor *</label>
          <input name="fuel_distributer" value={formData.fuel_distributer} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">የነዳጅ አከፋፋይ ደብዳቤ ቁጥር / Fuel distributer letter no</label>
          <input name="fuel_distributor_letter_no" value={formData.fuel_distributor_letter_no} onChange={handleChange} className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">የነዳጅ አከፋፋይ ደብዳቤ የተጻፈበት ቀን / Fuel distributer letter date</label>
          <EthiopianDatePicker 
            name="fuel_distributor_letter_date"
            value={formData.fuel_distributor_letter_date} 
            onChange={handleChange}
            className={inputClass} 
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">የድጋፍ ሰጪ ተቋም ሥም / Supporting Organization Name *</label>
          <input name="support_organization_name" value={formData.support_organization_name} onChange={handleChange} className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">የድጋፍ ሰጪ ተቋም ደብዳቤ ቁጥር / Support organization letter no</label>
          <input name="support_org_letter_no" value={formData.support_org_letter_no} onChange={handleChange} className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">የድጋፍ ሰጪ ተቋም ደብዳቤ የተጻፈበት ቀን / Support organization letter date</label>
          <EthiopianDatePicker 
            name="support_org_letter_date"
            value={formData.support_org_letter_date} 
            onChange={handleChange}
            className={inputClass} 
          />
        </div>
        {renderFileInput('የማመልከቻ ደብዳቤ / Application Letter *', 'application_letter', formData.existing_application_letter)}
        {renderFileInput('የድጋፍ ደብዳቤ / Support Letter', 'support_letter', formData.existing_support_letter)}
        {renderFileInput('የስምምነት ደብዳቤ / Agreement Letter', 'aggrement_letter', formData.existing_aggrement_letter)}
        
        <div className="col-span-2 mt-4">
          <button
            type="button"
            onClick={handleAddAttachment}
            className="text-[#006400] hover:text-green-500 flex items-center gap-2 text-sm font-medium"
          >
            <FaPaperPlane /> Add Attachment
          </button>
        </div>
        
        {additionalAttachments.map((att, index) => (
          <div key={att.id} className="col-span-2 flex gap-2 items-start">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Attachment name"
                value={att.name || ''}
                onChange={(e) => handleAdditionalNameChange(att.id, e)}
                className={inputClass}
              />
            </div>
            <div className="flex-1">
              <input
                type="file"
                onChange={(e) => handleAdditionalFileChangeDirect(att.id, e)}
                className={fileInputClass}
                accept=".pdf"
              />
            </div>
            <button
              type="button"
              onClick={() => handleRemoveAttachment(att.id)}
              className="text-red-600 hover:text-red-800 p-2"
            >
              <FaTimes />
            </button>
          </div>
        ))}
        
        <div>
          <label className="block text-sm font-medium mb-1">Remark</label>
          <input name="remark" value={formData.remark} onChange={handleChange} className={inputClass} />
        </div>
      </div>
      <div className="mb-6">
        <label className="block text-sm font-medium mb-1">ያመለከቱበት ቀን / Request date</label>
        <EthiopianDatePicker 
          name="request_date"
          value={formData.request_date} 
          onChange={handleChange}
          className="w-full border rounded px-3 py-2" 
          required 
        />
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2">
          <FaTimes /> Cancel
        </button>
        <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 flex items-center gap-2">
          <FaPaperPlane /> {isEdit ? 'Update Request' : 'Submit'}
        </button>
      </div>
    </form>
  );
}

function FuelOrderCancellationServiceForm({ onSubmit, onCancel, initialData = null, isEdit = false, title: formTitle = null }) {
  useFormDraft(() => [formData, setFormData], initialData);
  const [formData, setFormData] = useState({
    fuel_company_name: '',
    letter_issued_date: '',
    letter_number: '',
    fuel_unloading_site: '',
    reason_for_cancellation: '',
    request_date: toEthiopianDateStr(new Date())
  });
  const [applicationLetter, setApplicationLetter] = useState(null);

  useEffect(() => {
    if (initialData) {
      setFormData({
        fuel_company_name: initialData.fuel_company_name || '',
        letter_issued_date: initialData.letter_issued_date ? convertDbDateToDisplay(initialData.letter_issued_date) : '',
        letter_number: initialData.letter_number || '',
        fuel_unloading_site: initialData.fuel_unloading_site || '',
        reason_for_cancellation: initialData.reason_for_cancellation || '',
        request_date: initialData.request_date ? convertDbDateToDisplay(initialData.request_date) : toEthiopianDateStr(new Date())
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    const DATE_FIELDS = ['letter_issued_date', 'request_date'];

    Object.keys(formData).forEach(key => {
      if (formData[key] !== null && !DATE_FIELDS.includes(key)) {
        formDataToSend.append(key, formData[key]);
      }
    });

    DATE_FIELDS.forEach(key => {
      if (formData[key]) formDataToSend.append(key, ethDateStrToGregStr(formData[key]));
    });

    if (isEdit && initialData) {
      formDataToSend.append('status', initialData.status || 'pending');
      formDataToSend.append('response_reason', initialData.response_reason || '');
      formDataToSend.append('rejection_reason', initialData.rejection_reason || '');
    }

    if (applicationLetter) {
      formDataToSend.append('application_letter', applicationLetter);
    } else if (isEdit && initialData && initialData.application_letter) {
      formDataToSend.append('existing_application_letter', initialData.application_letter);
    }

    onSubmit(formDataToSend, isEdit ? initialData.id : null);
  };

  const inputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]";

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-[#006400]">
          {isEdit ? 'Edit Service Request' : (formTitle || 'የነዳጅ ጭነት ትእዛዝ አገልግሎት ማቋረጥ / Fuel delivery order cancellation service')}
        </h2>
        {isEdit && (
          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm">Editing Request</span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">የነዳጅ ካምፓኒ ሥም / Fuel company name *</label>
          <input name="fuel_company_name" value={formData.fuel_company_name} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">ደብዳቤ የተጻፈበት ቀን / Letter issued date *</label>
          <EthiopianDatePicker name="letter_issued_date" value={formData.letter_issued_date} onChange={handleChange} className="w-full border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">የደብዳቤ ቁጥር / Letter number *</label>
          <input name="letter_number" value={formData.letter_number} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">የነዳጅ ጭነት የሚራገፍበት ቦታ / Fuel unloading site *</label>
          <input name="fuel_unloading_site" value={formData.fuel_unloading_site} onChange={handleChange} className={inputClass} required />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-1">የሚሰረዝበት ምክንያት / Reason for cancellation *</label>
          <textarea name="reason_for_cancellation" value={formData.reason_for_cancellation} onChange={handleChange} className={inputClass} rows="4" required />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-1">የደብዳቤ ቅጂ ማያያዣ / Attachment (PDF)</label>
          <input
            type="file"
            accept=".pdf,application/pdf"
            onChange={(e) => { const f = e.target.files[0]; if (f && f.size === 0) { toast.error('The selected file is empty. Please choose a valid PDF document.'); e.target.value = ''; return; } setApplicationLetter(f || null); }}
            className={inputClass}
          />
          {applicationLetter && (
            <p className="text-xs text-green-600 mt-1">Selected: {applicationLetter.name}</p>
          )}
          {!applicationLetter && isEdit && initialData && initialData.application_letter && (
            <a href={fileUrl(initialData.application_letter)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 underline mt-1 inline-block">
              View current attachment
            </a>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">ያመለከቱበት ቀን / Application date *</label>
          <EthiopianDatePicker name="request_date" value={formData.request_date} onChange={handleChange} className="w-full border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2">
          <FaTimes /> Cancel
        </button>
        <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 flex items-center gap-2">
          <FaPaperPlane /> {isEdit ? 'Update Request' : 'Submit'}
        </button>
      </div>
    </form>
  );
}

function VehicleBreakdownReportingForm({ onSubmit, onCancel, initialData = null, isEdit = false, title: formTitle = null }) {
  useFormDraft(() => [formData, setFormData], initialData);
  const DATETIME_FIELDS = ['fuel_load_date', 'letter_date', 'breakdown_date'];
  const [formData, setFormData] = useState({
    requester_name: '',
    fuel_load_date: '',
    fuel_load_amount: '',
    fuel_unloading_site: '',
    plate_number: '',
    trailer_plate_number: '',
    order_number: '',
    fdc_number: '',
    letter_date: '',
    letter_number: '',
    breakdown_location: '',
    breakdown_date: '',
    breakdown_description: '',
    report_date: toEthiopianDateStr(new Date())
  });
  const [applicationLetter, setApplicationLetter] = useState(null);

  const splitDbDateTime = (val) => {
    if (!val) return '';
    const s = String(val).trim();
    return convertDbDateToDisplay(s.length >= 10 ? s.slice(0, 10) : s);
  };

  useEffect(() => {
    if (initialData) {
      const next = {
        requester_name: initialData.requester_name || '',
        fuel_load_date: initialData.fuel_load_date ? splitDbDateTime(initialData.fuel_load_date) : '',
        fuel_load_amount: initialData.fuel_load_amount != null ? String(initialData.fuel_load_amount) : '',
        fuel_unloading_site: initialData.fuel_unloading_site || '',
        plate_number: initialData.plate_number || '',
        trailer_plate_number: initialData.trailer_plate_number || '',
        order_number: initialData.order_number != null ? String(initialData.order_number) : '',
        fdc_number: initialData.fdc_number != null ? String(initialData.fdc_number) : '',
        letter_date: initialData.letter_date ? splitDbDateTime(initialData.letter_date) : '',
        letter_number: initialData.letter_number || '',
        breakdown_location: initialData.breakdown_location || '',
        breakdown_date: initialData.breakdown_date ? splitDbDateTime(initialData.breakdown_date) : '',
        breakdown_description: initialData.breakdown_description || '',
        report_date: initialData.report_date || toEthiopianDateStr(new Date())
      };
      setFormData(next);
    }
  }, [initialData]);

  useEffect(() => {
    setFormData((current) => {
      const legacyFields = {
        company_name: 'company_name_en',
        company_city: 'company_city_en',
        company_subcity: 'company_subcity_en',
        company_woreda: 'company_woreda_en',
        company_zone: 'company_zone_en',
        company_landmark: 'company_landmark_en'
      };
      const next = { ...current };
      let changed = false;
      Object.entries(legacyFields).forEach(([legacyName, englishName]) => {
        if (!next[englishName] && next[legacyName]) {
          next[englishName] = next[legacyName];
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [formData.company_name, formData.company_city, formData.company_subcity, formData.company_woreda, formData.company_zone, formData.company_landmark]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    const DATETIME_KEYS = new Set(DATETIME_FIELDS);

    Object.keys(formData).forEach(key => {
      if (DATETIME_KEYS.has(key)) return;
      if (formData[key] !== null && formData[key] !== undefined && String(formData[key]).trim() !== '') {
        formDataToSend.append(key, formData[key]);
      }
    });

    DATETIME_FIELDS.forEach(key => {
      if (formData[key]) {
        formDataToSend.append(key, ethDateStrToGregStr(formData[key]));
      }
    });

    if (isEdit && initialData) {
      formDataToSend.append('status', initialData.status || 'pending');
      formDataToSend.append('response_reason', initialData.response_reason || '');
      formDataToSend.append('rejection_reason', initialData.rejection_reason || '');
    }

    if (applicationLetter) {
      formDataToSend.append('application_letter', applicationLetter);
    } else if (isEdit && initialData && initialData.application_letter) {
      formDataToSend.append('existing_application_letter', initialData.application_letter);
    }

    onSubmit(formDataToSend, isEdit ? initialData.id : null);
  };

  const inputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]";

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-[#006400]">
          {isEdit ? 'Edit Service Request' : (formTitle || 'ብልሽት የገጠመውን ተሸከርካሪ ማሳወቅ አገልግሎት / Vehicle Breakdown Reporting Service')}
        </h2>
        {isEdit && (
          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm">Editing Request</span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-1">የጠየቀው ካምፓኒ / ግለሰብ ሥም / Requester company or person name *</label>
          <input name="requester_name" value={formData.requester_name} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">ነዳጅ የተጫነበት ቀን / Fuel loading date *</label>
          <EthiopianDatePicker name="fuel_load_date" value={formData.fuel_load_date} onChange={handleChange} className="w-full border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">የተጫነው ነዳጅ መጠን / Fuel load amount (liters) *</label>
          <input type="number" name="fuel_load_amount" value={formData.fuel_load_amount} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">ነዳጅ የሚራገፍበት ቦታ / Fuel unloading site *</label>
          <input name="fuel_unloading_site" value={formData.fuel_unloading_site} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">የሠሌዳ ቁጥር / Plate number *</label>
          <input name="plate_number" value={formData.plate_number} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">የተሳቢ ሠሌዳ ቁጥር / Trailer plate number</label>
          <input name="trailer_plate_number" value={formData.trailer_plate_number} onChange={handleChange} className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">የትዕዛዝ ቁጥር / Order number *</label>
          <input type="number" name="order_number" value={formData.order_number} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">የFDC ቁጥር / FDC number *</label>
          <input type="number" name="fdc_number" value={formData.fdc_number} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">ማመልከቻ / ደብዳቤ የተጻፈበት ቀን / Application letter date *</label>
          <EthiopianDatePicker name="letter_date" value={formData.letter_date} onChange={handleChange} className="w-full border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">የማመልከቻ / ደብዳቤ ቁጥር / Application letter number *</label>
          <input name="letter_number" value={formData.letter_number} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">ብልሽቱ የተከሰተበት ቦታ / Breakdown location *</label>
          <input name="breakdown_location" value={formData.breakdown_location} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">ብልሽቱ የተከሰሰበት ቀን / Breakdown date *</label>
          <EthiopianDatePicker name="breakdown_date" value={formData.breakdown_date} onChange={handleChange} className="w-full border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">የብልሽቱ ዓይነት በአጭሩ / Brief description of breakdown *</label>
          <input name="breakdown_description" value={formData.breakdown_description} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">ያመለከቱበት ቀን / Reporting date *</label>
          <input name="report_date" value={formData.report_date} onChange={handleChange} className={inputClass} required />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-1">የደብዳቤ ቅጂ ማያያዣ / Attachment (PDF, optional)</label>
          <input
            type="file"
            accept=".pdf,application/pdf"
            onChange={(e) => { const f = e.target.files[0]; if (f && f.size === 0) { toast.error('The selected file is empty. Please choose a valid PDF document.'); e.target.value = ''; return; } setApplicationLetter(f || null); }}
            className={inputClass}
          />
          {applicationLetter && (
            <p className="text-xs text-green-600 mt-1">Selected: {applicationLetter.name}</p>
          )}
          {!applicationLetter && isEdit && initialData && initialData.application_letter && (
            <a href={fileUrl(initialData.application_letter)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 underline mt-1 inline-block">
              View current attachment
            </a>
          )}
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2">
          <FaTimes /> Cancel
        </button>
        <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 flex items-center gap-2">
          <FaPaperPlane /> {isEdit ? 'Update Request' : 'Submit'}
        </button>
      </div>
    </form>
  );
}

function FuelDeliveryExtensionForm({ onSubmit, onCancel, initialData = null, isEdit = false, onRegister = null, title: formTitle = null }) {
  useFormDraft(() => [formData, setFormData], initialData);
  const [formData, setFormData] = useState({
    reference_no: '',
    extension_start_date: '',
    extension_end_date: '',
    fuel_quantity: '',
    request_date: toEthiopianDateStr(new Date())
  });
  const [searchedCompany, setSearchedCompany] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');

  const fetchCompanyByRef = async (refNo) => {
    if (!refNo.trim()) return;
    setLookupLoading(true); setLookupError('');
    try {
      const res = await axios.get(`${API_URL}/direct-fuel-beneficiary?reference_no=${refNo}`);
      if (res.data.length > 0) { setSearchedCompany(res.data[0]); }
      else { setLookupError('No record found with this reference number'); setSearchedCompany(null); }
    } catch (err) { setLookupError('Failed to lookup reference'); setSearchedCompany(null); }
    finally { setLookupLoading(false); }
  };

  useEffect(() => {
    if (initialData) {
      const next = {
        reference_no: initialData.reference_no || '',
        extension_start_date: initialData.extension_start_date ? convertDbDateToDisplay(initialData.extension_start_date.slice(0, 10)) : '',
        extension_end_date: initialData.extension_end_date ? convertDbDateToDisplay(initialData.extension_end_date.slice(0, 10)) : '',
        fuel_quantity: initialData.fuel_quantity != null ? String(initialData.fuel_quantity) : '',
        request_date: initialData.request_date ? initialData.request_date.slice(0, 10) : toEthiopianDateStr(new Date())
      };
      setFormData(next);
      if (initialData.existing_ref_no) {
        axios.get(`${API_URL}/direct-fuel-beneficiary?reference_no=${initialData.existing_ref_no}`).then(res => {
          if (res.data.length > 0) setSearchedCompany(res.data[0]);
        }).catch(() => {});
      }
    }
  }, [initialData]);

  const handleLookup = () => { fetchCompanyByRef(formData.reference_no); };
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.name === 'reference_no') { setSearchedCompany(null); setLookupError(''); }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!searchedCompany) { toast.warning('Please lookup the reference number first'); return; }
    const formDataToSend = new FormData();
    formDataToSend.append('reference_no', formData.reference_no);
    formDataToSend.append('existing_ref_no', searchedCompany.reference_no);
    formDataToSend.append('requester_name', searchedCompany.manager_name || searchedCompany.organization_name || '');
    formDataToSend.append('extension_start_date', ethDateStrToGregStr(formData.extension_start_date));
    formDataToSend.append('extension_end_date', ethDateStrToGregStr(formData.extension_end_date));
    formDataToSend.append('fuel_quantity', formData.fuel_quantity);
    formDataToSend.append('request_date', ethDateStrToGregStr(formData.request_date));
    if (isEdit && initialData) {
      formDataToSend.append('status', initialData.status || 'pending');
      formDataToSend.append('response_reason', initialData.response_reason || '');
      formDataToSend.append('rejection_reason', initialData.rejection_reason || '');
    }
    onSubmit(formDataToSend, isEdit ? initialData.id : null);
  };

  const inputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-[#006400]">
          {isEdit ? 'Edit Service Request' : (formTitle || 'የነዳጅ አቅርቦት ማራዘም ጥያቄ አገልግሎት / Fuel Delivery Extension Service')}
        </h2>
        {isEdit && (
          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm">Editing Request</span>
        )}
      </div>

      <div className="mb-4 p-4 bg-yellow-50 rounded border border-yellow-200 flex items-center justify-between">
        <p className="text-sm text-yellow-800">ከዚህ ቀደም በሲስተም ውስጥ ካልተመዘገቡ ይመዝገቡ የሚለውን ቁልፍ ይጫኑ / If you are not previously registered in the system, click the Register button.</p>
        {onRegister && (
          <button type="button" onClick={onRegister} className="ml-4 whitespace-nowrap bg-[#006400] text-white px-4 py-2 rounded hover:bg-green-700 text-sm font-medium flex-shrink-0">
            ይመዝገቡ / Register
          </button>
        )}
      </div>

      <div className="mb-4 p-4 bg-blue-50 rounded border border-blue-200">
        <p className="text-sm text-blue-800">Enter the reference number to look up its details, then fill in the extension information. The linked record details are shown for reference only.</p>
      </div>

      <div className="mb-6">
        <label className={labelClass}>Reference No (የማመልከቻ ቁጥር) *</label>
        <div className="flex gap-2">
          <input type="text" name="reference_no" value={formData.reference_no} onChange={handleChange} className={inputClass + " flex-1"} placeholder="e.g. DBF-XXXXXXXX" required />
          <button type="button" onClick={handleLookup} disabled={lookupLoading || !formData.reference_no.trim()} className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 disabled:bg-gray-400 flex items-center gap-2">
            <FaSearch /> {lookupLoading ? 'Searching...' : 'Search'}
          </button>
        </div>
        {lookupError && <p className="text-red-600 text-sm mt-1">{lookupError}</p>}
      </div>

      {searchedCompany && (
        <div className="mb-6 p-4 bg-gray-50 rounded border">
          <h4 className="font-bold text-gray-700 mb-3">የነዳጅ ቀጥታ ተጠቃሚ ተቋማት ትስስር ፈቃድ አገልግሎት / Service for direct fuel beneficiary (Read-only)</h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><strong>Organization Name:</strong> {searchedCompany.organization_name || 'N/A'}</div>
            <div><strong>Project Name:</strong> {searchedCompany.project_name || 'N/A'}</div>
            <div><strong>Location:</strong> {searchedCompany.location || 'N/A'}</div>
            <div><strong>Manager Name:</strong> {searchedCompany.manager_name || 'N/A'}</div>
            <div><strong>Nationality:</strong> {searchedCompany.nationality || 'N/A'}</div>
            <div><strong>National ID:</strong> {searchedCompany.fan_national_id || 'N/A'}</div>
            <div><strong>Region:</strong> {searchedCompany.region || 'N/A'}</div>
            <div><strong>Sub City:</strong> {searchedCompany.subcity || 'N/A'}</div>
            <div><strong>Woreda:</strong> {searchedCompany.woreda || 'N/A'}</div>
            <div><strong>House No:</strong> {searchedCompany.hno || 'N/A'}</div>
            <div><strong>Phone No:</strong> {searchedCompany.phone_no || 'N/A'}</div>
            <div><strong>Email:</strong> {searchedCompany.email || 'N/A'}</div>
            <div><strong>Product Type:</strong> {searchedCompany.product_type || 'N/A'}</div>
            <div><strong>Product Amount:</strong> {searchedCompany.product_amount != null ? searchedCompany.product_amount : 'N/A'}</div>
            <div><strong>No of Machines:</strong> {searchedCompany.no_of_machines != null ? searchedCompany.no_of_machines : 'N/A'}</div>
            <div><strong>Type of Machine:</strong> {searchedCompany.type_of_machine || 'N/A'}</div>
            <div><strong>Fuel Type:</strong> {searchedCompany.fuel_type || 'N/A'}</div>
            <div><strong>Amount of Fuel:</strong> {searchedCompany.amount_of_fuel != null ? searchedCompany.amount_of_fuel : 'N/A'}</div>
            <div><strong>Monthly Fuel Need:</strong> {searchedCompany.monthly_fuel_need != null ? searchedCompany.monthly_fuel_need : 'N/A'}</div>
            <div><strong>Supplier Name:</strong> {searchedCompany.supplier_name || 'N/A'}</div>
            <div><strong>Fuel Distributor:</strong> {searchedCompany.fuel_distributer || 'N/A'}</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>የሚራዘምለት መነሻ ቀን / Extension start date *</label>
          <EthiopianDatePicker name="extension_start_date" value={formData.extension_start_date} onChange={handleChange} className="w-full border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
        </div>
        <div>
          <label className={labelClass}>የሚራዘምለት መጨረሻ ቀን / Extension end date *</label>
          <EthiopianDatePicker name="extension_end_date" value={formData.extension_end_date} onChange={handleChange} className="w-full border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
        </div>
        <div>
          <label className={labelClass}>የነዳጅ መጠን / Fuel quantity (liters) *</label>
          <input type="number" name="fuel_quantity" value={formData.fuel_quantity} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className={labelClass}>ያመለከቱበት ቀን / Request date *</label>
          <input name="request_date" value={formData.request_date} onChange={handleChange} className={inputClass} required />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2">
          <FaTimes /> Cancel
        </button>
        <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 flex items-center gap-2">
          <FaPaperPlane /> {isEdit ? 'Update Request' : 'Submit'}
        </button>
      </div>
    </form>
  );
}

function ElectricVehicleChargingForm({ onSubmit, onCancel, initialData = null, isEdit = false, title: formTitle = null }) {
  useFormDraft(() => [formData, setFormData], initialData);
  const [formData, setFormData] = useState({
    license_type: '',
    license_category: '',
    company_name_am: '',
    company_name_en: '',
    company_address_am: '',
    company_address_en: '',
    company_city_am: '',
    company_city_en: '',
    company_subcity_am: '',
    company_subcity_en: '',
    company_woreda_am: '',
    company_woreda_en: '',
    company_zone_am: '',
    company_zone_en: '',
    company_phone_no: '',
    company_po_box: '',
    company_tin_no: '',
    company_fax_no: '',
    company_email: '',
    company_landmark_am: '',
    company_landmark_en: '',
    site_region: '',
    site_city: '',
    site_zone: '',
    site_subcity: '',
    site_woreda: '',
    site_house_no: '',
    site_phone_no: '',
    gm_name: '',
    gm_job_title: '',
    gm_city: '',
    gm_phone_no: '',
    gm_email: '',
    charging_capacity_kw: '',
    electric_supply_source: '',
    estimated_start_date: '',
    request_date: toEthiopianDateStr(new Date()),
    id_photo: null,
    existing_id_photo: ''
  });

  const splitDbDate = (val) => {
    if (!val) return '';
    const s = String(val).trim();
    return convertDbDateToDisplay(s.length >= 10 ? s.slice(0, 10) : s);
  };

  useEffect(() => {
    if (initialData) {
      const next = {
        license_type: initialData.license_type || '',
        license_category: initialData.license_category || '',
        company_name_am: initialData.company_name_am || '',
        company_name_en: initialData.company_name_en || initialData.company_name || '',
        company_address_am: initialData.company_address_am || '',
        company_address_en: initialData.company_address_en || '',
        company_city_am: initialData.company_city_am || '',
        company_city_en: initialData.company_city_en || initialData.company_city || '',
        company_subcity_am: initialData.company_subcity_am || '',
        company_subcity_en: initialData.company_subcity_en || initialData.company_subcity || '',
        company_woreda_am: initialData.company_woreda_am || '',
        company_woreda_en: initialData.company_woreda_en || initialData.company_woreda || '',
        company_zone_am: initialData.company_zone_am || '',
        company_zone_en: initialData.company_zone_en || initialData.company_zone || '',
        company_phone_no: initialData.company_phone_no != null ? String(initialData.company_phone_no) : '',
        company_po_box: initialData.company_po_box != null ? String(initialData.company_po_box) : '',
        company_tin_no: initialData.company_tin_no != null ? String(initialData.company_tin_no) : '',
        company_fax_no: initialData.company_fax_no != null ? String(initialData.company_fax_no) : '',
        company_email: initialData.company_email || '',
        company_landmark_am: initialData.company_landmark_am || '',
        company_landmark_en: initialData.company_landmark_en || initialData.company_landmark || '',
        site_region: initialData.site_region || '',
        site_city: initialData.site_city || '',
        site_zone: initialData.site_zone || '',
        site_subcity: initialData.site_subcity || '',
        site_woreda: initialData.site_woreda || '',
        site_house_no: initialData.site_house_no != null ? String(initialData.site_house_no) : '',
        site_phone_no: initialData.site_phone_no != null ? String(initialData.site_phone_no) : '',
        gm_name: initialData.gm_name || '',
        gm_job_title: initialData.gm_job_title || '',
        gm_city: initialData.gm_city || '',
        gm_phone_no: initialData.gm_phone_no != null ? String(initialData.gm_phone_no) : '',
        gm_email: initialData.gm_email || '',
        charging_capacity_kw: initialData.charging_capacity_kw != null ? String(initialData.charging_capacity_kw) : '',
        electric_supply_source: initialData.electric_supply_source || '',
        estimated_start_date: initialData.estimated_start_date ? splitDbDate(initialData.estimated_start_date) : '',
        request_date: initialData.request_date ? splitDbDate(initialData.request_date) : toEthiopianDateStr(new Date()),
        id_photo: null,
        existing_id_photo: initialData.id_photo || ''
      };
      setFormData(next);
    }
  }, [initialData]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid ID photo/document.');
      e.target.value = '';
      return;
    }
    setFormData({ ...formData, id_photo: selectedFile || null });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    Object.keys(formData).forEach(key => {
      if (key === 'estimated_start_date' || key === 'request_date' || key === 'id_photo' || key === 'existing_id_photo') return;
      if (formData[key] !== null && formData[key] !== undefined && String(formData[key]).trim() !== '') {
        formDataToSend.append(key, formData[key]);
      }
    });
    if (formData.estimated_start_date) formDataToSend.append('estimated_start_date', ethDateStrToGregStr(formData.estimated_start_date));
    formDataToSend.append('request_date', ethDateStrToGregStr(formData.request_date));
    if (formData.id_photo && typeof formData.id_photo === 'object') {
      formDataToSend.append('id_photo', formData.id_photo);
    }
    if (isEdit && initialData) {
      formDataToSend.append('status', initialData.status || 'pending');
      formDataToSend.append('response_reason', initialData.response_reason || '');
      formDataToSend.append('rejection_reason', initialData.rejection_reason || '');
    }
    onSubmit(formDataToSend, isEdit ? initialData.id : null);
  };

  const inputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";
  const sectionClass = "md:col-span-2 mt-4 pt-3 border-t-2 border-[#006400] text-[#006400] font-bold text-base";

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-[#006400]">
          {isEdit ? 'Edit Service Request' : (formTitle || 'የኤሌክትሪክ ተሽከርካሪ ቻርጅ መሙሌት አገልግሎት / Electric Vehicle Charging Service License Request')}
        </h2>
        {isEdit && (
          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm">Editing Request</span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        <div className={sectionClass}>1. የፈቃድ ዓይነት / Type of License</div>
        <div className="md:col-span-2">
          <label className={labelClass}>የፈቃድ ዓይነት / Type of License *</label>
          <select name="license_type" value={formData.license_type} onChange={handleChange} className={inputClass} required>
            <option value="">--- Select ---</option>
            <option value="Public Electric Vehicle Charging Service License">የህዝብ የኤሌክትሪክ ተሽከርካሪ የኃይል መሙያ አገልግሎት ፈቃድ / Public Electric Vehicle Charging Service License</option>
            <option value="Self-Charging License for Non-Commercial Purposes CAPTIVE">ለንግድ ላልሆነ ዓላማ ለራስ ኃይል መሙያ ፈቃድ / Self-Charging License for Non-Commercial Purposes CAPTIVE</option>
          </select>
        </div>

        <div className={sectionClass}>2. የፍቃድ ደረጃ / License Category</div>
        <div className="md:col-span-2">
          <label className={labelClass}>የፍቃድ ደረጃ / License Category *</label>
          <select name="license_category" value={formData.license_category} onChange={handleChange} className={inputClass} required>
            <option value="">--- Select ---</option>
            <option value="Having a charging capacity <= 100 kW">ከ100 ኪ.ዋ {'< ='} ኃይል የመሙላት አቅም / Having a charging capacity {'< ='} 100 kW</option>
            <option value="Having a charging capacity >= 100 kW">ከ100 ኪ.ዋ {'> ='} ኃይል የመሙላት አቅም / Having a charging capacity {'> ='} 100 kW</option>
          </select>
        </div>

        <div className={sectionClass}>2b. የመታወቂያ ፎቶ / ID Photo</div>
        <div className="md:col-span-2">
          <label className={labelClass}>የመታወቂያ ፎቶ / ID Photo *</label>
          <input type="file" name="id_photo" accept="image/*,.pdf" onChange={handleFileChange} className={inputClass} required={!isEdit} placeholder="የመታወቂያ ፎቶ ያስገቡ / Attach ID Photo" />
          <p className="text-xs text-gray-500 mt-1">የመታወቂያ ፎቶ ያስገቡ (jpg, png, pdf) / Attach your ID photo here (jpg, png, pdf)</p>
          {isEdit && formData.existing_id_photo && (
            <p className="text-sm text-gray-600 mt-1">Current: <a href={fileUrl(formData.existing_id_photo)} target="_blank" rel="noopener noreferrer" className="text-green-600 underline">{formData.existing_id_photo.split('/').pop()}</a></p>
          )}
        </div>

        <div className={sectionClass}>3. የአገልግሎት አቅራቢዉ /የኩባንያዉ/ አድራሻ / Address of the Service Provider / Company</div>
        <div>
          <label className={labelClass}>አድራሻ (አማ)</label>
          <input name="company_address_am" value={formData.company_address_am} onChange={handleChange} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Address (En)</label>
          <input name="company_address_en" value={formData.company_address_en} onChange={handleChange} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>ሥም (አማ) *</label>
          <input name="company_name_am" value={formData.company_name_am} onChange={handleChange} className={inputClass} required={!isEdit} />
        </div>
        <div>
          <label className={labelClass}>Name (En) *</label>
          <input name="company_name_en" value={formData.company_name_en} onChange={handleChange} className={inputClass} required={!isEdit} />
        </div>
        <div>
          <label className={labelClass}>ከተማ (አማ) *</label>
          <input name="company_city_am" value={formData.company_city_am} onChange={handleChange} className={inputClass} required={!isEdit} />
        </div>
        <div>
          <label className={labelClass}>City (En) *</label>
          <input name="company_city_en" value={formData.company_city_en} onChange={handleChange} className={inputClass} required={!isEdit} />
        </div>
        <div>
          <label className={labelClass}>ክፍለ ከተማ (አማ) *</label>
          <input name="company_subcity_am" value={formData.company_subcity_am} onChange={handleChange} className={inputClass} required={!isEdit} />
        </div>
        <div>
          <label className={labelClass}>Sub-City (En) *</label>
          <input name="company_subcity_en" value={formData.company_subcity_en} onChange={handleChange} className={inputClass} required={!isEdit} />
        </div>
        <div>
          <label className={labelClass}>ወረዳ (አማ) *</label>
          <input name="company_woreda_am" value={formData.company_woreda_am} onChange={handleChange} className={inputClass} required={!isEdit} />
        </div>
        <div>
          <label className={labelClass}>Woreda (En) *</label>
          <input name="company_woreda_en" value={formData.company_woreda_en} onChange={handleChange} className={inputClass} required={!isEdit} />
        </div>
        <div>
          <label className={labelClass}>ዞን (አማ)</label>
          <input name="company_zone_am" value={formData.company_zone_am} onChange={handleChange} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Zone (En)</label>
          <input name="company_zone_en" value={formData.company_zone_en} onChange={handleChange} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>ስልክ ቁ / Phone No *</label>
          <input type="number" name="company_phone_no" value={formData.company_phone_no} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className={labelClass}>የፓ.ሳ.ቁ / P.O. Box</label>
          <input type="number" name="company_po_box" value={formData.company_po_box} onChange={handleChange} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>የግ.ከ.ቁ / TIN No</label>
          <input type="number" name="company_tin_no" value={formData.company_tin_no} onChange={handleChange} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>ፋክስ ቁጥር / Fax No</label>
          <input type="number" name="company_fax_no" value={formData.company_fax_no} onChange={handleChange} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>ኢሜይል / Email *</label>
          <input type="email" name="company_email" value={formData.company_email} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className={labelClass}>የቦታዉ ልዩ ስም (አመላካች) (አማ)</label>
          <input name="company_landmark_am" value={formData.company_landmark_am} onChange={handleChange} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Specific Name/Landmark of the Location (En)</label>
          <input name="company_landmark_en" value={formData.company_landmark_en} onChange={handleChange} className={inputClass} />
        </div>

        <div className={sectionClass}>4. Address of the Charging Service Location</div>
        <div>
          <label className={labelClass}>ክልል / Region *</label>
          <input name="site_region" value={formData.site_region} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className={labelClass}>ከተማ / City *</label>
          <input name="site_city" value={formData.site_city} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className={labelClass}>ዞን / Zone</label>
          <input name="site_zone" value={formData.site_zone} onChange={handleChange} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>ክፍለ ከተማ / Sub-City *</label>
          <input name="site_subcity" value={formData.site_subcity} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className={labelClass}>ወረዳ / Woreda *</label>
          <input name="site_woreda" value={formData.site_woreda} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className={labelClass}>የቤት ቁ / House No</label>
          <input name="site_house_no" value={formData.site_house_no} onChange={handleChange} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>ስልክ ቁ / Phone No</label>
          <input type="number" name="site_phone_no" value={formData.site_phone_no} onChange={handleChange} className={inputClass} />
        </div>

        <div className={sectionClass}>5. Name and Address of the Applicant's or Service Provider's/Company's General Manager</div>
        <div>
          <label className={labelClass}>ስም / Name *</label>
          <input name="gm_name" value={formData.gm_name} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className={labelClass}>የስራ ኃላፊነት / Job Title/Position *</label>
          <input name="gm_job_title" value={formData.gm_job_title} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className={labelClass}>ከተማ / City</label>
          <input name="gm_city" value={formData.gm_city} onChange={handleChange} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>ስልክ ቁጥር / Phone Number *</label>
          <input type="number" name="gm_phone_no" value={formData.gm_phone_no} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className={labelClass}>ኢ.ሜ.ል / Email *</label>
          <input type="email" name="gm_email" value={formData.gm_email} onChange={handleChange} className={inputClass} required />
        </div>

        <div className={sectionClass}>6. ለህዝብ የቻርጅ መሙያ አገልግሎት ፈቃድ ማመልከቻ ፍቃድን ለማግኘት የሚያስፈልጉ ልዩ መስፈርቶች ከ 5.1 እስከ 5.3 ያሉት መሟላት ይጠበቅባቸዋል / Special Requirements for Public Charging Service License Application</div>
        <div>
          <label className={labelClass}>የቻርጅ መሙያ አገልግሎቱ አቅም በኪሎ ዋት / ኬ.ቪ.ኤ / Capacity of the charging service in KW / KVA *</label>
          <input name="charging_capacity_kw" value={formData.charging_capacity_kw} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className={labelClass}>ለታሰበዉ የቻርጅ የመሙያ አገልግሎት የኤሌክትሪክ አቅርቦት ምንጭ / Source of electric supply for the intended charging service *</label>
          <input name="electric_supply_source" value={formData.electric_supply_source} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className={labelClass}>አመልካቹ/ኩባንያዉ/ አገልግሎት መስጠት ለመጀመር ያሰበበት ግምታዊ ቀን / Estimated date the applicant/company intends to start providing the service *</label>
          <EthiopianDatePicker name="estimated_start_date" value={formData.estimated_start_date} onChange={handleChange} className="w-full border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
        </div>
        <div>
          <label className={labelClass}>ያመለከቱበት ቀን / Request date *</label>
          <input name="request_date" value={formData.request_date} onChange={handleChange} className={inputClass} required />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2">
          <FaTimes /> Cancel
        </button>
        <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 flex items-center gap-2">
          <FaPaperPlane /> {isEdit ? 'Update Request' : 'Submit'}
        </button>
      </div>
    </form>
  );
}

function SuccessModal({ referenceNo, onClose }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(referenceNo);
    } else {
      const ta = document.createElement('textarea');
      ta.value = referenceNo;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success('Reference number copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FaPaperPlane className="text-green-500 text-2xl" />
        </div>
        <h3 className="text-2xl font-bold mb-2 text-[#006400]">Application Submitted!</h3>
        <p className="text-gray-600 mb-4">Your request has been submitted successfully.</p>
        <div className="bg-gray-100 rounded p-4 mb-4">
          <p className="text-sm text-gray-500 mb-1">Reference Number</p>
          <div className="flex items-center justify-center gap-2">
            <span className="text-xl font-mono font-bold text-[#006400]">{referenceNo}</span>
            <button onClick={handleCopy} className="text-[#006400] hover:text-green-500">
              {copied ? <span className="text-sm">Copied!</span> : <FaCopy />}
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-500 mb-4">Please save your reference number to check the status later.</p>
        <button onClick={onClose} className="bg-[#4CAF50] text-white px-6 py-2 rounded hover:bg-green-400">
          Close
        </button>
      </div>
    </div>
  );
}

function GasStationTransferForm({ onSubmit, onCancel, initialData = null, isEdit = false }) {
  useFormDraft(() => [formData, setFormData], initialData);
  const [formData, setFormData] = useState({
    nameofapplicant: '',
    nationality: '',
    fan_national_id: '',
    passport_no: '',
    applicant_region: '',
    applicant_subcity: '',
    applicant_wereda: '',
    applicant_hno: '',
    applicant_pobox: '',
    applicant_telno: '',
    applicant_email: '',
    site_region: '',
    site_zone: '',
    site_subcity: '',
    site_woreda: '',
    existingcompanyname: '',
    transferto: '',
    letter_no: '',
    letter_date: '',
    certifying_body: '',
    certification_date: '',
    supplier_name: '',
    fuel_retail_price_from: '',
    fuel_retail_price_to: '',
    capacity_diesel: '',
    capacity_gasoline: '',
    capacity_kerosene: '',
    total_capacity: '',
    remark: '',
    id_photo: null,
    application_letter: null,
    support_letter: null,
    aggrement_letter: null,
    existing_application_letter: '',
    existing_support_letter: '',
    existing_aggrement_letter: '',
    existing_id_photo: '',
    request_date: toEthiopianDateStr(new Date())
  });

  const [additionalAttachments, setAdditionalAttachments] = useState([]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        nameofapplicant: initialData.nameofapplicant || '',
        nationality: initialData.nationality || '',
        fan_national_id: initialData.fan_national_id || '',
        passport_no: initialData.passport_no || '',
        applicant_region: initialData.applicant_region || '',
        applicant_subcity: initialData.applicant_subcity || '',
        applicant_wereda: initialData.applicant_wereda || '',
        applicant_hno: initialData.applicant_hno || '',
        applicant_pobox: initialData.applicant_pobox || '',
        applicant_telno: initialData.applicant_telno || '',
        applicant_email: initialData.applicant_email || '',
        site_region: initialData.site_region || '',
        site_zone: initialData.site_zone || '',
        site_subcity: initialData.site_subcity || '',
        site_woreda: initialData.site_woreda || '',
        existingcompanyname: initialData.existingcompanyname || '',
        transferto: initialData.transferto || '',
        letter_no: initialData.letter_no || '',
        letter_date: initialData.letter_date ? convertDbDateToDisplay(initialData.letter_date) : '',
        certifying_body: initialData.certifying_body || '',
        certification_date: initialData.certification_date ? convertDbDateToDisplay(initialData.certification_date) : '',
        supplier_name: initialData.supplier_name || '',
        fuel_retail_price_from: initialData.fuel_retail_price_from ? convertDbDateToDisplay(initialData.fuel_retail_price_from) : '',
        fuel_retail_price_to: initialData.fuel_retail_price_to ? convertDbDateToDisplay(initialData.fuel_retail_price_to) : '',
        capacity_diesel: initialData.capacity_diesel || '',
        capacity_gasoline: initialData.capacity_gasoline || '',
        capacity_kerosene: initialData.capacity_kerosene || '',
        total_capacity: initialData.total_capacity || '',
        remark: initialData.remark || '',
        id_photo: null,
        application_letter: null,
        support_letter: null,
        aggrement_letter: null,
        existing_application_letter: initialData.application_letter || '',
        existing_support_letter: initialData.support_letter || '',
        existing_aggrement_letter: initialData.aggrement_letter || '',
        existing_id_photo: initialData.id_photo || '',
        request_date: initialData.request_date ? convertDbDateToDisplay(initialData.request_date) : toEthiopianDateStr(new Date())
      });
    }
  }, [initialData]);

  useEffect(() => {
    const td = parseInt(formData.tanks_diesel) || 0;
    const tg = parseInt(formData.tanks_gasoline) || 0;
    const tk = parseInt(formData.tanks_kerosene) || 0;
    const cd = parseInt(formData.capacity_diesel) || 0;
    const cg = parseInt(formData.capacity_gasoline) || 0;
    const ck = parseInt(formData.capacity_kerosene) || 0;
    const total = (td * cd) + (tg * cg) + (tk * ck);
    setFormData(prev => ({ ...prev, total_capacity: total ? String(total) : '' }));
  }, [formData.tanks_diesel, formData.tanks_gasoline, formData.tanks_kerosene, formData.capacity_diesel, formData.capacity_gasoline, formData.capacity_kerosene]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e, field) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid document.');
      e.target.value = '';
      return;
    }
    if (field === 'id_photo' && selectedFile && !['image/jpeg', 'image/png'].includes(selectedFile.type)) {
      toast.error('ID photo must be a JPG or PNG image.');
      e.target.value = '';
      return;
    }
    setFormData({ ...formData, [field]: selectedFile });
  };

  const handleAdditionalFileChange = (e, index) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    const newAttachments = [...additionalAttachments];
    newAttachments[index].file = selectedFile;
    setAdditionalAttachments(newAttachments);
  };

  const handleAdditionalFileNameChange = (e, index) => {
    const newAttachments = [...additionalAttachments];
    newAttachments[index].name = e.target.value;
    setAdditionalAttachments(newAttachments);
  };

  const addAdditionalAttachment = () => {
    if (additionalAttachments.length >= 10) {
      toast.warn('You can only add up to 10 attachments');
      return;
    }
    setAdditionalAttachments([...additionalAttachments, { name: '', file: null }]);
  };

  const removeAdditionalAttachment = (index) => {
    const newAttachments = additionalAttachments.filter((_, i) => i !== index);
    setAdditionalAttachments(newAttachments);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    
    Object.keys(formData).forEach(key => {
      if (formData[key] !== null && key !== 'existing_application_letter' && 
          key !== 'existing_support_letter' && key !== 'existing_aggrement_letter' &&
          key !== 'existing_id_photo' && key !== 'request_date' &&
          key !== 'letter_date' && key !== 'certification_date' &&
          key !== 'fuel_retail_price_from' && key !== 'fuel_retail_price_to') {
        if (formData[key] instanceof File) {
          if (formData[key]) {
            formDataToSend.append(key, formData[key]);
          }
        } else {
          formDataToSend.append(key, formData[key]);
        }
      }
    });
    
    if (isEdit && initialData) {
      formDataToSend.append('existing_application_letter', formData.existing_application_letter);
      formDataToSend.append('existing_support_letter', formData.existing_support_letter);
      formDataToSend.append('aggrement_letter', formData.existing_aggrement_letter);
      formDataToSend.append('existing_id_photo', formData.existing_id_photo || '');
      formDataToSend.append('status', initialData.status || 'pending');
      formDataToSend.append('response_reason', initialData.response_reason || '');
      formDataToSend.append('rejection_reason', initialData.rejection_reason || '');
    }
    
    additionalAttachments.forEach((att, index) => {
      if (att.file) {
        formDataToSend.append(`additional_attachment_${index}_name`, att.name || '');
        formDataToSend.append(`additional_attachment_${index}`, att.file);
      }
    });
    formDataToSend.append('additional_attachments_count', additionalAttachments.filter(a => a.file).length);
    
    formDataToSend.append('request_date', ethDateStrToGregStr(formData.request_date));
    formDataToSend.append('letter_date', formData.letter_date ? ethDateStrToGregStr(formData.letter_date) : '');
    formDataToSend.append('certification_date', formData.certification_date ? ethDateStrToGregStr(formData.certification_date) : '');
    formDataToSend.append('fuel_retail_price_from', formData.fuel_retail_price_from ? ethDateStrToGregStr(formData.fuel_retail_price_from) : '');
    formDataToSend.append('fuel_retail_price_to', formData.fuel_retail_price_to ? ethDateStrToGregStr(formData.fuel_retail_price_to) : '');
    
    onSubmit(formDataToSend, isEdit ? initialData.id : null);
  };

  const inputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]";
  const fileInputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400] bg-white";

  const renderFileInput = (label, field, existingValue) => (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {existingValue && (
        <div className="flex items-center gap-2 mb-2 text-sm text-green-500">
          <FaFile /> Currently uploaded: 
          <a href={fileUrl(existingValue)} target="_blank" rel="noopener noreferrer" className="underline">
            View file
          </a>
        </div>
      )}
      <input type="file" onChange={(e) => handleFileChange(e, field)} className={fileInputClass} accept=".pdf" />
      <p className="text-xs text-gray-500 mt-1">PDF (max 10MB)</p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-[#006400]">
          {isEdit ? 'Edit Service Request' : 'የነዳጅ ማደያ ወደ ሌላ የነዳጅ አከፋፋይ ኩባንያ የዝውውር ትስስር ፈቃድ አገልግሎት / Service for existing gas station transfer from one company to the other'}
        </h2>
        {isEdit && (
          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm">Editing Request</span>
        )}
      </div>

      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Applicant Information (የአመልካች መረጃ)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Name of Applicant (የአመልካች ሥም) *</label>
            <input name="nameofapplicant" value={formData.nameofapplicant} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
          <label className="block text-sm font-medium mb-1">ዜግነት / Nationality *</label>
          <input name="nationality" value={formData.nationality} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">የፋይዳ ተለዋጭ ቁጥር (FAN) / National ID *</label>
              <input name="fan_national_id" value={formData.fan_national_id} onChange={handleChange} className={inputClass} required />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">ፓስፖርት ቁጥር / Passport No.</label>
              <input name="passport_no" value={formData.passport_no} onChange={handleChange} className={inputClass} />
            </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1">የመታወቂያ ፎቶ / ID Photo</label>
            <input
              type="file"
              name="id_photo"
              accept="image/jpeg,image/png"
              onChange={(e) => handleFileChange(e, 'id_photo')}
              className={fileInputClass}
            />
            {(formData.id_photo || formData.existing_id_photo) ? (
              <div className="mt-2 rounded border border-dashed border-gray-300 bg-gray-50 p-3 text-sm text-gray-700">
                {formData.id_photo ? (
                  <span>Selected image: {formData.id_photo.name}</span>
                ) : (
                  <a href={fileUrl(formData.existing_id_photo)} target="_blank" rel="noopener noreferrer" className="text-green-600 underline">
                    View existing ID photo
                  </a>
                )}
              </div>
            ) : (
              <div className="mt-2 rounded border border-dashed border-gray-300 bg-gray-50 p-3 text-sm text-gray-500">
                የመታወቂያ ፎቶ እዚህ ይገባል / ID photo placeholder
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Applicant Location (የአመልካች አድራሻ)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Region (ክልል) *</label>
            <input name="applicant_region" value={formData.applicant_region} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Sub City /Zone (ክፍለ ከተማ) *</label>
            <input name="applicant_subcity" value={formData.applicant_subcity} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Wereda/Kebele (ወረዳ/ቀበሌ) *</label>
            <input name="applicant_wereda" value={formData.applicant_wereda} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">H.No (የቤት.ቁ)</label>
            <input name="applicant_hno" value={formData.applicant_hno} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">P.O.Box (ፖ.ሣ.ቁ.)</label>
            <input type="number" name="applicant_pobox" value={formData.applicant_pobox} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tel. No (ስልክ ቁ.)</label>
            <input name="applicant_telno" value={formData.applicant_telno} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Email (ኢሜይል)</label>
            <input type="email" name="applicant_email" value={formData.applicant_email} onChange={handleChange} className={inputClass} />
          </div>
        </div>
      </div>

      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Location of fuel station</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Region (ክልል) *</label>
            <input name="site_region" value={formData.site_region} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Zone/City (ዞን/ከተማ) *</label>
            <input name="site_zone" value={formData.site_zone} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Sub City (ክፍለ ከተማ) *</label>
            <input name="site_subcity" value={formData.site_subcity} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ) *</label>
            <input name="site_woreda" value={formData.site_woreda} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Name of Existing oil Company / ነባሩ የነዳጅ ካምፓኒ ሥም</label>
            <input name="existingcompanyname" value={formData.existingcompanyname} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Name of transfered company name / የተዛወረው የነዳጅ ካምፓኒ ሥም *</label>
            <input name="transferto" value={formData.transferto} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የተጠየቀበት ደብዳቤ ቁጥር / Request letter no</label>
            <input name="letter_no" value={formData.letter_no} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የተጠየቀበት ደብዳቤ ቀን / Request letter date</label>
            <EthiopianDatePicker 
              name="letter_date"
              value={formData.letter_date} 
              onChange={handleChange}
              className={inputClass} 
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">ብቃት የሰጠው አካል / Certifying body</label>
            <input name="certifying_body" value={formData.certifying_body} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">ብቃት የተሰጠበት ቀን / Certification date</label>
            <EthiopianDatePicker 
              name="certification_date"
              value={formData.certification_date} 
              onChange={handleChange}
              className={inputClass} 
            />
          </div>
          {/* Fuel retail price dates and supplier name shown in request details under current status */}
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-2">የማደያው ታንከር ብዛት እና የመያዝ አቅም በሊትር / Number of Tank & capacity in liters</label>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-600">ናፍጣ / Diesel</label>
                <input name="capacity_diesel" value={formData.capacity_diesel} onChange={handleChange} className={inputClass} placeholder="Liters" />
                <input name="tanks_diesel" value={formData.tanks_diesel} onChange={handleChange} className={inputClass + ' mt-1'} placeholder="No. of Tanks" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-600">ቤንዚን / Gasoline</label>
                <input name="capacity_gasoline" value={formData.capacity_gasoline} onChange={handleChange} className={inputClass} placeholder="Liters" />
                <input name="tanks_gasoline" value={formData.tanks_gasoline} onChange={handleChange} className={inputClass + ' mt-1'} placeholder="No. of Tanks" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-600">ኬሮሲን / Kerosene</label>
                <input name="capacity_kerosene" value={formData.capacity_kerosene} onChange={handleChange} className={inputClass} placeholder="Liters" />
                <input name="tanks_kerosene" value={formData.tanks_kerosene} onChange={handleChange} className={inputClass + ' mt-1'} placeholder="No. of Tanks" />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">ጠቅላላ አቅም / Total capacity (liters)</label>
            <input name="total_capacity" value={formData.total_capacity} onChange={handleChange} className={inputClass} />
          </div>
        </div>
      </div>

      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Attachments (ተያያዥ መረጃ)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderFileInput('Application Letter (የማመልከቻ ደብዳቤ) *', 'application_letter', formData.existing_application_letter)}
          {renderFileInput('Support Letter (የድጋፍ ደብዳቤ)', 'support_letter', formData.existing_support_letter)}
          {renderFileInput('Agreement Letter (የስምምነት ደብዳቤ)', 'aggrement_letter', formData.existing_aggrement_letter)}
        </div>
        
        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={addAdditionalAttachment}
            disabled={additionalAttachments.length >= 10}
            className="text-[#006400] hover:text-green-500 flex items-center gap-2 text-sm font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            <FaPlus /> Add Attachment
          </button>
          <span className="text-xs text-gray-500">({additionalAttachments.length}/10)</span>
        </div>
        
        {additionalAttachments.map((att, index) => (
          <div key={index} className="col-span-2 flex gap-2 items-start mt-2">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Attachment name"
                value={att.name || ''}
                onChange={(e) => handleAdditionalFileNameChange(e, index)}
                className={inputClass}
              />
            </div>
            <div className="flex-1">
              <input
                type="file"
                onChange={(e) => handleAdditionalFileChange(e, index)}
                className={fileInputClass}
                accept=".pdf"
              />
            </div>
            <button
              type="button"
              onClick={() => removeAdditionalAttachment(index)}
              className="text-red-600 hover:text-red-800 p-2"
            >
              <FaTimes />
            </button>
          </div>
        ))}
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium mb-1">Remark</label>
        <textarea name="remark" value={formData.remark} onChange={handleChange} className={inputClass} rows="3" />
      </div>

      {isEdit && (
        <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Response Details (የምላሽ ዝርዝሮች)</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">የነዳጅ አቅራቢ ድርጅት ሥም / Supplier name</label>
              <input name="supplier_name" value={formData.supplier_name} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">የነዳጅ ችርቻሮ ዋጋ የሚጀምርበት ቀን / Fuel retail price from date</label>
              <EthiopianDatePicker 
                name="fuel_retail_price_from"
                value={formData.fuel_retail_price_from} 
                onChange={handleChange}
                className={inputClass} 
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">የነዳጅ ችርቻሮ ዋጋ የሚያልቅበት ቀን / Fuel retail price to date</label>
              <EthiopianDatePicker 
                name="fuel_retail_price_to"
                value={formData.fuel_retail_price_to} 
                onChange={handleChange}
                className={inputClass} 
              />
            </div>
          </div>
        </div>
      )}

      <div className="mb-6">
        <label className="block text-sm font-medium mb-1">ያመለከቱበት ቀን / Request date</label>
        <EthiopianDatePicker 
          name="request_date"
          value={formData.request_date} 
          onChange={handleChange}
          className="w-full border rounded px-3 py-2" 
          required 
        />
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2">
          <FaTimes /> Cancel
        </button>
        <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 flex items-center gap-2">
          <FaPaperPlane /> {isEdit ? 'Update Request' : 'Submit'}
        </button>
      </div>
    </form>
  );
}

function DiversionLicenseForm({ onSubmit, onCancel, initialData = null, isEdit = false }) {
  useFormDraft(() => [formData, setFormData], initialData);
  const [formData, setFormData] = useState({
    fuel_distributer_name: '',
    letter_no: '',
    letter_date: '',
    site_region: '',
    site_zone: '',
    site_subcity: '',
    site_woreda: '',
    fuel_station_name: '',
    manager_name: '',
    manager_nationality: '',
    manager_idno: '',
    manager_region: '',
    manager_k_ketema: '',
    manager_wereda: '',
    manager_hno: '',
    manager_telno: '',
    manager_mobno: '',
    fuel_loading_date: '',
    fuel_load_amount: '',
    plate_number: '',
    trailer_plate_number: '',
    fdc: '',
    document_no: '',
    loading_fuel_type: '',
    change_of_place: false,
    changing_truck: false,
    detailed_reason: '',
    first_destination_name: '',
    first_destination_region: '',
    first_destination_woreda: '',
    first_destination_specific_place: '',
    replacing_plate_number: '',
    replacing_trailer_plate_number: '',
    replaced_plate_number: '',
    replaced_trailer_plate_number: '',
    gas_station_name: '',
    gas_station_region: '',
    gas_station_zone: '',
    gas_station_subcity: '',
    gas_station_woreda: '',
    gas_station_specific_place: '',
    remark: '',
    application_letter: null,
    support_letter: null,
    aggrement_letter: null,
    existing_application_letter: '',
    existing_support_letter: '',
    existing_aggrement_letter: '',
    request_date: toEthiopianDateStr(new Date())
  });

  const [additionalAttachments, setAdditionalAttachments] = useState([]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        fuel_distributer_name: initialData.fuel_distributer_name || '',
        letter_no: initialData.letter_no || '',
        letter_date: initialData.letter_date ? convertDbDateToDisplay(initialData.letter_date) : '',
        site_region: initialData.site_region || '',
        site_zone: initialData.site_zone || '',
        site_subcity: initialData.site_subcity || '',
        site_woreda: initialData.site_woreda || '',
        fuel_station_name: initialData.fuel_station_name || '',
        manager_name: initialData.manager_name || '',
        manager_nationality: initialData.manager_nationality || '',
        manager_idno: initialData.manager_idno || '',
        manager_region: initialData.manager_region || '',
        manager_k_ketema: initialData.manager_k_ketema || '',
        manager_wereda: initialData.manager_wereda || '',
        manager_hno: initialData.manager_hno || '',
        manager_telno: initialData.manager_telno || '',
        manager_mobno: initialData.manager_mobno || '',
        fuel_loading_date: initialData.fuel_loading_date ? convertDbDateToDisplay(initialData.fuel_loading_date) : '',
        fuel_load_amount: initialData.fuel_load_amount || '',
        plate_number: initialData.plate_number || '',
        trailer_plate_number: initialData.trailer_plate_number || '',
        fdc: initialData.fdc || '',
        document_no: initialData.document_no || '',
        loading_fuel_type: initialData.loading_fuel_type || '',
        change_of_place: initialData.change_of_place || false,
        changing_truck: initialData.changing_truck || false,
        detailed_reason: initialData.detailed_reason || '',
        first_destination_name: initialData.first_destination_name || '',
        first_destination_region: initialData.first_destination_region || '',
        first_destination_woreda: initialData.first_destination_woreda || '',
        first_destination_specific_place: initialData.first_destination_specific_place || '',
        replacing_plate_number: initialData.replacing_plate_number || '',
        replacing_trailer_plate_number: initialData.replacing_trailer_plate_number || '',
        replaced_plate_number: initialData.replaced_plate_number || '',
        replaced_trailer_plate_number: initialData.replaced_trailer_plate_number || '',
        gas_station_name: initialData.gas_station_name || '',
        gas_station_region: initialData.gas_station_region || '',
        gas_station_zone: initialData.gas_station_zone || '',
        gas_station_subcity: initialData.gas_station_subcity || '',
        gas_station_woreda: initialData.gas_station_woreda || '',
        gas_station_specific_place: initialData.gas_station_specific_place || '',
        remark: initialData.remark || '',
        application_letter: null,
        support_letter: null,
        aggrement_letter: null,
        existing_application_letter: initialData.application_letter || '',
        existing_support_letter: initialData.support_letter || '',
        existing_aggrement_letter: initialData.aggrement_letter || '',
        request_date: initialData.request_date ? convertDbDateToDisplay(initialData.request_date) : toEthiopianDateStr(new Date())
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value,
      ...(e.target.name === 'change_of_place' && value ? { changing_truck: false } : {}),
      ...(e.target.name === 'changing_truck' && value ? { change_of_place: false } : {})
    });
  };

  const handleFileChange = (e, field) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    setFormData({ ...formData, [field]: selectedFile });
  };

  const handleAdditionalFileChange = (e, index) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    const newAttachments = [...additionalAttachments];
    newAttachments[index].file = selectedFile;
    setAdditionalAttachments(newAttachments);
  };

  const handleAdditionalFileNameChange = (e, index) => {
    const newAttachments = [...additionalAttachments];
    newAttachments[index].name = e.target.value;
    setAdditionalAttachments(newAttachments);
  };

  const addAdditionalAttachment = () => {
    if (additionalAttachments.length >= 10) {
      toast.warn('You can only add up to 10 attachments');
      return;
    }
    setAdditionalAttachments([...additionalAttachments, { name: '', file: null }]);
  };

  const removeAdditionalAttachment = (index) => {
    const newAttachments = additionalAttachments.filter((_, i) => i !== index);
    setAdditionalAttachments(newAttachments);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    
    Object.keys(formData).forEach(key => {
      if (formData[key] !== null && key !== 'existing_application_letter' && 
          key !== 'existing_support_letter' && key !== 'existing_aggrement_letter' &&
          key !== 'request_date' && key !== 'project_end_date' &&
          key !== 'fuel_distributor_letter_date' && key !== 'support_org_letter_date' &&
          key !== 'letter_date') {
        if (formData[key] instanceof File) {
          if (formData[key]) {
            formDataToSend.append(key, formData[key]);
          }
        } else {
          formDataToSend.append(key, formData[key]);
        }
      }
    });
    
    if (formData.request_date) formDataToSend.append('request_date', ethDateStrToGregStr(formData.request_date));
    if (formData.project_end_date) formDataToSend.append('project_end_date', ethDateStrToGregStr(formData.project_end_date));
    if (formData.fuel_distributor_letter_date) formDataToSend.append('fuel_distributor_letter_date', ethDateStrToGregStr(formData.fuel_distributor_letter_date));
    if (formData.support_org_letter_date) formDataToSend.append('support_org_letter_date', ethDateStrToGregStr(formData.support_org_letter_date));
    if (formData.fuel_loading_date) formDataToSend.append('fuel_loading_date', ethDateStrToGregStr(formData.fuel_loading_date));
    if (formData.letter_date) formDataToSend.append('letter_date', ethDateStrToGregStr(formData.letter_date));
    
    if (isEdit && initialData) {
      formDataToSend.append('existing_application_letter', formData.existing_application_letter);
      formDataToSend.append('existing_support_letter', formData.existing_support_letter);
      formDataToSend.append('aggrement_letter', formData.existing_aggrement_letter);
      formDataToSend.append('status', initialData.status || 'pending');
      formDataToSend.append('response_reason', initialData.response_reason || '');
      formDataToSend.append('rejection_reason', initialData.rejection_reason || '');
    }
    
    additionalAttachments.forEach((att, index) => {
      if (att.file) {
        formDataToSend.append(`additional_attachment_${index}_name`, att.name || '');
        formDataToSend.append(`additional_attachment_${index}`, att.file);
      }
    });
    formDataToSend.append('additional_attachments_count', additionalAttachments.filter(a => a.file).length);
    
    onSubmit(formDataToSend, isEdit ? initialData.id : null);
  };

  const inputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]";
  const fileInputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400] bg-white";

  const renderFileInput = (label, field, existingValue) => (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {existingValue && (
        <div className="flex items-center gap-2 mb-2 text-sm text-green-500">
          <FaFile /> Currently uploaded: 
          <a href={fileUrl(existingValue)} target="_blank" rel="noopener noreferrer" className="underline">
            View file
          </a>
        </div>
      )}
      <input type="file" onChange={(e) => handleFileChange(e, field)} className={fileInputClass} accept=".pdf" />
      <p className="text-xs text-gray-500 mt-1">PDF (max 10MB)</p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-[#006400]">
          {isEdit ? 'Edit Service Request' : 'የዳይቨርሽን ፈቃድ አገልግሎት / Service for diversion permit'}
        </h2>
        {isEdit && (
          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm">Editing Request</span>
        )}
      </div>

      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Name of Fuel Distributer Company (የነዳጅ አከፋፋይ ካምፓኒ ሥም)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Name of Fuel Distributer Company (የነዳጅ አከፋፋይ ካምፓኒ ሥም) *</label>
            <input name="fuel_distributer_name" value={formData.fuel_distributer_name} onChange={handleChange} className={inputClass} required />
          </div>
        </div>
      </div>

      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Site Location (የሳይት አድራሻ)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Region (ክልል) *</label>
            <input name="site_region" value={formData.site_region} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Zone/City (ዞን/ከተማ) *</label>
            <input name="site_zone" value={formData.site_zone} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Sub City (ክፍለ ከተማ) *</label>
            <input name="site_subcity" value={formData.site_subcity} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ከበሌ) *</label>
            <input name="site_woreda" value={formData.site_woreda} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የማደያ ሥም (Name of fuel station name)</label>
            <input name="fuel_station_name" value={formData.fuel_station_name} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የማመልከቻ ደብዳቤ ቁጥር (Application letter no)</label>
            <input name="letter_no" value={formData.letter_no} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የማመልከቻ ደብዳቤ ቀን (Application letter date)</label>
            <EthiopianDatePicker name="letter_date" value={formData.letter_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" />
          </div>
        </div>
      </div>

      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Manager Location (የሥራአስኪያጅ አድራሻ)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Name of Manager (የሥራ አስኪያጅ ሥም) *</label>
            <input name="manager_name" value={formData.manager_name} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Nationality (ዜግነት) *</label>
            <input name="manager_nationality" value={formData.manager_nationality} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የፋይዳ ተለዋጭ ቁጥር (FAN) National ID *</label>
            <input name="manager_idno" value={formData.manager_idno} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Region (ክልል) *</label>
            <input name="manager_region" value={formData.manager_region} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">K/Ketema (ክፍለ ከተማ)</label>
            <input name="manager_k_ketema" value={formData.manager_k_ketema} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Wereda/Kebele (ወረዳ/ቀበሌ)</label>
            <input name="manager_wereda" value={formData.manager_wereda} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">H.No (የቤት.ቁ)</label>
            <input name="manager_hno" value={formData.manager_hno} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tel. No (ስልክ ቁ.)</label>
            <input name="manager_telno" value={formData.manager_telno} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Mob. No (ሞባይል)</label>
            <input name="manager_mobno" value={formData.manager_mobno} onChange={handleChange} className={inputClass} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Fuel Loading Date (የነዳጅ ጭነት ቀን) *</label>
          <EthiopianDatePicker name="fuel_loading_date" value={formData.fuel_loading_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Fuel Load Amount (የነዳጅ ጭነት መጠን) *</label>
          <input name="fuel_load_amount" value={formData.fuel_load_amount} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Plate Number (የተሽከርካሪ ታርጋ ቁጥር) *</label>
          <input name="plate_number" value={formData.plate_number} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Trailer Plate Number (የተሳቢ ታርጋ ቁጥር)</label>
          <input name="trailer_plate_number" value={formData.trailer_plate_number} onChange={handleChange} className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">FDC (የነዳጅ ማከፋፈያ ቁጥር) *</label>
          <input name="fdc" value={formData.fdc} onChange={handleChange} className={inputClass} required />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">የሰነድ ቁጥር (Document no)</label>
          <input name="document_no" value={formData.document_no} onChange={handleChange} className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">የነዳጅ አይነት / Type of fuel *</label>
          <select name="loading_fuel_type" value={formData.loading_fuel_type} onChange={handleChange} className={inputClass} required>
            <option value="">Select...</option>
            <option value="ቤንዚን">ቤንዚን (Benzene)</option>
            <option value="ናፍጣ">ናፍጣ (Naphtha)</option>
            <option value="ኬሮዚን">ኬሮዚን (Kerosene)</option>
          </select>
        </div>
      </div>

      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Type of Diversion (የዳይቨርሽን አይነት)</h4>
        <div className="flex gap-6 mt-2">
          <div className="flex items-center gap-2">
            <input 
              type="checkbox" 
              name="change_of_place" 
              checked={formData.change_of_place} 
              onChange={handleChange}
              className="w-4 h-4"
            />
            <label className="text-sm font-medium">Change of place (የቦታ ለውጥ)</label>
          </div>
          <div className="flex items-center gap-2">
            <input 
              type="checkbox" 
              name="changing_truck" 
              checked={formData.changing_truck} 
              onChange={handleChange}
              className="w-4 h-4"
            />
            <label className="text-sm font-medium">Changing truck (የማጓጓዣ ለውጥ)</label>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium mb-1">Detailed reason for requesting the diversion (የተጠየቀበት ምክንያት) *</label>
        <textarea name="detailed_reason" value={formData.detailed_reason} onChange={handleChange} className={inputClass} rows="3" required />
      </div>



      {formData.changing_truck && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Truck Replacement Information (የማጓጓዣ ተሽከርካሪ መረጃ)</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Replacing Plate Number (የሚተካበት ተሽከርካሪ ታርጋ ቁጥር) *</label>
              <input name="replacing_plate_number" value={formData.replacing_plate_number} onChange={handleChange} className={inputClass} required />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Replacing Trailer Plate Number (የሚተካበት ተራይለር ታርጋ ቁጥር)</label>
              <input name="replacing_trailer_plate_number" value={formData.replacing_trailer_plate_number} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Replaced Plate Number (የተተካው ተሽከርካሪ ታርጋ ቁጥር) *</label>
              <input name="replaced_plate_number" value={formData.replaced_plate_number} onChange={handleChange} className={inputClass} required />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Replaced Trailer Plate Number (የተተካው ተራይለር ታርጋ ቁጥር)</label>
              <input name="replaced_trailer_plate_number" value={formData.replaced_trailer_plate_number} onChange={handleChange} className={inputClass} />
            </div>
          </div>
        </div>
      )}

      {formData.change_of_place && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Name of the Gas Station Where the Fuel Load was Requested to be Diverted (ተጫነው ነዳጅ ዳይቨርት የሚሆንበት የነዳጅ ማደያ ሥም)</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name (ሥም) *</label>
              <input name="gas_station_name" value={formData.gas_station_name} onChange={handleChange} className={inputClass} required />
            </div>
            <div>
          <label className="block text-sm font-medium mb-1">ክልል / Region *</label>
              <input name="gas_station_region" value={formData.gas_station_region} onChange={handleChange} className={inputClass} required />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">ዞን (Zone)</label>
              <input name="gas_station_zone" value={formData.gas_station_zone} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">ክፍለ ከተማ (Subcity)</label>
              <input name="gas_station_subcity" value={formData.gas_station_subcity} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Woreda (ወረዳ) *</label>
              <input name="gas_station_woreda" value={formData.gas_station_woreda} onChange={handleChange} className={inputClass} required />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Specific place (ልዩ ቦታ) *</label>
              <input name="gas_station_specific_place" value={formData.gas_station_specific_place} onChange={handleChange} className={inputClass} required />
            </div>
          </div>
        </div>
      )}

      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Attachments (ተያያዥ መረጃዎች)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderFileInput('Application Letter (የማመልከቻ ደብዳቤ)', 'application_letter', formData.existing_application_letter)}
          {renderFileInput('Support Letter (የድጋፍ ደብዳቤ)', 'support_letter', formData.existing_support_letter)}
          {renderFileInput('Agreement Letter (የስምምነት ደብዳቤ)', 'aggrement_letter', formData.existing_aggrement_letter)}
        </div>
        
        <div className="mt-4">
          <button type="button" onClick={addAdditionalAttachment} className="text-[#006400] font-medium flex items-center gap-1 hover:underline">
            <FaPlus /> Add Attachment
          </button>
        </div>
        
        {additionalAttachments.map((att, index) => (
          <div key={index} className="mt-3 p-3 border border-gray-200 rounded bg-gray-50">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Additional Attachment {index + 1}</span>
              <button type="button" onClick={() => removeAdditionalAttachment(index)} className="text-red-500 hover:text-red-700">
                <FaTrash />
              </button>
            </div>
            <input 
              type="text" 
              placeholder="Attachment name/description" 
              value={att.name} 
              onChange={(e) => handleAdditionalFileNameChange(e, index)} 
              className={inputClass} 
            />
            <input 
              type="file" 
              onChange={(e) => handleAdditionalFileChange(e, index)} 
              className={`${fileInputClass} mt-2`} 
              accept=".pdf" 
            />
          </div>
        ))}
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium mb-1">Remark</label>
        <textarea name="remark" value={formData.remark} onChange={handleChange} className={inputClass} rows="3" />
      </div>

            <div className="col-span-2">
        <label className="block text-sm font-medium mb-1">ያመለከቱበት ቀን / Request date</label>
        <EthiopianDatePicker name="request_date" value={formData.request_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2">
          <FaTimes /> Cancel
        </button>
        <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 flex items-center gap-2">
          <FaPaperPlane /> {isEdit ? 'Update Request' : 'Submit'}
        </button>
      </div>
    </form>
  );
}

function OperatorChangeLicenseForm({ onSubmit, onCancel, initialData = null, isEdit = false }) {
  useFormDraft(() => [formData, setFormData], initialData);
  const [formData, setFormData] = useState({
    dealer_requester_name: '',
    site_region: '',
    site_zone: '',
    site_subcity: '',
    site_woreda: '',
    site_hno: '',
    manager_name: '',
    manager_nationality: '',
    manager_idno: '',
    manager_region: '',
    manager_k_ketema: '',
    manager_wereda: '',
    manager_hno: '',
    manager_telno: '',
    manager_mobno: '',
    new_dealer_name: '',
    new_dealer_region: '',
    new_dealer_zone: '',
    new_dealer_subcity: '',
    new_dealer_wereda: '',
    new_dealer_hno: '',
    new_dealer_telno: '',
    merchant_id_short_code: '',
    phone_orders: '',
    existing_operator_name: '',
    transferred_operator_name: '',
    supplier_name: '',
    existingcompanyname: '',
    transferto: '',
    station_type: '',
    site_area: '',
    request_letter_no: '',
    request_letter_date: '',
    certifying_body: '',
    certification_date: '',
    capacity_diesel: '',
    tanks_diesel: '',
    capacity_gasoline: '',
    tanks_gasoline: '',
    capacity_kerosene: '',
    tanks_kerosene: '',
    total_capacity: '',
    remark: '',
    application_letter: null,
    support_letter: null,
    aggrement_letter: null,
    existing_application_letter: '',
    existing_support_letter: '',
    existing_aggrement_letter: '',
    request_date: toEthiopianDateStr(new Date())
  });

  const [additionalAttachments, setAdditionalAttachments] = useState([]);
  const [showOperator, setShowOperator] = useState(initialData ? (initialData.operator_transfer ?? !!initialData.existing_operator_name) : false);
  const [showCompany, setShowCompany] = useState(initialData ? (initialData.company_transfer ?? !!initialData.existingcompanyname) : false);

  useEffect(() => {
    const td = parseInt(formData.tanks_diesel) || 0;
    const tg = parseInt(formData.tanks_gasoline) || 0;
    const tk = parseInt(formData.tanks_kerosene) || 0;
    const cd = parseInt(formData.capacity_diesel) || 0;
    const cg = parseInt(formData.capacity_gasoline) || 0;
    const ck = parseInt(formData.capacity_kerosene) || 0;
    const total = (td * cd) + (tg * cg) + (tk * ck);
    setFormData(prev => ({ ...prev, total_capacity: total ? String(total) : '' }));
  }, [formData.tanks_diesel, formData.tanks_gasoline, formData.tanks_kerosene, formData.capacity_diesel, formData.capacity_gasoline, formData.capacity_kerosene]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        dealer_requester_name: initialData.dealer_requester_name || '',
        site_region: initialData.site_region || '',
        site_zone: initialData.site_zone || '',
        site_subcity: initialData.site_subcity || '',
        site_woreda: initialData.site_woreda || '',
        site_hno: initialData.site_hno || '',
        manager_name: initialData.manager_name || '',
        manager_nationality: initialData.manager_nationality || '',
        manager_idno: initialData.manager_idno || '',
        manager_region: initialData.manager_region || '',
        manager_k_ketema: initialData.manager_k_ketema || '',
        manager_wereda: initialData.manager_wereda || '',
        manager_hno: initialData.manager_hno || '',
        manager_telno: initialData.manager_telno || '',
        manager_mobno: initialData.manager_mobno || '',
        new_dealer_name: initialData.new_dealer_name || '',
        new_dealer_region: initialData.new_dealer_region || '',
        new_dealer_subcity: initialData.new_dealer_subcity || '',
        new_dealer_woreda: initialData.new_dealer_woreda || '',
        new_dealer_hno: initialData.new_dealer_hno || '',
        new_dealer_telno: initialData.new_dealer_telno || '',
        existing_operator_name: initialData.existing_operator_name || '',
        transferred_operator_name: initialData.transferred_operator_name || '',
        supplier_name: initialData.supplier_name || '',
        existingcompanyname: initialData.existingcompanyname || '',
        transferto: initialData.transferto || '',
        station_type: initialData.station_type || '',
        site_area: initialData.site_area || '',
        request_letter_no: initialData.request_letter_no || '',
        request_letter_date: initialData.request_letter_date ? convertDbDateToDisplay(initialData.request_letter_date) : '',
        certifying_body: initialData.certifying_body || '',
        certification_date: initialData.certification_date ? convertDbDateToDisplay(initialData.certification_date) : '',
        capacity_diesel: initialData.capacity_diesel || '',
        tanks_diesel: initialData.tanks_diesel || '',
        capacity_gasoline: initialData.capacity_gasoline || '',
        tanks_gasoline: initialData.tanks_gasoline || '',
        capacity_kerosene: initialData.capacity_kerosene || '',
        tanks_kerosene: initialData.tanks_kerosene || '',
        total_capacity: initialData.total_capacity || '',
        existing_application_letter: initialData.application_letter || '',
        existing_support_letter: initialData.support_letter || '',
        existing_aggrement_letter: initialData.aggrement_letter || '',
        request_date: initialData.request_date ? convertDbDateToDisplay(initialData.request_date) : toEthiopianDateStr(new Date())
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e, field) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    setFormData({ ...formData, [field]: selectedFile });
  };

  const handleAdditionalFileChange = (e, index) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    const newAttachments = [...additionalAttachments];
    newAttachments[index].file = selectedFile;
    setAdditionalAttachments(newAttachments);
  };

  const handleAdditionalFileNameChange = (e, index) => {
    const newAttachments = [...additionalAttachments];
    newAttachments[index].name = e.target.value;
    setAdditionalAttachments(newAttachments);
  };

  const addAdditionalAttachment = () => {
    if (additionalAttachments.length >= 10) {
      toast.warn('You can only add up to 10 attachments');
      return;
    }
    setAdditionalAttachments([...additionalAttachments, { name: '', file: null }]);
  };

  const removeAdditionalAttachment = (index) => {
    const newAttachments = additionalAttachments.filter((_, i) => i !== index);
    setAdditionalAttachments(newAttachments);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    
    Object.keys(formData).forEach(key => {
      if (formData[key] !== null && key !== 'existing_application_letter' && 
          key !== 'existing_support_letter' && key !== 'existing_aggrement_letter' &&
          key !== 'request_date') {
        if (formData[key] instanceof File) {
          if (formData[key]) {
            formDataToSend.append(key, formData[key]);
          }
        } else {
          formDataToSend.append(key, formData[key]);
        }
      }
    });
    
    if (formData.request_date) formDataToSend.append('request_date', ethDateStrToGregStr(formData.request_date));
    
    if (isEdit && initialData) {
      formDataToSend.append('existing_application_letter', formData.existing_application_letter);
      formDataToSend.append('existing_support_letter', formData.existing_support_letter);
      formDataToSend.append('aggrement_letter', formData.existing_aggrement_letter);
      formDataToSend.append('status', initialData.status || 'pending');
      formDataToSend.append('response_reason', initialData.response_reason || '');
      formDataToSend.append('rejection_reason', initialData.rejection_reason || '');
    }
    
    additionalAttachments.forEach((att, index) => {
      if (att.file) {
        formDataToSend.append(`additional_attachment_${index}_name`, att.name || '');
        formDataToSend.append(`additional_attachment_${index}`, att.file);
      }
    });
    formDataToSend.append('additional_attachments_count', additionalAttachments.filter(a => a.file).length);
    formDataToSend.append('operator_transfer', String(showOperator));
    formDataToSend.append('company_transfer', String(showCompany));
    
    onSubmit(formDataToSend, isEdit ? initialData.id : null);
  };

  const inputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]";
  const fileInputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400] bg-white";

  const renderFileInput = (label, field, existingValue) => (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {existingValue && (
        <div className="flex items-center gap-2 mb-2 text-sm text-green-500">
          <FaFile /> Currently uploaded: 
          <a href={fileUrl(existingValue)} target="_blank" rel="noopener noreferrer" className="underline">
            View file
          </a>
        </div>
      )}
      <input type="file" onChange={(e) => handleFileChange(e, field)} className={fileInputClass} accept=".pdf" />
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-[#006400]">
          {isEdit ? 'Edit Service Request' : 'የነዳጅ ማደያ የኦፕረተር/ዲለር ለውጥ ትስስር ፈቃድ አገልግሎት / Service for operator/dealer change permit'}
        </h2>
        {isEdit && (
          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm">Editing Request</span>
        )}
      </div>

      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Name of Operator or Dealer Change company (ኦፐሬተር ወይም ዲለር ለውጥ የጠየቀው የነዳጅ አከፋፋይ ኩባንያ ሥም)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Name of Operator or Dealer Change company (ኦፐሬተር ወይም ዲለር ለውጥ የጠየቀው የነዳጅ አከፋፋይ ኩባንያ ሥም) *</label>
            <input name="dealer_requester_name" value={formData.dealer_requester_name} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የነዳጅ አቅራቢ ድርጅት ሥም / Supplier Name</label>
            <input name="supplier_name" value={formData.supplier_name} onChange={handleChange} className={inputClass} />
          </div>
          <div className="col-span-2 flex items-center gap-4 mb-2 mt-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={showOperator} onChange={(e) => setShowOperator(e.target.checked)} className="w-4 h-4" />
              <span className="text-sm font-medium text-gray-700">Operator Transfer (የኦፕረተር ዝውውር)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={showCompany} onChange={(e) => setShowCompany(e.target.checked)} className="w-4 h-4" />
              <span className="text-sm font-medium text-gray-700">Company Transfer (የኩባንያ ዝውውር)</span>
            </label>
          </div>
          {showOperator && (
          <div>
            <label className="block text-sm font-medium mb-1">Name of Existing operator name / ነባሩ ኦፐሬተር ሥም *</label>
            <input name="existing_operator_name" value={formData.existing_operator_name} onChange={handleChange} className={inputClass} required={showOperator} />
          </div>
          )}
          {showOperator && (
          <div>
            <label className="block text-sm font-medium mb-1">Name of transfered operator name / የተዛወረው ኦፐሬተር ሥም</label>
            <input name="transferred_operator_name" value={formData.transferred_operator_name} onChange={handleChange} className={inputClass} />
          </div>
          )}
          {showCompany && (
          <div>
            <label className="block text-sm font-medium mb-1">Name of Existing oil Company / ነባሩ የነዳጅ ካምፓኒ ሥም</label>
            <input name="existingcompanyname" value={formData.existingcompanyname} onChange={handleChange} className={inputClass} required={showCompany} />
          </div>
          )}
          {showCompany && (
          <div>
            <label className="block text-sm font-medium mb-1">Name of transfered company name / የተዛወረው የነዳጅ ካምፓኒ ሥም *</label>
            <input name="transferto" value={formData.transferto} onChange={handleChange} className={inputClass} required={showCompany} />
          </div>
          )}
        </div>
      </div>

      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Address of Fuel station የነዳጅ ማደያ አድራሻ</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Region (ክልል) *</label>
            <input name="site_region" value={formData.site_region} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Zone/City (ዞን/ከተማ) *</label>
            <input name="site_zone" value={formData.site_zone} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Sub City (ክፍለ ከተማ) *</label>
            <input name="site_subcity" value={formData.site_subcity} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ) *</label>
            <input name="site_woreda" value={formData.site_woreda} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.)</label>
            <input name="site_hno" value={formData.site_hno} onChange={handleChange} className={inputClass} />
          </div>
        </div>
      </div>

      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Address of fuel station owner (manager) የነዳጅ ማደያው ባለቤት(ሥራ አስኪያጅ) አድራሻ</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Name of Manager (የሥራ አስኪያጅ ሥም) *</label>
            <input name="manager_name" value={formData.manager_name} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Nationality (ዜግነት) *</label>
            <input name="manager_nationality" value={formData.manager_nationality} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የፋይዳ መታወቂያ ቁጥር (National ID) *</label>
            <input name="manager_idno" value={formData.manager_idno} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Region (ክልል) *</label>
            <input name="manager_region" value={formData.manager_region} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">K/Ketema (ክፍለ ከተማ)</label>
            <input name="manager_k_ketema" value={formData.manager_k_ketema} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Wereda/Kebele (ወረዳ/ቀበሌ)</label>
            <input name="manager_wereda" value={formData.manager_wereda} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.)</label>
            <input name="manager_hno" value={formData.manager_hno} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tel. No (ስልክ ቁ)</label>
            <input name="manager_telno" value={formData.manager_telno} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Mob.No (ሞባይል)</label>
            <input name="manager_mobno" value={formData.manager_mobno} onChange={handleChange} className={inputClass} />
          </div>
        </div>
      </div>

      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">አዲሱ የዲለር አድራሻ (New Dealer Location)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Name of Manager (የሥራ አስኪያጅ ሥም) *</label>
            <input name="new_dealer_name" value={formData.new_dealer_name} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Region (ክልል) *</label>
            <input name="new_dealer_region" value={formData.new_dealer_region} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Zone/City (ዞን/ከተማ) *</label>
            <input name="new_dealer_zone" value={formData.new_dealer_zone} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Sub city (ክፍለ ከተማ) *</label>
            <input name="new_dealer_subcity" value={formData.new_dealer_subcity} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Wereda/Kebele (ወረዳ/ቀበሌ) *</label>
            <input name="new_dealer_wereda" value={formData.new_dealer_wereda} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.)</label>
            <input name="new_dealer_hno" value={formData.new_dealer_hno} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tel. No (ስልክ ቁ)</label>
            <input name="new_dealer_telno" value={formData.new_dealer_telno} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Short Code ቴሌ ብር</label>
            <input name="merchant_id_short_code" value={formData.merchant_id_short_code} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Phone number who gives orders on the system (ትእዛዝ የሚሰጥ ሰው ስልክ ቁጥር)</label>
            <input name="phone_orders" value={formData.phone_orders} onChange={handleChange} className={inputClass} />
          </div>
        </div>
      </div>

      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">የተጠየቀበት ደብዳቤ / Request letter</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">የተጠየቀበት ደብዳቤ ቁጥር / Request letter no</label>
            <input name="request_letter_no" value={formData.request_letter_no} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የተጠየቀበት ደብዳቤ ቀን / Request letter date</label>
            <EthiopianDatePicker name="request_letter_date" value={formData.request_letter_date} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">ብቃት የሰጠው አካል / Certifying body</label>
            <input name="certifying_body" value={formData.certifying_body} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">ብቃት የተሰጠበት ቀን / Certification date</label>
            <EthiopianDatePicker name="certification_date" value={formData.certification_date} onChange={handleChange} className={inputClass} />
          </div>
        </div>
      </div>

      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">የማደያው ታንከር ብዛት እና የመያዝ አቅም በሊትር / Number of Tank & capacity in liters</h4>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600">ናፍጣ / Diesel</label>
            <input name="capacity_diesel" value={formData.capacity_diesel} onChange={handleChange} className={inputClass} placeholder="Liters" />
            <input name="tanks_diesel" value={formData.tanks_diesel} onChange={handleChange} className={inputClass + ' mt-1'} placeholder="No. of Tanks" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600">ቤንዚን / Gasoline</label>
            <input name="capacity_gasoline" value={formData.capacity_gasoline} onChange={handleChange} className={inputClass} placeholder="Liters" />
            <input name="tanks_gasoline" value={formData.tanks_gasoline} onChange={handleChange} className={inputClass + ' mt-1'} placeholder="No. of Tanks" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600">ኬሮሲን / Kerosene</label>
            <input name="capacity_kerosene" value={formData.capacity_kerosene} onChange={handleChange} className={inputClass} placeholder="Liters" />
            <input name="tanks_kerosene" value={formData.tanks_kerosene} onChange={handleChange} className={inputClass + ' mt-1'} placeholder="No. of Tanks" />
          </div>
        </div>
        <div className="mt-2">
          <label className="block text-sm font-medium mb-1">ጠቅላላ አቅም / Total capacity (liters)</label>
          <input name="total_capacity" value={formData.total_capacity} onChange={handleChange} className={inputClass} readOnly />
        </div>
      </div>

      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">የማደያ አይነት (Type of station)</h4>
        <div>
          <select name="station_type" value={formData.station_type} onChange={handleChange} className={inputClass}>
            <option value="">-- Select / ይምረጡ --</option>
            <option value="ነባር">ነባር (Existing)</option>
            <option value="አዲስ">አዲስ (New)</option>
          </select>
        </div>
        <div className="mt-3">
          <label className="block text-sm font-medium mb-1">የቦታው ስፋት በካሬ (Area of the site)</label>
          <input name="site_area" value={formData.site_area} onChange={handleChange} className={inputClass} placeholder="e.g. 500 sqm" />
        </div>
      </div>

      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Attachments (ተያያዥ መረጃዎች)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderFileInput('Application Letter (የማመልከቻ ደብዳቤ)', 'application_letter', formData.existing_application_letter)}
          {renderFileInput('Support Letter (የድጋፍ ደብዳቤ)', 'support_letter', formData.existing_support_letter)}
          {renderFileInput('Agreement Letter (የስምምነት ደብዳቤ)', 'aggrement_letter', formData.existing_aggrement_letter)}
        </div>
        
        <div className="mt-4">
          <button type="button" onClick={addAdditionalAttachment} className="text-[#006400] font-medium flex items-center gap-1 hover:underline">
            <FaPlus /> Add Attachment
          </button>
        </div>
        
        {additionalAttachments.map((att, index) => (
          <div key={index} className="mt-3 p-3 border border-gray-200 rounded bg-gray-50">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Additional Attachment {index + 1}</span>
              <button type="button" onClick={() => removeAdditionalAttachment(index)} className="text-red-500 hover:text-red-700">
                <FaTrash />
              </button>
            </div>
            <input 
              type="text" 
              placeholder="Attachment name/description" 
              value={att.name} 
              onChange={(e) => handleAdditionalFileNameChange(e, index)} 
              className={inputClass} 
            />
            <input 
              type="file" 
              onChange={(e) => handleAdditionalFileChange(e, index)} 
              className={`${fileInputClass} mt-2`} 
              accept=".pdf" 
            />
          </div>
        ))}
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium mb-1">Remark</label>
        <textarea name="remark" value={formData.remark} onChange={handleChange} className={inputClass} rows="3" />
      </div>

            <div className="col-span-2">
        <label className="block text-sm font-medium mb-1">ያመለከቱበት ቀን / Request date</label>
        <EthiopianDatePicker name="request_date" value={formData.request_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2">
          <FaTimes /> Cancel
        </button>
        <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 flex items-center gap-2">
          <FaPaperPlane /> {isEdit ? 'Update Request' : 'Submit'}
        </button>
      </div>
    </form>
  );
}

function NewFuelCompanyForm({ onSubmit, onCancel, initialData = null, isEdit = false }) {
  useFormDraft(() => [formData, setFormData], initialData);
  const [formData, setFormData] = useState({
    applicant_name: '',
    nationality: '',
    fan_national_id: '',
    place_of_renewed: '',
    date_of_renewed: '',
    region: '',
    subcity: '',
    woreda: '',
    hno: '',
    pobox: '',
    telno: '',
    email: '',
    company_name: '',
    manager_name: '',
    manager_nationality: '',
    manager_date_place_issued: '',
    manager_idno: '',
    manager_region: '',
    manager_ketema: '',
    manager_woreda: '',
    manager_hno: '',
    manager_pobox: '',
    manager_fixed_phone: '',
    manager_mobile: '',
    manager_email: '',
    type_of_operation: '', type_of_operation_en: '', operation_code: '', station_type: '',
    trade_name: '', trade_name_en: '',
    site_region: '', site_region_en: '',
    site_zone: '', site_zone_en: '',
    site_woreda: '', site_woreda_en: '',
    site_town: '', site_town_en: '',
    site_kebele: '', site_kebele_en: '',
    site_phone: '',
    type_of_certificate: '',
    financial_capacity: '',
    site_plan_drawing: null,
    area_of_land: '',
    remark: '',
    application_letter: null, support_letter: null, aggrement_letter: null,
    existing_application_letter: '', existing_support_letter: '', existing_aggrement_letter: '',
    existing_site_plan_drawing: '',
    request_date: toEthiopianDateStr(new Date())
  });

  const [additionalAttachments, setAdditionalAttachments] = useState([]);
  const [legalPersonType, setLegalPersonType] = useState('');

  useEffect(() => {
    if (initialData) {
      setFormData({
        applicant_name: initialData.applicant_name || '',
        nationality: initialData.nationality || '',
        fan_national_id: initialData.fan_national_id || '',
        place_of_renewed: initialData.place_of_renewed || '',
        date_of_renewed: initialData.date_of_renewed ? initialData.date_of_renewed.split('T')[0] : '',
        region: initialData.region || '',
        subcity: initialData.subcity || '',
        woreda: initialData.woreda || '',
        hno: initialData.hno || '',
        pobox: initialData.pobox || '',
        telno: initialData.telno || '',
        email: initialData.email || '',
        company_name: initialData.company_name || '',
        manager_name: initialData.manager_name || '',
        manager_nationality: initialData.manager_nationality || '',
        manager_date_place_issued: initialData.manager_date_place_issued ? initialData.manager_date_place_issued.split('T')[0] : '',
        manager_idno: initialData.manager_idno || '',
        manager_region: initialData.manager_region || '',
        manager_ketema: initialData.manager_ketema || '',
        manager_woreda: initialData.manager_woreda || '',
        manager_hno: initialData.manager_hno || '',
        manager_pobox: initialData.manager_pobox || '',
        manager_fixed_phone: initialData.manager_fixed_phone || '',
        manager_mobile: initialData.manager_mobile || '',
        manager_email: initialData.manager_email || '',
        type_of_operation: initialData.type_of_operation || '', type_of_operation_en: initialData.type_of_operation_en || '', operation_code: initialData.operation_code || '', station_type: initialData.station_type || '',
        trade_name: initialData.trade_name || '', trade_name_en: initialData.trade_name_en || '',
        site_region: initialData.site_region || '', site_region_en: initialData.site_region_en || '',
        site_zone: initialData.site_zone || '', site_zone_en: initialData.site_zone_en || '',
        site_woreda: initialData.site_woreda || '', site_woreda_en: initialData.site_woreda_en || '',
        site_town: initialData.site_town || '', site_town_en: initialData.site_town_en || '',
        site_kebele: initialData.site_kebele || '', site_kebele_en: initialData.site_kebele_en || '',
        site_phone: initialData.site_phone || '',
        type_of_certificate: initialData.type_of_certificate || '',
        financial_capacity: initialData.financial_capacity || '',
        site_plan_drawing: null,
        area_of_land: initialData.area_of_land || '',
        remark: initialData.remark || '',
        application_letter: null, support_letter: null, aggrement_letter: null,
        existing_application_letter: initialData.application_letter || '',
        existing_support_letter: initialData.support_letter || '',
        existing_aggrement_letter: initialData.aggrement_letter || '',
        existing_site_plan_drawing: initialData.site_plan_drawing || '',
        request_date: initialData.request_date ? convertDbDateToDisplay(initialData.request_date) : toEthiopianDateStr(new Date())
      });
      if (initialData.company_name) setLegalPersonType('company'); else setLegalPersonType('personal');
    }
  }, [initialData]);

  const handleChange = (e) => { setFormData({ ...formData, [e.target.name]: e.target.value }); };
  const handleFileChange = (e, field) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    setFormData({ ...formData, [field]: selectedFile });
  };

  const handleAdditionalFileChange = (e, index) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    const newAttachments = [...additionalAttachments];
    newAttachments[index].file = selectedFile;
    setAdditionalAttachments(newAttachments);
  };

  const handleAdditionalFileNameChange = (e, index) => {
    const newAttachments = [...additionalAttachments];
    newAttachments[index].name = e.target.value;
    setAdditionalAttachments(newAttachments);
  };

  const addAdditionalAttachment = () => {
    if (additionalAttachments.length >= 10) {
      toast.warn('You can only add up to 10 attachments');
      return;
    }
    setAdditionalAttachments([...additionalAttachments, { name: '', file: null }]);
  };

  const removeAdditionalAttachment = (index) => {
    const newAttachments = additionalAttachments.filter((_, i) => i !== index);
    setAdditionalAttachments(newAttachments);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    Object.keys(formData).forEach(key => {
      if (formData[key] !== null && key !== 'existing_application_letter' && key !== 'existing_support_letter' && key !== 'existing_aggrement_letter' && key !== 'existing_site_plan_drawing' && key !== 'request_date' && key !== 'date_of_renewed' && key !== 'manager_date_place_issued') {
        if (formData[key] instanceof File) { if (formData[key]) formDataToSend.append(key, formData[key]); }
        else { formDataToSend.append(key, formData[key]); }
      }
    });
    if (formData.request_date) formDataToSend.append('request_date', ethDateStrToGregStr(formData.request_date));
    if (formData.date_of_renewed) formDataToSend.append('date_of_renewed', ethDateStrToGregStr(formData.date_of_renewed));
    if (formData.manager_date_place_issued) formDataToSend.append('manager_date_place_issued', ethDateStrToGregStr(formData.manager_date_place_issued));
    if (isEdit && initialData) {
      formDataToSend.append('existing_application_letter', formData.existing_application_letter);
      formDataToSend.append('existing_support_letter', formData.existing_support_letter);
      formDataToSend.append('existing_aggrement_letter', formData.existing_aggrement_letter);
      formDataToSend.append('existing_site_plan_drawing', formData.existing_site_plan_drawing);
      formDataToSend.append('status', initialData.status || 'pending');
      formDataToSend.append('response_reason', initialData.response_reason || '');
      formDataToSend.append('rejection_reason', initialData.rejection_reason || '');
    }
    additionalAttachments.forEach((att, index) => {
      if (att.file) {
        formDataToSend.append(`additional_attachment_${index}_name`, att.name || '');
        formDataToSend.append(`additional_attachment_${index}`, att.file);
      }
    });
    formDataToSend.append('additional_attachments_count', additionalAttachments.filter(a => a.file).length);
    onSubmit(formDataToSend, isEdit ? initialData.id : null);
  };

  const inputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]";
  const fileInputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400] bg-white";

  const renderFileInput = (label, field, existingValue) => (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {existingValue && (
        <div className="flex items-center gap-2 mb-2 text-sm text-green-500">
          <FaFile /> Currently uploaded: <a href={fileUrl(existingValue)} target="_blank" rel="noopener noreferrer" className="underline">View file</a>
        </div>
      )}
      <input type="file" onChange={(e) => handleFileChange(e, field)} className={fileInputClass} accept=".pdf" />
      <p className="text-xs text-gray-500 mt-1">PDF (max 10MB)</p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-[#006400]">{isEdit ? 'Edit Service Request' : 'አዲስ የነዳጅ አከፋፋይ ኩባንያ ትስስር ፈቃድ አገልግሎት / Service for New Fuel Distributor Company Connection permit'}</h2>
        {isEdit && <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm">Editing Request</span>}
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Applicant Type (የአመልካች አይነት)</h4>
        <label className="block text-sm font-medium mb-1">አመልካች ዓይነት ይምረጡ / Select Applicant Type *</label>
        <select
          value={legalPersonType}
          onChange={(e) => setLegalPersonType(e.target.value)}
          className={inputClass}
          style={{ maxWidth: '420px' }}
          required
        >
          <option value="">ይምረጡ / Select Applicant Type</option>
          <option value="personal">ግለሰብ / Personal</option>
          <option value="company">ድርጅት / Company</option>
        </select>
      </div>
      {legalPersonType === 'personal' && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">1. አመልካች ግለሰብ ከሆነ / If Legal Person is Personal</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Name (ስም) *</label><input name="applicant_name" value={formData.applicant_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Nationality (ዜግነት) *</label><input name="nationality" value={formData.nationality} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">የፋይዳ ተለዋጭ ቁጥር (FAN) National ID *</label><input name="fan_national_id" value={formData.fan_national_id} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Place of Renewed (የታደሰበት ቦታ) *</label><input name="place_of_renewed" value={formData.place_of_renewed} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Date of Renewed (የታደሰበት ቀን) *</label><EthiopianDatePicker name="date_of_renewed" value={formData.date_of_renewed} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required /></div>
            <div><label className="block text-sm font-medium mb-1">Region (ክልል) *</label><input name="region" value={formData.region} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Sub City (ክ/ከተማ) *</label><input name="subcity" value={formData.subcity} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ) *</label><input name="woreda" value={formData.woreda} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.)</label><input name="hno" value={formData.hno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">P.O.Box (ፖ.ኦ.ቡክስ)</label><input name="pobox" value={formData.pobox} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Tel. No. (ስልክ ቁ.)</label><input name="telno" value={formData.telno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">E-Mail (ኢመይል)</label><input type="email" name="email" value={formData.email} onChange={handleChange} className={inputClass} /></div>
          </div>
        </div>
      )}
      {legalPersonType === 'company' && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">2. አመልካች ድርጅት ከሆነ / If Legal Person is Company</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Company Name (የኩባንያው ስም) *</label><input name="company_name" value={formData.company_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Name of Manager (የስራ አስኪካጅ ስም) *</label><input name="manager_name" value={formData.manager_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Nationality (ዜግነት) *</label><input name="manager_nationality" value={formData.manager_nationality} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Date and Place Issued (የተሰጠበት ቀን እና ቦታ) *</label><EthiopianDatePicker name="manager_date_place_issued" value={formData.manager_date_place_issued} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required /></div>
            <div><label className="block text-sm font-medium mb-1">I.d. No (መታወቂያ ቁ.) *</label><input name="manager_idno" value={formData.manager_idno} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Region (ክልል) *</label><input name="manager_region" value={formData.manager_region} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">K/Ketema (ክ/ከተማ)</label><input name="manager_ketema" value={formData.manager_ketema} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ)</label><input name="manager_woreda" value={formData.manager_woreda} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.)</label><input name="manager_hno" value={formData.manager_hno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">P.O.Box (ፖ.ኦ.ቡክስ)</label><input name="manager_pobox" value={formData.manager_pobox} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Fixed Phone No (ማዕከላዊ ስልክ ቁ.)</label><input name="manager_fixed_phone" value={formData.manager_fixed_phone} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Mobile Phone No (ሞባይል ስልክ ቁ.)</label><input name="manager_mobile" value={formData.manager_mobile} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">E-mail (ኢመይል)</label><input type="email" name="manager_email" value={formData.manager_email} onChange={handleChange} className={inputClass} /></div>
          </div>
        </div>
      )}
      <div className="mb-6">        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">3. Type of Operation (የሥራው አይነት)</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><label className="block text-sm font-medium mb-1">Type of Operation /የሥራው አይነት (አማ) *</label><input name="type_of_operation" value={formData.type_of_operation} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Type of Operation /የሥራው አይነት (En) *</label><input name="type_of_operation_en" value={formData.type_of_operation_en || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">ኮድ / Code</label><input name="operation_code" value={formData.operation_code} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">የማደያው አይነት / Types of station *</label><select name="station_type" value={formData.station_type} onChange={handleChange} className={inputClass} required><option value="">-- Select --</option><option value="አዲስ/New">አዲስ/New</option><option value="ነባር/Existing">ነባር/Existing</option></select></div>
        </div>
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">4. Site Location (የስራ ቦታ አድራሻ)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium mb-1">Trade Name /የንግድ ስም (አማ) *</label><input name="trade_name" value={formData.trade_name || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Trade Name /የንግድ ሥም (En) *</label><input name="trade_name_en" value={formData.trade_name_en || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Region /ክልል (አማ) *</label><input name="site_region" value={formData.site_region} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Region /ክልል (En) *</label><input name="site_region_en" value={formData.site_region_en || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Zone/City /ዞን/ከተማ (አማ) *</label><input name="site_zone" value={formData.site_zone} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Zone/City /ዞን/ከተማ (En) *</label><input name="site_zone_en" value={formData.site_zone_en || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Woreda /ወረዳ (አማ) *</label><input name="site_woreda" value={formData.site_woreda} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Woreda /ወረዳ (En) *</label><input name="site_woreda_en" value={formData.site_woreda_en || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Town /ከተማ (አማ)</label><input name="site_town" value={formData.site_town || ''} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">Town /ከተማ (En)</label><input name="site_town_en" value={formData.site_town_en || ''} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">Kebele /ቀበሌ (አማ)</label><input name="site_kebele" value={formData.site_kebele || ''} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">Kebele /ቀበሌ (En)</label><input name="site_kebele_en" value={formData.site_kebele_en || ''} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">Type of certificate *</label><select name="type_of_certificate" value={formData.type_of_certificate || ''} onChange={handleChange} className={inputClass} required><option value="">-- Select --</option><option value="New">New</option><option value="Renewal">Renewal</option><option value="Renewal with Change">Renewal with Change</option></select></div>
        </div>
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">የገንዘብ አቅም፣ የይዞታና ዲዛይን ንድፍ፣ የቦታ ስፋት / Financial Capacity, Site Plan & Design, Area of Land</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">የገንዘብ አቅም / Financial Capacity *</label>
            <input name="financial_capacity" value={formData.financial_capacity} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የቦታው ስፋት / Area of Land *</label>
            <input name="area_of_land" value={formData.area_of_land} onChange={handleChange} className={inputClass} required />
          </div>
          <div className="col-span-2">
            <div>
              <label className="block text-sm font-medium mb-1">የይዞታ ማረጋገጫና ዲዛይን ንድፍ / Site Plan and Design Drawing</label>
              {formData.existing_site_plan_drawing && (
                <div className="flex items-center gap-2 mb-2 text-sm text-green-500">
                  <FaFile /> Currently uploaded: <a href={fileUrl(formData.existing_site_plan_drawing)} target="_blank" rel="noopener noreferrer" className="underline">View file</a>
                </div>
              )}
              <input type="file" onChange={(e) => { const f = e.target.files[0]; if (f && f.size === 0) { toast.error('The selected file is empty. Please choose a valid PDF document.'); e.target.value = ''; return; } setFormData({ ...formData, site_plan_drawing: f }); }} className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400] bg-white" accept=".pdf" />
              <p className="text-xs text-gray-500 mt-1">PDF (max 10MB)</p>
            </div>
          </div>
        </div>
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Attachments (ተያዥ መረጃዎች)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderFileInput('Application Letter (የማመልከቻ ደብዳቤ)', 'application_letter', formData.existing_application_letter)}
          {renderFileInput('Support Letter (የድጋፍ ደብዳቤ)', 'support_letter', formData.existing_support_letter)}
          {renderFileInput('Agreement Letter (የስምምነት ደብዳቤ)', 'aggrement_letter', formData.existing_aggrement_letter)}
        </div>
        <div className="mt-4">
          <button type="button" onClick={addAdditionalAttachment} className="text-[#006400] font-medium flex items-center gap-1 hover:underline">
            <FaPlus /> Add Attachment
          </button>
        </div>
        {additionalAttachments.map((att, index) => (
          <div key={index} className="mt-3 p-3 border border-gray-200 rounded bg-gray-50">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Additional Attachment {index + 1}</span>
              <button type="button" onClick={() => removeAdditionalAttachment(index)} className="text-red-500 hover:text-red-700">
                <FaTrash />
              </button>
            </div>
            <input type="text" placeholder="Attachment name/description" value={att.name} onChange={(e) => handleAdditionalFileNameChange(e, index)} className={inputClass} />
            <input type="file" onChange={(e) => handleAdditionalFileChange(e, index)} className={`${fileInputClass} mt-2`} accept=".pdf" />
          </div>
        ))}
      </div>
      <div className="mb-6">
        <label className="block text-sm font-medium mb-1">Remark (ማስታወሻ)</label>
        <textarea name="remark" value={formData.remark} onChange={handleChange} className={inputClass} rows="3" />
      </div>
      <div className="col-span-2">
        <label className="block text-sm font-medium mb-1">ያመለከቱበት ቀን / Request date</label>
        <EthiopianDatePicker name="request_date" value={formData.request_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2"><FaTimes /> Cancel</button>
        <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 flex items-center gap-2"><FaPaperPlane /> {isEdit ? 'Update Request' : 'Submit'}</button>
      </div>
    </form>
  );
}
function NewGasImportForm({ onSubmit, onCancel, initialData = null, isEdit = false, title }) {
  useFormDraft(() => [formData, setFormData], initialData);
  const [formData, setFormData] = useState({
    applicant_name: '', applicant_name_en: '', nationality: '', fan_national_id: '',
        region: '', subcity: '', woreda: '', hno: '', pobox: '', telno: '', email: '',
        gas_importer_company_name: '',
        manager_name: '', manager_nationality: '',
    manager_region: '', manager_ketema: '', manager_woreda: '', manager_hno: '', manager_pobox: '',
    manager_fixed_phone: '', manager_mobile: '', manager_email: '',
    type_of_operation: '', type_of_operation_en: '', operation_code: '', station_type: '',
    trade_name: '', trade_name_en: '',
    site_region: '', site_region_en: '', site_zone: '', site_zone_en: '', site_subcity: '', site_subcity_en: '', site_woreda: '', site_woreda_en: '', site_hno: '', site_hno_en: '', site_phone: '',
    financial_capacity: '',
    classification_codes: [],
    site_plan_drawing: null,
    area_of_land: '',
    remark: '',
    request_date: toEthiopianDateStr(new Date()),
    application_letter: null, support_letter: null, aggrement_letter: null,
    existing_application_letter: '', existing_support_letter: '', existing_aggrement_letter: '',
    existing_site_plan_drawing: ''
  });
  const [legalPersonType, setLegalPersonType] = useState('');
  const [personalClassificationCode, setPersonalClassificationCode] = useState('');
  const [companyClassificationCode, setCompanyClassificationCode] = useState('');

  useEffect(() => {
    if (initialData) {
      setFormData({
        applicant_name: initialData.applicant_name || '', applicant_name_en: initialData.applicant_name_en || '', nationality: initialData.nationality || '', fan_national_id: initialData.fan_national_id || '',
        region: initialData.region || '', subcity: initialData.subcity || '', woreda: initialData.woreda || '',
        hno: initialData.hno || '', pobox: initialData.pobox || '', telno: initialData.telno || '', email: initialData.email || '',
        gas_importer_company_name: initialData.gas_importer_company_name || '',
        manager_name: initialData.manager_name || '', manager_nationality: initialData.manager_nationality || '',
        manager_region: initialData.manager_region || '',
        manager_ketema: initialData.manager_ketema || '', manager_woreda: initialData.manager_woreda || '',
        manager_hno: initialData.manager_hno || '', manager_pobox: initialData.manager_pobox || '',
        manager_fixed_phone: initialData.manager_fixed_phone || '', manager_mobile: initialData.manager_mobile || '',
        manager_email: initialData.manager_email || '',
        type_of_operation: initialData.type_of_operation || '', type_of_operation_en: initialData.type_of_operation_en || '', operation_code: initialData.operation_code || '', station_type: initialData.station_type || '',
        trade_name: initialData.trade_name || '', trade_name_en: initialData.trade_name_en || '',
        site_region: initialData.site_region || '', site_region_en: initialData.site_region_en || '', site_zone: initialData.site_zone || '', site_zone_en: initialData.site_zone_en || '',
        site_subcity: initialData.site_subcity || '', site_subcity_en: initialData.site_subcity_en || '', site_woreda: initialData.site_woreda || '', site_woreda_en: initialData.site_woreda_en || '',
        site_hno: initialData.site_hno || '', site_hno_en: initialData.site_hno_en || '', site_phone: initialData.site_phone || '',
        classification_codes: initialData.classification_codes ? JSON.parse(initialData.classification_codes) : [],
        financial_capacity: initialData.financial_capacity || '',
        site_plan_drawing: null,
        area_of_land: initialData.area_of_land || '',
        construction_service_for: initialData.construction_service_for || '',
        plan_no: initialData.plan_no || '',
        gps_north: initialData.gps_north || '',
        gps_east: initialData.gps_east || '',
        total_project_cost: initialData.total_project_cost || '',
        professional_in_charge: initialData.professional_in_charge || '',
        remark: initialData.remark || '',
        application_letter: null, support_letter: null, aggrement_letter: null,
        existing_application_letter: initialData.application_letter || '',
        existing_support_letter: initialData.support_letter || '',
        existing_aggrement_letter: initialData.aggrement_letter || '',
        existing_site_plan_drawing: initialData.site_plan_drawing || '',
        request_date: initialData.request_date ? convertDbDateToDisplay(initialData.request_date) : toEthiopianDateStr(new Date())
      });
      if (initialData.exporter_company_name) setLegalPersonType('company'); else setLegalPersonType('personal');
      if (initialData.classification_codes) {
        const firstCode = (JSON.parse(initialData.classification_codes) || [])[0] || '';
        if (initialData.exporter_company_name) setCompanyClassificationCode(firstCode);
        else setPersonalClassificationCode(firstCode);
      }
    }
  }, [initialData]);

  const handleChange = (e) => { setFormData({ ...formData, [e.target.name]: e.target.value }); };
  const handleFileChange = (e, field) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    setFormData({ ...formData, [field]: selectedFile });
  };;

  const applyClassification = (setter, code) => {
    const item = CLASSIFICATION_CODES.find(c => c.code === code);
    setter(code);
    setFormData(prev => ({
      ...prev,
      classification_codes: code ? [code] : [],
      type_of_operation: item ? item.amharic : '',
      type_of_operation_en: item ? item.english : '',
      operation_code: code || ''
    }));
  };

  const [additionalAttachments, setAdditionalAttachments] = useState([]);

  const handleAdditionalFileChange = (e, index) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    const newAttachments = [...additionalAttachments];
    newAttachments[index].file = selectedFile;
    setAdditionalAttachments(newAttachments);
  };

  const handleAdditionalFileNameChange = (e, index) => {
    const newAttachments = [...additionalAttachments];
    newAttachments[index].name = e.target.value;
    setAdditionalAttachments(newAttachments);
  };

  const addAdditionalAttachment = () => {
    if (additionalAttachments.length >= 10) {
      toast.warn('You can only add up to 10 attachments');
      return;
    }
    setAdditionalAttachments([...additionalAttachments, { name: '', file: null }]);
  };

  const removeAdditionalAttachment = (index) => {
    const newAttachments = additionalAttachments.filter((_, i) => i !== index);
    setAdditionalAttachments(newAttachments);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    Object.keys(formData).forEach(key => {
      if (formData[key] !== null && key !== 'existing_application_letter' && key !== 'existing_support_letter' && key !== 'existing_aggrement_letter' && key !== 'classification_codes' && key !== 'request_date') {
        if (formData[key] instanceof File) { if (formData[key]) formDataToSend.append(key, formData[key]); }
        else { formDataToSend.append(key, formData[key]); }
      }
    });
    if (isEdit && initialData) {
      formDataToSend.append('existing_application_letter', formData.existing_application_letter);
      formDataToSend.append('existing_support_letter', formData.existing_support_letter);
      formDataToSend.append('existing_aggrement_letter', formData.existing_aggrement_letter);
      formDataToSend.append('existing_site_plan_drawing', formData.existing_site_plan_drawing);
      formDataToSend.append('status', initialData.status || 'pending');
      formDataToSend.append('response_reason', initialData.response_reason || '');
      formDataToSend.append('rejection_reason', initialData.rejection_reason || '');
    }
    formDataToSend.append('classification_codes', JSON.stringify(formData.classification_codes));
    formDataToSend.append('request_date', ethDateStrToGregStr(formData.request_date));
    additionalAttachments.forEach((att, index) => {
      if (att.file) {
        formDataToSend.append(`additional_attachment_${index}_name`, att.name || '');
        formDataToSend.append(`additional_attachment_${index}`, att.file);
      }
    });
    formDataToSend.append('additional_attachments_count', additionalAttachments.filter(a => a.file).length);
    onSubmit(formDataToSend, isEdit ? initialData.id : null);
  };

  const inputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]";
  const fileInputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400] bg-white";

  const renderFileInput = (label, field, existingValue) => (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {existingValue && (
        <div className="flex items-center gap-2 mb-2 text-sm text-green-500">
          <FaFile /> Currently uploaded: <a href={fileUrl(existingValue)} target="_blank" rel="noopener noreferrer" className="underline">View file</a>
        </div>
      )}
      <input type="file" onChange={(e) => handleFileChange(e, field)} className={fileInputClass} accept=".pdf" />
      <p className="text-xs text-gray-500 mt-1">PDF (max 10MB)</p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow">
      <h2 className="text-xl font-bold text-[#006400]">{isEdit ? 'Edit Service Request' : title}</h2>

      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Applicant Type (የአመልካች አይነት)</h4>
        <label className="block text-sm font-medium mb-1">አመልካች ዓይነት ይምረጡ / Select Applicant Type *</label>
        <select
          value={legalPersonType}
          onChange={(e) => setLegalPersonType(e.target.value)}
          className={inputClass}
          style={{ maxWidth: '420px' }}
          required
        >
          <option value="">ይምረጡ / Select Applicant Type</option>
          <option value="personal">ግለሰብ / Personal</option>
          <option value="company">ድርጅት / Company</option>
        </select>
      </div>
      {legalPersonType === 'personal' && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">1. አመልካች ግለሰብ ከሆነ / If Legal Person is Personal</h4>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Select Classification Code (የምደባ ኮድ ይምረጡ) *</label>
            <select value={personalClassificationCode} onChange={(e) => applyClassification(setPersonalClassificationCode, e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]" required>
              <option value="">-- Select --</option>
              {CLASSIFICATION_CODES.map(item => (
                <option key={item.code} value={item.code}>{item.code} - {item.amharic}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Name /ስም (አማ) *</label><input name="applicant_name" value={formData.applicant_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Name /ስም (En) *</label><input name="applicant_name_en" value={formData.applicant_name_en} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Nationality (ዜግነት) *</label><input name="nationality" value={formData.nationality} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">የፋይዳ ተለዋጭ ቁጥር (FAN) National ID *</label><input name="fan_national_id" value={formData.fan_national_id} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Region (ክልል) *</label><input name="region" value={formData.region} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Sub City (ክ/ከተማ) *</label><input name="subcity" value={formData.subcity} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ) *</label><input name="woreda" value={formData.woreda} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.)</label><input name="hno" value={formData.hno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">P.O.Box (ፖ.ኦ.ቡክስ)</label><input name="pobox" value={formData.pobox} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Tel. No. (ስልክ ቁ.)</label><input name="telno" value={formData.telno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">E-Mail (ኢመይል)</label><input type="email" name="email" value={formData.email} onChange={handleChange} className={inputClass} /></div>
          </div>
        </div>
      )}
      {legalPersonType === 'company' && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">2. አመልካች ድርጅት ከሆነ / If Legal Person is Company</h4>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Select Classification Code (የምደባ ኮድ ይምረጡ) *</label>
            <select value={companyClassificationCode} onChange={(e) => applyClassification(setCompanyClassificationCode, e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]" required>
              <option value="">-- Select --</option>
              {CLASSIFICATION_CODES.map(item => (
                <option key={item.code} value={item.code}>{item.code} - {item.amharic}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Company Name (የኩባንያው ስም) *</label><input name="exporter_company_name" value={formData.exporter_company_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Name of Manager (የስራ አስኪካጅ ስም) *</label><input name="manager_name" value={formData.manager_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">የፋይዳ ተለዋጭ ቁጥር (FAN) National ID *</label><input name="fan_national_id" value={formData.fan_national_id} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Nationality (ዜግነት) *</label><input name="manager_nationality" value={formData.manager_nationality} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Region (ክልል) *</label><input name="manager_region" value={formData.manager_region} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">K/Ketema (ክ/ከተማ)</label><input name="manager_ketema" value={formData.manager_ketema} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ)</label><input name="manager_woreda" value={formData.manager_woreda} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.)</label><input name="manager_hno" value={formData.manager_hno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">P.O.Box (ፖ.ኦ.ቡክስ)</label><input name="manager_pobox" value={formData.manager_pobox} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Fixed Phone No (ማዕከላዊ ስልክ ቁ.)</label><input name="manager_fixed_phone" value={formData.manager_fixed_phone} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Mobile Phone No (ሞባይል ስልክ ቁ.)</label><input name="manager_mobile" value={formData.manager_mobile} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">E-mail (ኢመይል)</label><input type="email" name="manager_email" value={formData.manager_email} onChange={handleChange} className={inputClass} /></div>
          </div>
        </div>
      )}
      <div className="mb-6">        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">3. Type of Operation (የሥራው አይነት)</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><label className="block text-sm font-medium mb-1">Type of Operation /የሥራው አይነት (አማ) *</label><input name="type_of_operation" value={formData.type_of_operation} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Type of Operation /የሥራው አይነት (En) *</label><input name="type_of_operation_en" value={formData.type_of_operation_en || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">ኮድ / Code</label><input name="operation_code" value={formData.operation_code} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">የማደያው አይነት / Types of station *</label><select name="station_type" value={formData.station_type} onChange={handleChange} className={inputClass} required><option value="">-- Select --</option><option value="አዲስ/New">አዲስ/New</option><option value="ነባር/Existing">ነባር/Existing</option></select></div>
        </div>
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">4. Site Location (የስራ ቦታ አድራሻ)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium mb-1">Trade Name /የንግድ ስም (አማ) *</label><input name="trade_name" value={formData.trade_name} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Trade Name /የንግድ ሥም (En) *</label><input name="trade_name_en" value={formData.trade_name_en} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Region /ክልል (አማ) *</label><input name="site_region" value={formData.site_region} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Region /ክልል (En) *</label><input name="site_region_en" value={formData.site_region_en} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Zone/City ዞን/ከተማ (አማ) *</label><input name="site_zone" value={formData.site_zone} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Zone/City ዞን/ከተማ (En) *</label><input name="site_zone_en" value={formData.site_zone_en} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Sub City ክ/ከተማ (አማ) *</label><input name="site_subcity" value={formData.site_subcity} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Sub City ክ/ከተማ (En) *</label><input name="site_subcity_en" value={formData.site_subcity_en} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ) (አማ) *</label><input name="site_woreda" value={formData.site_woreda} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ) (En) *</label><input name="site_woreda_en" value={formData.site_woreda_en} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.) (አማ)</label><input name="site_hno" value={formData.site_hno} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.) (En)</label><input name="site_hno_en" value={formData.site_hno_en} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">Phone No (ስልክ ቁ.)</label><input name="site_phone" value={formData.site_phone} onChange={handleChange} className={inputClass} /></div>
        </div>
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">የገንዘብ አቅም፣ የይዞታና ዲዛይን ንድፍ፣ የቦታ ስፋት / Financial Capacity, Site Plan & Design, Area of Land</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">የገንዘብ አቅም / Financial Capacity *</label>
            <input name="financial_capacity" value={formData.financial_capacity} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የቦታው ስፋት / Area of Land *</label>
            <input name="area_of_land" value={formData.area_of_land} onChange={handleChange} className={inputClass} required />
          </div>
          <div className="col-span-2">
            <div>
              <label className="block text-sm font-medium mb-1">የይዞታ ማረጋገጫና ዲዛይን ንድፍ / Site Plan and Design Drawing</label>
              {formData.existing_site_plan_drawing && (
                <div className="flex items-center gap-2 mb-2 text-sm text-green-500">
                  <FaFile /> Currently uploaded: <a href={fileUrl(formData.existing_site_plan_drawing)} target="_blank" rel="noopener noreferrer" className="underline">View file</a>
                </div>
              )}
              <input type="file" onChange={(e) => { const f = e.target.files[0]; if (f && f.size === 0) { toast.error('The selected file is empty. Please choose a valid PDF document.'); e.target.value = ''; return; } setFormData({ ...formData, site_plan_drawing: f }); }} className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400] bg-white" accept=".pdf" />
              <p className="text-xs text-gray-500 mt-1">PDF (max 10MB)</p>
            </div>
          </div>
        </div>
      </div>

      
      {title && title.includes('Construction Permit') && (
        <div>
          {/* Construction Permeation Fields */}
          <div>
            <label className="block text-sm font-medium mb-1">የግንባታው አገልግሎት የሚውለው / Construction service for</label>
            <input name="construction_service_for" value={formData.construction_service_for} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የካርታ ቁጥር / Plan no</label>
            <input name="plan_no" value={formData.plan_no} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የጂፒኤስ አድራሻ: (ሰሜን) / GPS Location: (North)</label>
            <input name="gps_north" value={formData.gps_north} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የጂፒኤስ አድራሻ: (ምስራቅ) / GPS Location: (East)</label>
            <input name="gps_east" value={formData.gps_east} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Scope of work station: Total project cost</label>
            <input name="total_project_cost" value={formData.total_project_cost} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Professional in charge construction</label>
            <input name="professional_in_charge" value={formData.professional_in_charge} onChange={handleChange} className={inputClass} />
          </div>
        </div>
      )}
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Attachments (ተያዥ መረጃዎች)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderFileInput('Application Letter (የማመልከቻ ደብዳቤ)', 'application_letter', formData.existing_application_letter)}
          {renderFileInput('Support Letter (የድጋፍ ደብዳቤ)', 'support_letter', formData.existing_support_letter)}
          {renderFileInput('Agreement Letter (የስምምነት ደብዳቤ)', 'aggrement_letter', formData.existing_aggrement_letter)}
        </div>
      </div>
      
      {title && title.includes('Construction Permit') && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">መያያዝ ያለባቸዉ መረጃዎች ዝርዝር / Attachments:</h4>
          <ul className="list-decimal pl-5 space-y-1 text-sm">
            <li>ፋይዳ መታወቂያ</li>
            <li>ፓስፖርት / I.D / Passport</li>
            <li>የባለቤትነት/የኪራይ ማረጋገጫ ውል/Investment /Rent Agreement</li>
            <li>የንግድ የምዝገባ የምስክር ወረቀት/Trade Registration Certificate</li>
            <li>የቦታ ካርታና ዲዛይን ንድፍ/Site Plan and Design Drawing</li>
            <li>የገንዘብ አቅም የባንክ መረጃ/Bank Statement for Financial Capacity</li>
            <li>ቢዝነስ ፕላን/የአዋጭነት ጥናት/ Business Plan / Feasibility Study</li>
            <li>የአካባቢ ተጽዕኖ ግምገማ ጥናት/Environmental Impact Assessment Study Report</li>
            <li>አመልካቹ ባለቤት ካልሆነ ህጋዊ ውክልና/Legal Representation (If applicant is not the owner)</li>
            <li>የማህበር መመስረቻ ደንብ/Article of Association (for PLC)</li>
            <li>የኢንሹራንስ ሽፋን/ Insurance Commitment letter</li>
            <li>የድርጅቱ የንግድ ልውውጥ መረጃ Company's Supply Records</li>
            <li>የሥራው እቅድ/Working program</li>
            <li>መሬቱ ለፔትሮሊየም ውጤቶች ግንባታ የሚሆን መሆኑን የሚገልፅ የአፈር ምርምር ጥናት/soil analysis study</li>
            <li>(የነዳጅ ማደያ መሣሪያዎች ተከላና ጥገና አገልግሎት) የሚያደርግ ባለሞያ ወይም ተቋም ከነዳጅና ኢነርጂ ባለስልጣን ብቃት የወሰደ መሆኑ የሚገልፅ ሰነድ</li>
          </ul>
        </div>
      )}
      <div className="mb-6">
        <label className="block text-sm font-medium mb-1">Remark (ማስታወሻ)</label>
        <textarea name="remark" value={formData.remark} onChange={handleChange} className={inputClass} rows="3" />
      </div>
      <div className="col-span-2">
        <label className="block text-sm font-medium mb-1">ያመለከቱበት ቀን / Request date</label>
        <EthiopianDatePicker name="request_date" value={formData.request_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Additional Attachments (ተያያዥ መረጃዎች)</h4>
        <button type="button" onClick={addAdditionalAttachment} className="text-[#006400] font-medium flex items-center gap-1 hover:underline">
          <FaPlus /> Add Attachment
        </button>
        {additionalAttachments.map((att, index) => (
          <div key={index} className="mt-3 p-3 border border-gray-200 rounded bg-gray-50">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Additional Attachment {index + 1}</span>
              <button type="button" onClick={() => removeAdditionalAttachment(index)} className="text-red-500 hover:text-red-700">
                <FaTrash />
              </button>
            </div>
            <input 
              type="text" 
              placeholder="Attachment name/description" 
              value={att.name} 
              onChange={(e) => handleAdditionalFileNameChange(e, index)} 
              className={inputClass} 
            />
            <input 
              type="file" 
              onChange={(e) => handleAdditionalFileChange(e, index)} 
              className="mt-2 text-sm" 
              accept=".pdf" 
            />
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2"><FaTimes /> Cancel</button>
        <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 flex items-center gap-2"><FaPaperPlane /> {isEdit ? 'Update Request' : 'Submit'}</button>
      </div>
    </form>
  );
}

function NewFuelRefineryForm({ onSubmit, onCancel, initialData = null, isEdit = false, title }) {
  useFormDraft(() => [formData, setFormData], initialData);
  const [formData, setFormData] = useState({
    applicant_name: '', applicant_name_en: '', nationality: '', fan_national_id: '',
        region: '', subcity: '', woreda: '', hno: '', pobox: '', telno: '', email: '',
        refinery_name: '',
        manager_name: '', manager_nationality: '',
    manager_region: '', manager_ketema: '', manager_woreda: '', manager_hno: '', manager_pobox: '',
    manager_fixed_phone: '', manager_mobile: '', manager_email: '',
    type_of_operation: '', type_of_operation_en: '', operation_code: '', station_type: '',
    trade_name: '', trade_name_en: '',
    site_region: '', site_region_en: '', site_zone: '', site_zone_en: '', site_subcity: '', site_subcity_en: '', site_woreda: '', site_woreda_en: '', site_hno: '', site_hno_en: '', site_phone: '',
    classification_codes: [],
    financial_capacity: '',
    site_plan_drawing: null,
    area_of_land: '',

        remark: '',
    request_date: toEthiopianDateStr(new Date()),
    application_letter: null, support_letter: null, aggrement_letter: null,
    existing_application_letter: '', existing_support_letter: '', existing_aggrement_letter: '',
    existing_site_plan_drawing: ''
  });
  const [legalPersonType, setLegalPersonType] = useState('');
  const [personalClassificationCode, setPersonalClassificationCode] = useState('');
  const [companyClassificationCode, setCompanyClassificationCode] = useState('');

  useEffect(() => {
    if (initialData) {
      setFormData({
        applicant_name: initialData.applicant_name || '', applicant_name_en: initialData.applicant_name_en || '', nationality: initialData.nationality || '', fan_national_id: initialData.fan_national_id || '',
        region: initialData.region || '', subcity: initialData.subcity || '', woreda: initialData.woreda || '',
        hno: initialData.hno || '', pobox: initialData.pobox || '', telno: initialData.telno || '', email: initialData.email || '',
        refinery_name: initialData.refinery_name || '',
        manager_name: initialData.manager_name || '', manager_nationality: initialData.manager_nationality || '',
        manager_region: initialData.manager_region || '',
        manager_ketema: initialData.manager_ketema || '', manager_woreda: initialData.manager_woreda || '',
        manager_hno: initialData.manager_hno || '', manager_pobox: initialData.manager_pobox || '',
        manager_fixed_phone: initialData.manager_fixed_phone || '', manager_mobile: initialData.manager_mobile || '',
        manager_email: initialData.manager_email || '',
        type_of_operation: initialData.type_of_operation || '', type_of_operation_en: initialData.type_of_operation_en || '', operation_code: initialData.operation_code || '', station_type: initialData.station_type || '',
        trade_name: initialData.trade_name || '', trade_name_en: initialData.trade_name_en || '',
        site_region: initialData.site_region || '', site_region_en: initialData.site_region_en || '',
        site_zone: initialData.site_zone || '', site_zone_en: initialData.site_zone_en || '',
        site_subcity: initialData.site_subcity || '', site_subcity_en: initialData.site_subcity_en || '',
        site_woreda: initialData.site_woreda || '', site_woreda_en: initialData.site_woreda_en || '',
        site_hno: initialData.site_hno || '', site_hno_en: initialData.site_hno_en || '',
        site_phone: initialData.site_phone || '',
        classification_codes: initialData.classification_codes ? JSON.parse(initialData.classification_codes) : [],
        financial_capacity: initialData.financial_capacity || '',
        site_plan_drawing: null,
        area_of_land: initialData.area_of_land || '',
        construction_service_for: initialData.construction_service_for || '',
        plan_no: initialData.plan_no || '',
        gps_north: initialData.gps_north || '',
        gps_east: initialData.gps_east || '',
        total_project_cost: initialData.total_project_cost || '',
        professional_in_charge: initialData.professional_in_charge || '',
        remark: initialData.remark || '',
        application_letter: null, support_letter: null, aggrement_letter: null,
        existing_application_letter: initialData.application_letter || '',
        existing_support_letter: initialData.support_letter || '',
        existing_aggrement_letter: initialData.aggrement_letter || '',
        existing_site_plan_drawing: initialData.site_plan_drawing || '',
        request_date: initialData.request_date ? convertDbDateToDisplay(initialData.request_date) : toEthiopianDateStr(new Date())
      });
      if (initialData.refinery_name) setLegalPersonType('company'); else setLegalPersonType('personal');
      if (initialData.classification_codes) {
        const firstCode = (JSON.parse(initialData.classification_codes) || [])[0] || '';
        if (initialData.refinery_name) setCompanyClassificationCode(firstCode);
        else setPersonalClassificationCode(firstCode);
      }
    }
  }, [initialData]);

  const handleChange = (e) => { setFormData({ ...formData, [e.target.name]: e.target.value }); };
  const handleFileChange = (e, field) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    setFormData({ ...formData, [field]: selectedFile });
  };;

  const applyClassification = (setter, code) => {
    const item = CLASSIFICATION_CODES.find(c => c.code === code);
    setter(code);
    setFormData(prev => ({
      ...prev,
      classification_codes: code ? [code] : [],
      type_of_operation: item ? item.amharic : '',
      type_of_operation_en: item ? item.english : '',
      operation_code: code || ''
    }));
  };

  const [additionalAttachments, setAdditionalAttachments] = useState([]);

  const handleAdditionalFileChange = (e, index) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    const newAttachments = [...additionalAttachments];
    newAttachments[index].file = selectedFile;
    setAdditionalAttachments(newAttachments);
  };

  const handleAdditionalFileNameChange = (e, index) => {
    const newAttachments = [...additionalAttachments];
    newAttachments[index].name = e.target.value;
    setAdditionalAttachments(newAttachments);
  };

  const addAdditionalAttachment = () => {
    if (additionalAttachments.length >= 10) {
      toast.warn('You can only add up to 10 attachments');
      return;
    }
    setAdditionalAttachments([...additionalAttachments, { name: '', file: null }]);
  };

  const removeAdditionalAttachment = (index) => {
    const newAttachments = additionalAttachments.filter((_, i) => i !== index);
    setAdditionalAttachments(newAttachments);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    Object.keys(formData).forEach(key => {
      if (formData[key] !== null && key !== 'existing_application_letter' && key !== 'existing_support_letter' && key !== 'existing_aggrement_letter' && key !== 'classification_codes' && key !== 'request_date') {
        if (formData[key] instanceof File) { if (formData[key]) formDataToSend.append(key, formData[key]); }
        else { formDataToSend.append(key, formData[key]); }
      }
    });
    if (isEdit && initialData) {
      formDataToSend.append('existing_application_letter', formData.existing_application_letter);
      formDataToSend.append('existing_support_letter', formData.existing_support_letter);
      formDataToSend.append('existing_aggrement_letter', formData.existing_aggrement_letter);
      formDataToSend.append('existing_site_plan_drawing', formData.existing_site_plan_drawing);
      formDataToSend.append('status', initialData.status || 'pending');
      formDataToSend.append('response_reason', initialData.response_reason || '');
      formDataToSend.append('rejection_reason', initialData.rejection_reason || '');
    }
    formDataToSend.append('classification_codes', JSON.stringify(formData.classification_codes));
    formDataToSend.append('request_date', ethDateStrToGregStr(formData.request_date));
    additionalAttachments.forEach((att, index) => {
      if (att.file) {
        formDataToSend.append(`additional_attachment_${index}_name`, att.name || '');
        formDataToSend.append(`additional_attachment_${index}`, att.file);
      }
    });
    formDataToSend.append('additional_attachments_count', additionalAttachments.filter(a => a.file).length);
    onSubmit(formDataToSend, isEdit ? initialData.id : null);
  };

  const inputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]";
  const fileInputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400] bg-white";

  const renderFileInput = (label, field, existingValue) => (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {existingValue && (
        <div className="flex items-center gap-2 mb-2 text-sm text-green-500">
          <FaFile /> Currently uploaded: <a href={fileUrl(existingValue)} target="_blank" rel="noopener noreferrer" className="underline">View file</a>
        </div>
      )}
      <input type="file" onChange={(e) => handleFileChange(e, field)} className={fileInputClass} accept=".pdf" />
      <p className="text-xs text-gray-500 mt-1">PDF (max 10MB)</p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow">
      <h2 className="text-xl font-bold text-[#006400]">{isEdit ? 'Edit Service Request' : title}</h2>

      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Applicant Type (የአመልካች አይነት)</h4>
        <label className="block text-sm font-medium mb-1">አመልካች ዓይነት ይምረጡ / Select Applicant Type *</label>
        <select
          value={legalPersonType}
          onChange={(e) => setLegalPersonType(e.target.value)}
          className={inputClass}
          style={{ maxWidth: '420px' }}
          required
        >
          <option value="">ይምረጡ / Select Applicant Type</option>
          <option value="personal">ግለሰብ / Personal</option>
          <option value="company">ድርጅት / Company</option>
        </select>
      </div>
      {legalPersonType === 'personal' && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">1. አመልካች ግለሰብ ከሆነ / If Legal Person is Personal</h4>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Select Classification Code (የምደባ ኮድ ይምረጡ) *</label>
            <select value={personalClassificationCode} onChange={(e) => applyClassification(setPersonalClassificationCode, e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]" required>
              <option value="">-- Select --</option>
              {CLASSIFICATION_CODES.map(item => (
                <option key={item.code} value={item.code}>{item.code} - {item.amharic}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Name /ስም (አማ) *</label><input name="applicant_name" value={formData.applicant_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Name /ስም (En) *</label><input name="applicant_name_en" value={formData.applicant_name_en || ''} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Nationality (ዜግነት) *</label><input name="nationality" value={formData.nationality} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">የፋይዳ ተለዋጭ ቁጥር (FAN) National ID *</label><input name="fan_national_id" value={formData.fan_national_id} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Region (ክልል) *</label><input name="region" value={formData.region} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Sub City (ክ/ከተማ) *</label><input name="subcity" value={formData.subcity} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ) *</label><input name="woreda" value={formData.woreda} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.)</label><input name="hno" value={formData.hno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">P.O.Box (ፖ.ኦ.ቡክስ)</label><input name="pobox" value={formData.pobox} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Tel. No. (ስልክ ቁ.)</label><input name="telno" value={formData.telno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">E-Mail (ኢመይል)</label><input type="email" name="email" value={formData.email} onChange={handleChange} className={inputClass} /></div>
          </div>
        </div>
      )}
      {legalPersonType === 'company' && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">2. አመልካች ድርጅት ከሆነ / If Legal Person is Company</h4>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Select Classification Code (የምደባ ኮድ ይምረጡ) *</label>
            <select value={companyClassificationCode} onChange={(e) => applyClassification(setCompanyClassificationCode, e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]" required>
              <option value="">-- Select --</option>
              {CLASSIFICATION_CODES.map(item => (
                <option key={item.code} value={item.code}>{item.code} - {item.amharic}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Company Name (የኩባንያው ስም) *</label><input name="refinery_name" value={formData.refinery_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Name of Manager (የስራ አስኪካጅ ስም) *</label><input name="manager_name" value={formData.manager_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">የፋይዳ ተለዋጭ ቁጥር (FAN) National ID *</label><input name="fan_national_id" value={formData.fan_national_id} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Nationality (ዜግነት) *</label><input name="manager_nationality" value={formData.manager_nationality} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Region (ክልል) *</label><input name="manager_region" value={formData.manager_region} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">K/Ketema (ክ/ከተማ)</label><input name="manager_ketema" value={formData.manager_ketema} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ)</label><input name="manager_woreda" value={formData.manager_woreda} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.)</label><input name="manager_hno" value={formData.manager_hno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">P.O.Box (ፖ.ኦ.ቡክስ)</label><input name="manager_pobox" value={formData.manager_pobox} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Fixed Phone No (ማዕከላዊ ስልክ ቁ.)</label><input name="manager_fixed_phone" value={formData.manager_fixed_phone} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Mobile Phone No (ሞባይል ስልክ ቁ.)</label><input name="manager_mobile" value={formData.manager_mobile} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">E-mail (ኢመይል)</label><input type="email" name="manager_email" value={formData.manager_email} onChange={handleChange} className={inputClass} /></div>
          </div>
        </div>
      )}
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">3. Type of Operation (የሥራው አይነት)</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><label className="block text-sm font-medium mb-1">Type of Operation /የሥራው አይነት (አማ) *</label><input name="type_of_operation" value={formData.type_of_operation} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Type of Operation /የሥራው አይነት (En) *</label><input name="type_of_operation_en" value={formData.type_of_operation_en || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">ኮድ / Code</label><input name="operation_code" value={formData.operation_code} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">የማደያው አይነት / Types of station *</label><select name="station_type" value={formData.station_type} onChange={handleChange} className={inputClass} required><option value="">-- Select --</option><option value="አዲስ/New">አዲስ/New</option><option value="ነባር/Existing">ነባር/Existing</option></select></div>
        </div>
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">4. Site Location (የስራ ቦታ አድራሻ)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium mb-1">Trade Name /የንግድ ስም (አማ) *</label><input name="trade_name" value={formData.trade_name || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Trade Name /የንግድ ሥም (En) *</label><input name="trade_name_en" value={formData.trade_name_en || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Region /ክልል (አማ) *</label><input name="site_region" value={formData.site_region} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Region /ክልል (En) *</label><input name="site_region_en" value={formData.site_region_en || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Zone/City /ዞን/ከተማ (አማ) *</label><input name="site_zone" value={formData.site_zone} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Zone/City /ዞን/ከተማ (En) *</label><input name="site_zone_en" value={formData.site_zone_en || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Sub City /ክ/ከተማ (አማ) *</label><input name="site_subcity" value={formData.site_subcity} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Sub City /ክ/ከተማ (En) *</label><input name="site_subcity_en" value={formData.site_subcity_en || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Woreda/Kebele /ወረዳ/ቀበሌ (አማ) *</label><input name="site_woreda" value={formData.site_woreda} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Woreda/Kebele /ወረዳ/ቀበሌ (En) *</label><input name="site_woreda_en" value={formData.site_woreda_en || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">H.No /የቤት ቁ. (አማ)</label><input name="site_hno" value={formData.site_hno} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">H.No /የቤት ቁ. (En)</label><input name="site_hno_en" value={formData.site_hno_en || ''} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">Phone No (ስልክ ቁ.)</label><input name="site_phone" value={formData.site_phone} onChange={handleChange} className={inputClass} /></div>
        </div>
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">የገንዘብ አቅም፣ የይዞታና ዲዛይን ንድፍ፣ የቦታ ስፋት / Financial Capacity, Site Plan & Design, Area of Land</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">የገንዘብ አቅም / Financial Capacity *</label>
            <input name="financial_capacity" value={formData.financial_capacity} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የቦታው ስፋት / Area of Land *</label>
            <input name="area_of_land" value={formData.area_of_land} onChange={handleChange} className={inputClass} required />
          </div>
          <div className="col-span-2">
            <div>
              <label className="block text-sm font-medium mb-1">የይዞታ ማረጋገጫና ዲዛይን ንድፍ / Site Plan and Design Drawing</label>
              {formData.existing_site_plan_drawing && (
                <div className="flex items-center gap-2 mb-2 text-sm text-green-500">
                  <FaFile /> Currently uploaded: <a href={fileUrl(formData.existing_site_plan_drawing)} target="_blank" rel="noopener noreferrer" className="underline">View file</a>
                </div>
              )}
              <input type="file" onChange={(e) => { const f = e.target.files[0]; if (f && f.size === 0) { toast.error('The selected file is empty. Please choose a valid PDF document.'); e.target.value = ''; return; } setFormData({ ...formData, site_plan_drawing: f }); }} className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400] bg-white" accept=".pdf" />
              <p className="text-xs text-gray-500 mt-1">PDF (max 10MB)</p>
            </div>
          </div>
        </div>
      </div>

      
      {title && title.includes('Construction Permit') && (
        <div>
          {/* Construction Permeation Fields */}
          <div>
            <label className="block text-sm font-medium mb-1">የግንባታው አገልግሎት የሚውለው / Construction service for</label>
            <input name="construction_service_for" value={formData.construction_service_for} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የካርታ ቁጥር / Plan no</label>
            <input name="plan_no" value={formData.plan_no} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የጂፒኤስ አድራሻ: (ሰሜን) / GPS Location: (North)</label>
            <input name="gps_north" value={formData.gps_north} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የጂፒኤስ አድራሻ: (ምስራቅ) / GPS Location: (East)</label>
            <input name="gps_east" value={formData.gps_east} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Scope of work station: Total project cost</label>
            <input name="total_project_cost" value={formData.total_project_cost} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Professional in charge construction</label>
            <input name="professional_in_charge" value={formData.professional_in_charge} onChange={handleChange} className={inputClass} />
          </div>
        </div>
      )}
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Attachments (ተያዥ መረጃዎች)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderFileInput('Application Letter (የማመልከቻ ደብዳቤ)', 'application_letter', formData.existing_application_letter)}
          {renderFileInput('Support Letter (የድጋፍ ደብዳቤ)', 'support_letter', formData.existing_support_letter)}
          {renderFileInput('Agreement Letter (የስምምነት ደብዳቤ)', 'aggrement_letter', formData.existing_aggrement_letter)}
        </div>
      </div>
      
      {title && title.includes('Construction Permit') && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">መያያዝ ያለባቸዉ መረጃዎች ዝርዝር / Attachments:</h4>
          <ul className="list-decimal pl-5 space-y-1 text-sm">
            <li>ፋይዳ መታወቂያ</li>
            <li>ፓስፖርት / I.D / Passport</li>
            <li>የባለቤትነት/የኪራይ ማረጋገጫ ውል/Investment /Rent Agreement</li>
            <li>የንግድ የምዝገባ የምስክር ወረቀት/Trade Registration Certificate</li>
            <li>የቦታ ካርታና ዲዛይን ንድፍ/Site Plan and Design Drawing</li>
            <li>የገንዘብ አቅም የባንክ መረጃ/Bank Statement for Financial Capacity</li>
            <li>ቢዝነስ ፕላን/የአዋጭነት ጥናት/ Business Plan / Feasibility Study</li>
            <li>የአካባቢ ተጽዕኖ ግምገማ ጥናት/Environmental Impact Assessment Study Report</li>
            <li>አመልካቹ ባለቤት ካልሆነ ህጋዊ ውክልና/Legal Representation (If applicant is not the owner)</li>
            <li>የማህበር መመስረቻ ደንብ/Article of Association (for PLC)</li>
            <li>የኢንሹራንስ ሽፋን/ Insurance Commitment letter</li>
            <li>የድርጅቱ የንግድ ልውውጥ መረጃ Company's Supply Records</li>
            <li>የሥራው እቅድ/Working program</li>
            <li>መሬቱ ለፔትሮሊየም ውጤቶች ግንባታ የሚሆን መሆኑን የሚገልፅ የአፈር ምርምር ጥናት/soil analysis study</li>
            <li>(የነዳጅ ማደያ መሣሪያዎች ተከላና ጥገና አገልግሎት) የሚያደርግ ባለሞያ ወይም ተቋም ከነዳጅና ኢነርጂ ባለስልጣን ብቃት የወሰደ መሆኑ የሚገልፅ ሰነድ</li>
          </ul>
        </div>
      )}
      <div className="mb-6">
        <label className="block text-sm font-medium mb-1">Remark (ማስታወሻ)</label>
        <textarea name="remark" value={formData.remark} onChange={handleChange} className={inputClass} rows="3" />
      </div>
      <div className="col-span-2">
        <label className="block text-sm font-medium mb-1">ያመለከቱበት ቀን / Request date</label>
        <EthiopianDatePicker name="request_date" value={formData.request_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Additional Attachments (ተያያዥ መረጃዎች)</h4>
        <button type="button" onClick={addAdditionalAttachment} className="text-[#006400] font-medium flex items-center gap-1 hover:underline">
          <FaPlus /> Add Attachment
        </button>
        {additionalAttachments.map((att, index) => (
          <div key={index} className="mt-3 p-3 border border-gray-200 rounded bg-gray-50">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Additional Attachment {index + 1}</span>
              <button type="button" onClick={() => removeAdditionalAttachment(index)} className="text-red-500 hover:text-red-700">
                <FaTrash />
              </button>
            </div>
            <input 
              type="text" 
              placeholder="Attachment name/description" 
              value={att.name} 
              onChange={(e) => handleAdditionalFileNameChange(e, index)} 
              className={inputClass} 
            />
            <input 
              type="file" 
              onChange={(e) => handleAdditionalFileChange(e, index)} 
              className="mt-2 text-sm" 
              accept=".pdf" 
            />
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2"><FaTimes /> Cancel</button>
        <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 flex items-center gap-2"><FaPaperPlane /> {isEdit ? 'Update Request' : 'Submit'}</button>
      </div>
    </form>
  );
}

function NewFuelTransportationForm({ onSubmit, onCancel, initialData = null, isEdit = false, title }) {
  useFormDraft(() => [formData, setFormData], initialData);
  const [formData, setFormData] = useState({
    applicant_name: '', applicant_name_en: '', nationality: '', fan_national_id: '',
        region: '', subcity: '', woreda: '', hno: '', pobox: '', telno: '', email: '',
        transporter_company_name: '',
        manager_name: '', manager_nationality: '',
    manager_region: '', manager_ketema: '', manager_woreda: '', manager_hno: '', manager_pobox: '',
    manager_fixed_phone: '', manager_mobile: '', manager_email: '',
    type_of_operation: '', type_of_operation_en: '', operation_code: '', station_type: '',
    trade_name: '', trade_name_en: '',
    site_region: '', site_region_en: '', site_zone: '', site_zone_en: '', site_subcity: '', site_subcity_en: '', site_woreda: '', site_woreda_en: '', site_hno: '', site_hno_en: '', site_phone: '',
    classification_codes: [],
    financial_capacity: '',
    site_plan_drawing: null,
    area_of_land: '',

        remark: '',
    application_letter: null, support_letter: null, aggrement_letter: null,
    existing_application_letter: '', existing_support_letter: '', existing_aggrement_letter: '',
    existing_site_plan_drawing: '',
    request_date: toEthiopianDateStr(new Date())
  });
  const [additionalAttachments, setAdditionalAttachments] = useState([]);
  const [legalPersonType, setLegalPersonType] = useState('');
  const [personalClassificationCode, setPersonalClassificationCode] = useState('');
  const [companyClassificationCode, setCompanyClassificationCode] = useState('');

  const handleAdditionalFileChange = (e, index) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    const newAttachments = [...additionalAttachments];
    newAttachments[index].file = selectedFile;
    setAdditionalAttachments(newAttachments);
  };

  const handleAdditionalFileNameChange = (e, index) => {
    const newAttachments = [...additionalAttachments];
    newAttachments[index].name = e.target.value;
    setAdditionalAttachments(newAttachments);
  };

  const addAdditionalAttachment = () => {
    if (additionalAttachments.length >= 10) {
      toast.warn('You can only add up to 10 attachments');
      return;
    }
    setAdditionalAttachments([...additionalAttachments, { name: '', file: null }]);
  };

  const removeAdditionalAttachment = (index) => {
    const newAttachments = additionalAttachments.filter((_, i) => i !== index);
    setAdditionalAttachments(newAttachments);
  };

  useEffect(() => {
    if (initialData) {
      setFormData({
        applicant_name: initialData.applicant_name || '', applicant_name_en: initialData.applicant_name_en || '', nationality: initialData.nationality || '', fan_national_id: initialData.fan_national_id || '',
        region: initialData.region || '', subcity: initialData.subcity || '', woreda: initialData.woreda || '',
        hno: initialData.hno || '', pobox: initialData.pobox || '', telno: initialData.telno || '', email: initialData.email || '',
        distributor_company_name: initialData.distributor_company_name || '',
        manager_name: initialData.manager_name || '', manager_nationality: initialData.manager_nationality || '',
        manager_region: initialData.manager_region || '',
        manager_ketema: initialData.manager_ketema || '', manager_woreda: initialData.manager_woreda || '',
        manager_hno: initialData.manager_hno || '', manager_pobox: initialData.manager_pobox || '',
        manager_fixed_phone: initialData.manager_fixed_phone || '', manager_mobile: initialData.manager_mobile || '',
        manager_email: initialData.manager_email || '',
        type_of_operation: initialData.type_of_operation || '', type_of_operation_en: initialData.type_of_operation_en || '', operation_code: initialData.operation_code || '', station_type: initialData.station_type || '',
        trade_name: initialData.trade_name || '', trade_name_en: initialData.trade_name_en || '',
        site_region: initialData.site_region || '', site_region_en: initialData.site_region_en || '',
        site_zone: initialData.site_zone || '', site_zone_en: initialData.site_zone_en || '',
        site_subcity: initialData.site_subcity || '', site_subcity_en: initialData.site_subcity_en || '',
        site_woreda: initialData.site_woreda || '', site_woreda_en: initialData.site_woreda_en || '',
        site_hno: initialData.site_hno || '', site_hno_en: initialData.site_hno_en || '',
        site_phone: initialData.site_phone || '',
        classification_codes: initialData.classification_codes ? JSON.parse(initialData.classification_codes) : [],
        financial_capacity: initialData.financial_capacity || '',
        site_plan_drawing: null,
        area_of_land: initialData.area_of_land || '',
        construction_service_for: initialData.construction_service_for || '',
        plan_no: initialData.plan_no || '',
        gps_north: initialData.gps_north || '',
        gps_east: initialData.gps_east || '',
        total_project_cost: initialData.total_project_cost || '',
        professional_in_charge: initialData.professional_in_charge || '',
        remark: initialData.remark || '',
        application_letter: null, support_letter: null, aggrement_letter: null,
        existing_application_letter: initialData.application_letter || '',
        existing_support_letter: initialData.support_letter || '',
        existing_aggrement_letter: initialData.aggrement_letter || '',
        existing_site_plan_drawing: initialData.site_plan_drawing || '',
        request_date: initialData.request_date ? convertDbDateToDisplay(initialData.request_date) : toEthiopianDateStr(new Date())
      });
      if (initialData.transporter_company_name) setLegalPersonType('company'); else setLegalPersonType('personal');
      if (initialData.classification_codes) {
        const firstCode = (JSON.parse(initialData.classification_codes) || [])[0] || '';
        if (initialData.transporter_company_name) setCompanyClassificationCode(firstCode);
        else setPersonalClassificationCode(firstCode);
      }
    }
  }, [initialData]);

  const handleChange = (e) => { setFormData({ ...formData, [e.target.name]: e.target.value }); };
  const handleFileChange = (e, field) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    setFormData({ ...formData, [field]: selectedFile });
  };;

  const applyClassification = (setter, code) => {
    const item = CLASSIFICATION_CODES.find(c => c.code === code);
    setter(code);
    setFormData(prev => ({
      ...prev,
      classification_codes: code ? [code] : [],
      type_of_operation: item ? item.amharic : '',
      type_of_operation_en: item ? item.english : '',
      operation_code: code || ''
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    Object.keys(formData).forEach(key => {
      if (formData[key] !== null && key !== 'existing_application_letter' && key !== 'existing_support_letter' && key !== 'existing_aggrement_letter' && key !== 'classification_codes' && key !== 'request_date') {
        if (formData[key] instanceof File) { if (formData[key]) formDataToSend.append(key, formData[key]); }
        else { formDataToSend.append(key, formData[key]); }
      }
    });
    if (isEdit && initialData) {
      formDataToSend.append('existing_application_letter', formData.existing_application_letter);
      formDataToSend.append('existing_support_letter', formData.existing_support_letter);
      formDataToSend.append('existing_aggrement_letter', formData.existing_aggrement_letter);
      formDataToSend.append('existing_site_plan_drawing', formData.existing_site_plan_drawing);
      formDataToSend.append('status', initialData.status || 'pending');
      formDataToSend.append('response_reason', initialData.response_reason || '');
      formDataToSend.append('rejection_reason', initialData.rejection_reason || '');
    }
    formDataToSend.append('classification_codes', JSON.stringify(formData.classification_codes));
    formDataToSend.append('request_date', ethDateStrToGregStr(formData.request_date));
    additionalAttachments.forEach((att, index) => {
      if (att.file) {
        formDataToSend.append(`additional_attachment_${index}_name`, att.name || '');
        formDataToSend.append(`additional_attachment_${index}`, att.file);
      }
    });
    formDataToSend.append('additional_attachments_count', additionalAttachments.filter(a => a.file).length);
    onSubmit(formDataToSend, isEdit ? initialData.id : null);
  };

  const inputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]";
  const fileInputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400] bg-white";

  const renderFileInput = (label, field, existingValue) => (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {existingValue && (
        <div className="flex items-center gap-2 mb-2 text-sm text-green-500">
          <FaFile /> Currently uploaded: <a href={fileUrl(existingValue)} target="_blank" rel="noopener noreferrer" className="underline">View file</a>
        </div>
      )}
      <input type="file" onChange={(e) => handleFileChange(e, field)} className={fileInputClass} accept=".pdf" />
      <p className="text-xs text-gray-500 mt-1">PDF (max 10MB)</p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow">
      <h2 className="text-xl font-bold text-[#006400]">{isEdit ? 'Edit Service Request' : title}</h2>

      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Applicant Type (የአመልካች አይነት)</h4>
        <label className="block text-sm font-medium mb-1">አመልካች ዓይነት ይምረጡ / Select Applicant Type *</label>
        <select
          value={legalPersonType}
          onChange={(e) => setLegalPersonType(e.target.value)}
          className={inputClass}
          style={{ maxWidth: '420px' }}
          required
        >
          <option value="">ይምረጡ / Select Applicant Type</option>
          <option value="personal">ግለሰብ / Personal</option>
          <option value="company">ድርጅት / Company</option>
        </select>
      </div>
      {legalPersonType === 'personal' && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">1. አመልካች ግለሰብ ከሆነ / If Legal Person is Personal</h4>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Select Classification Code (የምደባ ኮድ ይምረጡ) *</label>
            <select value={personalClassificationCode} onChange={(e) => applyClassification(setPersonalClassificationCode, e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]" required>
              <option value="">-- Select --</option>
              {CLASSIFICATION_CODES.map(item => (
                <option key={item.code} value={item.code}>{item.code} - {item.amharic}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Name /ስም (አማ) *</label><input name="applicant_name" value={formData.applicant_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Name /ስም (En) *</label><input name="applicant_name_en" value={formData.applicant_name_en || ''} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Nationality (ዜግነት) *</label><input name="nationality" value={formData.nationality} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">የፋይዳ ተለዋጭ ቁጥር (FAN) National ID *</label><input name="fan_national_id" value={formData.fan_national_id} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Region (ክልል) *</label><input name="region" value={formData.region} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Sub City (ክ/ከተማ) *</label><input name="subcity" value={formData.subcity} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ) *</label><input name="woreda" value={formData.woreda} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.)</label><input name="hno" value={formData.hno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">P.O.Box (ፖ.ኦ.ቡክስ)</label><input name="pobox" value={formData.pobox} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Tel. No. (ስልክ ቁ.)</label><input name="telno" value={formData.telno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">E-Mail (ኢመይል)</label><input type="email" name="email" value={formData.email} onChange={handleChange} className={inputClass} /></div>
          </div>
        </div>
      )}
      {legalPersonType === 'company' && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">2. አመልካች ድርጅት ከሆነ / If Legal Person is Company</h4>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Select Classification Code (የምደባ ኮድ ይምረጡ) *</label>
            <select value={companyClassificationCode} onChange={(e) => applyClassification(setCompanyClassificationCode, e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]" required>
              <option value="">-- Select --</option>
              {CLASSIFICATION_CODES.map(item => (
                <option key={item.code} value={item.code}>{item.code} - {item.amharic}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Company Name (የኩባንያው ስም) *</label><input name="transporter_company_name" value={formData.transporter_company_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Name of Manager (የስራ አስኪካጅ ስም) *</label><input name="manager_name" value={formData.manager_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">የፋይዳ ተለዋጭ ቁጥር (FAN) National ID *</label><input name="fan_national_id" value={formData.fan_national_id} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Nationality (ዜግነት) *</label><input name="manager_nationality" value={formData.manager_nationality} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Region (ክልል) *</label><input name="manager_region" value={formData.manager_region} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">K/Ketema (ክ/ከተማ)</label><input name="manager_ketema" value={formData.manager_ketema} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ)</label><input name="manager_woreda" value={formData.manager_woreda} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.)</label><input name="manager_hno" value={formData.manager_hno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">P.O.Box (ፖ.ኦ.ቡክስ)</label><input name="manager_pobox" value={formData.manager_pobox} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Fixed Phone No (ማዕከላዊ ስልክ ቁ.)</label><input name="manager_fixed_phone" value={formData.manager_fixed_phone} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Mobile Phone No (ሞባይል ስልክ ቁ.)</label><input name="manager_mobile" value={formData.manager_mobile} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">E-mail (ኢመይል)</label><input type="email" name="manager_email" value={formData.manager_email} onChange={handleChange} className={inputClass} /></div>
          </div>
        </div>
      )}
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">3. Type of Operation (የሥራው አይነት)</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><label className="block text-sm font-medium mb-1">Type of Operation /የሥራው አይነት (አማ) *</label><input name="type_of_operation" value={formData.type_of_operation} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Type of Operation /የሥራው አይነት (En) *</label><input name="type_of_operation_en" value={formData.type_of_operation_en || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">ኮድ / Code</label><input name="operation_code" value={formData.operation_code} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">የማደያው አይነት / Types of station *</label><select name="station_type" value={formData.station_type} onChange={handleChange} className={inputClass} required><option value="">-- Select --</option><option value="አዲስ/New">አዲስ/New</option><option value="ነባር/Existing">ነባር/Existing</option></select></div>
        </div>
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">4. Site Location (የስራ ቦታ አድራሻ)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium mb-1">Trade Name /የንግድ ስም (አማ) *</label><input name="trade_name" value={formData.trade_name || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Trade Name /የንግድ ሥም (En) *</label><input name="trade_name_en" value={formData.trade_name_en || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Region /ክልል (አማ) *</label><input name="site_region" value={formData.site_region} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Region /ክልል (En) *</label><input name="site_region_en" value={formData.site_region_en || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Zone/City /ዞን/ከተማ (አማ) *</label><input name="site_zone" value={formData.site_zone} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Zone/City /ዞን/ከተማ (En) *</label><input name="site_zone_en" value={formData.site_zone_en || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Sub City /ክ/ከተማ (አማ) *</label><input name="site_subcity" value={formData.site_subcity} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Sub City /ክ/ከተማ (En) *</label><input name="site_subcity_en" value={formData.site_subcity_en || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Woreda/Kebele /ወረዳ/ቀበሌ (አማ) *</label><input name="site_woreda" value={formData.site_woreda} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Woreda/Kebele /ወረዳ/ቀበሌ (En) *</label><input name="site_woreda_en" value={formData.site_woreda_en || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">H.No /የቤት ቁ. (አማ)</label><input name="site_hno" value={formData.site_hno} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">H.No /የቤት ቁ. (En)</label><input name="site_hno_en" value={formData.site_hno_en || ''} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">Phone No (ስልክ ቁ.)</label><input name="site_phone" value={formData.site_phone} onChange={handleChange} className={inputClass} /></div>
        </div>
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">የገንዘብ አቅም፣ የይዞታና ዲዛይን ንድፍ፣ የቦታ ስፋት / Financial Capacity, Site Plan & Design, Area of Land</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">የገንዘብ አቅም / Financial Capacity *</label>
            <input name="financial_capacity" value={formData.financial_capacity} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የቦታው ስፋት / Area of Land *</label>
            <input name="area_of_land" value={formData.area_of_land} onChange={handleChange} className={inputClass} required />
          </div>
          <div className="col-span-2">
            <div>
              <label className="block text-sm font-medium mb-1">የይዞታ ማረጋገጫና ዲዛይን ንድፍ / Site Plan and Design Drawing</label>
              {formData.existing_site_plan_drawing && (
                <div className="flex items-center gap-2 mb-2 text-sm text-green-500">
                  <FaFile /> Currently uploaded: <a href={fileUrl(formData.existing_site_plan_drawing)} target="_blank" rel="noopener noreferrer" className="underline">View file</a>
                </div>
              )}
              <input type="file" onChange={(e) => { const f = e.target.files[0]; if (f && f.size === 0) { toast.error('The selected file is empty. Please choose a valid PDF document.'); e.target.value = ''; return; } setFormData({ ...formData, site_plan_drawing: f }); }} className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400] bg-white" accept=".pdf" />
              <p className="text-xs text-gray-500 mt-1">PDF (max 10MB)</p>
            </div>
          </div>
        </div>
      </div>

      
      {title && title.includes('Construction Permit') && (
        <div>
          {/* Construction Permeation Fields */}
          <div>
            <label className="block text-sm font-medium mb-1">የግንባታው አገልግሎት የሚውለው / Construction service for</label>
            <input name="construction_service_for" value={formData.construction_service_for} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የካርታ ቁጥር / Plan no</label>
            <input name="plan_no" value={formData.plan_no} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የጂፒኤስ አድራሻ: (ሰሜን) / GPS Location: (North)</label>
            <input name="gps_north" value={formData.gps_north} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የጂፒኤስ አድራሻ: (ምስራቅ) / GPS Location: (East)</label>
            <input name="gps_east" value={formData.gps_east} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Scope of work station: Total project cost</label>
            <input name="total_project_cost" value={formData.total_project_cost} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Professional in charge construction</label>
            <input name="professional_in_charge" value={formData.professional_in_charge} onChange={handleChange} className={inputClass} />
          </div>
        </div>
      )}
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Attachments (ተያዥ መረጃዎች)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderFileInput('Application Letter (የማመልከቻ ደብዳቤ)', 'application_letter', formData.existing_application_letter)}
          {renderFileInput('Support Letter (የድጋፍ ደብዳቤ)', 'support_letter', formData.existing_support_letter)}
          {renderFileInput('Agreement Letter (የስምምነት ደብዳቤ)', 'aggrement_letter', formData.existing_aggrement_letter)}
        </div>
      </div>
      
      {title && title.includes('Construction Permit') && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">መያያዝ ያለባቸዉ መረጃዎች ዝርዝር / Attachments:</h4>
          <ul className="list-decimal pl-5 space-y-1 text-sm">
            <li>ፋይዳ መታወቂያ</li>
            <li>ፓስፖርት / I.D / Passport</li>
            <li>የባለቤትነት/የኪራይ ማረጋገጫ ውል/Investment /Rent Agreement</li>
            <li>የንግድ የምዝገባ የምስክር ወረቀት/Trade Registration Certificate</li>
            <li>የቦታ ካርታና ዲዛይን ንድፍ/Site Plan and Design Drawing</li>
            <li>የገንዘብ አቅም የባንክ መረጃ/Bank Statement for Financial Capacity</li>
            <li>ቢዝነስ ፕላን/የአዋጭነት ጥናት/ Business Plan / Feasibility Study</li>
            <li>የአካባቢ ተጽዕኖ ግምገማ ጥናት/Environmental Impact Assessment Study Report</li>
            <li>አመልካቹ ባለቤት ካልሆነ ህጋዊ ውክልና/Legal Representation (If applicant is not the owner)</li>
            <li>የማህበር መመስረቻ ደንብ/Article of Association (for PLC)</li>
            <li>የኢንሹራንስ ሽፋን/ Insurance Commitment letter</li>
            <li>የድርጅቱ የንግድ ልውውጥ መረጃ Company's Supply Records</li>
            <li>የሥራው እቅድ/Working program</li>
            <li>መሬቱ ለፔትሮሊየም ውጤቶች ግንባታ የሚሆን መሆኑን የሚገልፅ የአፈር ምርምር ጥናት/soil analysis study</li>
            <li>(የነዳጅ ማደያ መሣሪያዎች ተከላና ጥገና አገልግሎት) የሚያደርግ ባለሞያ ወይም ተቋም ከነዳጅና ኢነርጂ ባለስልጣን ብቃት የወሰደ መሆኑ የሚገልፅ ሰነድ</li>
          </ul>
        </div>
      )}
      <div className="mb-6">
        <label className="block text-sm font-medium mb-1">Remark (ማስታወሻ)</label>
        <textarea name="remark" value={formData.remark} onChange={handleChange} className={inputClass} rows="3" />
      </div>
      <div className="col-span-2">
        <label className="block text-sm font-medium mb-1">ያመለከቱበት ቀን / Request date</label>
        <EthiopianDatePicker name="request_date" value={formData.request_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Additional Attachments (ተያያዥ መረጃዎች)</h4>
        <button type="button" onClick={addAdditionalAttachment} className="text-[#006400] font-medium flex items-center gap-1 hover:underline">
          <FaPlus /> Add Attachment
        </button>
        {additionalAttachments.map((att, index) => (
          <div key={index} className="mt-3 p-3 border border-gray-200 rounded bg-gray-50">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Additional Attachment {index + 1}</span>
              <button type="button" onClick={() => removeAdditionalAttachment(index)} className="text-red-500 hover:text-red-700">
                <FaTrash />
              </button>
            </div>
            <input 
              type="text" 
              placeholder="Attachment name/description" 
              value={att.name} 
              onChange={(e) => handleAdditionalFileNameChange(e, index)} 
              className={inputClass} 
            />
            <input 
              type="file" 
              onChange={(e) => handleAdditionalFileChange(e, index)} 
              className="mt-2 text-sm" 
              accept=".pdf" 
            />
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2"><FaTimes /> Cancel</button>
        <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 flex items-center gap-2"><FaPaperPlane /> {isEdit ? 'Update Request' : 'Submit'}</button>
      </div>
    </form>
  );
}

function GasStationRetailRenewalForm({ onSubmit, onCancel, initialData = null, isEdit = false, onRegister = null }) {
  useFormDraft(() => [formData, setFormData], initialData);
  const [formData, setFormData] = useState({ reference_no: '', renewal_year: '', payment_fee: '', renewal_date: toEthiopianDateStr(new Date()), request_date: toEthiopianDateStr(new Date()) });
  const [searchedCompany, setSearchedCompany] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');

  const fetchCompanyByRef = async (refNo) => {
    if (!refNo.trim()) return;
    setLookupLoading(true); setLookupError('');
    try {
      const res = await axios.get(`${API_URL}/new-gas-station-retail?reference_no=${refNo}`);
      if (res.data.length > 0) { setSearchedCompany(res.data[0]); }
      else { setLookupError('No record found with this reference number'); setSearchedCompany(null); }
    } catch (err) { setLookupError('Failed to lookup reference'); setSearchedCompany(null); }
    finally { setLookupLoading(false); }
  };

  const handleLookup = () => { fetchCompanyByRef(formData.reference_no); };
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.name === 'reference_no') { setSearchedCompany(null); setLookupError(''); }
  };

  const [additionalAttachments, setAdditionalAttachments] = useState([]);

  const handleAdditionalFileChange = (e, index) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    const newAttachments = [...additionalAttachments];
    newAttachments[index].file = selectedFile;
    setAdditionalAttachments(newAttachments);
  };

  const handleAdditionalFileNameChange = (e, index) => {
    const newAttachments = [...additionalAttachments];
    newAttachments[index].name = e.target.value;
    setAdditionalAttachments(newAttachments);
  };

  const addAdditionalAttachment = () => {
    if (additionalAttachments.length >= 10) {
      toast.warn('You can only add up to 10 attachments');
      return;
    }
    setAdditionalAttachments([...additionalAttachments, { name: '', file: null }]);
  };

  const removeAdditionalAttachment = (index) => {
    const newAttachments = additionalAttachments.filter((_, i) => i !== index);
    setAdditionalAttachments(newAttachments);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!searchedCompany) { toast.warning('Please lookup the reference number first'); return; }
    const formDataToSend = new FormData();
    formDataToSend.append('reference_no', formData.reference_no);
    formDataToSend.append('existing_ref_no', searchedCompany.reference_no);
    formDataToSend.append('renewal_year', formData.renewal_year);
    formDataToSend.append('payment_fee', formData.payment_fee);
    formDataToSend.append('renewal_date', ethDateStrToGregStr(formData.renewal_date));
    formDataToSend.append('request_date', ethDateStrToGregStr(formData.request_date));
    if (isEdit && initialData) {
      formDataToSend.append('status', initialData.status || 'pending');
      formDataToSend.append('response_reason', initialData.response_reason || '');
      formDataToSend.append('rejection_reason', initialData.rejection_reason || '');
    }
    additionalAttachments.forEach((att, index) => {
      if (att.file) {
        formDataToSend.append(`additional_attachment_${index}_name`, att.name || '');
        formDataToSend.append(`additional_attachment_${index}`, att.file);
      }
    });
    formDataToSend.append('additional_attachments_count', additionalAttachments.filter(a => a.file).length);
    onSubmit(formDataToSend, isEdit ? initialData.id : null);
  };

  const inputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow">

      <div className="mb-4 p-4 bg-blue-50 rounded border border-blue-200">
        <p className="text-sm text-blue-800">Enter the reference number to look up its details, then fill in the renewal information.</p>
      </div>
      <div className="mb-4 p-4 bg-yellow-50 rounded border border-yellow-200 flex items-center justify-between">
        <p className="text-sm text-yellow-800">ከዚህ ቀደም በሲስተም ውስጥ ካልተመዘገቡ ይመዝገቡ የሚለውን ቁልፍ ይጫኑ / If you are not previously registered in the system, click the Register button.</p>
        {onRegister && (
          <button type="button" onClick={onRegister} className="ml-4 whitespace-nowrap bg-[#006400] text-white px-4 py-2 rounded hover:bg-green-700 text-sm font-medium flex-shrink-0">
            ይመዝገቡ / Register
          </button>
        )}
      </div>
      <div className="mb-6">
        <label className={labelClass}>Reference No (የማመልከቻ ቁጥር) *</label>
        <div className="flex gap-2">
          <input type="text" name="reference_no" value={formData.reference_no} onChange={handleChange} className={inputClass + " flex-1"} placeholder="e.g. GRR-XXXXXXXX" required />
          <button type="button" onClick={handleLookup} disabled={lookupLoading || !formData.reference_no.trim()} className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 disabled:bg-gray-400 flex items-center gap-2">
            <FaSearch /> {lookupLoading ? 'Searching...' : 'Search'}
          </button>
        </div>
        {lookupError && <p className="text-red-600 text-sm mt-1">{lookupError}</p>}
      </div>
      {searchedCompany && (
        <div className="mb-6 p-4 bg-gray-50 rounded border">
          <h4 className="font-bold text-gray-700 mb-3">Details (ዝርዝር)</h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><strong>Applicant Name:</strong> {searchedCompany.applicant_name || 'N/A'}</div>
            <div><strong>Nationality:</strong> {searchedCompany.nationality || 'N/A'}</div>
            <div><strong>Company Name:</strong> {searchedCompany.gas_station_retail_name || 'N/A'}</div>
            <div><strong>Type of Operation:</strong> {searchedCompany.type_of_operation || 'N/A'}</div>
            <div><strong>Region:</strong> {searchedCompany.region || 'N/A'}</div>
            <div><strong>Sub City:</strong> {searchedCompany.subcity || 'N/A'}</div>
            <div><strong>Manager Name:</strong> {searchedCompany.manager_name || 'N/A'}</div>
            <div><strong>Manager Mobile:</strong> {searchedCompany.manager_mobile || 'N/A'}</div>
          </div>
        </div>
      )}
      <div className="border-t pt-4">
        <h4 className="font-bold text-gray-700 mb-3">Renewal Information (የእድሳት መረጃ)</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="mb-6"><label className={labelClass}>እድሳት አመት / Renewal Year *</label><input type="text" name="renewal_year" value={formData.renewal_year} onChange={handleChange} className={inputClass} placeholder="e.g. 2026" required /></div>
          <div className="mb-6"><label className={labelClass}>የአገልግሎት ክፍያ / Payment Fee *</label><input type="text" name="payment_fee" value={formData.payment_fee} onChange={handleChange} className={inputClass} placeholder="Enter payment fee" required /></div>
          <div className="mb-6"><label className={labelClass}>የታደሰበት ቀን / Renewal Date *</label><EthiopianDatePicker name="renewal_date" value={formData.renewal_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required /></div>
        </div>
      </div>
      <div className="col-span-2">
        <label className="block text-sm font-medium mb-1">ያመለከቱበት ቀን / Request date</label>
        <EthiopianDatePicker name="request_date" value={formData.request_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Additional Attachments (ተያያዥ መረጃዎች)</h4>
        <button type="button" onClick={addAdditionalAttachment} className="text-[#006400] font-medium flex items-center gap-1 hover:underline">
          <FaPlus /> Add Attachment
        </button>
        {additionalAttachments.map((att, index) => (
          <div key={index} className="mt-3 p-3 border border-gray-200 rounded bg-gray-50">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Additional Attachment {index + 1}</span>
              <button type="button" onClick={() => removeAdditionalAttachment(index)} className="text-red-500 hover:text-red-700">
                <FaTrash />
              </button>
            </div>
            <input 
              type="text" 
              placeholder="Attachment name/description" 
              value={att.name} 
              onChange={(e) => handleAdditionalFileNameChange(e, index)} 
              className={inputClass} 
            />
            <input 
              type="file" 
              onChange={(e) => handleAdditionalFileChange(e, index)} 
              className="mt-2 text-sm" 
              accept=".pdf" 
            />
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2"><FaTimes /> Cancel</button>
        <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 flex items-center gap-2"><FaPaperPlane /> {isEdit ? 'Update Request' : 'Submit'}</button>
      </div>
    </form>
  );
}

function FuelDistributorRenewalForm({ onSubmit, onCancel, initialData = null, isEdit = false, onRegister = null }) {
  useFormDraft(() => [formData, setFormData], initialData);
  const [formData, setFormData] = useState({ reference_no: '', renewal_year: '', payment_fee: '', renewal_date: toEthiopianDateStr(new Date()), request_date: toEthiopianDateStr(new Date()) });
  const [searchedCompany, setSearchedCompany] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');

  const fetchCompanyByRef = async (refNo) => {
    if (!refNo.trim()) return;
    setLookupLoading(true); setLookupError('');
    try {
      const res = await axios.get(`${API_URL}/new-fuel-distributor?reference_no=${refNo}`);
      if (res.data.length > 0) { setSearchedCompany(res.data[0]); }
      else { setLookupError('No record found with this reference number'); setSearchedCompany(null); }
    } catch (err) { setLookupError('Failed to lookup reference'); setSearchedCompany(null); }
    finally { setLookupLoading(false); }
  };

  const handleLookup = () => { fetchCompanyByRef(formData.reference_no); };
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.name === 'reference_no') { setSearchedCompany(null); setLookupError(''); }
  };

  const [additionalAttachments, setAdditionalAttachments] = useState([]);

  const handleAdditionalFileChange = (e, index) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    const newAttachments = [...additionalAttachments];
    newAttachments[index].file = selectedFile;
    setAdditionalAttachments(newAttachments);
  };

  const handleAdditionalFileNameChange = (e, index) => {
    const newAttachments = [...additionalAttachments];
    newAttachments[index].name = e.target.value;
    setAdditionalAttachments(newAttachments);
  };

  const addAdditionalAttachment = () => {
    if (additionalAttachments.length >= 10) {
      toast.warn('You can only add up to 10 attachments');
      return;
    }
    setAdditionalAttachments([...additionalAttachments, { name: '', file: null }]);
  };

  const removeAdditionalAttachment = (index) => {
    const newAttachments = additionalAttachments.filter((_, i) => i !== index);
    setAdditionalAttachments(newAttachments);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!searchedCompany) { toast.warning('Please lookup the reference number first'); return; }
    const formDataToSend = new FormData();
    formDataToSend.append('reference_no', formData.reference_no);
    formDataToSend.append('existing_ref_no', searchedCompany.reference_no);
    formDataToSend.append('renewal_year', formData.renewal_year);
    formDataToSend.append('payment_fee', formData.payment_fee);
    formDataToSend.append('renewal_date', ethDateStrToGregStr(formData.renewal_date));
    formDataToSend.append('request_date', ethDateStrToGregStr(formData.request_date));
    if (isEdit && initialData) {
      formDataToSend.append('status', initialData.status || 'pending');
      formDataToSend.append('response_reason', initialData.response_reason || '');
      formDataToSend.append('rejection_reason', initialData.rejection_reason || '');
    }
    additionalAttachments.forEach((att, index) => {
      if (att.file) {
        formDataToSend.append(`additional_attachment_${index}_name`, att.name || '');
        formDataToSend.append(`additional_attachment_${index}`, att.file);
      }
    });
    formDataToSend.append('additional_attachments_count', additionalAttachments.filter(a => a.file).length);
    onSubmit(formDataToSend, isEdit ? initialData.id : null);
  };

  const inputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow">

      <div className="mb-4 p-4 bg-blue-50 rounded border border-blue-200">
        <p className="text-sm text-blue-800">Enter the reference number to look up its details, then fill in the renewal information.</p>
      </div>
      <div className="mb-4 p-4 bg-yellow-50 rounded border border-yellow-200 flex items-center justify-between">
        <p className="text-sm text-yellow-800">ከዚህ ቀደም በሲስተም ውስጥ ካልተመዘገቡ ይመዝገቡ የሚለውን ቁልፍ ይጫኑ / If you are not previously registered in the system, click the Register button.</p>
        {onRegister && (
          <button type="button" onClick={onRegister} className="ml-4 whitespace-nowrap bg-[#006400] text-white px-4 py-2 rounded hover:bg-green-700 text-sm font-medium flex-shrink-0">
            ይመዝገቡ / Register
          </button>
        )}
      </div>
      <div className="mb-6">
        <label className={labelClass}>Reference No (የማመልከቻ ቁጥር) *</label>
        <div className="flex gap-2">
          <input type="text" name="reference_no" value={formData.reference_no} onChange={handleChange} className={inputClass + " flex-1"} placeholder="e.g. FDR-XXXXXXXX" required />
          <button type="button" onClick={handleLookup} disabled={lookupLoading || !formData.reference_no.trim()} className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 disabled:bg-gray-400 flex items-center gap-2">
            <FaSearch /> {lookupLoading ? 'Searching...' : 'Search'}
          </button>
        </div>
        {lookupError && <p className="text-red-600 text-sm mt-1">{lookupError}</p>}
      </div>
      {searchedCompany && (
        <div className="mb-6 p-4 bg-gray-50 rounded border">
          <h4 className="font-bold text-gray-700 mb-3">Details (ዝርዝር)</h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><strong>Applicant Name:</strong> {searchedCompany.applicant_name || 'N/A'}</div>
            <div><strong>Nationality:</strong> {searchedCompany.nationality || 'N/A'}</div>
            <div><strong>Company Name:</strong> {searchedCompany.distributor_company_name || 'N/A'}</div>
            <div><strong>Type of Operation:</strong> {searchedCompany.type_of_operation || 'N/A'}</div>
            <div><strong>Region:</strong> {searchedCompany.region || 'N/A'}</div>
            <div><strong>Sub City:</strong> {searchedCompany.subcity || 'N/A'}</div>
            <div><strong>Manager Name:</strong> {searchedCompany.manager_name || 'N/A'}</div>
            <div><strong>Manager Mobile:</strong> {searchedCompany.manager_mobile || 'N/A'}</div>
          </div>
        </div>
      )}
      <div className="border-t pt-4">
        <h4 className="font-bold text-gray-700 mb-3">Renewal Information (የእድሳት መረጃ)</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="mb-6"><label className={labelClass}>እድሳት አመት / Renewal Year *</label><input type="text" name="renewal_year" value={formData.renewal_year} onChange={handleChange} className={inputClass} placeholder="e.g. 2026" required /></div>
          <div className="mb-6"><label className={labelClass}>የአገልግሎት ክፍያ / Payment Fee *</label><input type="text" name="payment_fee" value={formData.payment_fee} onChange={handleChange} className={inputClass} placeholder="Enter payment fee" required /></div>
          <div className="mb-6"><label className={labelClass}>የታደሰበት ቀን / Renewal Date *</label><EthiopianDatePicker name="renewal_date" value={formData.renewal_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required /></div>
        </div>
      </div>
      <div className="col-span-2">
        <label className="block text-sm font-medium mb-1">ያመለከቱበት ቀን / Request date</label>
        <EthiopianDatePicker name="request_date" value={formData.request_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Additional Attachments (ተያያዥ መረጃዎች)</h4>
        <button type="button" onClick={addAdditionalAttachment} className="text-[#006400] font-medium flex items-center gap-1 hover:underline">
          <FaPlus /> Add Attachment
        </button>
        {additionalAttachments.map((att, index) => (
          <div key={index} className="mt-3 p-3 border border-gray-200 rounded bg-gray-50">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Additional Attachment {index + 1}</span>
              <button type="button" onClick={() => removeAdditionalAttachment(index)} className="text-red-500 hover:text-red-700">
                <FaTrash />
              </button>
            </div>
            <input 
              type="text" 
              placeholder="Attachment name/description" 
              value={att.name} 
              onChange={(e) => handleAdditionalFileNameChange(e, index)} 
              className={inputClass} 
            />
            <input 
              type="file" 
              onChange={(e) => handleAdditionalFileChange(e, index)} 
              className="mt-2 text-sm" 
              accept=".pdf" 
            />
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2"><FaTimes /> Cancel</button>
        <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 flex items-center gap-2"><FaPaperPlane /> {isEdit ? 'Update Request' : 'Submit'}</button>
      </div>
    </form>
  );
}

function FuelImportRenewalForm({ onSubmit, onCancel, initialData = null, isEdit = false, onRegister = null }) {
  useFormDraft(() => [formData, setFormData], initialData);
  const [formData, setFormData] = useState({ reference_no: '', renewal_year: '', payment_fee: '', renewal_date: toEthiopianDateStr(new Date()), request_date: toEthiopianDateStr(new Date()) });
  const [searchedCompany, setSearchedCompany] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');

  const fetchCompanyByRef = async (refNo) => {
    if (!refNo.trim()) return;
    setLookupLoading(true); setLookupError('');
    try {
      const res = await axios.get(`${API_URL}/new-fuel-import?reference_no=${refNo}`);
      if (res.data.length > 0) { setSearchedCompany(res.data[0]); }
      else { setLookupError('No record found with this reference number'); setSearchedCompany(null); }
    } catch (err) { setLookupError('Failed to lookup reference'); setSearchedCompany(null); }
    finally { setLookupLoading(false); }
  };

  const handleLookup = () => { fetchCompanyByRef(formData.reference_no); };
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.name === 'reference_no') { setSearchedCompany(null); setLookupError(''); }
  };

  const [additionalAttachments, setAdditionalAttachments] = useState([]);

  const handleAdditionalFileChange = (e, index) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    const newAttachments = [...additionalAttachments];
    newAttachments[index].file = selectedFile;
    setAdditionalAttachments(newAttachments);
  };

  const handleAdditionalFileNameChange = (e, index) => {
    const newAttachments = [...additionalAttachments];
    newAttachments[index].name = e.target.value;
    setAdditionalAttachments(newAttachments);
  };

  const addAdditionalAttachment = () => {
    if (additionalAttachments.length >= 10) {
      toast.warn('You can only add up to 10 attachments');
      return;
    }
    setAdditionalAttachments([...additionalAttachments, { name: '', file: null }]);
  };

  const removeAdditionalAttachment = (index) => {
    const newAttachments = additionalAttachments.filter((_, i) => i !== index);
    setAdditionalAttachments(newAttachments);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!searchedCompany) { toast.warning('Please lookup the reference number first'); return; }
    const formDataToSend = new FormData();
    formDataToSend.append('reference_no', formData.reference_no);
    formDataToSend.append('existing_ref_no', searchedCompany.reference_no);
    formDataToSend.append('renewal_year', formData.renewal_year);
    formDataToSend.append('payment_fee', formData.payment_fee);
    formDataToSend.append('renewal_date', ethDateStrToGregStr(formData.renewal_date));
    formDataToSend.append('request_date', ethDateStrToGregStr(formData.request_date));
    if (isEdit && initialData) {
      formDataToSend.append('status', initialData.status || 'pending');
      formDataToSend.append('response_reason', initialData.response_reason || '');
      formDataToSend.append('rejection_reason', initialData.rejection_reason || '');
    }
    additionalAttachments.forEach((att, index) => {
      if (att.file) {
        formDataToSend.append(`additional_attachment_${index}_name`, att.name || '');
        formDataToSend.append(`additional_attachment_${index}`, att.file);
      }
    });
    formDataToSend.append('additional_attachments_count', additionalAttachments.filter(a => a.file).length);
    onSubmit(formDataToSend, isEdit ? initialData.id : null);
  };

  const inputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow">

      <div className="mb-4 p-4 bg-blue-50 rounded border border-blue-200">
        <p className="text-sm text-blue-800">Enter the reference number to look up its details, then fill in the renewal information.</p>
      </div>
      <div className="mb-4 p-4 bg-yellow-50 rounded border border-yellow-200 flex items-center justify-between">
        <p className="text-sm text-yellow-800">ከዚህ ቀደም በሲስተም ውስጥ ካልተመዘገቡ ይመዝገቡ የሚለውን ቁልፍ ይጫኑ / If you are not previously registered in the system, click the Register button.</p>
        {onRegister && (
          <button type="button" onClick={onRegister} className="ml-4 whitespace-nowrap bg-[#006400] text-white px-4 py-2 rounded hover:bg-green-700 text-sm font-medium flex-shrink-0">
            ይመዝገቡ / Register
          </button>
        )}
      </div>
      <div className="mb-6">
        <label className={labelClass}>Reference No (የማመልከቻ ቁጥር) *</label>
        <div className="flex gap-2">
          <input type="text" name="reference_no" value={formData.reference_no} onChange={handleChange} className={inputClass + " flex-1"} placeholder="e.g. FIR-XXXXXXXX" required />
          <button type="button" onClick={handleLookup} disabled={lookupLoading || !formData.reference_no.trim()} className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 disabled:bg-gray-400 flex items-center gap-2">
            <FaSearch /> {lookupLoading ? 'Searching...' : 'Search'}
          </button>
        </div>
        {lookupError && <p className="text-red-600 text-sm mt-1">{lookupError}</p>}
      </div>
      {searchedCompany && (
        <div className="mb-6 p-4 bg-gray-50 rounded border">
          <h4 className="font-bold text-gray-700 mb-3">Details (ዝርዝር)</h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><strong>Applicant Name:</strong> {searchedCompany.applicant_name || 'N/A'}</div>
            <div><strong>Nationality:</strong> {searchedCompany.nationality || 'N/A'}</div>
            <div><strong>Company Name:</strong> {searchedCompany.importer_company_name || 'N/A'}</div>
            <div><strong>Type of Operation:</strong> {searchedCompany.type_of_operation || 'N/A'}</div>
            <div><strong>Region:</strong> {searchedCompany.region || 'N/A'}</div>
            <div><strong>Sub City:</strong> {searchedCompany.subcity || 'N/A'}</div>
            <div><strong>Manager Name:</strong> {searchedCompany.manager_name || 'N/A'}</div>
            <div><strong>Manager Mobile:</strong> {searchedCompany.manager_mobile || 'N/A'}</div>
          </div>
        </div>
      )}
      <div className="border-t pt-4">
        <h4 className="font-bold text-gray-700 mb-3">Renewal Information (የእድሳት መረጃ)</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="mb-6"><label className={labelClass}>እድሳት አመት / Renewal Year *</label><input type="text" name="renewal_year" value={formData.renewal_year} onChange={handleChange} className={inputClass} placeholder="e.g. 2026" required /></div>
          <div className="mb-6"><label className={labelClass}>የአገልግሎት ክፍያ / Payment Fee *</label><input type="text" name="payment_fee" value={formData.payment_fee} onChange={handleChange} className={inputClass} placeholder="Enter payment fee" required /></div>
          <div className="mb-6"><label className={labelClass}>የታደሰበት ቀን / Renewal Date *</label><EthiopianDatePicker name="renewal_date" value={formData.renewal_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required /></div>
        </div>
      </div>
      <div className="col-span-2">
        <label className="block text-sm font-medium mb-1">ያመለከቱበት ቀን / Request date</label>
        <EthiopianDatePicker name="request_date" value={formData.request_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Additional Attachments (ተያያዥ መረጃዎች)</h4>
        <button type="button" onClick={addAdditionalAttachment} className="text-[#006400] font-medium flex items-center gap-1 hover:underline">
          <FaPlus /> Add Attachment
        </button>
        {additionalAttachments.map((att, index) => (
          <div key={index} className="mt-3 p-3 border border-gray-200 rounded bg-gray-50">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Additional Attachment {index + 1}</span>
              <button type="button" onClick={() => removeAdditionalAttachment(index)} className="text-red-500 hover:text-red-700">
                <FaTrash />
              </button>
            </div>
            <input 
              type="text" 
              placeholder="Attachment name/description" 
              value={att.name} 
              onChange={(e) => handleAdditionalFileNameChange(e, index)} 
              className={inputClass} 
            />
            <input 
              type="file" 
              onChange={(e) => handleAdditionalFileChange(e, index)} 
              className="mt-2 text-sm" 
              accept=".pdf" 
            />
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2"><FaTimes /> Cancel</button>
        <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 flex items-center gap-2"><FaPaperPlane /> {isEdit ? 'Update Request' : 'Submit'}</button>
      </div>
    </form>
  );
}

function GasImportRenewalForm({ onSubmit, onCancel, initialData = null, isEdit = false, onRegister = null }) {
  useFormDraft(() => [formData, setFormData], initialData);
  const [formData, setFormData] = useState({ reference_no: '', renewal_year: '', payment_fee: '', renewal_date: toEthiopianDateStr(new Date()), financial_capacity: '', site_plan_drawing: null, area_of_land: '', request_date: toEthiopianDateStr(new Date()) });
  const [searchedCompany, setSearchedCompany] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');

  useEffect(() => {
    if (initialData) {
      setFormData({
        reference_no: initialData.reference_no || '', renewal_year: initialData.renewal_year || '',
        payment_fee: initialData.payment_fee || '', renewal_date: initialData.renewal_date ? convertDbDateToDisplay(initialData.renewal_date) : toEthiopianDateStr(new Date()),
        financial_capacity: initialData.financial_capacity || '',
        site_plan_drawing: null,
        area_of_land: initialData.area_of_land || '',
        request_date: initialData.request_date ? convertDbDateToDisplay(initialData.request_date) : toEthiopianDateStr(new Date())
      });
      if (initialData.existing_ref_no) fetchCompanyByRef(initialData.existing_ref_no);
    }
  }, [initialData]);

  const fetchCompanyByRef = async (refNo) => {
    if (!refNo.trim()) return;
    setLookupLoading(true); setLookupError('');
    try {
      const res = await axios.get(`${API_URL}/new-gas-import?reference_no=${refNo}`);
      if (res.data.length > 0) { setSearchedCompany(res.data[0]); }
      else { setLookupError('No record found with this reference number'); setSearchedCompany(null); }
    } catch (err) { setLookupError('Failed to lookup reference'); setSearchedCompany(null); }
    finally { setLookupLoading(false); }
  };

  const handleLookup = () => { fetchCompanyByRef(formData.reference_no); };
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.name === 'reference_no') { setSearchedCompany(null); setLookupError(''); }
  };

  const [additionalAttachments, setAdditionalAttachments] = useState([]);

  const handleAdditionalFileChange = (e, index) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    const newAttachments = [...additionalAttachments];
    newAttachments[index].file = selectedFile;
    setAdditionalAttachments(newAttachments);
  };

  const handleAdditionalFileNameChange = (e, index) => {
    const newAttachments = [...additionalAttachments];
    newAttachments[index].name = e.target.value;
    setAdditionalAttachments(newAttachments);
  };

  const addAdditionalAttachment = () => {
    if (additionalAttachments.length >= 10) {
      toast.warn('You can only add up to 10 attachments');
      return;
    }
    setAdditionalAttachments([...additionalAttachments, { name: '', file: null }]);
  };

  const removeAdditionalAttachment = (index) => {
    const newAttachments = additionalAttachments.filter((_, i) => i !== index);
    setAdditionalAttachments(newAttachments);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!searchedCompany) { toast.warning('Please lookup the reference number first'); return; }
    const formDataToSend = new FormData();
    formDataToSend.append('reference_no', formData.reference_no);
    formDataToSend.append('existing_ref_no', searchedCompany.reference_no);
    formDataToSend.append('renewal_year', formData.renewal_year);
    formDataToSend.append('payment_fee', formData.payment_fee);
    formDataToSend.append('renewal_date', ethDateStrToGregStr(formData.renewal_date));
    formDataToSend.append('request_date', ethDateStrToGregStr(formData.request_date));
    if (isEdit && initialData) {
      formDataToSend.append('status', initialData.status || 'pending');
      formDataToSend.append('response_reason', initialData.response_reason || '');
      formDataToSend.append('rejection_reason', initialData.rejection_reason || '');
    }
    additionalAttachments.forEach((att, index) => {
      if (att.file) {
        formDataToSend.append(`additional_attachment_${index}_name`, att.name || '');
        formDataToSend.append(`additional_attachment_${index}`, att.file);
      }
    });
    formDataToSend.append('additional_attachments_count', additionalAttachments.filter(a => a.file).length);
    onSubmit(formDataToSend, isEdit ? initialData.id : null);
  };

  const inputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow">

      <div className="mb-4 p-4 bg-blue-50 rounded border border-blue-200">
        <p className="text-sm text-blue-800">Enter the reference number to look up its details, then fill in the renewal information.</p>
      </div>
      <div className="mb-4 p-4 bg-yellow-50 rounded border border-yellow-200 flex items-center justify-between">
        <p className="text-sm text-yellow-800">ከዚህ ቀደም በሲስተም ውስጥ ካልተመዘገቡ ይመዝገቡ የሚለውን ቁልፍ ይጫኑ / If you are not previously registered in the system, click the Register button.</p>
        {onRegister && (
          <button type="button" onClick={onRegister} className="ml-4 whitespace-nowrap bg-[#006400] text-white px-4 py-2 rounded hover:bg-green-700 text-sm font-medium flex-shrink-0">
            ይመዝገቡ / Register
          </button>
        )}
      </div>
      <div className="mb-6">
        <label className={labelClass}>Reference No (የማመልከቻ ቁጥር) *</label>
        <div className="flex gap-2">
          <input type="text" name="reference_no" value={formData.reference_no} onChange={handleChange} className={inputClass + " flex-1"} placeholder="e.g. GIR-XXXXXXXX" required />
          <button type="button" onClick={handleLookup} disabled={lookupLoading || !formData.reference_no.trim()} className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 disabled:bg-gray-400 flex items-center gap-2">
            <FaSearch /> {lookupLoading ? 'Searching...' : 'Search'}
          </button>
        </div>
        {lookupError && <p className="text-red-600 text-sm mt-1">{lookupError}</p>}
      </div>
      {searchedCompany && (
        <div className="mb-6 p-4 bg-gray-50 rounded border">
          <h4 className="font-bold text-gray-700 mb-3">Details (ዝርዝር)</h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><strong>Applicant Name:</strong> {searchedCompany.applicant_name || 'N/A'}</div>
            <div><strong>Nationality:</strong> {searchedCompany.nationality || 'N/A'}</div>
            <div><strong>Company Name:</strong> {searchedCompany.gas_importer_company_name || 'N/A'}</div>
            <div><strong>Type of Operation:</strong> {searchedCompany.type_of_operation || 'N/A'}</div>
            <div><strong>Region:</strong> {searchedCompany.region || 'N/A'}</div>
            <div><strong>Sub City:</strong> {searchedCompany.subcity || 'N/A'}</div>
            <div><strong>Manager Name:</strong> {searchedCompany.manager_name || 'N/A'}</div>
            <div><strong>Manager Mobile:</strong> {searchedCompany.manager_mobile || 'N/A'}</div>
          </div>
        </div>
      )}
      <div className="border-t pt-4">
        <h4 className="font-bold text-gray-700 mb-3">Renewal Information (የእድሳት መረጃ)</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="mb-6"><label className={labelClass}>እድሳት አመት / Renewal Year *</label><input type="text" name="renewal_year" value={formData.renewal_year} onChange={handleChange} className={inputClass} placeholder="e.g. 2026" required /></div>
          <div className="mb-6"><label className={labelClass}>የአገልግሎት ክፍያ / Payment Fee *</label><input type="text" name="payment_fee" value={formData.payment_fee} onChange={handleChange} className={inputClass} placeholder="Enter payment fee" required /></div>
          <div className="mb-6"><label className={labelClass}>የታደሰበት ቀን / Renewal Date *</label><EthiopianDatePicker name="renewal_date" value={formData.renewal_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required /></div>
        </div>
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">የገንዘብ አቅም፣ የይዞታና ዲዛይን ንድፍ፣ የቦታ ስፋት / Financial Capacity, Site Plan & Design, Area of Land</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">የገንዘብ አቅም / Financial Capacity *</label>
            <input name="financial_capacity" value={formData.financial_capacity} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የቦታው ስፋት / Area of Land *</label>
            <input name="area_of_land" value={formData.area_of_land} onChange={handleChange} className={inputClass} required />
          </div>
          <div className="col-span-2">
            <div>
              <label className="block text-sm font-medium mb-1">የይዞታ ማረጋገጫና ዲዛይን ንድፍ / Site Plan and Design Drawing</label>
              {formData.existing_site_plan_drawing && (
                <div className="flex items-center gap-2 mb-2 text-sm text-green-500">
                  <FaFile /> Currently uploaded: <a href={fileUrl(formData.existing_site_plan_drawing)} target="_blank" rel="noopener noreferrer" className="underline">View file</a>
                </div>
              )}
              <input type="file" onChange={(e) => { const f = e.target.files[0]; if (f && f.size === 0) { toast.error('The selected file is empty. Please choose a valid PDF document.'); e.target.value = ''; return; } setFormData({ ...formData, site_plan_drawing: f }); }} className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400] bg-white" accept=".pdf" />
              <p className="text-xs text-gray-500 mt-1">PDF (max 10MB)</p>
            </div>
          </div>
        </div>
      </div>
      <div className="col-span-2">
        <label className="block text-sm font-medium mb-1">ያመለከቱበት ቀን / Request date</label>
        <EthiopianDatePicker name="request_date" value={formData.request_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Additional Attachments (ተያያዥ መረጃዎች)</h4>
        <button type="button" onClick={addAdditionalAttachment} className="text-[#006400] font-medium flex items-center gap-1 hover:underline">
          <FaPlus /> Add Attachment
        </button>
        {additionalAttachments.map((att, index) => (
          <div key={index} className="mt-3 p-3 border border-gray-200 rounded bg-gray-50">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Additional Attachment {index + 1}</span>
              <button type="button" onClick={() => removeAdditionalAttachment(index)} className="text-red-500 hover:text-red-700">
                <FaTrash />
              </button>
            </div>
            <input 
              type="text" 
              placeholder="Attachment name/description" 
              value={att.name} 
              onChange={(e) => handleAdditionalFileNameChange(e, index)} 
              className={inputClass} 
            />
            <input 
              type="file" 
              onChange={(e) => handleAdditionalFileChange(e, index)} 
              className="mt-2 text-sm" 
              accept=".pdf" 
            />
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2"><FaTimes /> Cancel</button>
        <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 flex items-center gap-2"><FaPaperPlane /> {isEdit ? 'Update Request' : 'Submit'}</button>
      </div>
    </form>
  );
}

function OilExporterRenewalForm({ onSubmit, onCancel, initialData = null, isEdit = false, onRegister = null }) {
  useFormDraft(() => [formData, setFormData], initialData);
  const [formData, setFormData] = useState({ reference_no: '', renewal_year: '', payment_fee: '', renewal_date: toEthiopianDateStr(new Date()), request_date: toEthiopianDateStr(new Date()) });
  const [searchedCompany, setSearchedCompany] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');

  const fetchCompanyByRef = async (refNo) => {
    if (!refNo.trim()) return;
    setLookupLoading(true); setLookupError('');
    try {
      const res = await axios.get(`${API_URL}/new-oil-exporter?reference_no=${refNo}`);
      if (res.data.length > 0) { setSearchedCompany(res.data[0]); }
      else { setLookupError('No record found with this reference number'); setSearchedCompany(null); }
    } catch (err) { setLookupError('Failed to lookup reference'); setSearchedCompany(null); }
    finally { setLookupLoading(false); }
  };

  const handleLookup = () => { fetchCompanyByRef(formData.reference_no); };
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.name === 'reference_no') { setSearchedCompany(null); setLookupError(''); }
  };

  const [additionalAttachments, setAdditionalAttachments] = useState([]);

  const handleAdditionalFileChange = (e, index) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    const newAttachments = [...additionalAttachments];
    newAttachments[index].file = selectedFile;
    setAdditionalAttachments(newAttachments);
  };

  const handleAdditionalFileNameChange = (e, index) => {
    const newAttachments = [...additionalAttachments];
    newAttachments[index].name = e.target.value;
    setAdditionalAttachments(newAttachments);
  };

  const addAdditionalAttachment = () => {
    if (additionalAttachments.length >= 10) {
      toast.warn('You can only add up to 10 attachments');
      return;
    }
    setAdditionalAttachments([...additionalAttachments, { name: '', file: null }]);
  };

  const removeAdditionalAttachment = (index) => {
    const newAttachments = additionalAttachments.filter((_, i) => i !== index);
    setAdditionalAttachments(newAttachments);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!searchedCompany) { toast.warning('Please lookup the reference number first'); return; }
    const formDataToSend = new FormData();
    formDataToSend.append('reference_no', formData.reference_no);
    formDataToSend.append('existing_ref_no', searchedCompany.reference_no);
    formDataToSend.append('renewal_year', formData.renewal_year);
    formDataToSend.append('payment_fee', formData.payment_fee);
    formDataToSend.append('renewal_date', ethDateStrToGregStr(formData.renewal_date));
    formDataToSend.append('request_date', ethDateStrToGregStr(formData.request_date));
    if (isEdit && initialData) {
      formDataToSend.append('status', initialData.status || 'pending');
      formDataToSend.append('response_reason', initialData.response_reason || '');
      formDataToSend.append('rejection_reason', initialData.rejection_reason || '');
    }
    additionalAttachments.forEach((att, index) => {
      if (att.file) {
        formDataToSend.append(`additional_attachment_${index}_name`, att.name || '');
        formDataToSend.append(`additional_attachment_${index}`, att.file);
      }
    });
    formDataToSend.append('additional_attachments_count', additionalAttachments.filter(a => a.file).length);
    onSubmit(formDataToSend, isEdit ? initialData.id : null);
  };

  const inputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow">

      <div className="mb-4 p-4 bg-yellow-50 rounded border border-yellow-200 flex items-center justify-between">
        <p className="text-sm text-yellow-800">ከዚህ ቀደም በሲስተም ውስጥ ካልተመዘገቡ ይመዝገቡ የሚለውን ቁልፍ ይጫኑ / If you are not previously registered in the system, click the Register button.</p>
        {onRegister && (
          <button type="button" onClick={onRegister} className="ml-4 whitespace-nowrap bg-[#006400] text-white px-4 py-2 rounded hover:bg-green-700 text-sm font-medium flex-shrink-0">
            ይመዝገቡ / Register
          </button>
        )}
      </div>
      <div className="mb-4 p-4 bg-blue-50 rounded border border-blue-200">
        <p className="text-sm text-blue-800">Enter the reference number to look up its details, then fill in the renewal information.</p>
      </div>
      <div className="mb-6">
        <label className={labelClass}>Reference No (የማመልከቻ ቁጥር) *</label>
        <div className="flex gap-2">
          <input type="text" name="reference_no" value={formData.reference_no} onChange={handleChange} className={inputClass + " flex-1"} placeholder="e.g. OER-XXXXXXXX" required />
          <button type="button" onClick={handleLookup} disabled={lookupLoading || !formData.reference_no.trim()} className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 disabled:bg-gray-400 flex items-center gap-2">
            <FaSearch /> {lookupLoading ? 'Searching...' : 'Search'}
          </button>
        </div>
        {lookupError && <p className="text-red-600 text-sm mt-1">{lookupError}</p>}
      </div>
      {searchedCompany && (
        <div className="mb-6 p-4 bg-gray-50 rounded border">
          <h4 className="font-bold text-gray-700 mb-3">Details (ዝርዝር)</h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><strong>Applicant Name:</strong> {searchedCompany.applicant_name || 'N/A'}</div>
            <div><strong>Nationality:</strong> {searchedCompany.nationality || 'N/A'}</div>
            <div><strong>Company Name:</strong> {searchedCompany.exporter_company_name || 'N/A'}</div>
            <div><strong>Type of Operation:</strong> {searchedCompany.type_of_operation || 'N/A'}</div>
            <div><strong>Region:</strong> {searchedCompany.region || 'N/A'}</div>
            <div><strong>Sub City:</strong> {searchedCompany.subcity || 'N/A'}</div>
            <div><strong>Manager Name:</strong> {searchedCompany.manager_name || 'N/A'}</div>
            <div><strong>Manager Mobile:</strong> {searchedCompany.manager_mobile || 'N/A'}</div>
          </div>
        </div>
      )}
      <div className="border-t pt-4">
        <h4 className="font-bold text-gray-700 mb-3">Renewal Information (የእድሳት መረጃ)</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="mb-6"><label className={labelClass}>እድሳት አመት / Renewal Year *</label><input type="text" name="renewal_year" value={formData.renewal_year} onChange={handleChange} className={inputClass} placeholder="e.g. 2026" required /></div>
          <div className="mb-6"><label className={labelClass}>የአገልግሎት ክፍያ / Payment Fee *</label><input type="text" name="payment_fee" value={formData.payment_fee} onChange={handleChange} className={inputClass} placeholder="Enter payment fee" required /></div>
          <div className="mb-6"><label className={labelClass}>የታደሰበት ቀን / Renewal Date *</label><EthiopianDatePicker name="renewal_date" value={formData.renewal_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required /></div>
        </div>
      </div>
      <div className="col-span-2">
        <label className="block text-sm font-medium mb-1">ያመለከቱበት ቀን / Request date</label>
        <EthiopianDatePicker name="request_date" value={formData.request_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Additional Attachments (ተያያዥ መረጃዎች)</h4>
        <button type="button" onClick={addAdditionalAttachment} className="text-[#006400] font-medium flex items-center gap-1 hover:underline">
          <FaPlus /> Add Attachment
        </button>
        {additionalAttachments.map((att, index) => (
          <div key={index} className="mt-3 p-3 border border-gray-200 rounded bg-gray-50">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Additional Attachment {index + 1}</span>
              <button type="button" onClick={() => removeAdditionalAttachment(index)} className="text-red-500 hover:text-red-700">
                <FaTrash />
              </button>
            </div>
            <input 
              type="text" 
              placeholder="Attachment name/description" 
              value={att.name} 
              onChange={(e) => handleAdditionalFileNameChange(e, index)} 
              className={inputClass} 
            />
            <input 
              type="file" 
              onChange={(e) => handleAdditionalFileChange(e, index)} 
              className="mt-2 text-sm" 
              accept=".pdf" 
            />
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2"><FaTimes /> Cancel</button>
        <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 flex items-center gap-2"><FaPaperPlane /> {isEdit ? 'Update Request' : 'Submit'}</button>
      </div>
    </form>
  );
}

function FuelRefineryRenewalForm({ onSubmit, onCancel, initialData = null, isEdit = false, onRegister = null }) {
  useFormDraft(() => [formData, setFormData], initialData);
  const [formData, setFormData] = useState({ reference_no: '', renewal_year: '', payment_fee: '', renewal_date: toEthiopianDateStr(new Date()), request_date: toEthiopianDateStr(new Date()) });
  const [searchedCompany, setSearchedCompany] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');

  useEffect(() => {
    if (initialData) {
      setFormData({
        reference_no: initialData.reference_no || '', renewal_year: initialData.renewal_year || '',
        payment_fee: initialData.payment_fee || '', renewal_date: initialData.renewal_date ? convertDbDateToDisplay(initialData.renewal_date) : toEthiopianDateStr(new Date()),
        request_date: initialData.request_date ? convertDbDateToDisplay(initialData.request_date) : toEthiopianDateStr(new Date())
      });
      if (initialData.existing_ref_no) fetchCompanyByRef(initialData.existing_ref_no);
    }
  }, [initialData]);

  const fetchCompanyByRef = async (refNo) => {
    if (!refNo.trim()) return;
    setLookupLoading(true); setLookupError('');
    try {
      const res = await axios.get(`${API_URL}/new-fuel-refinery?reference_no=${refNo}`);
      if (res.data.length > 0) { setSearchedCompany(res.data[0]); }
      else { setLookupError('No record found with this reference number'); setSearchedCompany(null); }
    } catch (err) { setLookupError('Failed to lookup reference'); setSearchedCompany(null); }
    finally { setLookupLoading(false); }
  };

  const handleLookup = () => { fetchCompanyByRef(formData.reference_no); };
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.name === 'reference_no') { setSearchedCompany(null); setLookupError(''); }
  };

  const [additionalAttachments, setAdditionalAttachments] = useState([]);

  const handleAdditionalFileChange = (e, index) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    const newAttachments = [...additionalAttachments];
    newAttachments[index].file = selectedFile;
    setAdditionalAttachments(newAttachments);
  };

  const handleAdditionalFileNameChange = (e, index) => {
    const newAttachments = [...additionalAttachments];
    newAttachments[index].name = e.target.value;
    setAdditionalAttachments(newAttachments);
  };

  const addAdditionalAttachment = () => {
    if (additionalAttachments.length >= 10) {
      toast.warn('You can only add up to 10 attachments');
      return;
    }
    setAdditionalAttachments([...additionalAttachments, { name: '', file: null }]);
  };

  const removeAdditionalAttachment = (index) => {
    const newAttachments = additionalAttachments.filter((_, i) => i !== index);
    setAdditionalAttachments(newAttachments);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!searchedCompany) { toast.warning('Please lookup the reference number first'); return; }
    const formDataToSend = new FormData();
    formDataToSend.append('reference_no', formData.reference_no);
    formDataToSend.append('existing_ref_no', searchedCompany.reference_no);
    formDataToSend.append('renewal_year', formData.renewal_year);
    formDataToSend.append('payment_fee', formData.payment_fee);
    formDataToSend.append('renewal_date', ethDateStrToGregStr(formData.renewal_date));
    formDataToSend.append('request_date', ethDateStrToGregStr(formData.request_date));
    if (isEdit && initialData) {
      formDataToSend.append('status', initialData.status || 'pending');
      formDataToSend.append('response_reason', initialData.response_reason || '');
      formDataToSend.append('rejection_reason', initialData.rejection_reason || '');
    }
    additionalAttachments.forEach((att, index) => {
      if (att.file) {
        formDataToSend.append(`additional_attachment_${index}_name`, att.name || '');
        formDataToSend.append(`additional_attachment_${index}`, att.file);
      }
    });
    formDataToSend.append('additional_attachments_count', additionalAttachments.filter(a => a.file).length);
    onSubmit(formDataToSend, isEdit ? initialData.id : null);
  };

  const inputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow">

      <div className="mb-4 p-4 bg-yellow-50 rounded border border-yellow-200 flex items-center justify-between">
        <p className="text-sm text-yellow-800">ከዚህ ቀደም በሲስተም ውስጥ ካልተመዘገቡ ይመዝገቡ የሚለውን ቁልፍ ይጫኑ / If you are not previously registered in the system, click the Register button.</p>
        {onRegister && (
          <button type="button" onClick={onRegister} className="ml-4 whitespace-nowrap bg-[#006400] text-white px-4 py-2 rounded hover:bg-green-700 text-sm font-medium flex-shrink-0">
            ይመዝገቡ / Register
          </button>
        )}
      </div>
      <div className="mb-4 p-4 bg-blue-50 rounded border border-blue-200">
        <p className="text-sm text-blue-800">Enter the reference number to look up its details, then fill in the renewal information.</p>
      </div>
      <div className="mb-6">
        <label className={labelClass}>Reference No (የማመልከቻ ቁጥር) *</label>
        <div className="flex gap-2">
          <input type="text" name="reference_no" value={formData.reference_no} onChange={handleChange} className={inputClass + " flex-1"} placeholder="e.g. FOE-XXXXXXXX" required />
          <button type="button" onClick={handleLookup} disabled={lookupLoading || !formData.reference_no.trim()} className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 disabled:bg-gray-400 flex items-center gap-2">
            <FaSearch /> {lookupLoading ? 'Searching...' : 'Search'}
          </button>
        </div>
        {lookupError && <p className="text-red-600 text-sm mt-1">{lookupError}</p>}
      </div>
      {searchedCompany && (
        <div className="mb-6 p-4 bg-gray-50 rounded border">
          <h4 className="font-bold text-gray-700 mb-3">Company Details (የኩባንያ ዝርዝር)</h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><strong>Company Name:</strong> {searchedCompany.oil_exporter_company_name || 'N/A'}</div>
            <div><strong>Trade License No:</strong> {searchedCompany.trade_license_no || 'N/A'}</div>
            <div><strong>TIN:</strong> {searchedCompany.tin_no || 'N/A'}</div>
            <div><strong>Region:</strong> {searchedCompany.region || 'N/A'}</div>
            <div><strong>Sub City:</strong> {searchedCompany.subcity || 'N/A'}</div>
            <div><strong>Manager Name:</strong> {searchedCompany.manager_name || 'N/A'}</div>
            <div><strong>Manager Mobile:</strong> {searchedCompany.manager_mobile || 'N/A'}</div>
          </div>
        </div>
      )}
      <div className="border-t pt-4">
        <h4 className="font-bold text-gray-700 mb-3">Renewal Information (የእድሳት መረጃ)</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="mb-6"><label className={labelClass}>እድሳት አመት / Renewal Year *</label><input type="text" name="renewal_year" value={formData.renewal_year} onChange={handleChange} className={inputClass} placeholder="e.g. 2026" required /></div>
          <div className="mb-6"><label className={labelClass}>የአገልግሎት ክፍያ / Payment Fee *</label><input type="text" name="payment_fee" value={formData.payment_fee} onChange={handleChange} className={inputClass} placeholder="Enter payment fee" required /></div>
          <div className="mb-6"><label className={labelClass}>የታደሰበት ቀን / Renewal Date *</label><EthiopianDatePicker name="renewal_date" value={formData.renewal_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required /></div>
        </div>
      </div>
      <div className="col-span-2">
        <label className="block text-sm font-medium mb-1">ያመለከቱበት ቀን / Request date</label>
        <EthiopianDatePicker name="request_date" value={formData.request_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Additional Attachments (ተያያዥ መረጃዎች)</h4>
        <button type="button" onClick={addAdditionalAttachment} className="text-[#006400] font-medium flex items-center gap-1 hover:underline">
          <FaPlus /> Add Attachment
        </button>
        {additionalAttachments.map((att, index) => (
          <div key={index} className="mt-3 p-3 border border-gray-200 rounded bg-gray-50">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Additional Attachment {index + 1}</span>
              <button type="button" onClick={() => removeAdditionalAttachment(index)} className="text-red-500 hover:text-red-700">
                <FaTrash />
              </button>
            </div>
            <input 
              type="text" 
              placeholder="Attachment name/description" 
              value={att.name} 
              onChange={(e) => handleAdditionalFileNameChange(e, index)} 
              className={inputClass} 
            />
            <input 
              type="file" 
              onChange={(e) => handleAdditionalFileChange(e, index)} 
              className="mt-2 text-sm" 
              accept=".pdf" 
            />
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2"><FaTimes /> Cancel</button>
        <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 flex items-center gap-2"><FaPaperPlane /> {isEdit ? 'Update Request' : 'Submit'}</button>
      </div>
    </form>
  );
}


function NewGasStationConnectionForm({ onSubmit, onCancel, initialData = null, isEdit = false }) {
  useFormDraft(() => [formData, setFormData], initialData);
  const [formData, setFormData] = useState({
    applicant_name: '', nationality: '', fan_national_id: '', place_of_renewed: '', date_of_renewed: '',
    region: '', subcity: '', woreda: '', hno: '', pobox: '', telno: '', email: '',
    gas_station_name: '',
    manager_name: '', manager_nationality: '', manager_date_place_issued: '', manager_idno: '',
    manager_region: '', manager_ketema: '', manager_woreda: '', manager_hno: '', manager_pobox: '',
    manager_fixed_phone: '', manager_mobile: '', manager_email: '',
    type_of_operation: '', type_of_operation_en: '', station_type: '',
    capacity_diesel: '', capacity_gasoline: '', capacity_white_gas: '',
    site_region: '', site_zone: '', site_subcity: '', site_woreda: '', site_hno: '', site_phone: '',
    financial_capacity: '',
    site_plan_drawing: null,
    area_of_land: '',
    remark: '',
    application_letter: null, support_letter: null, aggrement_letter: null,
    existing_application_letter: '', existing_support_letter: '', existing_aggrement_letter: '',
    existing_site_plan_drawing: '',
    request_date: toEthiopianDateStr(new Date())
  });
  const [legalPersonType, setLegalPersonType] = useState('');

  useEffect(() => {
    if (initialData) {
      setFormData({
        applicant_name: initialData.applicant_name || '', nationality: initialData.nationality || '', fan_national_id: initialData.fan_national_id || '',
        place_of_renewed: initialData.place_of_renewed || '', date_of_renewed: initialData.date_of_renewed ? initialData.date_of_renewed.split('T')[0] : '',
        region: initialData.region || '', subcity: initialData.subcity || '', woreda: initialData.woreda || '',
        hno: initialData.hno || '', pobox: initialData.pobox || '', telno: initialData.telno || '', email: initialData.email || '',
        gas_station_name: initialData.gas_station_name || '',
        manager_name: initialData.manager_name || '', manager_nationality: initialData.manager_nationality || '',
        manager_date_place_issued: initialData.manager_date_place_issued ? initialData.manager_date_place_issued.split('T')[0] : '',
        manager_idno: initialData.manager_idno || '', manager_region: initialData.manager_region || '',
        manager_ketema: initialData.manager_ketema || '', manager_woreda: initialData.manager_woreda || '',
        manager_hno: initialData.manager_hno || '', manager_pobox: initialData.manager_pobox || '',
        manager_fixed_phone: initialData.manager_fixed_phone || '', manager_mobile: initialData.manager_mobile || '',
        manager_email: initialData.manager_email || '',
        type_of_operation: initialData.type_of_operation || '', type_of_operation_en: initialData.type_of_operation_en || '', station_type: initialData.station_type || '',
        capacity_diesel: initialData.capacity_diesel || '', capacity_gasoline: initialData.capacity_gasoline || '', capacity_white_gas: initialData.capacity_white_gas || '',
        site_region: initialData.site_region || '', site_zone: initialData.site_zone || '',
        site_subcity: initialData.site_subcity || '', site_woreda: initialData.site_woreda || '',
        site_hno: initialData.site_hno || '',         site_phone: initialData.site_phone || '',
        financial_capacity: initialData.financial_capacity || '',
        site_plan_drawing: null,
        area_of_land: initialData.area_of_land || '',
        remark: initialData.remark || '',
        application_letter: null, support_letter: null, aggrement_letter: null,
        existing_application_letter: initialData.application_letter || '',
        existing_support_letter: initialData.support_letter || '',
        existing_aggrement_letter: initialData.aggrement_letter || '',
        existing_site_plan_drawing: initialData.site_plan_drawing || '',
        request_date: initialData.request_date ? convertDbDateToDisplay(initialData.request_date) : toEthiopianDateStr(new Date())
      });
      if (initialData.gas_station_name) setLegalPersonType('company'); else setLegalPersonType('personal');
    }
  }, [initialData]);

  const handleChange = (e) => { setFormData({ ...formData, [e.target.name]: e.target.value }); };
  const handleFileChange = (e, field) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    setFormData({ ...formData, [field]: selectedFile });
  };

  const [additionalAttachments, setAdditionalAttachments] = useState([]);

  const handleAdditionalFileChange = (e, index) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    const newAttachments = [...additionalAttachments];
    newAttachments[index].file = selectedFile;
    setAdditionalAttachments(newAttachments);
  };

  const handleAdditionalFileNameChange = (e, index) => {
    const newAttachments = [...additionalAttachments];
    newAttachments[index].name = e.target.value;
    setAdditionalAttachments(newAttachments);
  };

  const addAdditionalAttachment = () => {
    if (additionalAttachments.length >= 10) {
      toast.warn('You can only add up to 10 attachments');
      return;
    }
    setAdditionalAttachments([...additionalAttachments, { name: '', file: null }]);
  };

  const removeAdditionalAttachment = (index) => {
    const newAttachments = additionalAttachments.filter((_, i) => i !== index);
    setAdditionalAttachments(newAttachments);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    Object.keys(formData).forEach(key => {
      if (formData[key] !== null && key !== 'existing_application_letter' && key !== 'existing_support_letter' && key !== 'existing_aggrement_letter' && key !== 'request_date') {
        if (formData[key] instanceof File) { if (formData[key]) formDataToSend.append(key, formData[key]); }
        else { formDataToSend.append(key, formData[key]); }
      }
    });
    if (isEdit && initialData) {
      formDataToSend.append('existing_application_letter', formData.existing_application_letter);
      formDataToSend.append('existing_support_letter', formData.existing_support_letter);
      formDataToSend.append('existing_aggrement_letter', formData.existing_aggrement_letter);
      formDataToSend.append('existing_site_plan_drawing', formData.existing_site_plan_drawing);
      formDataToSend.append('status', initialData.status || 'pending');
      formDataToSend.append('response_reason', initialData.response_reason || '');
      formDataToSend.append('rejection_reason', initialData.rejection_reason || '');
    }
    formDataToSend.append('request_date', ethDateStrToGregStr(formData.request_date));
    additionalAttachments.forEach((att, index) => {
      if (att.file) {
        formDataToSend.append(`additional_attachment_${index}_name`, att.name || '');
        formDataToSend.append(`additional_attachment_${index}`, att.file);
      }
    });
    formDataToSend.append('additional_attachments_count', additionalAttachments.filter(a => a.file).length);
    onSubmit(formDataToSend, isEdit ? initialData.id : null);
  };

  const inputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]";
  const fileInputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400] bg-white";

  const renderFileInput = (label, field, existingValue) => (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {existingValue && (
        <div className="flex items-center gap-2 mb-2 text-sm text-green-500">
          <FaFile /> Currently uploaded: <a href={fileUrl(existingValue)} target="_blank" rel="noopener noreferrer" className="underline">View file</a>
        </div>
      )}
      <input type="file" onChange={(e) => handleFileChange(e, field)} className={fileInputClass} accept=".pdf" />
      <p className="text-xs text-gray-500 mt-1">PDF (max 10MB)</p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-[#006400]">{isEdit ? 'Edit Service Request' : 'አዲስ የነዳጅ ማደያ ትስስር ፈቃድ አገልግሎት / Service for New Gas Station Permit'}</h2>
        {isEdit && <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm">Editing Request</span>}
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Applicant Type (የአመልካች አይነት)</h4>
        <label className="block text-sm font-medium mb-1">አመልካች ዓይነት ይምረጡ / Select Applicant Type *</label>
        <select
          value={legalPersonType}
          onChange={(e) => setLegalPersonType(e.target.value)}
          className={inputClass}
          style={{ maxWidth: '420px' }}
          required
        >
          <option value="">ይምረጡ / Select Applicant Type</option>
          <option value="personal">ግለሰብ / Personal</option>
          <option value="company">ድርጅት / Company</option>
        </select>
      </div>
      {legalPersonType === 'personal' && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">1. አመልካች ግለሰብ ከሆነ / If Legal Person is Personal</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Name (ስም) *</label><input name="applicant_name" value={formData.applicant_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Nationality (ዜግነት) *</label><input name="nationality" value={formData.nationality} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">የፋይዳ ተለዋጭ ቁጥር (FAN) National ID *</label><input name="fan_national_id" value={formData.fan_national_id} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Region (ክልል) *</label><input name="region" value={formData.region} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Sub City (ክ/ከተማ) *</label><input name="subcity" value={formData.subcity} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ) *</label><input name="woreda" value={formData.woreda} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.)</label><input name="hno" value={formData.hno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">P.O.Box (ፖ.ኦ.ቡክስ)</label><input name="pobox" value={formData.pobox} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Tel. No. (ስልክ ቁ.)</label><input name="telno" value={formData.telno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">E-Mail (ኢመይል)</label><input type="email" name="email" value={formData.email} onChange={handleChange} className={inputClass} /></div>
          </div>
        </div>
      )}
      {legalPersonType === 'company' && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">2. አመልካች ድርጅት ከሆነ / If Legal Person is Company</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Company Name (የኩባንያው ስም) *</label><input name="gas_station_name" value={formData.gas_station_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Name of Manager (የስራ አስኪካጅ ስም) *</label><input name="manager_name" value={formData.manager_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Nationality (ዜግነት) *</label><input name="manager_nationality" value={formData.manager_nationality} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">የፋይዳ ተለዋጭ ቁጥር (FAN) National ID *</label><input name="fan_national_id" value={formData.fan_national_id} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Region (ክልል) *</label><input name="manager_region" value={formData.manager_region} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">K/Ketema (ክ/ከተማ)</label><input name="manager_ketema" value={formData.manager_ketema} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ)</label><input name="manager_woreda" value={formData.manager_woreda} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.)</label><input name="manager_hno" value={formData.manager_hno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">P.O.Box (ፖ.ኦ.ቡክስ)</label><input name="manager_pobox" value={formData.manager_pobox} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Fixed Phone No (ማዕከላዊ ስልክ ቁ.)</label><input name="manager_fixed_phone" value={formData.manager_fixed_phone} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Mobile Phone No (ሞባይል ስልክ ቁ.)</label><input name="manager_mobile" value={formData.manager_mobile} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">E-mail (ኢመይል)</label><input type="email" name="manager_email" value={formData.manager_email} onChange={handleChange} className={inputClass} /></div>
          </div>
        </div>
      )}
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">3. Type of Operation (የሥራው አይነት)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium mb-1">Type of Operation /የሥራው አይነት (አማ) *</label><input name="type_of_operation" value={formData.type_of_operation} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Type of Operation /የሥራው አይነት (En) *</label><input name="type_of_operation_en" value={formData.type_of_operation_en || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">የማደያው አይነት / Types of station *</label><select name="station_type" value={formData.station_type} onChange={handleChange} className={inputClass} required><option value="">-- Select --</option><option value="አዲስ/New">አዲስ/New</option><option value="ነባር/Existing">ነባር/Existing</option></select></div>
          <div className="md:col-span-2"><label className="block text-sm font-medium mb-1">የማደያው የመያዝ አቅም በነዳጅ አይነት (በሊትር)</label></div>
          <div><label className="block text-sm font-medium mb-1">ናፍጣ</label><input type="text" name="capacity_diesel" value={formData.capacity_diesel} onChange={handleChange} className={inputClass} placeholder="ሊትር" /></div>
          <div><label className="block text-sm font-medium mb-1">ቤንዚን</label><input type="text" name="capacity_gasoline" value={formData.capacity_gasoline} onChange={handleChange} className={inputClass} placeholder="ሊትር" /></div>
          <div><label className="block text-sm font-medium mb-1">ነጭ ጋዝ</label><input type="text" name="capacity_white_gas" value={formData.capacity_white_gas} onChange={handleChange} className={inputClass} placeholder="ሊትር" /></div>
        </div>
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">4. Site Location (የስራ ቦታ አድራሻ)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium mb-1">Region (ክልል) *</label><input name="site_region" value={formData.site_region} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Zone/City (ዞን/ከተማ) *</label><input name="site_zone" value={formData.site_zone} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Sub City (ክ/ከተማ) *</label><input name="site_subcity" value={formData.site_subcity} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ) *</label><input name="site_woreda" value={formData.site_woreda} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.)</label><input name="site_hno" value={formData.site_hno} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">Phone No (ስልክ ቁ.)</label><input name="site_phone" value={formData.site_phone} onChange={handleChange} className={inputClass} /></div>
        </div>
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">የገንዘብ አቅም፣ የይዞታና ዲዛይን ንድፍ፣ የቦታ ስፋት / Financial Capacity, Site Plan & Design, Area of Land</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">የገንዘብ አቅም / Financial Capacity *</label>
            <input name="financial_capacity" value={formData.financial_capacity} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የቦታው ስፋት / Area of Land *</label>
            <input name="area_of_land" value={formData.area_of_land} onChange={handleChange} className={inputClass} required />
          </div>
          <div className="col-span-2">
            <div>
              <label className="block text-sm font-medium mb-1">የይዞታ ማረጋገጫና ዲዛይን ንድፍ / Site Plan and Design Drawing</label>
              {formData.existing_site_plan_drawing && (
                <div className="flex items-center gap-2 mb-2 text-sm text-green-500">
                  <FaFile /> Currently uploaded: <a href={fileUrl(formData.existing_site_plan_drawing)} target="_blank" rel="noopener noreferrer" className="underline">View file</a>
                </div>
              )}
              <input type="file" onChange={(e) => { const f = e.target.files[0]; if (f && f.size === 0) { toast.error('The selected file is empty. Please choose a valid PDF document.'); e.target.value = ''; return; } setFormData({ ...formData, site_plan_drawing: f }); }} className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400] bg-white" accept=".pdf" />
              <p className="text-xs text-gray-500 mt-1">PDF (max 10MB)</p>
            </div>
          </div>
        </div>
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Attachments (ተያዥ መረጃዎች)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderFileInput('Application Letter (የማመልከቻ ደብዳቤ)', 'application_letter', formData.existing_application_letter)}
          {renderFileInput('Support Letter (የድጋፍ ደብዳቤ)', 'support_letter', formData.existing_support_letter)}
          {renderFileInput('Agreement Letter (የስምምነት ደብዳቤ)', 'aggrement_letter', formData.existing_aggrement_letter)}
        </div>
      </div>
      <div className="mb-6">
        <label className="block text-sm font-medium mb-1">Remark (ማስታወሻ)</label>
        <textarea name="remark" value={formData.remark} onChange={handleChange} className={inputClass} rows="3" />
      </div>
      <div className="col-span-2">
        <label className="block text-sm font-medium mb-1">ያመለከቱበት ቀን / Request date</label>
        <EthiopianDatePicker name="request_date" value={formData.request_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Additional Attachments (ተያያዥ መረጃዎች)</h4>
        <button type="button" onClick={addAdditionalAttachment} className="text-[#006400] font-medium flex items-center gap-1 hover:underline">
          <FaPlus /> Add Attachment
        </button>
        {additionalAttachments.map((att, index) => (
          <div key={index} className="mt-3 p-3 border border-gray-200 rounded bg-gray-50">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Additional Attachment {index + 1}</span>
              <button type="button" onClick={() => removeAdditionalAttachment(index)} className="text-red-500 hover:text-red-700">
                <FaTrash />
              </button>
            </div>
            <input 
              type="text" 
              placeholder="Attachment name/description" 
              value={att.name} 
              onChange={(e) => handleAdditionalFileNameChange(e, index)} 
              className={inputClass} 
            />
            <input 
              type="file" 
              onChange={(e) => handleAdditionalFileChange(e, index)} 
              className="mt-2 text-sm" 
              accept=".pdf" 
            />
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2"><FaTimes /> Cancel</button>
        <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 flex items-center gap-2"><FaPaperPlane /> {isEdit ? 'Update Request' : 'Submit'}</button>
      </div>
    </form>
  );
}


function NewDepotFuelCargoForm({ onSubmit, onCancel, initialData = null, isEdit = false }) {
  useFormDraft(() => [formData, setFormData], initialData);
  const [formData, setFormData] = useState({
    applicant_name: '', nationality: '', fan_national_id: '', place_of_renewed: '', date_of_renewed: '',
    region: '', subcity: '', woreda: '', hno: '', pobox: '', telno: '', email: '',
    manager_name: '', manager_nationality: '', manager_date_place_issued: '', manager_idno: '',
    manager_region: '', manager_ketema: '', manager_woreda: '', manager_hno: '', manager_pobox: '',
    manager_fixed_phone: '', manager_mobile: '', manager_email: '',
    type_of_operation: '', type_of_operation_en: '',
    site_region: '', site_zone: '', site_subcity: '', site_woreda: '', site_hno: '', site_phone: '',
    financial_capacity: '',
    site_plan_drawing: null,
    area_of_land: '',
    request_letter_no: '',
    request_letter_date: '',
    approved_fuel_amount: '',
    fuel_type: '',
    approved_months: '',
    company_name: '',
    depot_name: '',
    fuel_supplier_name: '',
    remark: '',
    application_letter: null, support_letter: null, aggrement_letter: null,
    existing_application_letter: '', existing_support_letter: '', existing_aggrement_letter: '',
    existing_site_plan_drawing: '',
    request_date: toEthiopianDateStr(new Date())
  });
  const [legalPersonType, setLegalPersonType] = useState('');

  useEffect(() => {
    if (initialData) {
      setFormData({
        applicant_name: initialData.applicant_name || '', nationality: initialData.nationality || '', fan_national_id: initialData.fan_national_id || '',
        place_of_renewed: initialData.place_of_renewed || '', date_of_renewed: initialData.date_of_renewed ? initialData.date_of_renewed.split('T')[0] : '',
        region: initialData.region || '', subcity: initialData.subcity || '', woreda: initialData.woreda || '',
        hno: initialData.hno || '', pobox: initialData.pobox || '', telno: initialData.telno || '', email: initialData.email || '',
        manager_name: initialData.manager_name || '', manager_nationality: initialData.manager_nationality || '',
        manager_date_place_issued: initialData.manager_date_place_issued ? initialData.manager_date_place_issued.split('T')[0] : '',
        manager_idno: initialData.manager_idno || '', manager_region: initialData.manager_region || '',
        manager_ketema: initialData.manager_ketema || '', manager_woreda: initialData.manager_woreda || '',
        manager_hno: initialData.manager_hno || '', manager_pobox: initialData.manager_pobox || '',
        manager_fixed_phone: initialData.manager_fixed_phone || '', manager_mobile: initialData.manager_mobile || '',
        manager_email: initialData.manager_email || '',
        type_of_operation: initialData.type_of_operation || '', type_of_operation_en: initialData.type_of_operation_en || '',
        site_region: initialData.site_region || '', site_zone: initialData.site_zone || '',
        site_subcity: initialData.site_subcity || '', site_woreda: initialData.site_woreda || '',
        site_hno: initialData.site_hno || '',         site_phone: initialData.site_phone || '',
        financial_capacity: initialData.financial_capacity || '',
        site_plan_drawing: null,
        area_of_land: initialData.area_of_land || '',
        request_letter_no: initialData.request_letter_no || '',
        request_letter_date: initialData.request_letter_date || '',
        approved_fuel_amount: initialData.approved_fuel_amount || '',
        fuel_type: initialData.fuel_type || '',
        approved_months: initialData.approved_months || '',
        company_name: initialData.company_name || '',
        depot_name: initialData.depot_name || '',
        fuel_supplier_name: initialData.fuel_supplier_name || '',
        remark: initialData.remark || '',
        application_letter: null, support_letter: null, aggrement_letter: null,
        existing_application_letter: initialData.application_letter || '',
        existing_support_letter: initialData.support_letter || '',
        existing_aggrement_letter: initialData.aggrement_letter || '',
        existing_site_plan_drawing: initialData.site_plan_drawing || '',
        request_date: initialData.request_date ? convertDbDateToDisplay(initialData.request_date) : toEthiopianDateStr(new Date())
      });
      if (initialData.company_name) setLegalPersonType('company'); else setLegalPersonType('personal');
    }
  }, [initialData]);

  const handleChange = (e) => { setFormData({ ...formData, [e.target.name]: e.target.value }); };
  const handleFileChange = (e, field) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    setFormData({ ...formData, [field]: selectedFile });
  };

  const [additionalAttachments, setAdditionalAttachments] = useState([]);

  const handleAdditionalFileChange = (e, index) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    const newAttachments = [...additionalAttachments];
    newAttachments[index].file = selectedFile;
    setAdditionalAttachments(newAttachments);
  };

  const handleAdditionalFileNameChange = (e, index) => {
    const newAttachments = [...additionalAttachments];
    newAttachments[index].name = e.target.value;
    setAdditionalAttachments(newAttachments);
  };

  const addAdditionalAttachment = () => {
    if (additionalAttachments.length >= 10) {
      toast.warn('You can only add up to 10 attachments');
      return;
    }
    setAdditionalAttachments([...additionalAttachments, { name: '', file: null }]);
  };

  const removeAdditionalAttachment = (index) => {
    const newAttachments = additionalAttachments.filter((_, i) => i !== index);
    setAdditionalAttachments(newAttachments);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    Object.keys(formData).forEach(key => {
      if (formData[key] !== null && key !== 'existing_application_letter' && key !== 'existing_support_letter' && key !== 'existing_aggrement_letter' && key !== 'request_date') {
        if (formData[key] instanceof File) { if (formData[key]) formDataToSend.append(key, formData[key]); }
        else { formDataToSend.append(key, formData[key]); }
      }
    });
    if (isEdit && initialData) {
      formDataToSend.append('existing_application_letter', formData.existing_application_letter);
      formDataToSend.append('existing_support_letter', formData.existing_support_letter);
      formDataToSend.append('existing_aggrement_letter', formData.existing_aggrement_letter);
      formDataToSend.append('existing_site_plan_drawing', formData.existing_site_plan_drawing);
      formDataToSend.append('status', initialData.status || 'pending');
      formDataToSend.append('response_reason', initialData.response_reason || '');
      formDataToSend.append('rejection_reason', initialData.rejection_reason || '');
    }
    formDataToSend.append('request_date', ethDateStrToGregStr(formData.request_date));
    additionalAttachments.forEach((att, index) => {
      if (att.file) {
        formDataToSend.append(`additional_attachment_${index}_name`, att.name || '');
        formDataToSend.append(`additional_attachment_${index}`, att.file);
      }
    });
    formDataToSend.append('additional_attachments_count', additionalAttachments.filter(a => a.file).length);
    onSubmit(formDataToSend, isEdit ? initialData.id : null);
  };

  const inputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]";
  const fileInputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400] bg-white";

  const renderFileInput = (label, field, existingValue) => (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {existingValue && (
        <div className="flex items-center gap-2 mb-2 text-sm text-green-500">
          <FaFile /> Currently uploaded: <a href={fileUrl(existingValue)} target="_blank" rel="noopener noreferrer" className="underline">View file</a>
        </div>
      )}
      <input type="file" onChange={(e) => handleFileChange(e, field)} className={fileInputClass} accept=".pdf" />
      <p className="text-xs text-gray-500 mt-1">PDF (max 10MB)</p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-[#006400]">{isEdit ? 'Edit Service Request' : 'አዲስ ከዲፓ የነዳጅ ጭነት ትስስር ፈቃድ አገልግሎት / Service for New Fuel Loading Connection Permit from Depot'}</h2>
        {isEdit && <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm">Editing Request</span>}
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Applicant Type (የአመልካች አይነት)</h4>
        <label className="block text-sm font-medium mb-1">አመልካች ዓይነት ይምረጡ / Select Applicant Type *</label>
        <select
          value={legalPersonType}
          onChange={(e) => setLegalPersonType(e.target.value)}
          className={inputClass}
          style={{ maxWidth: '420px' }}
          required
        >
          <option value="">ይምረጡ / Select Applicant Type</option>
          <option value="personal">ግለሰብ / Personal</option>
          <option value="company">ድርጅት / Company</option>
        </select>
      </div>
      {legalPersonType === 'personal' && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">1. አመልካች ግለሰብ ከሆነ / If Legal Person is Personal</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Name (ስም) *</label><input name="applicant_name" value={formData.applicant_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Nationality (ዜግነት) *</label><input name="nationality" value={formData.nationality} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">የፋይዳ ተለዋጭ ቁጥር (FAN) National ID *</label><input name="fan_national_id" value={formData.fan_national_id} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Region (ክልል) *</label><input name="region" value={formData.region} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Sub City (ክ/ከተማ) *</label><input name="subcity" value={formData.subcity} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ) *</label><input name="woreda" value={formData.woreda} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.)</label><input name="hno" value={formData.hno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">P.O.Box (ፖ.ኦ.ቡክስ)</label><input name="pobox" value={formData.pobox} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Tel. No. (ስልክ ቁ.)</label><input name="telno" value={formData.telno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">E-Mail (ኢመይል)</label><input type="email" name="email" value={formData.email} onChange={handleChange} className={inputClass} /></div>
          </div>
        </div>
      )}
      {legalPersonType === 'company' && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">2. አመልካች ድርጅት ከሆነ / If Legal Person is Company</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Company Name (የኩባንያው ስም) *</label><input name="company_name" value={formData.company_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Name of Manager (የስራ አስኪካጅ ስም) *</label><input name="manager_name" value={formData.manager_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Nationality (ዜግነት) *</label><input name="manager_nationality" value={formData.manager_nationality} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">የፋይዳ ተለዋጭ ቁጥር (FAN) National ID *</label><input name="fan_national_id" value={formData.fan_national_id} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Region (ክልል) *</label><input name="manager_region" value={formData.manager_region} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">K/Ketema (ክ/ከተማ)</label><input name="manager_ketema" value={formData.manager_ketema} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ)</label><input name="manager_woreda" value={formData.manager_woreda} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.)</label><input name="manager_hno" value={formData.manager_hno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">P.O.Box (ፖ.ኦ.ቡክስ)</label><input name="manager_pobox" value={formData.manager_pobox} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Fixed Phone No (ማዕከላዊ ስልክ ቁ.)</label><input name="manager_fixed_phone" value={formData.manager_fixed_phone} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Mobile Phone No (ሞባይል ስልክ ቁ.)</label><input name="manager_mobile" value={formData.manager_mobile} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">E-mail (ኢመይል)</label><input type="email" name="manager_email" value={formData.manager_email} onChange={handleChange} className={inputClass} /></div>
          </div>
        </div>
      )}
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">3. Type of Operation (የሥራው አይነት)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium mb-1">Type of Operation /የሥራው አይነት (አማ) *</label><input name="type_of_operation" value={formData.type_of_operation} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Type of Operation /የሥራው አይነት (En) *</label><input name="type_of_operation_en" value={formData.type_of_operation_en || ''} onChange={handleChange} className={inputClass} required /></div>
        </div>
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">4. Site Location (የስራ ቦታ አድራሻ)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium mb-1">Region (ክልል) *</label><input name="site_region" value={formData.site_region} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Zone/City (ዞን/ከተማ) *</label><input name="site_zone" value={formData.site_zone} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Sub City (ክ/ከተማ) *</label><input name="site_subcity" value={formData.site_subcity} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ) *</label><input name="site_woreda" value={formData.site_woreda} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.)</label><input name="site_hno" value={formData.site_hno} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">Phone No (ስልክ ቁ.)</label><input name="site_phone" value={formData.site_phone} onChange={handleChange} className={inputClass} /></div>
        </div>
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">የገንዘብ አቅም፣ የይዞታና ዲዛይን ንድፍ፣ የቦታ ስፋት / Financial Capacity, Site Plan & Design, Area of Land</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">የገንዘብ አቅም / Financial Capacity *</label>
            <input name="financial_capacity" value={formData.financial_capacity} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የቦታው ስፋት / Area of Land *</label>
            <input name="area_of_land" value={formData.area_of_land} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የተጠየቀበት ደብዳቤ ቁጥር / Request Letter No</label>
            <input name="request_letter_no" value={formData.request_letter_no} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የተጠየቀበት ደብዳቤ ቀን / Request Letter Date</label>
            <EthiopianDatePicker name="request_letter_date" value={formData.request_letter_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የተፈቀደው ነዳጅ መጠን / Approved Fuel Amount</label>
            <input name="approved_fuel_amount" value={formData.approved_fuel_amount} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የነዳጅ አይነት / Fuel Type</label>
            <select name="fuel_type" value={formData.fuel_type} onChange={handleChange} className={inputClass}>
              <option value="">-- Select --</option>
              <option value="ቤንዚን">ቤንዚን</option>
              <option value="ናፍጣ">ናፍጣ</option>
              <option value="ኬሮሲን">ኬሮሲን</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የተፈቀደለት ወራት / Approved Months</label>
            <input name="approved_months" value={formData.approved_months} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የኩባንያ ሥም / Company Name</label>
            <input name="company_name" value={formData.company_name} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የዴፖ ሥም / Depot Name</label>
            <input name="depot_name" value={formData.depot_name} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የነዳጅ አቅራቢ ሥም / Fuel Supplier Name</label>
            <input name="fuel_supplier_name" value={formData.fuel_supplier_name} onChange={handleChange} className={inputClass} />
          </div>
          <div className="col-span-2">
            <div>
              <label className="block text-sm font-medium mb-1">የይዞታ ማረጋገጫና ዲዛይን ንድፍ / Site Plan and Design Drawing</label>
              {formData.existing_site_plan_drawing && (
                <div className="flex items-center gap-2 mb-2 text-sm text-green-500">
                  <FaFile /> Currently uploaded: <a href={fileUrl(formData.existing_site_plan_drawing)} target="_blank" rel="noopener noreferrer" className="underline">View file</a>
                </div>
              )}
              <input type="file" onChange={(e) => { const f = e.target.files[0]; if (f && f.size === 0) { toast.error('The selected file is empty. Please choose a valid PDF document.'); e.target.value = ''; return; } setFormData({ ...formData, site_plan_drawing: f }); }} className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400] bg-white" accept=".pdf" />
              <p className="text-xs text-gray-500 mt-1">PDF (max 10MB)</p>
            </div>
          </div>
        </div>
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Attachments (ተያዥ መረጃዎች)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderFileInput('Application Letter (የማመልከቻ ደብዳቤ)', 'application_letter', formData.existing_application_letter)}
          {renderFileInput('Support Letter (የድጋፍ ደብዳቤ)', 'support_letter', formData.existing_support_letter)}
          {renderFileInput('Agreement Letter (የስምምነት ደብዳቤ)', 'aggrement_letter', formData.existing_aggrement_letter)}
        </div>
      </div>
      <div className="mb-6">
        <label className="block text-sm font-medium mb-1">Remark (ማስታወሻ)</label>
        <textarea name="remark" value={formData.remark} onChange={handleChange} className={inputClass} rows="3" />
      </div>
      <div className="col-span-2">
        <label className="block text-sm font-medium mb-1">ያመለከቱበት ቀን / Request date</label>
        <EthiopianDatePicker name="request_date" value={formData.request_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Additional Attachments (ተያያዥ መረጃዎች)</h4>
        <button type="button" onClick={addAdditionalAttachment} className="text-[#006400] font-medium flex items-center gap-1 hover:underline">
          <FaPlus /> Add Attachment
        </button>
        {additionalAttachments.map((att, index) => (
          <div key={index} className="mt-3 p-3 border border-gray-200 rounded bg-gray-50">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Additional Attachment {index + 1}</span>
              <button type="button" onClick={() => removeAdditionalAttachment(index)} className="text-red-500 hover:text-red-700">
                <FaTrash />
              </button>
            </div>
            <input 
              type="text" 
              placeholder="Attachment name/description" 
              value={att.name} 
              onChange={(e) => handleAdditionalFileNameChange(e, index)} 
              className={inputClass} 
            />
            <input 
              type="file" 
              onChange={(e) => handleAdditionalFileChange(e, index)} 
              className="mt-2 text-sm" 
              accept=".pdf" 
            />
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2"><FaTimes /> Cancel</button>
        <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 flex items-center gap-2"><FaPaperPlane /> {isEdit ? 'Update Request' : 'Submit'}</button>
      </div>
    </form>
  );
}


function NewGasStationRetailForm({ onSubmit, onCancel, initialData = null, isEdit = false, title }) {
  useFormDraft(() => [formData, setFormData], initialData);
  const [formData, setFormData] = useState({
    applicant_name: '', nationality: '', fan_national_id: '',
        region: '', subcity: '', woreda: '', hno: '', pobox: '', telno: '', email: '',
        gas_station_retail_name: '',
        manager_name: '', manager_nationality: '',
    manager_region: '', manager_ketema: '', manager_woreda: '', manager_hno: '', manager_pobox: '',
    manager_fixed_phone: '', manager_mobile: '', manager_email: '',
    type_of_operation: '', type_of_operation_en: '', operation_code: '', station_type: '',
    trade_name: '', trade_name_en: '',
    site_region: '', site_region_en: '', site_zone: '', site_zone_en: '', site_subcity: '', site_subcity_en: '', site_woreda: '', site_woreda_en: '', site_hno: '', site_hno_en: '', site_phone: '',
    classification_codes: [],
    financial_capacity: '',
    site_plan_drawing: null,
    area_of_land: '',
    applicant_name_en: '',
    construction_service_for: '',
    construction_service_for_en: '',
        remark: '',
    application_letter: null, support_letter: null, aggrement_letter: null,
    existing_application_letter: '', existing_support_letter: '', existing_aggrement_letter: '',
    existing_site_plan_drawing: '',
    request_date: toEthiopianDateStr(new Date())
  });
  const [legalPersonType, setLegalPersonType] = useState('');
  const [personalClassificationCode, setPersonalClassificationCode] = useState('');
  const [companyClassificationCode, setCompanyClassificationCode] = useState('');

  useEffect(() => {
    if (initialData) {
      setFormData({
        applicant_name: initialData.applicant_name || '', nationality: initialData.nationality || '', fan_national_id: initialData.fan_national_id || '',
        region: initialData.region || '', subcity: initialData.subcity || '', woreda: initialData.woreda || '',
        hno: initialData.hno || '', pobox: initialData.pobox || '', telno: initialData.telno || '', email: initialData.email || '',
        gas_station_retail_name: initialData.gas_station_retail_name || '',
        manager_name: initialData.manager_name || '', manager_nationality: initialData.manager_nationality || '',
        manager_region: initialData.manager_region || '',
        manager_ketema: initialData.manager_ketema || '', manager_woreda: initialData.manager_woreda || '',
        manager_hno: initialData.manager_hno || '', manager_pobox: initialData.manager_pobox || '',
        manager_fixed_phone: initialData.manager_fixed_phone || '', manager_mobile: initialData.manager_mobile || '',
        manager_email: initialData.manager_email || '',
        type_of_operation: initialData.type_of_operation || '', type_of_operation_en: initialData.type_of_operation_en || '', operation_code: initialData.operation_code || '', station_type: initialData.station_type || '',
        trade_name: initialData.trade_name || '', trade_name_en: initialData.trade_name_en || '',
        site_region: initialData.site_region || '', site_region_en: initialData.site_region_en || '',
        site_zone: initialData.site_zone || '', site_zone_en: initialData.site_zone_en || '',
        site_subcity: initialData.site_subcity || '', site_subcity_en: initialData.site_subcity_en || '',
        site_woreda: initialData.site_woreda || '', site_woreda_en: initialData.site_woreda_en || '',
        site_hno: initialData.site_hno || '', site_hno_en: initialData.site_hno_en || '',
        site_phone: initialData.site_phone || '',
        classification_codes: initialData.classification_codes ? JSON.parse(initialData.classification_codes) : [],
        financial_capacity: initialData.financial_capacity || '',
        site_plan_drawing: null,
        area_of_land: initialData.area_of_land || '',
        applicant_name_en: initialData.applicant_name_en || '',
        construction_service_for: initialData.construction_service_for || '',
        construction_service_for_en: initialData.construction_service_for_en || '',
        plan_no: initialData.plan_no || '',
        gps_north: initialData.gps_north || '',
        gps_east: initialData.gps_east || '',
        total_project_cost: initialData.total_project_cost || '',
        professional_in_charge: initialData.professional_in_charge || '',
        remark: initialData.remark || '',
        application_letter: null, support_letter: null, aggrement_letter: null,
        existing_application_letter: initialData.application_letter || '',
        existing_support_letter: initialData.support_letter || '',
        existing_aggrement_letter: initialData.aggrement_letter || '',
        existing_site_plan_drawing: initialData.site_plan_drawing || '',
        request_date: initialData.request_date ? convertDbDateToDisplay(initialData.request_date) : toEthiopianDateStr(new Date())
      });
      if (initialData.gas_station_retail_name) setLegalPersonType('company'); else setLegalPersonType('personal');
      if (initialData.classification_codes) {
        const firstCode = (JSON.parse(initialData.classification_codes) || [])[0] || '';
        if (initialData.gas_station_retail_name) setCompanyClassificationCode(firstCode);
        else setPersonalClassificationCode(firstCode);
      }
    }
  }, [initialData]);

  const handleChange = (e) => { setFormData({ ...formData, [e.target.name]: e.target.value }); };
  const handleFileChange = (e, field) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    setFormData({ ...formData, [field]: selectedFile });
  };

  const applyClassification = (setter, code) => {
    const item = CLASSIFICATION_CODES.find(c => c.code === code);
    setter(code);
    setFormData(prev => ({
      ...prev,
      classification_codes: code ? [code] : [],
      type_of_operation: item ? item.amharic : '',
      type_of_operation_en: item ? item.english : '',
      operation_code: code || ''
    }));
  };

  const [additionalAttachments, setAdditionalAttachments] = useState([]);

  const handleAdditionalFileChange = (e, index) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    const newAttachments = [...additionalAttachments];
    newAttachments[index].file = selectedFile;
    setAdditionalAttachments(newAttachments);
  };

  const handleAdditionalFileNameChange = (e, index) => {
    const newAttachments = [...additionalAttachments];
    newAttachments[index].name = e.target.value;
    setAdditionalAttachments(newAttachments);
  };

  const addAdditionalAttachment = () => {
    if (additionalAttachments.length >= 10) {
      toast.warn('You can only add up to 10 attachments');
      return;
    }
    setAdditionalAttachments([...additionalAttachments, { name: '', file: null }]);
  };

  const removeAdditionalAttachment = (index) => {
    const newAttachments = additionalAttachments.filter((_, i) => i !== index);
    setAdditionalAttachments(newAttachments);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    Object.keys(formData).forEach(key => {
      if (formData[key] !== null && key !== 'existing_application_letter' && key !== 'existing_support_letter' && key !== 'existing_aggrement_letter' && key !== 'classification_codes' && key !== 'request_date') {
        if (formData[key] instanceof File) { if (formData[key]) formDataToSend.append(key, formData[key]); }
        else { formDataToSend.append(key, formData[key]); }
      }
    });
    if (isEdit && initialData) {
      formDataToSend.append('existing_application_letter', formData.existing_application_letter);
      formDataToSend.append('existing_support_letter', formData.existing_support_letter);
      formDataToSend.append('existing_aggrement_letter', formData.existing_aggrement_letter);
      formDataToSend.append('existing_site_plan_drawing', formData.existing_site_plan_drawing);
      formDataToSend.append('status', initialData.status || 'pending');
      formDataToSend.append('response_reason', initialData.response_reason || '');
      formDataToSend.append('rejection_reason', initialData.rejection_reason || '');
    }
    formDataToSend.append('classification_codes', JSON.stringify(formData.classification_codes));
    formDataToSend.append('request_date', ethDateStrToGregStr(formData.request_date));
    additionalAttachments.forEach((att, index) => {
      if (att.file) {
        formDataToSend.append(`additional_attachment_${index}_name`, att.name || '');
        formDataToSend.append(`additional_attachment_${index}`, att.file);
      }
    });
    formDataToSend.append('additional_attachments_count', additionalAttachments.filter(a => a.file).length);
    onSubmit(formDataToSend, isEdit ? initialData.id : null);
  };

  const inputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]";
  const fileInputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400] bg-white";

  const renderFileInput = (label, field, existingValue) => (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {existingValue && (
        <div className="flex items-center gap-2 mb-2 text-sm text-green-500">
          <FaFile /> Currently uploaded: <a href={fileUrl(existingValue)} target="_blank" rel="noopener noreferrer" className="underline">View file</a>
        </div>
      )}
      <input type="file" onChange={(e) => handleFileChange(e, field)} className={fileInputClass} accept=".pdf" />
      <p className="text-xs text-gray-500 mt-1">PDF (max 10MB)</p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow">
      <h2 className="text-xl font-bold text-[#006400]">{isEdit ? 'Edit Service Request' : title}</h2>

      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Applicant Type (የአመልካች አይነት)</h4>
        <label className="block text-sm font-medium mb-1">አመልካች ዓይነት ይምረጡ / Select Applicant Type *</label>
        <select
          value={legalPersonType}
          onChange={(e) => setLegalPersonType(e.target.value)}
          className={inputClass}
          style={{ maxWidth: '420px' }}
          required
        >
          <option value="">ይምረጡ / Select Applicant Type</option>
          <option value="personal">ግለሰብ / Personal</option>
          <option value="company">ድርጅት / Company</option>
        </select>
      </div>
      {legalPersonType === 'personal' && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">1. አመልካች ግለሰብ ከሆነ / If Legal Person is Personal</h4>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Select Classification Code (የምደባ ኮድ ይምረጡ) *</label>
            <select value={personalClassificationCode} onChange={(e) => applyClassification(setPersonalClassificationCode, e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]" required>
              <option value="">-- Select --</option>
              {CLASSIFICATION_CODES.map(item => (
                <option key={item.code} value={item.code}>{item.code} - {item.amharic}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Name (አማ) *</label><input name="applicant_name" value={formData.applicant_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Name (En)</label><input name="applicant_name_en" value={formData.applicant_name_en} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Nationality (ዜግነት) *</label><input name="nationality" value={formData.nationality} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">የፋይዳ ተለዋጭ ቁጥር (FAN) National ID *</label><input name="fan_national_id" value={formData.fan_national_id} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Region (ክልል) *</label><input name="region" value={formData.region} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Sub City (ክ/ከተማ) *</label><input name="subcity" value={formData.subcity} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ) *</label><input name="woreda" value={formData.woreda} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.)</label><input name="hno" value={formData.hno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">P.O.Box (ፖ.ኦ.ቡክስ)</label><input name="pobox" value={formData.pobox} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Tel. No. (ስልክ ቁ.)</label><input name="telno" value={formData.telno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">E-Mail (ኢመይል)</label><input type="email" name="email" value={formData.email} onChange={handleChange} className={inputClass} /></div>
          </div>
        </div>
      )}
      {legalPersonType === 'company' && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">2. አመልካች ድርጅት ከሆነ / If Legal Person is Company</h4>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Select Classification Code (የምደባ ኮድ ይምረጡ) *</label>
            <select value={companyClassificationCode} onChange={(e) => applyClassification(setCompanyClassificationCode, e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]" required>
              <option value="">-- Select --</option>
              {CLASSIFICATION_CODES.map(item => (
                <option key={item.code} value={item.code}>{item.code} - {item.amharic}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Company Name (የኩባንያው ስም) *</label><input name="gas_station_retail_name" value={formData.gas_station_retail_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Name of Manager (የስራ አስኪካጅ ስም) *</label><input name="manager_name" value={formData.manager_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">የፋይዳ ተለዋጭ ቁጥር (FAN) National ID *</label><input name="fan_national_id" value={formData.fan_national_id} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Nationality (ዜግነት) *</label><input name="manager_nationality" value={formData.manager_nationality} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Region (ክልል) *</label><input name="manager_region" value={formData.manager_region} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">K/Ketema (ክ/ከተማ)</label><input name="manager_ketema" value={formData.manager_ketema} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ)</label><input name="manager_woreda" value={formData.manager_woreda} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.)</label><input name="manager_hno" value={formData.manager_hno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">P.O.Box (ፖ.ኦ.ቡክስ)</label><input name="manager_pobox" value={formData.manager_pobox} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Fixed Phone No (ማዕከላዊ ስልክ ቁ.)</label><input name="manager_fixed_phone" value={formData.manager_fixed_phone} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Mobile Phone No (ሞባይል ስልክ ቁ.)</label><input name="manager_mobile" value={formData.manager_mobile} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">E-mail (ኢመይል)</label><input type="email" name="manager_email" value={formData.manager_email} onChange={handleChange} className={inputClass} /></div>
          </div>
        </div>
      )}
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">3. Type of Operation (የሥራው አይነት)</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><label className="block text-sm font-medium mb-1">Type of Operation /የሥራው አይነት (አማ) *</label><input name="type_of_operation" value={formData.type_of_operation} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Type of Operation /የሥራው አይነት (En) *</label><input name="type_of_operation_en" value={formData.type_of_operation_en || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">ኮድ / Code</label><input name="operation_code" value={formData.operation_code} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">የማደያው አይነት / Types of station *</label><select name="station_type" value={formData.station_type} onChange={handleChange} className={inputClass} required><option value="">-- Select --</option><option value="አዲስ/New">አዲስ/New</option><option value="ነባር/Existing">ነባር/Existing</option></select></div>
        </div>
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">4. Site Location (የስራ ቦታ አድራሻ)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium mb-1">Trade name (አማ) *</label><input name="trade_name" value={formData.trade_name} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Trade name (En)</label><input name="trade_name_en" value={formData.trade_name_en || ''} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">Region /ክልል (አማ) *</label><input name="site_region" value={formData.site_region} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Region/ክልል (En)</label><input name="site_region_en" value={formData.site_region_en || ''} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">Zone/City ዞን/ከተማ (አማ) *</label><input name="site_zone" value={formData.site_zone} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Zone/City ዞን/ከተማ (En)</label><input name="site_zone_en" value={formData.site_zone_en || ''} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">Sub City ክ/ከተማ (አማ) *</label><input name="site_subcity" value={formData.site_subcity} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Sub City ክ/ከተማ (En)</label><input name="site_subcity_en" value={formData.site_subcity_en || ''} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ) (አማ) *</label><input name="site_woreda" value={formData.site_woreda} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ) (En)</label><input name="site_woreda_en" value={formData.site_woreda_en || ''} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.) (አማ)</label><input name="site_hno" value={formData.site_hno} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.) (En)</label><input name="site_hno_en" value={formData.site_hno_en || ''} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">Phone No (ስልክ ቁ.)</label><input name="site_phone" value={formData.site_phone} onChange={handleChange} className={inputClass} /></div>
        </div>
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">የገንዘብ አቅም፣ የይዞታና ዲዛይን ንድፍ፣ የቦታ ስፋት / Financial Capacity, Site Plan & Design, Area of Land</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">የገንዘብ አቅም / Financial Capacity *</label>
            <input name="financial_capacity" value={formData.financial_capacity} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የቦታው ስፋት / Area of Land *</label>
            <input name="area_of_land" value={formData.area_of_land} onChange={handleChange} className={inputClass} required />
          </div>
          <div className="col-span-2">
            <div>
              <label className="block text-sm font-medium mb-1">የይዞታ ማረጋገጫና ዲዛይን ንድፍ / Site Plan and Design Drawing</label>
              {formData.existing_site_plan_drawing && (
                <div className="flex items-center gap-2 mb-2 text-sm text-green-500">
                  <FaFile /> Currently uploaded: <a href={fileUrl(formData.existing_site_plan_drawing)} target="_blank" rel="noopener noreferrer" className="underline">View file</a>
                </div>
              )}
              <input type="file" onChange={(e) => { const f = e.target.files[0]; if (f && f.size === 0) { toast.error('The selected file is empty. Please choose a valid PDF document.'); e.target.value = ''; return; } setFormData({ ...formData, site_plan_drawing: f }); }} className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400] bg-white" accept=".pdf" />
              <p className="text-xs text-gray-500 mt-1">PDF (max 10MB)</p>
            </div>
          </div>
        </div>
      </div>

      
      {title && title.includes('Construction Permit') && (
        <div>
          {/* Construction Permeation Fields */}
          <div>
            <label className="block text-sm font-medium mb-1">የግንባታው አገልግሎት የሚውለው / Construction service for_አማ</label>
            <input name="construction_service_for" value={formData.construction_service_for} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የግንባታው አገልግሎት የሚውለው / Construction service for_En</label>
            <input name="construction_service_for_en" value={formData.construction_service_for_en || ''} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የካርታ ቁጥር / Plan no</label>
            <input name="plan_no" value={formData.plan_no} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የጂፒኤስ አድራሻ: (ሰሜን) / GPS Location: (North)</label>
            <input name="gps_north" value={formData.gps_north} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የጂፒኤስ አድራሻ: (ምስራቅ) / GPS Location: (East)</label>
            <input name="gps_east" value={formData.gps_east} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Scope of work station: Total project cost</label>
            <input name="total_project_cost" value={formData.total_project_cost} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Professional in charge construction</label>
            <input name="professional_in_charge" value={formData.professional_in_charge} onChange={handleChange} className={inputClass} />
          </div>
        </div>
      )}
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Attachments (ተያዥ መረጃዎች)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderFileInput('Application Letter (የማመልከቻ ደብዳቤ)', 'application_letter', formData.existing_application_letter)}
          {renderFileInput('Support Letter (የድጋፍ ደብዳቤ)', 'support_letter', formData.existing_support_letter)}
          {renderFileInput('Agreement Letter (የስምምነት ደብዳቤ)', 'aggrement_letter', formData.existing_aggrement_letter)}
        </div>
      </div>
      {title && title.includes('Construction Permit') && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">መያያዝ ያለባቸዉ መረጃዎች ዝርዝር / Attachments:</h4>
          <ul className="list-decimal pl-5 space-y-1 text-sm">
            <li>ፋይዳ መታወቂያ</li>
            <li>ፓስፖርት / I.D / Passport</li>
            <li>የባለቤትነት/የኪራይ ማረጋገጫ ውል/Investment /Rent Agreement</li>
            <li>የንግድ የምዝገባ የምስክር ወረቀት/Trade Registration Certificate</li>
            <li>የቦታ ካርታና ዲዛይን ንድፍ/Site Plan and Design Drawing</li>
            <li>የገንዘብ አቅም የባንክ መረጃ/Bank Statement for Financial Capacity</li>
            <li>ቢዝነስ ፕላን/የአዋጭነት ጥናት/ Business Plan / Feasibility Study</li>
            <li>የአካባቢ ተጽዕኖ ግምገማ ጥናት/Environmental Impact Assessment Study Report</li>
            <li>አመልካቹ ባለቤት ካልሆነ ህጋዊ ውክልና/Legal Representation (If applicant is not the owner)</li>
            <li>የማህበር መመስረቻ ደንብ/Article of Association (for PLC)</li>
            <li>የኢንሹራንስ ሽፋን/ Insurance Commitment letter</li>
            <li>የድርጅቱ የንግድ ልውውጥ መረጃ Company's Supply Records</li>
            <li>የሥራው እቅድ/Working program</li>
            <li>መሬቱ ለፔትሮሊየም ውጤቶች ግንባታ የሚሆን መሆኑን የሚገልፅ የአፈር ምርምር ጥናት/soil analysis study</li>
            <li>(የነዳጅ ማደያ መሣሪያዎች ተከላና ጥገና አገልግሎት) የሚያደርግ ባለሞያ ወይም ተቋም ከነዳጅና ኢነርጂ ባለስልጣን ብቃት የወሰደ መሆኑ የሚገልፅ ሰነድ</li>
          </ul>
        </div>
      )}
      <div className="mb-6">
        <label className="block text-sm font-medium mb-1">Remark (ማስታወሻ)</label>
        <textarea name="remark" value={formData.remark} onChange={handleChange} className={inputClass} rows="3" />
      </div>
      <div className="col-span-2">
        <label className="block text-sm font-medium mb-1">ያመለከቱበት ቀን / Request date</label>
        <EthiopianDatePicker name="request_date" value={formData.request_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Additional Attachments (ተያያዥ መረጃዎች)</h4>
        <button type="button" onClick={addAdditionalAttachment} className="text-[#006400] font-medium flex items-center gap-1 hover:underline">
          <FaPlus /> Add Attachment
        </button>
        {additionalAttachments.map((att, index) => (
          <div key={index} className="mt-3 p-3 border border-gray-200 rounded bg-gray-50">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Additional Attachment {index + 1}</span>
              <button type="button" onClick={() => removeAdditionalAttachment(index)} className="text-red-500 hover:text-red-700">
                <FaTrash />
              </button>
            </div>
            <input 
              type="text" 
              placeholder="Attachment name/description" 
              value={att.name} 
              onChange={(e) => handleAdditionalFileNameChange(e, index)} 
              className={inputClass} 
            />
            <input 
              type="file" 
              onChange={(e) => handleAdditionalFileChange(e, index)} 
              className="mt-2 text-sm" 
              accept=".pdf" 
            />
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2"><FaTimes /> Cancel</button>
        <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 flex items-center gap-2"><FaPaperPlane /> {isEdit ? 'Update Request' : 'Submit'}</button>
      </div>
    </form>
  );
}


function NewFuelDistributorForm({ onSubmit, onCancel, initialData = null, isEdit = false, title }) {
  useFormDraft(() => [formData, setFormData], initialData);
  const [formData, setFormData] = useState({
    applicant_name: '', applicant_name_en: '', nationality: '', fan_national_id: '',
        region: '', subcity: '', woreda: '', hno: '', pobox: '', telno: '', email: '',
        distributor_company_name: '',
        manager_name: '', manager_nationality: '',
    manager_region: '', manager_ketema: '', manager_woreda: '', manager_hno: '', manager_pobox: '',
    manager_fixed_phone: '', manager_mobile: '', manager_email: '',
    type_of_operation: '', type_of_operation_en: '', operation_code: '', station_type: '',
    trade_name: '', trade_name_en: '',
    site_region: '', site_region_en: '', site_zone: '', site_zone_en: '', site_subcity: '', site_subcity_en: '', site_woreda: '', site_woreda_en: '', site_hno: '', site_hno_en: '', site_phone: '',
    classification_codes: [],
    financial_capacity: '',
    site_plan_drawing: null,
    area_of_land: '',

        remark: '',
    application_letter: null, support_letter: null, aggrement_letter: null,
    existing_application_letter: '', existing_support_letter: '', existing_aggrement_letter: '',
    existing_site_plan_drawing: '',
    request_date: toEthiopianDateStr(new Date())
  });
  const [legalPersonType, setLegalPersonType] = useState('');
  const [personalClassificationCode, setPersonalClassificationCode] = useState('');
  const [companyClassificationCode, setCompanyClassificationCode] = useState('');

  useEffect(() => {
    if (initialData) {
      setFormData({
        applicant_name: initialData.applicant_name || '', applicant_name_en: initialData.applicant_name_en || '', nationality: initialData.nationality || '', fan_national_id: initialData.fan_national_id || '',
        region: initialData.region || '', subcity: initialData.subcity || '', woreda: initialData.woreda || '',
        hno: initialData.hno || '', pobox: initialData.pobox || '', telno: initialData.telno || '', email: initialData.email || '',
        distributor_company_name: initialData.distributor_company_name || '',
        manager_name: initialData.manager_name || '', manager_nationality: initialData.manager_nationality || '',
        manager_region: initialData.manager_region || '',
        manager_ketema: initialData.manager_ketema || '', manager_woreda: initialData.manager_woreda || '',
        manager_hno: initialData.manager_hno || '', manager_pobox: initialData.manager_pobox || '',
        manager_fixed_phone: initialData.manager_fixed_phone || '', manager_mobile: initialData.manager_mobile || '',
        manager_email: initialData.manager_email || '',
        type_of_operation: initialData.type_of_operation || '', type_of_operation_en: initialData.type_of_operation_en || '', operation_code: initialData.operation_code || '', station_type: initialData.station_type || '',
        trade_name: initialData.trade_name || '', trade_name_en: initialData.trade_name_en || '',
        site_region: initialData.site_region || '', site_region_en: initialData.site_region_en || '', site_zone: initialData.site_zone || '', site_zone_en: initialData.site_zone_en || '',
        site_subcity: initialData.site_subcity || '', site_subcity_en: initialData.site_subcity_en || '', site_woreda: initialData.site_woreda || '', site_woreda_en: initialData.site_woreda_en || '',
        site_hno: initialData.site_hno || '', site_hno_en: initialData.site_hno_en || '', site_phone: initialData.site_phone || '',
        classification_codes: initialData.classification_codes ? JSON.parse(initialData.classification_codes) : [],
        financial_capacity: initialData.financial_capacity || '',
        site_plan_drawing: null,
        area_of_land: initialData.area_of_land || '',
        construction_service_for: initialData.construction_service_for || '',
        plan_no: initialData.plan_no || '',
        gps_north: initialData.gps_north || '',
        gps_east: initialData.gps_east || '',
        total_project_cost: initialData.total_project_cost || '',
        professional_in_charge: initialData.professional_in_charge || '',
        remark: initialData.remark || '',
        application_letter: null, support_letter: null, aggrement_letter: null,
        existing_application_letter: initialData.application_letter || '',
        existing_support_letter: initialData.support_letter || '',
        existing_aggrement_letter: initialData.aggrement_letter || '',
        existing_site_plan_drawing: initialData.site_plan_drawing || '',
        request_date: initialData.request_date ? convertDbDateToDisplay(initialData.request_date) : toEthiopianDateStr(new Date())
      });
      if (initialData.distributor_company_name) setLegalPersonType('company'); else setLegalPersonType('personal');
      if (initialData.classification_codes) {
        const firstCode = (JSON.parse(initialData.classification_codes) || [])[0] || '';
        if (initialData.distributor_company_name) setCompanyClassificationCode(firstCode);
        else setPersonalClassificationCode(firstCode);
      }
    }
  }, [initialData]);

  const handleChange = (e) => { setFormData({ ...formData, [e.target.name]: e.target.value }); };
  const handleFileChange = (e, field) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    setFormData({ ...formData, [field]: selectedFile });
  };

  const applyClassification = (setter, code) => {
    const item = CLASSIFICATION_CODES.find(c => c.code === code);
    setter(code);
    setFormData(prev => ({
      ...prev,
      classification_codes: code ? [code] : [],
      type_of_operation: item ? item.amharic : '',
      type_of_operation_en: item ? item.english : '',
      operation_code: code || ''
    }));
  };

  const [additionalAttachments, setAdditionalAttachments] = useState([]);

  const handleAdditionalFileChange = (e, index) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    const newAttachments = [...additionalAttachments];
    newAttachments[index].file = selectedFile;
    setAdditionalAttachments(newAttachments);
  };

  const handleAdditionalFileNameChange = (e, index) => {
    const newAttachments = [...additionalAttachments];
    newAttachments[index].name = e.target.value;
    setAdditionalAttachments(newAttachments);
  };

  const addAdditionalAttachment = () => {
    if (additionalAttachments.length >= 10) {
      toast.warn('You can only add up to 10 attachments');
      return;
    }
    setAdditionalAttachments([...additionalAttachments, { name: '', file: null }]);
  };

  const removeAdditionalAttachment = (index) => {
    const newAttachments = additionalAttachments.filter((_, i) => i !== index);
    setAdditionalAttachments(newAttachments);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    Object.keys(formData).forEach(key => {
      if (formData[key] !== null && key !== 'existing_application_letter' && key !== 'existing_support_letter' && key !== 'existing_aggrement_letter' && key !== 'classification_codes' && key !== 'request_date') {
        if (formData[key] instanceof File) { if (formData[key]) formDataToSend.append(key, formData[key]); }
        else { formDataToSend.append(key, formData[key]); }
      }
    });
    if (isEdit && initialData) {
      formDataToSend.append('existing_application_letter', formData.existing_application_letter);
      formDataToSend.append('existing_support_letter', formData.existing_support_letter);
      formDataToSend.append('existing_aggrement_letter', formData.existing_aggrement_letter);
      formDataToSend.append('existing_site_plan_drawing', formData.existing_site_plan_drawing);
      formDataToSend.append('status', initialData.status || 'pending');
      formDataToSend.append('response_reason', initialData.response_reason || '');
      formDataToSend.append('rejection_reason', initialData.rejection_reason || '');
    }
    formDataToSend.append('classification_codes', JSON.stringify(formData.classification_codes));
    formDataToSend.append('request_date', ethDateStrToGregStr(formData.request_date));
    additionalAttachments.forEach((att, index) => {
      if (att.file) {
        formDataToSend.append(`additional_attachment_${index}_name`, att.name || '');
        formDataToSend.append(`additional_attachment_${index}`, att.file);
      }
    });
    formDataToSend.append('additional_attachments_count', additionalAttachments.filter(a => a.file).length);
    onSubmit(formDataToSend, isEdit ? initialData.id : null);
  };

  const inputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]";
  const fileInputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400] bg-white";

  const renderFileInput = (label, field, existingValue) => (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {existingValue && (
        <div className="flex items-center gap-2 mb-2 text-sm text-green-500">
          <FaFile /> Currently uploaded: <a href={fileUrl(existingValue)} target="_blank" rel="noopener noreferrer" className="underline">View file</a>
        </div>
      )}
      <input type="file" onChange={(e) => handleFileChange(e, field)} className={fileInputClass} accept=".pdf" />
      <p className="text-xs text-gray-500 mt-1">PDF (max 10MB)</p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow">
      <h2 className="text-xl font-bold text-[#006400]">{isEdit ? 'Edit Service Request' : title}</h2>

      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Applicant Type (የአመልካች አይነት)</h4>
        <label className="block text-sm font-medium mb-1">አመልካች ዓይነት ይምረጡ / Select Applicant Type *</label>
        <select
          value={legalPersonType}
          onChange={(e) => setLegalPersonType(e.target.value)}
          className={inputClass}
          style={{ maxWidth: '420px' }}
          required
        >
          <option value="">ይምረጡ / Select Applicant Type</option>
          <option value="personal">ግለሰብ / Personal</option>
          <option value="company">ድርጅት / Company</option>
        </select>
      </div>
      {legalPersonType === 'personal' && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">1. አመልካች ግለሰብ ከሆነ / If Legal Person is Personal</h4>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Select Classification Code (የምደባ ኮድ ይምረጡ) *</label>
            <select value={personalClassificationCode} onChange={(e) => applyClassification(setPersonalClassificationCode, e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]" required>
              <option value="">-- Select --</option>
              {CLASSIFICATION_CODES.map(item => (
                <option key={item.code} value={item.code}>{item.code} - {item.amharic}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Name /ስም (አማ) *</label><input name="applicant_name" value={formData.applicant_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Name /ስም (En) *</label><input name="applicant_name_en" value={formData.applicant_name_en} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Nationality (ዜግነት) *</label><input name="nationality" value={formData.nationality} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">የፋይዳ ተለዋጭ ቁጥር (FAN) National ID *</label><input name="fan_national_id" value={formData.fan_national_id} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Region (ክልል) *</label><input name="region" value={formData.region} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Sub City (ክ/ከተማ) *</label><input name="subcity" value={formData.subcity} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ) *</label><input name="woreda" value={formData.woreda} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.)</label><input name="hno" value={formData.hno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">P.O.Box (ፖ.ኦ.ቡክስ)</label><input name="pobox" value={formData.pobox} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Tel. No. (ስልክ ቁ.)</label><input name="telno" value={formData.telno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">E-Mail (ኢመይል)</label><input type="email" name="email" value={formData.email} onChange={handleChange} className={inputClass} /></div>
          </div>
        </div>
      )}
      {legalPersonType === 'company' && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">2. አመልካች ድርጅት ከሆነ / If Legal Person is Company</h4>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Select Classification Code (የምደባ ኮድ ይምረጡ) *</label>
            <select value={companyClassificationCode} onChange={(e) => applyClassification(setCompanyClassificationCode, e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]" required>
              <option value="">-- Select --</option>
              {CLASSIFICATION_CODES.map(item => (
                <option key={item.code} value={item.code}>{item.code} - {item.amharic}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Company Name (የኩባንያው ስም) *</label><input name="distributor_company_name" value={formData.distributor_company_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Name of Manager (የስራ አስኪካጅ ስም) *</label><input name="manager_name" value={formData.manager_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Nationality (ዜግነት) *</label><input name="manager_nationality" value={formData.manager_nationality} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Region (ክልል) *</label><input name="manager_region" value={formData.manager_region} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">K/Ketema (ክ/ከተማ)</label><input name="manager_ketema" value={formData.manager_ketema} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ)</label><input name="manager_woreda" value={formData.manager_woreda} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.)</label><input name="manager_hno" value={formData.manager_hno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">P.O.Box (ፖ.ኦ.ቡክስ)</label><input name="manager_pobox" value={formData.manager_pobox} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Fixed Phone No (ማዕከላዊ ስልክ ቁ.)</label><input name="manager_fixed_phone" value={formData.manager_fixed_phone} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Mobile Phone No (ሞባይል ስልክ ቁ.)</label><input name="manager_mobile" value={formData.manager_mobile} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">E-mail (ኢመይል)</label><input type="email" name="manager_email" value={formData.manager_email} onChange={handleChange} className={inputClass} /></div>
          </div>
        </div>
      )}
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">3. Type of Operation (የሥራው አይነት)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium mb-1">Type of Operation /የሥራው አይነት (አማ) *</label><input name="type_of_operation" value={formData.type_of_operation} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Type of Operation /የሥራው አይነት (En) *</label><input name="type_of_operation_en" value={formData.type_of_operation_en || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">ኮድ / Code</label><input name="operation_code" value={formData.operation_code} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">የማደያው አይነት / Types of station *</label><select name="station_type" value={formData.station_type} onChange={handleChange} className={inputClass} required><option value="">-- Select --</option><option value="አዲስ/New">አዲስ/New</option><option value="ነባር/Existing">ነባር/Existing</option></select></div>
        </div>
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">4. Site Location (የስራ ቦታ አድራሻ)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium mb-1">Trade Name /የንግድ ስም (አማ) *</label><input name="trade_name" value={formData.trade_name || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Trade Name /የንግድ ሥም (En) *</label><input name="trade_name_en" value={formData.trade_name_en || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Region /ክልል (አማ) *</label><input name="site_region" value={formData.site_region} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Region /ክልል (En) *</label><input name="site_region_en" value={formData.site_region_en} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Zone/City ዞን/ከተማ (አማ) *</label><input name="site_zone" value={formData.site_zone} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Zone/City ዞን/ከተማ (En) *</label><input name="site_zone_en" value={formData.site_zone_en} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Sub City ክ/ከተማ (አማ) *</label><input name="site_subcity" value={formData.site_subcity} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Sub City ክ/ከተማ (En) *</label><input name="site_subcity_en" value={formData.site_subcity_en} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ) (አማ) *</label><input name="site_woreda" value={formData.site_woreda} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ) (En) *</label><input name="site_woreda_en" value={formData.site_woreda_en} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.) (አማ)</label><input name="site_hno" value={formData.site_hno} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.) (En)</label><input name="site_hno_en" value={formData.site_hno_en} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">Phone No (ስልክ ቁ.)</label><input name="site_phone" value={formData.site_phone} onChange={handleChange} className={inputClass} /></div>
        </div>
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">የገንዘብ አቅም፣ የይዞታና ዲዛይን ንድፍ፣ የቦታ ስፋት / Financial Capacity, Site Plan & Design, Area of Land</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">የገንዘብ አቅም / Financial Capacity *</label>
            <input name="financial_capacity" value={formData.financial_capacity} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የቦታው ስፋት / Area of Land *</label>
            <input name="area_of_land" value={formData.area_of_land} onChange={handleChange} className={inputClass} required />
          </div>
          <div className="col-span-2">
            <div>
              <label className="block text-sm font-medium mb-1">የይዞታ ማረጋገጫና ዲዛይን ንድፍ / Site Plan and Design Drawing</label>
              {formData.existing_site_plan_drawing && (
                <div className="flex items-center gap-2 mb-2 text-sm text-green-500">
                  <FaFile /> Currently uploaded: <a href={fileUrl(formData.existing_site_plan_drawing)} target="_blank" rel="noopener noreferrer" className="underline">View file</a>
                </div>
              )}
              <input type="file" onChange={(e) => { const f = e.target.files[0]; if (f && f.size === 0) { toast.error('The selected file is empty. Please choose a valid PDF document.'); e.target.value = ''; return; } setFormData({ ...formData, site_plan_drawing: f }); }} className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400] bg-white" accept=".pdf" />
              <p className="text-xs text-gray-500 mt-1">PDF (max 10MB)</p>
            </div>
          </div>
        </div>
      </div>

      
      {title && title.includes('Construction Permit') && (
        <div>
          {/* Construction Permeation Fields */}
          <div>
            <label className="block text-sm font-medium mb-1">የግንባታው አገልግሎት የሚውለው / Construction service for</label>
            <input name="construction_service_for" value={formData.construction_service_for} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የካርታ ቁጥር / Plan no</label>
            <input name="plan_no" value={formData.plan_no} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የጂፒኤስ አድራሻ: (ሰሜን) / GPS Location: (North)</label>
            <input name="gps_north" value={formData.gps_north} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የጂፒኤስ አድራሻ: (ምስራቅ) / GPS Location: (East)</label>
            <input name="gps_east" value={formData.gps_east} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Scope of work station: Total project cost</label>
            <input name="total_project_cost" value={formData.total_project_cost} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Professional in charge construction</label>
            <input name="professional_in_charge" value={formData.professional_in_charge} onChange={handleChange} className={inputClass} />
          </div>
        </div>
      )}
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Attachments (ተያዥ መረጃዎች)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderFileInput('Application Letter (የማመልከቻ ደብዳቤ)', 'application_letter', formData.existing_application_letter)}
          {renderFileInput('Support Letter (የድጋፍ ደብዳቤ)', 'support_letter', formData.existing_support_letter)}
          {renderFileInput('Agreement Letter (የስምምነት ደብዳቤ)', 'aggrement_letter', formData.existing_aggrement_letter)}
          {title && title.includes('New Fuel Distributor Competency') && renderFileInput('የይዞታ ማረጋገጫና ዲዛይን ንድፍ / Site Plan and Design Drawing', 'site_plan_drawing', formData.existing_site_plan_drawing)}
        </div>
      </div>
      
      {title && title.includes('Construction Permit') && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">መያያዝ ያለባቸዉ መረጃዎች ዝርዝር / Attachments:</h4>
          <ul className="list-decimal pl-5 space-y-1 text-sm">
            <li>ፋይዳ መታወቂያ</li>
            <li>ፓስፖርት / I.D / Passport</li>
            <li>የባለቤትነት/የኪራይ ማረጋገጫ ውል/Investment /Rent Agreement</li>
            <li>የንግድ የምዝገባ የምስክር ወረቀት/Trade Registration Certificate</li>
            <li>የቦታ ካርታና ዲዛይን ንድፍ/Site Plan and Design Drawing</li>
            <li>የገንዘብ አቅም የባንክ መረጃ/Bank Statement for Financial Capacity</li>
            <li>ቢዝነስ ፕላን/የአዋጭነት ጥናት/ Business Plan / Feasibility Study</li>
            <li>የአካባቢ ተጽዕኖ ግምገማ ጥናት/Environmental Impact Assessment Study Report</li>
            <li>አመልካቹ ባለቤት ካልሆነ ህጋዊ ውክልና/Legal Representation (If applicant is not the owner)</li>
            <li>የማህበር መመስረቻ ደንብ/Article of Association (for PLC)</li>
            <li>የኢንሹራንስ ሽፋን/ Insurance Commitment letter</li>
            <li>የድርጅቱ የንግድ ልውውጥ መረጃ Company's Supply Records</li>
            <li>የሥራው እቅድ/Working program</li>
            <li>መሬቱ ለፔትሮሊየም ውጤቶች ግንባታ የሚሆን መሆኑን የሚገልፅ የአፈር ምርምር ጥናት/soil analysis study</li>
            <li>(የነዳጅ ማደያ መሣሪያዎች ተከላና ጥገና አገልግሎት) የሚያደርግ ባለሞያ ወይም ተቋም ከነዳጅና ኢነርጂ ባለስልጣን ብቃት የወሰደ መሆኑ የሚገልፅ ሰነድ</li>
          </ul>
        </div>
      )}
      <div className="mb-6">
        <label className="block text-sm font-medium mb-1">Remark (ማስታወሻ)</label>
        <textarea name="remark" value={formData.remark} onChange={handleChange} className={inputClass} rows="3" />
      </div>
      <div className="col-span-2">
        <label className="block text-sm font-medium mb-1">ያመለከቱበት ቀን / Request date</label>
        <EthiopianDatePicker name="request_date" value={formData.request_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Additional Attachments (ተያያዥ መረጃዎች)</h4>
        <button type="button" onClick={addAdditionalAttachment} className="text-[#006400] font-medium flex items-center gap-1 hover:underline">
          <FaPlus /> Add Attachment
        </button>
        {additionalAttachments.map((att, index) => (
          <div key={index} className="mt-3 p-3 border border-gray-200 rounded bg-gray-50">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Additional Attachment {index + 1}</span>
              <button type="button" onClick={() => removeAdditionalAttachment(index)} className="text-red-500 hover:text-red-700">
                <FaTrash />
              </button>
            </div>
            <input 
              type="text" 
              placeholder="Attachment name/description" 
              value={att.name} 
              onChange={(e) => handleAdditionalFileNameChange(e, index)} 
              className={inputClass} 
            />
            <input 
              type="file" 
              onChange={(e) => handleAdditionalFileChange(e, index)} 
              className="mt-2 text-sm" 
              accept=".pdf" 
            />
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2"><FaTimes /> Cancel</button>
        <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 flex items-center gap-2"><FaPaperPlane /> {isEdit ? 'Update Request' : 'Submit'}</button>
      </div>
    </form>
  );
}


function NewFuelImportForm({ onSubmit, onCancel, initialData = null, isEdit = false, title }) {
  useFormDraft(() => [formData, setFormData], initialData);
  const [formData, setFormData] = useState({
    applicant_name: '', applicant_name_en: '', nationality: '', fan_national_id: '',
        region: '', subcity: '', woreda: '', hno: '', pobox: '', telno: '', email: '',
        importer_company_name: '',
        manager_name: '', manager_nationality: '',
    manager_region: '', manager_ketema: '', manager_woreda: '', manager_hno: '', manager_pobox: '',
    manager_fixed_phone: '', manager_mobile: '', manager_email: '',
    type_of_operation: '', type_of_operation_en: '', operation_code: '', station_type: '',
    trade_name: '', trade_name_en: '',
    site_region: '', site_region_en: '', site_zone: '', site_zone_en: '', site_subcity: '', site_subcity_en: '', site_woreda: '', site_woreda_en: '', site_hno: '', site_hno_en: '', site_phone: '',
    classification_codes: [],
    financial_capacity: '',
    site_plan_drawing: null,
    area_of_land: '',

        remark: '',
    application_letter: null, support_letter: null, aggrement_letter: null,
    existing_application_letter: '', existing_support_letter: '', existing_aggrement_letter: '',
    existing_site_plan_drawing: '',
    request_date: toEthiopianDateStr(new Date())
  });
  const [legalPersonType, setLegalPersonType] = useState('');
  const [personalClassificationCode, setPersonalClassificationCode] = useState('');
  const [companyClassificationCode, setCompanyClassificationCode] = useState('');

  useEffect(() => {
    if (initialData) {
      setFormData({
        applicant_name: initialData.applicant_name || '', applicant_name_en: initialData.applicant_name_en || '', nationality: initialData.nationality || '', fan_national_id: initialData.fan_national_id || '',
        region: initialData.region || '', subcity: initialData.subcity || '', woreda: initialData.woreda || '',
        hno: initialData.hno || '', pobox: initialData.pobox || '', telno: initialData.telno || '', email: initialData.email || '',
        importer_company_name: initialData.importer_company_name || '',
        manager_name: initialData.manager_name || '', manager_nationality: initialData.manager_nationality || '',
        manager_region: initialData.manager_region || '',
        manager_ketema: initialData.manager_ketema || '', manager_woreda: initialData.manager_woreda || '',
        manager_hno: initialData.manager_hno || '', manager_pobox: initialData.manager_pobox || '',
        manager_fixed_phone: initialData.manager_fixed_phone || '', manager_mobile: initialData.manager_mobile || '',
        manager_email: initialData.manager_email || '',
        type_of_operation: initialData.type_of_operation || '', type_of_operation_en: initialData.type_of_operation_en || '', operation_code: initialData.operation_code || '', station_type: initialData.station_type || '',
        trade_name: initialData.trade_name || '', trade_name_en: initialData.trade_name_en || '',
        site_region: initialData.site_region || '', site_region_en: initialData.site_region_en || '', site_zone: initialData.site_zone || '', site_zone_en: initialData.site_zone_en || '',
        site_subcity: initialData.site_subcity || '', site_subcity_en: initialData.site_subcity_en || '', site_woreda: initialData.site_woreda || '', site_woreda_en: initialData.site_woreda_en || '',
        site_hno: initialData.site_hno || '', site_hno_en: initialData.site_hno_en || '', site_phone: initialData.site_phone || '',
        classification_codes: initialData.classification_codes ? JSON.parse(initialData.classification_codes) : [],
        financial_capacity: initialData.financial_capacity || '',
        site_plan_drawing: null,
        area_of_land: initialData.area_of_land || '',
        construction_service_for: initialData.construction_service_for || '',
        plan_no: initialData.plan_no || '',
        gps_north: initialData.gps_north || '',
        gps_east: initialData.gps_east || '',
        total_project_cost: initialData.total_project_cost || '',
        professional_in_charge: initialData.professional_in_charge || '',
        remark: initialData.remark || '',
        application_letter: null, support_letter: null, aggrement_letter: null,
        existing_application_letter: initialData.application_letter || '',
        existing_support_letter: initialData.support_letter || '',
        existing_aggrement_letter: initialData.aggrement_letter || '',
        existing_site_plan_drawing: initialData.site_plan_drawing || '',
        request_date: initialData.request_date ? convertDbDateToDisplay(initialData.request_date) : toEthiopianDateStr(new Date())
      });
      if (initialData.importer_company_name) setLegalPersonType('company'); else setLegalPersonType('personal');
      if (initialData.classification_codes) {
        const firstCode = (JSON.parse(initialData.classification_codes) || [])[0] || '';
        if (initialData.importer_company_name) setCompanyClassificationCode(firstCode);
        else setPersonalClassificationCode(firstCode);
      }
    }
  }, [initialData]);

  const handleChange = (e) => { setFormData({ ...formData, [e.target.name]: e.target.value }); };
  const handleFileChange = (e, field) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    setFormData({ ...formData, [field]: selectedFile });
  };

  const applyClassification = (setter, code) => {
    const item = CLASSIFICATION_CODES.find(c => c.code === code);
    setter(code);
    setFormData(prev => ({
      ...prev,
      classification_codes: code ? [code] : [],
      type_of_operation: item ? item.amharic : '',
      type_of_operation_en: item ? item.english : '',
      operation_code: code || ''
    }));
  };

  const [additionalAttachments, setAdditionalAttachments] = useState([]);

  const handleAdditionalFileChange = (e, index) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    const newAttachments = [...additionalAttachments];
    newAttachments[index].file = selectedFile;
    setAdditionalAttachments(newAttachments);
  };

  const handleAdditionalFileNameChange = (e, index) => {
    const newAttachments = [...additionalAttachments];
    newAttachments[index].name = e.target.value;
    setAdditionalAttachments(newAttachments);
  };

  const addAdditionalAttachment = () => {
    if (additionalAttachments.length >= 10) {
      toast.warn('You can only add up to 10 attachments');
      return;
    }
    setAdditionalAttachments([...additionalAttachments, { name: '', file: null }]);
  };

  const removeAdditionalAttachment = (index) => {
    const newAttachments = additionalAttachments.filter((_, i) => i !== index);
    setAdditionalAttachments(newAttachments);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    Object.keys(formData).forEach(key => {
      if (formData[key] !== null && key !== 'existing_application_letter' && key !== 'existing_support_letter' && key !== 'existing_aggrement_letter' && key !== 'classification_codes' && key !== 'request_date') {
        if (formData[key] instanceof File) { if (formData[key]) formDataToSend.append(key, formData[key]); }
        else { formDataToSend.append(key, formData[key]); }
      }
    });
    if (isEdit && initialData) {
      formDataToSend.append('existing_application_letter', formData.existing_application_letter);
      formDataToSend.append('existing_support_letter', formData.existing_support_letter);
      formDataToSend.append('existing_aggrement_letter', formData.existing_aggrement_letter);
      formDataToSend.append('existing_site_plan_drawing', formData.existing_site_plan_drawing);
      formDataToSend.append('status', initialData.status || 'pending');
      formDataToSend.append('response_reason', initialData.response_reason || '');
      formDataToSend.append('rejection_reason', initialData.rejection_reason || '');
    }
    formDataToSend.append('classification_codes', JSON.stringify(formData.classification_codes));
    formDataToSend.append('request_date', ethDateStrToGregStr(formData.request_date));
    additionalAttachments.forEach((att, index) => {
      if (att.file) {
        formDataToSend.append(`additional_attachment_${index}_name`, att.name || '');
        formDataToSend.append(`additional_attachment_${index}`, att.file);
      }
    });
    formDataToSend.append('additional_attachments_count', additionalAttachments.filter(a => a.file).length);
    onSubmit(formDataToSend, isEdit ? initialData.id : null);
  };

  const inputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]";
  const fileInputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400] bg-white";

  const renderFileInput = (label, field, existingValue) => (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {existingValue && (
        <div className="flex items-center gap-2 mb-2 text-sm text-green-500">
          <FaFile /> Currently uploaded: <a href={fileUrl(existingValue)} target="_blank" rel="noopener noreferrer" className="underline">View file</a>
        </div>
      )}
      <input type="file" onChange={(e) => handleFileChange(e, field)} className={fileInputClass} accept=".pdf" />
      <p className="text-xs text-gray-500 mt-1">PDF (max 10MB)</p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow">
      <h2 className="text-xl font-bold text-[#006400]">{isEdit ? 'Edit Service Request' : title}</h2>

      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Applicant Type (የአመልካች አይነት)</h4>
        <label className="block text-sm font-medium mb-1">አመልካች ዓይነት ይምረጡ / Select Applicant Type *</label>
        <select
          value={legalPersonType}
          onChange={(e) => setLegalPersonType(e.target.value)}
          className={inputClass}
          style={{ maxWidth: '420px' }}
          required
        >
          <option value="">ይምረጡ / Select Applicant Type</option>
          <option value="personal">ግለሰብ / Personal</option>
          <option value="company">ድርጅት / Company</option>
        </select>
      </div>
      {legalPersonType === 'personal' && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">1. አመልካች ግለሰብ ከሆነ / If Legal Person is Personal</h4>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Select Classification Code (የምደባ ኮድ ይምረጡ) *</label>
            <select value={personalClassificationCode} onChange={(e) => applyClassification(setPersonalClassificationCode, e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]" required>
              <option value="">-- Select --</option>
              {CLASSIFICATION_CODES.map(item => (
                <option key={item.code} value={item.code}>{item.code} - {item.amharic}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Name /ስም (አማ) *</label><input name="applicant_name" value={formData.applicant_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Name /ስም (En) *</label><input name="applicant_name_en" value={formData.applicant_name_en} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Nationality (ዜግነት) *</label><input name="nationality" value={formData.nationality} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">የፋይዳ ተለዋጭ ቁጥር (FAN) National ID *</label><input name="fan_national_id" value={formData.fan_national_id} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Region (ክልል) *</label><input name="region" value={formData.region} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Sub City (ክ/ከተማ) *</label><input name="subcity" value={formData.subcity} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ) *</label><input name="woreda" value={formData.woreda} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.)</label><input name="hno" value={formData.hno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">P.O.Box (ፖ.ኦ.ቡክስ)</label><input name="pobox" value={formData.pobox} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Tel. No. (ስልክ ቁ.)</label><input name="telno" value={formData.telno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">E-Mail (ኢመይል)</label><input type="email" name="email" value={formData.email} onChange={handleChange} className={inputClass} /></div>
          </div>
        </div>
      )}
      {legalPersonType === 'company' && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">2. አመልካች ድርጅት ከሆነ / If Legal Person is Company</h4>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Select Classification Code (የምደባ ኮድ ይምረጡ) *</label>
            <select value={companyClassificationCode} onChange={(e) => applyClassification(setCompanyClassificationCode, e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]" required>
              <option value="">-- Select --</option>
              {CLASSIFICATION_CODES.map(item => (
                <option key={item.code} value={item.code}>{item.code} - {item.amharic}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Company Name (የኩባንያው ስም) *</label><input name="importer_company_name" value={formData.importer_company_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Name of Manager (የስራ አስኪካጅ ስም) *</label><input name="manager_name" value={formData.manager_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Nationality (ዜግነት) *</label><input name="manager_nationality" value={formData.manager_nationality} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Region (ክልል) *</label><input name="manager_region" value={formData.manager_region} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">K/Ketema (ክ/ከተማ)</label><input name="manager_ketema" value={formData.manager_ketema} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ)</label><input name="manager_woreda" value={formData.manager_woreda} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.)</label><input name="manager_hno" value={formData.manager_hno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">P.O.Box (ፖ.ኦ.ቡክስ)</label><input name="manager_pobox" value={formData.manager_pobox} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Fixed Phone No (ማዕከላዊ ስልክ ቁ.)</label><input name="manager_fixed_phone" value={formData.manager_fixed_phone} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Mobile Phone No (ሞባይል ስልክ ቁ.)</label><input name="manager_mobile" value={formData.manager_mobile} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">E-mail (ኢመይል)</label><input type="email" name="manager_email" value={formData.manager_email} onChange={handleChange} className={inputClass} /></div>
          </div>
        </div>
      )}
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">3. Type of Operation (የሥራው አይነት)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium mb-1">Type of Operation /የሥራው አይነት (አማ) *</label><input name="type_of_operation" value={formData.type_of_operation} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Type of Operation /የሥራው አይነት (En) *</label><input name="type_of_operation_en" value={formData.type_of_operation_en || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">ኮድ / Code</label><input name="operation_code" value={formData.operation_code} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">የማደያው አይነት / Types of station *</label><select name="station_type" value={formData.station_type} onChange={handleChange} className={inputClass} required><option value="">-- Select --</option><option value="አዲስ/New">አዲስ/New</option><option value="ነባር/Existing">ነባር/Existing</option></select></div>
        </div>
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">4. Site Location (የስራ ቦታ አድራሻ)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium mb-1">Trade Name /የንግድ ስም (አማ) *</label><input name="trade_name" value={formData.trade_name || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Trade Name /የንግድ ሥም (En) *</label><input name="trade_name_en" value={formData.trade_name_en || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Type of Operation /የሥራው አይነት (አማ) *</label><input name="type_of_operation" value={formData.type_of_operation} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Type of Operation /የሥራው አይነት (En) *</label><input name="type_of_operation_en" value={formData.type_of_operation_en || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">ኮድ / Code</label><input name="operation_code" value={formData.operation_code} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">የማደያው አይነት / Types of station *</label><select name="station_type" value={formData.station_type} onChange={handleChange} className={inputClass} required><option value="">-- Select --</option><option value="አዲስ/New">አዲስ/New</option><option value="ነባር/Existing">ነባር/Existing</option></select></div>
        </div>
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">4. Site Location (የስራ ቦታ አድራሻ)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium mb-1">Region /ክልል (አማ) *</label><input name="site_region" value={formData.site_region} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Region /ክልል (En) *</label><input name="site_region_en" value={formData.site_region_en} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Zone/City ዞን/ከተማ (አማ) *</label><input name="site_zone" value={formData.site_zone} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Zone/City ዞን/ከተማ (En) *</label><input name="site_zone_en" value={formData.site_zone_en} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Sub City ክ/ከተማ (አማ) *</label><input name="site_subcity" value={formData.site_subcity} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Sub City ክ/ከተማ (En) *</label><input name="site_subcity_en" value={formData.site_subcity_en} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ) (አማ) *</label><input name="site_woreda" value={formData.site_woreda} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ) (En) *</label><input name="site_woreda_en" value={formData.site_woreda_en} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.) (አማ)</label><input name="site_hno" value={formData.site_hno} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.) (En)</label><input name="site_hno_en" value={formData.site_hno_en} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">Phone No (ስልክ ቁ.)</label><input name="site_phone" value={formData.site_phone} onChange={handleChange} className={inputClass} /></div>
        </div>
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">የገንዘብ አቅም፣ የይዞታና ዲዛይን ንድፍ፣ የቦታ ስፋት / Financial Capacity, Site Plan & Design, Area of Land</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">የገንዘብ አቅም / Financial Capacity *</label>
            <input name="financial_capacity" value={formData.financial_capacity} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የቦታው ስፋት / Area of Land *</label>
            <input name="area_of_land" value={formData.area_of_land} onChange={handleChange} className={inputClass} required />
          </div>
          <div className="col-span-2">
            <div>
              <label className="block text-sm font-medium mb-1">የይዞታ ማረጋገጫና ዲዛይን ንድፍ / Site Plan and Design Drawing</label>
              {formData.existing_site_plan_drawing && (
                <div className="flex items-center gap-2 mb-2 text-sm text-green-500">
                  <FaFile /> Currently uploaded: <a href={fileUrl(formData.existing_site_plan_drawing)} target="_blank" rel="noopener noreferrer" className="underline">View file</a>
                </div>
              )}
              <input type="file" onChange={(e) => { const f = e.target.files[0]; if (f && f.size === 0) { toast.error('The selected file is empty. Please choose a valid PDF document.'); e.target.value = ''; return; } setFormData({ ...formData, site_plan_drawing: f }); }} className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400] bg-white" accept=".pdf" />
              <p className="text-xs text-gray-500 mt-1">PDF (max 10MB)</p>
            </div>
          </div>
        </div>
      </div>

      
      {title && title.includes('Construction Permit') && (
        <div>
          {/* Construction Permeation Fields */}
          <div>
            <label className="block text-sm font-medium mb-1">የግንባታው አገልግሎት የሚውለው / Construction service for</label>
            <input name="construction_service_for" value={formData.construction_service_for} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የካርታ ቁጥር / Plan no</label>
            <input name="plan_no" value={formData.plan_no} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የጂፒኤስ አድራሻ: (ሰሜን) / GPS Location: (North)</label>
            <input name="gps_north" value={formData.gps_north} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የጂፒኤስ አድራሻ: (ምስራቅ) / GPS Location: (East)</label>
            <input name="gps_east" value={formData.gps_east} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Scope of work station: Total project cost</label>
            <input name="total_project_cost" value={formData.total_project_cost} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Professional in charge construction</label>
            <input name="professional_in_charge" value={formData.professional_in_charge} onChange={handleChange} className={inputClass} />
          </div>
        </div>
      )}
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Attachments (ተያዥ መረጃዎች)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderFileInput('Application Letter (የማመልከቻ ደብዳቤ)', 'application_letter', formData.existing_application_letter)}
          {renderFileInput('Support Letter (የድጋፍ ደብዳቤ)', 'support_letter', formData.existing_support_letter)}
          {renderFileInput('Agreement Letter (የስምምነት ደብዳቤ)', 'aggrement_letter', formData.existing_aggrement_letter)}
        </div>
      </div>
      
      {title && title.includes('Construction Permit') && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">መያያዝ ያለባቸዉ መረጃዎች ዝርዝር / Attachments:</h4>
          <ul className="list-decimal pl-5 space-y-1 text-sm">
            <li>ፋይዳ መታወቂያ</li>
            <li>ፓስፖርት / I.D / Passport</li>
            <li>የባለቤትነት/የኪራይ ማረጋገጫ ውል/Investment /Rent Agreement</li>
            <li>የንግድ የምዝገባ የምስክር ወረቀት/Trade Registration Certificate</li>
            <li>የቦታ ካርታና ዲዛይን ንድፍ/Site Plan and Design Drawing</li>
            <li>የገንዘብ አቅም የባንክ መረጃ/Bank Statement for Financial Capacity</li>
            <li>ቢዝነስ ፕላን/የአዋጭነት ጥናት/ Business Plan / Feasibility Study</li>
            <li>የአካባቢ ተጽዕኖ ግምገማ ጥናት/Environmental Impact Assessment Study Report</li>
            <li>አመልካቹ ባለቤት ካልሆነ ህጋዊ ውክልና/Legal Representation (If applicant is not the owner)</li>
            <li>የማህበር መመስረቻ ደንብ/Article of Association (for PLC)</li>
            <li>የኢንሹራንስ ሽፋን/ Insurance Commitment letter</li>
            <li>የድርጅቱ የንግድ ልውውጥ መረጃ Company's Supply Records</li>
            <li>የሥራው እቅድ/Working program</li>
            <li>መሬቱ ለፔትሮሊየም ውጤቶች ግንባታ የሚሆን መሆኑን የሚገልፅ የአፈር ምርምር ጥናት/soil analysis study</li>
            <li>(የነዳጅ ማደያ መሣሪያዎች ተከላና ጥገና አገልግሎት) የሚያደርግ ባለሞያ ወይም ተቋም ከነዳጅና ኢነርጂ ባለስልጣን ብቃት የወሰደ መሆኑ የሚገልፅ ሰነድ</li>
          </ul>
        </div>
      )}
      <div className="mb-6">
        <label className="block text-sm font-medium mb-1">Remark (ማስታወሻ)</label>
        <textarea name="remark" value={formData.remark} onChange={handleChange} className={inputClass} rows="3" />
      </div>
      <div className="col-span-2">
        <label className="block text-sm font-medium mb-1">ያመለከቱበት ቀን / Request date</label>
        <EthiopianDatePicker name="request_date" value={formData.request_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Additional Attachments (ተያያዥ መረጃዎች)</h4>
        <button type="button" onClick={addAdditionalAttachment} className="text-[#006400] font-medium flex items-center gap-1 hover:underline">
          <FaPlus /> Add Attachment
        </button>
        {additionalAttachments.map((att, index) => (
          <div key={index} className="mt-3 p-3 border border-gray-200 rounded bg-gray-50">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Additional Attachment {index + 1}</span>
              <button type="button" onClick={() => removeAdditionalAttachment(index)} className="text-red-500 hover:text-red-700">
                <FaTrash />
              </button>
            </div>
            <input 
              type="text" 
              placeholder="Attachment name/description" 
              value={att.name} 
              onChange={(e) => handleAdditionalFileNameChange(e, index)} 
              className={inputClass} 
            />
            <input 
              type="file" 
              onChange={(e) => handleAdditionalFileChange(e, index)} 
              className="mt-2 text-sm" 
              accept=".pdf" 
            />
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2"><FaTimes /> Cancel</button>
        <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 flex items-center gap-2"><FaPaperPlane /> {isEdit ? 'Update Request' : 'Submit'}</button>
      </div>
    </form>
  );
}

function NewOilExporterForm({ onSubmit, onCancel, initialData = null, isEdit = false, title }) {
  useFormDraft(() => [formData, setFormData], initialData);
  const [formData, setFormData] = useState({
    applicant_name: '', nationality: '', fan_national_id: '',
    region: '', subcity: '', woreda: '', hno: '', pobox: '', telno: '', email: '',
    exporter_company_name: '',
    manager_name: '', manager_nationality: '',
    manager_region: '', manager_ketema: '', manager_woreda: '', manager_hno: '', manager_pobox: '',
    manager_fixed_phone: '', manager_mobile: '', manager_email: '',
    type_of_operation: '', type_of_operation_en: '', operation_code: '', station_type: '',
    trade_name: '', trade_name_en: '',
    site_region: '', site_region_en: '', site_zone: '', site_zone_en: '', site_subcity: '', site_subcity_en: '', site_woreda: '', site_woreda_en: '', site_hno: '', site_hno_en: '', site_phone: '',
    financial_capacity: '',
    classification_codes: [],
    site_plan_drawing: null,
    area_of_land: '',
    applicant_name_en: '',
    remark: '',
    request_date: toEthiopianDateStr(new Date()),
    application_letter: null, support_letter: null, aggrement_letter: null,
    existing_application_letter: '', existing_support_letter: '', existing_aggrement_letter: '',
    existing_site_plan_drawing: ''
  });
  const [legalPersonType, setLegalPersonType] = useState('');
  const [personalClassificationCode, setPersonalClassificationCode] = useState('');
  const [companyClassificationCode, setCompanyClassificationCode] = useState('');

  useEffect(() => {
    if (initialData) {
      setFormData({
        applicant_name: initialData.applicant_name || '', nationality: initialData.nationality || '', fan_national_id: initialData.fan_national_id || '',
        region: initialData.region || '', subcity: initialData.subcity || '', woreda: initialData.woreda || '',
        hno: initialData.hno || '', pobox: initialData.pobox || '', telno: initialData.telno || '', email: initialData.email || '',
        exporter_company_name: initialData.exporter_company_name || '',
        manager_name: initialData.manager_name || '', manager_nationality: initialData.manager_nationality || '',
        manager_region: initialData.manager_region || '',
        manager_ketema: initialData.manager_ketema || '', manager_woreda: initialData.manager_woreda || '',
        manager_hno: initialData.manager_hno || '', manager_pobox: initialData.manager_pobox || '',
        manager_fixed_phone: initialData.manager_fixed_phone || '', manager_mobile: initialData.manager_mobile || '',
        manager_email: initialData.manager_email || '',
        type_of_operation: initialData.type_of_operation || '', type_of_operation_en: initialData.type_of_operation_en || '', operation_code: initialData.operation_code || '', station_type: initialData.station_type || '',
        trade_name: initialData.trade_name || '', trade_name_en: initialData.trade_name_en || '',
        site_region: initialData.site_region || '', site_region_en: initialData.site_region_en || '', site_zone: initialData.site_zone || '', site_zone_en: initialData.site_zone_en || '',
        site_subcity: initialData.site_subcity || '', site_subcity_en: initialData.site_subcity_en || '', site_woreda: initialData.site_woreda || '', site_woreda_en: initialData.site_woreda_en || '',
        site_hno: initialData.site_hno || '', site_hno_en: initialData.site_hno_en || '', site_phone: initialData.site_phone || '',
        classification_codes: initialData.classification_codes ? JSON.parse(initialData.classification_codes) : [],
        financial_capacity: initialData.financial_capacity || '',
        site_plan_drawing: null,
        area_of_land: initialData.area_of_land || '',
        applicant_name_en: initialData.applicant_name_en || '',
        construction_service_for: initialData.construction_service_for || '',
        plan_no: initialData.plan_no || '',
        gps_north: initialData.gps_north || '',
        gps_east: initialData.gps_east || '',
        total_project_cost: initialData.total_project_cost || '',
        professional_in_charge: initialData.professional_in_charge || '',
        remark: initialData.remark || '',
        application_letter: null, support_letter: null, aggrement_letter: null,
        existing_application_letter: initialData.application_letter || '',
        existing_support_letter: initialData.support_letter || '',
        existing_aggrement_letter: initialData.aggrement_letter || '',
        existing_site_plan_drawing: initialData.site_plan_drawing || '',
        request_date: initialData.request_date ? convertDbDateToDisplay(initialData.request_date) : toEthiopianDateStr(new Date())
      });
      if (initialData.exporter_company_name) setLegalPersonType('company'); else setLegalPersonType('personal');
      if (initialData.classification_codes) {
        const firstCode = (JSON.parse(initialData.classification_codes) || [])[0] || '';
        if (initialData.exporter_company_name) setCompanyClassificationCode(firstCode);
        else setPersonalClassificationCode(firstCode);
      }
    }
  }, [initialData]);

  const handleChange = (e) => { setFormData({ ...formData, [e.target.name]: e.target.value }); };
  const handleFileChange = (e, field) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    setFormData({ ...formData, [field]: selectedFile });
  };

  const applyClassification = (setter, code) => {
    const item = CLASSIFICATION_CODES.find(c => c.code === code);
    setter(code);
    setFormData(prev => ({
      ...prev,
      classification_codes: code ? [code] : [],
      type_of_operation: item ? item.amharic : '',
      type_of_operation_en: item ? item.english : '',
      operation_code: code || ''
    }));
  };

  const [additionalAttachments, setAdditionalAttachments] = useState([]);

  const handleAdditionalFileChange = (e, index) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    const newAttachments = [...additionalAttachments];
    newAttachments[index].file = selectedFile;
    setAdditionalAttachments(newAttachments);
  };

  const handleAdditionalFileNameChange = (e, index) => {
    const newAttachments = [...additionalAttachments];
    newAttachments[index].name = e.target.value;
    setAdditionalAttachments(newAttachments);
  };

  const addAdditionalAttachment = () => {
    if (additionalAttachments.length >= 10) {
      toast.warn('You can only add up to 10 attachments');
      return;
    }
    setAdditionalAttachments([...additionalAttachments, { name: '', file: null }]);
  };

  const removeAdditionalAttachment = (index) => {
    const newAttachments = additionalAttachments.filter((_, i) => i !== index);
    setAdditionalAttachments(newAttachments);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    Object.keys(formData).forEach(key => {
      if (formData[key] !== null && key !== 'existing_application_letter' && key !== 'existing_support_letter' && key !== 'existing_aggrement_letter' && key !== 'classification_codes' && key !== 'request_date') {
        if (formData[key] instanceof File) { if (formData[key]) formDataToSend.append(key, formData[key]); }
        else { formDataToSend.append(key, formData[key]); }
      }
    });
    if (isEdit && initialData) {
      formDataToSend.append('existing_application_letter', formData.existing_application_letter);
      formDataToSend.append('existing_support_letter', formData.existing_support_letter);
      formDataToSend.append('existing_aggrement_letter', formData.existing_aggrement_letter);
      formDataToSend.append('existing_site_plan_drawing', formData.existing_site_plan_drawing);
      formDataToSend.append('status', initialData.status || 'pending');
      formDataToSend.append('response_reason', initialData.response_reason || '');
      formDataToSend.append('rejection_reason', initialData.rejection_reason || '');
    }
    formDataToSend.append('classification_codes', JSON.stringify(formData.classification_codes));
    formDataToSend.append('request_date', ethDateStrToGregStr(formData.request_date));
    additionalAttachments.forEach((att, index) => {
      if (att.file) {
        formDataToSend.append(`additional_attachment_${index}_name`, att.name || '');
        formDataToSend.append(`additional_attachment_${index}`, att.file);
      }
    });
    formDataToSend.append('additional_attachments_count', additionalAttachments.filter(a => a.file).length);
    onSubmit(formDataToSend, isEdit ? initialData.id : null);
  };

  const inputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]";
  const fileInputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400] bg-white";

  const renderFileInput = (label, field, existingValue) => (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {existingValue && (
        <div className="flex items-center gap-2 mb-2 text-sm text-green-500">
          <FaFile /> Currently uploaded: <a href={fileUrl(existingValue)} target="_blank" rel="noopener noreferrer" className="underline">View file</a>
        </div>
      )}
      <input type="file" onChange={(e) => handleFileChange(e, field)} className={fileInputClass} accept=".pdf" />
      <p className="text-xs text-gray-500 mt-1">PDF (max 10MB)</p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow">
      <h2 className="text-xl font-bold text-[#006400]">{isEdit ? 'Edit Service Request' : title}</h2>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Applicant Type (የአመልካች አይነት)</h4>
        <label className="block text-sm font-medium mb-1">አመልካች ዓይነት ይምረጡ / Select Applicant Type *</label>
        <select
          value={legalPersonType}
          onChange={(e) => setLegalPersonType(e.target.value)}
          className={inputClass}
          style={{ maxWidth: '420px' }}
          required
        >
          <option value="">ይምረጡ / Select Applicant Type</option>
          <option value="personal">ግለሰብ / Personal</option>
          <option value="company">ድርጅት / Company</option>
        </select>
      </div>
      {legalPersonType === 'personal' && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">1. አመልካች ግለሰብ ከሆነ / If Legal Person is Personal</h4>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Select Classification Code (የምደባ ኮድ ይምረጡ) *</label>
            <select value={personalClassificationCode} onChange={(e) => applyClassification(setPersonalClassificationCode, e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]" required>
              <option value="">-- Select --</option>
              {CLASSIFICATION_CODES.map(item => (
                <option key={item.code} value={item.code}>{item.code} - {item.amharic}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Name (አማ) *</label><input name="applicant_name" value={formData.applicant_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Name (En)</label><input name="applicant_name_en" value={formData.applicant_name_en} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Nationality (ዜግነት) *</label><input name="nationality" value={formData.nationality} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">የፋይዳ ተለዋጭ ቁጥር (FAN) National ID *</label><input name="fan_national_id" value={formData.fan_national_id} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Region (ክልል) *</label><input name="region" value={formData.region} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Sub City (ክ/ከተማ) *</label><input name="subcity" value={formData.subcity} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ) *</label><input name="woreda" value={formData.woreda} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.)</label><input name="hno" value={formData.hno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">P.O.Box (ፖ.ኦ.ቡክስ)</label><input name="pobox" value={formData.pobox} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Tel. No. (ስልክ ቁ.)</label><input name="telno" value={formData.telno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">E-Mail (ኢመይል)</label><input type="email" name="email" value={formData.email} onChange={handleChange} className={inputClass} /></div>
          </div>
        </div>
      )}
      {legalPersonType === 'company' && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">2. አመልካች ድርጅት ከሆነ / If Legal Person is Company</h4>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Select Classification Code (የምደባ ኮድ ይምረጡ) *</label>
            <select value={companyClassificationCode} onChange={(e) => applyClassification(setCompanyClassificationCode, e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]" required>
              <option value="">-- Select --</option>
              {CLASSIFICATION_CODES.map(item => (
                <option key={item.code} value={item.code}>{item.code} - {item.amharic}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Company Name (የኩባንያው ስም) *</label><input name="exporter_company_name" value={formData.exporter_company_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Name of Manager (የስራ አስኪካጅ ስም) *</label><input name="manager_name" value={formData.manager_name} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">የፋይዳ ተለዋጭ ቁጥር (FAN) National ID *</label><input name="fan_national_id" value={formData.fan_national_id} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Nationality (ዜግነት) *</label><input name="manager_nationality" value={formData.manager_nationality} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">Region (ክልል) *</label><input name="manager_region" value={formData.manager_region} onChange={handleChange} className={inputClass} required /></div>
            <div><label className="block text-sm font-medium mb-1">K/Ketema (ክ/ከተማ)</label><input name="manager_ketema" value={formData.manager_ketema} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ)</label><input name="manager_woreda" value={formData.manager_woreda} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.)</label><input name="manager_hno" value={formData.manager_hno} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">P.O.Box (ፖ.ኦ.ቡክስ)</label><input name="manager_pobox" value={formData.manager_pobox} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Fixed Phone No (ማዕከላዊ ስልክ ቁ.)</label><input name="manager_fixed_phone" value={formData.manager_fixed_phone} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">Mobile Phone No (ሞባይል ስልክ ቁ.)</label><input name="manager_mobile" value={formData.manager_mobile} onChange={handleChange} className={inputClass} /></div>
            <div><label className="block text-sm font-medium mb-1">E-mail (ኢመይል)</label><input type="email" name="manager_email" value={formData.manager_email} onChange={handleChange} className={inputClass} /></div>
          </div>
        </div>
      )}
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">3. Type of Operation (የሥራው አይነት)</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><label className="block text-sm font-medium mb-1">Type of Operation /የሥራው አይነት (አማ) *</label><input name="type_of_operation" value={formData.type_of_operation} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Type of Operation /የሥራው አይነት (En) *</label><input name="type_of_operation_en" value={formData.type_of_operation_en || ''} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">ኮድ / Code</label><input name="operation_code" value={formData.operation_code} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">የማደያው አይነት / Types of station *</label><select name="station_type" value={formData.station_type} onChange={handleChange} className={inputClass} required><option value="">-- Select --</option><option value="አዲስ/New">አዲስ/New</option><option value="ነባር/Existing">ነባር/Existing</option></select></div>
        </div>
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">4. Site Location (የስራ ቦታ አድራሻ)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium mb-1">Trade Name (የንግድ ስም) (አማ) *</label><input name="trade_name" value={formData.trade_name} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Trade Name (የንግድ ስም) (En) *</label><input name="trade_name_en" value={formData.trade_name_en} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Region /ክልል (አማ) *</label><input name="site_region" value={formData.site_region} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Region /ክልል (En) *</label><input name="site_region_en" value={formData.site_region_en} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Zone/City ዞን/ከተማ (አማ) *</label><input name="site_zone" value={formData.site_zone} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Zone/City ዞን/ከተማ (En) *</label><input name="site_zone_en" value={formData.site_zone_en} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Sub City ክ/ከተማ (አማ) *</label><input name="site_subcity" value={formData.site_subcity} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Sub City ክ/ከተማ (En) *</label><input name="site_subcity_en" value={formData.site_subcity_en} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ) (አማ) *</label><input name="site_woreda" value={formData.site_woreda} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">Woreda/Kebele (ወረዳ/ቀበሌ) (En) *</label><input name="site_woreda_en" value={formData.site_woreda_en} onChange={handleChange} className={inputClass} required /></div>
          <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.) (አማ)</label><input name="site_hno" value={formData.site_hno} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">H.No (የቤት ቁ.) (En)</label><input name="site_hno_en" value={formData.site_hno_en} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">Phone No (ስልክ ቁ.)</label><input name="site_phone" value={formData.site_phone} onChange={handleChange} className={inputClass} /></div>
        </div>
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">የገንዘብ አቅም፣ የይዞታና ዲዛይን ንድፍ፣ የቦታ ስፋት / Financial Capacity, Site Plan & Design, Area of Land</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">የገንዘብ አቅም / Financial Capacity *</label>
            <input name="financial_capacity" value={formData.financial_capacity} onChange={handleChange} className={inputClass} required />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">የቦታው ስፋት / Area of Land *</label>
            <input name="area_of_land" value={formData.area_of_land} onChange={handleChange} className={inputClass} required />
          </div>
          <div className="col-span-2">
            <div>
              <label className="block text-sm font-medium mb-1">የይዞታ ማረጋገጫና ዲዛይን ንድፍ / Site Plan and Design Drawing</label>
              {formData.existing_site_plan_drawing && (
                <div className="flex items-center gap-2 mb-2 text-sm text-green-500">
                  <FaFile /> Currently uploaded: <a href={fileUrl(formData.existing_site_plan_drawing)} target="_blank" rel="noopener noreferrer" className="underline">View file</a>
                </div>
              )}
              <input type="file" onChange={(e) => { const f = e.target.files[0]; if (f && f.size === 0) { toast.error('The selected file is empty. Please choose a valid PDF document.'); e.target.value = ''; return; } setFormData({ ...formData, site_plan_drawing: f }); }} className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400] bg-white" accept=".pdf" />
              <p className="text-xs text-gray-500 mt-1">PDF (max 10MB)</p>
            </div>
          </div>
        </div>
      </div>
      {title && title.includes('Construction Permit') && (
        <div>
          <div><label className="block text-sm font-medium mb-1">የግንባታው አገልግሎት የሚውለው / Construction service for</label><input name="construction_service_for" value={formData.construction_service_for} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">የካርታ ቁጥር / Plan no</label><input name="plan_no" value={formData.plan_no} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">የጂፒኤስ አድራሻ: (ሰሜን) / GPS Location: (North)</label><input name="gps_north" value={formData.gps_north} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">የጂፒኤስ አድራሻ: (ምስራቅ) / GPS Location: (East)</label><input name="gps_east" value={formData.gps_east} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">Scope of work station: Total project cost</label><input name="total_project_cost" value={formData.total_project_cost} onChange={handleChange} className={inputClass} /></div>
          <div><label className="block text-sm font-medium mb-1">Professional in charge construction</label><input name="professional_in_charge" value={formData.professional_in_charge} onChange={handleChange} className={inputClass} /></div>
        </div>
      )}
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Attachments (ተያዥ መረጃዎች)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderFileInput('Application Letter (የማመልከቻ ደብዳቤ)', 'application_letter', formData.existing_application_letter)}
          {renderFileInput('Support Letter (የድጋፍ ደብዳቤ)', 'support_letter', formData.existing_support_letter)}
          {renderFileInput('Agreement Letter (የስምምነት ደብዳቤ)', 'aggrement_letter', formData.existing_aggrement_letter)}
        </div>
      </div>
      {title && title.includes('Construction Permit') && (
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">መያያዝ ያለባቸዉ መረጃዎች ዝርዝር / Attachments:</h4>
          <ul className="list-decimal pl-5 space-y-1 text-sm">
            <li>ፋይዳ መታወቂያ</li>
            <li>ፓስፖርት / I.D / Passport</li>
            <li>የባለቤትነት/የኪራይ ማረጋገጫ ውል/Investment /Rent Agreement</li>
            <li>የንግድ የምዝገባ የምስክር ወረቀት/Trade Registration Certificate</li>
            <li>የቦታ ካርታና ዲዛይን ንድፍ/Site Plan and Design Drawing</li>
            <li>የገንዘብ አቅም የባንክ መረጃ/Bank Statement for Financial Capacity</li>
            <li>ቢዝነስ ፕላን/የአዋጭነት ጥናት/ Business Plan / Feasibility Study</li>
            <li>የአካባቢ ተጽዕኖ ግምገማ ጥናት/Environmental Impact Assessment Study Report</li>
            <li>አመልካቹ ባለቤት ካልሆነ ህጋዊ ውክልና/Legal Representation (If applicant is not the owner)</li>
            <li>የማህበር መመስረቻ ደንብ/Article of Association (for PLC)</li>
            <li>የኢንሹራንስ ሽፋን/ Insurance Commitment letter</li>
            <li>የድርጅቱ የንግድ ልውውጥ መረጃ Company's Supply Records</li>
            <li>የሥራው እቅድ/Working program</li>
            <li>መሬቱ ለፔትሮሊየም ውጤቶች ግንባታ የሚሆን መሆኑን የሚገልፅ የአፈር ምርምር ጥናት/soil analysis study</li>
            <li>(የነዳጅ ማደያ መሣሪያዎች ተከላና ጥገና አገልግሎት) የሚያደርግ ባለሞያ ወይም ተቋም ከነዳጅና ኢነርጂ ባለስልጣን ብቃት የወሰደ መሆኑ የሚገልፅ ሰነድ</li>
          </ul>
        </div>
      )}
      <div className="mb-6">
        <label className="block text-sm font-medium mb-1">Remark (ማስታወሻ)</label>
        <textarea name="remark" value={formData.remark} onChange={handleChange} className={inputClass} rows="3" />
      </div>
      <div className="col-span-2">
        <label className="block text-sm font-medium mb-1">ያመለከቱበት ቀን / Request date</label>
        <EthiopianDatePicker name="request_date" value={formData.request_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Additional Attachments (ተያያዥ መረጃዎች)</h4>
        <button type="button" onClick={addAdditionalAttachment} className="text-[#006400] font-medium flex items-center gap-1 hover:underline">
          <FaPlus /> Add Attachment
        </button>
        {additionalAttachments.map((att, index) => (
          <div key={index} className="mt-3 p-3 border border-gray-200 rounded bg-gray-50">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Additional Attachment {index + 1}</span>
              <button type="button" onClick={() => removeAdditionalAttachment(index)} className="text-red-500 hover:text-red-700">
                <FaTrash />
              </button>
            </div>
            <input 
              type="text" 
              placeholder="Attachment name/description" 
              value={att.name} 
              onChange={(e) => handleAdditionalFileNameChange(e, index)} 
              className={inputClass} 
            />
            <input 
              type="file" 
              onChange={(e) => handleAdditionalFileChange(e, index)} 
              className="mt-2 text-sm" 
              accept=".pdf" 
            />
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2"><FaTimes /> Cancel</button>
        <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 flex items-center gap-2"><FaPaperPlane /> {isEdit ? 'Update Request' : 'Submit'}</button>
      </div>
    </form>
  );
}

function FuelTransportationRenewalForm({ onSubmit, onCancel, initialData = null, isEdit = false, onRegister = null }) {
  useFormDraft(() => [formData, setFormData], initialData);
  const [formData, setFormData] = useState({ reference_no: '', renewal_year: '', payment_fee: '', renewal_date: toEthiopianDateStr(new Date()), request_date: toEthiopianDateStr(new Date()) });
  const [searchedCompany, setSearchedCompany] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');

  const fetchCompanyByRef = async (refNo) => {
    if (!refNo.trim()) return;
    setLookupLoading(true); setLookupError('');
    try {
      const res = await axios.get(`${API_URL}/new-fuel-transportation?reference_no=${refNo}`);
      if (res.data.length > 0) { setSearchedCompany(res.data[0]); }
      else { setLookupError('No record found with this reference number'); setSearchedCompany(null); }
    } catch (err) { setLookupError('Failed to lookup reference'); setSearchedCompany(null); }
    finally { setLookupLoading(false); }
  };

  const handleLookup = () => { fetchCompanyByRef(formData.reference_no); };
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.name === 'reference_no') { setSearchedCompany(null); setLookupError(''); }
  };

  const [additionalAttachments, setAdditionalAttachments] = useState([]);

  const handleAdditionalFileChange = (e, index) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    const newAttachments = [...additionalAttachments];
    newAttachments[index].file = selectedFile;
    setAdditionalAttachments(newAttachments);
  };

  const handleAdditionalFileNameChange = (e, index) => {
    const newAttachments = [...additionalAttachments];
    newAttachments[index].name = e.target.value;
    setAdditionalAttachments(newAttachments);
  };

  const addAdditionalAttachment = () => {
    if (additionalAttachments.length >= 10) {
      toast.warn('You can only add up to 10 attachments');
      return;
    }
    setAdditionalAttachments([...additionalAttachments, { name: '', file: null }]);
  };

  const removeAdditionalAttachment = (index) => {
    const newAttachments = additionalAttachments.filter((_, i) => i !== index);
    setAdditionalAttachments(newAttachments);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!searchedCompany) { toast.warning('Please lookup the reference number first'); return; }
    const formDataToSend = new FormData();
    formDataToSend.append('reference_no', formData.reference_no);
    formDataToSend.append('existing_ref_no', searchedCompany.reference_no);
    formDataToSend.append('renewal_year', formData.renewal_year);
    formDataToSend.append('payment_fee', formData.payment_fee);
    formDataToSend.append('renewal_date', ethDateStrToGregStr(formData.renewal_date));
    formDataToSend.append('request_date', ethDateStrToGregStr(formData.request_date));
    if (isEdit && initialData) {
      formDataToSend.append('status', initialData.status || 'pending');
      formDataToSend.append('response_reason', initialData.response_reason || '');
      formDataToSend.append('rejection_reason', initialData.rejection_reason || '');
    }
    additionalAttachments.forEach((att, index) => {
      if (att.file) {
        formDataToSend.append(`additional_attachment_${index}_name`, att.name || '');
        formDataToSend.append(`additional_attachment_${index}`, att.file);
      }
    });
    formDataToSend.append('additional_attachments_count', additionalAttachments.filter(a => a.file).length);
    onSubmit(formDataToSend, isEdit ? initialData.id : null);
  };

  const inputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow">

      <div className="mb-4 p-4 bg-yellow-50 rounded border border-yellow-200 flex items-center justify-between">
        <p className="text-sm text-yellow-800">ከዚህ ቀደም በሲስተም ውስጥ ካልተመዘገቡ ይመዝገቡ የሚለውን ቁልፍ ይጫኑ / If you are not previously registered in the system, click the Register button.</p>
        {onRegister && (
          <button type="button" onClick={onRegister} className="ml-4 whitespace-nowrap bg-[#006400] text-white px-4 py-2 rounded hover:bg-green-700 text-sm font-medium flex-shrink-0">
            ይመዝገቡ / Register
          </button>
        )}
      </div>
      <div className="mb-4 p-4 bg-blue-50 rounded border border-blue-200">
        <p className="text-sm text-blue-800">Enter the reference number to look up its details, then fill in the renewal information.</p>
      </div>
      <div className="mb-6">
        <label className={labelClass}>Reference No (የማመልከቻ ቁጥር) *</label>
        <div className="flex gap-2">
          <input type="text" name="reference_no" value={formData.reference_no} onChange={handleChange} className={inputClass + " flex-1"} placeholder="e.g. FTR-XXXXXXXX" required />
          <button type="button" onClick={handleLookup} disabled={lookupLoading || !formData.reference_no.trim()} className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 disabled:bg-gray-400 flex items-center gap-2">
            <FaSearch /> {lookupLoading ? 'Searching...' : 'Search'}
          </button>
        </div>
        {lookupError && <p className="text-red-600 text-sm mt-1">{lookupError}</p>}
      </div>
      {searchedCompany && (
        <div className="mb-6 p-4 bg-gray-50 rounded border">
          <h4 className="font-bold text-gray-700 mb-3">Details (ዝርዝር)</h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><strong>Applicant Name:</strong> {searchedCompany.applicant_name || 'N/A'}</div>
            <div><strong>Nationality:</strong> {searchedCompany.nationality || 'N/A'}</div>
            <div><strong>Company Name:</strong> {searchedCompany.transporter_company_name || 'N/A'}</div>
            <div><strong>Type of Operation:</strong> {searchedCompany.type_of_operation || 'N/A'}</div>
            <div><strong>Region:</strong> {searchedCompany.region || 'N/A'}</div>
            <div><strong>Sub City:</strong> {searchedCompany.subcity || 'N/A'}</div>
            <div><strong>Manager Name:</strong> {searchedCompany.manager_name || 'N/A'}</div>
            <div><strong>Manager Mobile:</strong> {searchedCompany.manager_mobile || 'N/A'}</div>
          </div>
        </div>
      )}
      <div className="border-t pt-4">
        <h4 className="font-bold text-gray-700 mb-3">Renewal Information (የእድሳት መረጃ)</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="mb-6"><label className={labelClass}>እድሳት አመት / Renewal Year *</label><input type="text" name="renewal_year" value={formData.renewal_year} onChange={handleChange} className={inputClass} placeholder="e.g. 2026" required /></div>
          <div className="mb-6"><label className={labelClass}>የአገልግሎት ክፍያ / Payment Fee *</label><input type="text" name="payment_fee" value={formData.payment_fee} onChange={handleChange} className={inputClass} placeholder="Enter payment fee" required /></div>
          <div className="mb-6"><label className={labelClass}>የታደሰበት ቀን / Renewal Date *</label><EthiopianDatePicker name="renewal_date" value={formData.renewal_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required /></div>
        </div>
      </div>
      <div className="col-span-2">
        <label className="block text-sm font-medium mb-1">ያመለከቱበት ቀን / Request date</label>
        <EthiopianDatePicker name="request_date" value={formData.request_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Additional Attachments (ተያያዥ መረጃዎች)</h4>
        <button type="button" onClick={addAdditionalAttachment} className="text-[#006400] font-medium flex items-center gap-1 hover:underline">
          <FaPlus /> Add Attachment
        </button>
        {additionalAttachments.map((att, index) => (
          <div key={index} className="mt-3 p-3 border border-gray-200 rounded bg-gray-50">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Additional Attachment {index + 1}</span>
              <button type="button" onClick={() => removeAdditionalAttachment(index)} className="text-red-500 hover:text-red-700">
                <FaTrash />
              </button>
            </div>
            <input 
              type="text" 
              placeholder="Attachment name/description" 
              value={att.name} 
              onChange={(e) => handleAdditionalFileNameChange(e, index)} 
              className={inputClass} 
            />
            <input 
              type="file" 
              onChange={(e) => handleAdditionalFileChange(e, index)} 
              className="mt-2 text-sm" 
              accept=".pdf" 
            />
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2"><FaTimes /> Cancel</button>
        <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 flex items-center gap-2"><FaPaperPlane /> {isEdit ? 'Update Request' : 'Submit'}</button>
      </div>
    </form>
  );
}

function RenewalServiceForm({ onSubmit, onCancel, initialData = null, isEdit = false, lookupEndpoint, title }) {
  useFormDraft(() => [formData, setFormData], initialData);
  const [formData, setFormData] = useState({ reference_no: '', renewal_year: '', payment_fee: '', renewal_date: toEthiopianDateStr(new Date()), request_date: toEthiopianDateStr(new Date()), request_letter_no: '', request_letter_date: '' });
  const [searchedCompany, setSearchedCompany] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');

  const fetchCompanyByRef = async (refNo) => {
    if (!refNo.trim()) return;
    setLookupLoading(true); setLookupError('');
    try {
      const res = await axios.get(`${API_URL}${lookupEndpoint}?reference_no=${refNo}`);
      if (res.data.length > 0) { setSearchedCompany(res.data[0]); }
      else { setLookupError('No record found with this reference number'); setSearchedCompany(null); }
    } catch (err) { setLookupError('Failed to lookup reference'); setSearchedCompany(null); }
    finally { setLookupLoading(false); }
  };

  const handleLookup = () => { fetchCompanyByRef(formData.reference_no); };
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.name === 'reference_no') { setSearchedCompany(null); setLookupError(''); }
  };

  useEffect(() => {
    if (initialData) {
      setFormData({
        reference_no: initialData.existing_ref_no || initialData.reference_no || '',
        renewal_year: initialData.renewal_year || '',
        payment_fee: initialData.payment_fee || '',
        renewal_date: initialData.renewal_date ? convertDbDateToDisplay(initialData.renewal_date) : toEthiopianDateStr(new Date()),
        request_date: initialData.request_date ? convertDbDateToDisplay(initialData.request_date) : toEthiopianDateStr(new Date()),
        request_letter_no: initialData.request_letter_no || '',
        request_letter_date: initialData.request_letter_date ? convertDbDateToDisplay(initialData.request_letter_date) : '',
      });
      if (initialData.existing_ref_no || initialData.reference_no) {
        fetchCompanyByRef(initialData.existing_ref_no || initialData.reference_no);
      }
    }
  }, [initialData]);

  const [additionalAttachments, setAdditionalAttachments] = useState([]);

  const handleAdditionalFileChange = (e, index) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    const newAttachments = [...additionalAttachments];
    newAttachments[index].file = selectedFile;
    setAdditionalAttachments(newAttachments);
  };

  const handleAdditionalFileNameChange = (e, index) => {
    const newAttachments = [...additionalAttachments];
    newAttachments[index].name = e.target.value;
    setAdditionalAttachments(newAttachments);
  };

  const addAdditionalAttachment = () => {
    if (additionalAttachments.length >= 10) {
      toast.warn('You can only add up to 10 attachments');
      return;
    }
    setAdditionalAttachments([...additionalAttachments, { name: '', file: null }]);
  };

  const removeAdditionalAttachment = (index) => {
    const newAttachments = additionalAttachments.filter((_, i) => i !== index);
    setAdditionalAttachments(newAttachments);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!searchedCompany && !initialData) { toast.warning('Please lookup the reference number first'); return; }
    const data = new FormData();
    data.append('reference_no', formData.reference_no);
    data.append('renewal_year', formData.renewal_year);
    data.append('payment_fee', formData.payment_fee);
    data.append('renewal_date', ethDateStrToGregStr(formData.renewal_date));
    data.append('request_date', ethDateStrToGregStr(formData.request_date));
    data.append('request_letter_no', formData.request_letter_no);
    data.append('request_letter_date', formData.request_letter_date ? ethDateStrToGregStr(formData.request_letter_date) : '');
    if (isEdit && initialData) {
      data.append('status', initialData.status || 'pending');
      data.append('response_reason', initialData.response_reason || '');
    }
    additionalAttachments.forEach((att, index) => {
      if (att.file) {
        data.append(`additional_attachment_${index}_name`, att.name || '');
        data.append(`additional_attachment_${index}`, att.file);
      }
    });
    data.append('additional_attachments_count', additionalAttachments.filter(a => a.file).length);
    onSubmit(data, isEdit ? initialData.id : null);
  };

  const inputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-[#006400]">{isEdit ? 'Edit Service Request' : title}</h2>
        {isEdit && <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm">Editing Request</span>}
      </div>
      <div className="mb-4 p-4 bg-blue-50 rounded border border-blue-200">
        <p className="text-sm text-blue-800">Enter the reference number to look up its details, then fill in the renewal information.</p>
      </div>
      <div className="mb-6">
        <label className={labelClass}>Reference No (የማመልከቻ ቁጥር) *</label>
        <div className="flex gap-2">
          <input type="text" name="reference_no" value={formData.reference_no} onChange={handleChange} className={inputClass + " flex-1"} placeholder="e.g. NFC-XXXXXXXX" required />
          <button type="button" onClick={handleLookup} disabled={lookupLoading || !formData.reference_no.trim()} className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 disabled:bg-gray-400 flex items-center gap-2">
            <FaSearch /> {lookupLoading ? 'Searching...' : 'Search'}
          </button>
        </div>
        {lookupError && <p className="text-red-600 text-sm mt-1">{lookupError}</p>}
      </div>
      {searchedCompany && (
        <div className="mb-6 p-4 bg-gray-50 rounded border">
          <h4 className="font-bold text-gray-700 mb-3">Details (ዝርዝር)</h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><strong>Applicant Name:</strong> {searchedCompany.applicant_name || searchedCompany.full_name || 'N/A'}</div>
            <div><strong>Nationality:</strong> {searchedCompany.nationality || 'N/A'}</div>
            <div><strong>Company Name:</strong> {searchedCompany.company_name || searchedCompany.organization_name || 'N/A'}</div>
            <div><strong>Region:</strong> {searchedCompany.region || 'N/A'}</div>
            <div><strong>Sub City:</strong> {searchedCompany.subcity || 'N/A'}</div>
            <div><strong>Manager Name:</strong> {searchedCompany.manager_name || 'N/A'}</div>
            <div><strong>Manager Mobile:</strong> {searchedCompany.manager_mobile || searchedCompany.manager_phone || searchedCompany.phone_no || 'N/A'}</div>
          </div>
        </div>
      )}
      <div className="border-t pt-4 mb-4">
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div><label className={labelClass}>የተጠየቀበት ደብዳቤ ቁጥር / Request Letter No</label><input name="request_letter_no" value={formData.request_letter_no} onChange={handleChange} className={inputClass} /></div>
          <div><label className={labelClass}>የተጠየቀበት ደብዳቤ ቀን / Request Letter Date</label><EthiopianDatePicker name="request_letter_date" value={formData.request_letter_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" /></div>
        </div>
        <h4 className="font-bold text-gray-700 mb-3">Renewal Information (የእድሳት መረጃ)</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="mb-6"><label className={labelClass}>እድሳት አመት / Renewal Year *</label><input type="text" name="renewal_year" value={formData.renewal_year} onChange={handleChange} className={inputClass} placeholder="e.g. 2026" required /></div>
          <div className="mb-6"><label className={labelClass}>የአገልግሎት ክፍያ / Payment Fee *</label><input type="text" name="payment_fee" value={formData.payment_fee} onChange={handleChange} className={inputClass} placeholder="Enter payment fee" required /></div>
          <div className="mb-6"><label className={labelClass}>የታደሰበት ቀን / Renewal Date *</label><EthiopianDatePicker name="renewal_date" value={formData.renewal_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required /></div>
        </div>
      </div>
      <div className="col-span-2">
        <label className="block text-sm font-medium mb-1">ያመለከቱበት ቀን / Request date</label>
        <EthiopianDatePicker name="request_date" value={formData.request_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Additional Attachments (ተያያዥ መረጃዎች)</h4>
        <button type="button" onClick={addAdditionalAttachment} className="text-[#006400] font-medium flex items-center gap-1 hover:underline">
          <FaPlus /> Add Attachment
        </button>
        {additionalAttachments.map((att, index) => (
          <div key={index} className="mt-3 p-3 border border-gray-200 rounded bg-gray-50">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Additional Attachment {index + 1}</span>
              <button type="button" onClick={() => removeAdditionalAttachment(index)} className="text-red-500 hover:text-red-700">
                <FaTrash />
              </button>
            </div>
            <input 
              type="text" 
              placeholder="Attachment name/description" 
              value={att.name} 
              onChange={(e) => handleAdditionalFileNameChange(e, index)} 
              className={inputClass} 
            />
            <input 
              type="file" 
              onChange={(e) => handleAdditionalFileChange(e, index)} 
              className="mt-2 text-sm" 
              accept=".pdf" 
            />
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2"><FaTimes /> Cancel</button>
        <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 flex items-center gap-2"><FaPaperPlane /> {isEdit ? 'Update Request' : 'Submit'}</button>
      </div>
    </form>
  );
}

function TransferServiceForm({ onSubmit, onCancel, initialData = null, isEdit = false, lookupEndpoint, title }) {
  useFormDraft(() => [formData, setFormData], initialData);
  const [formData, setFormData] = useState({ reference_no: '', transfer_from: '', transfer_to: '', payment_fee: '', transfer_date: toEthiopianDateStr(new Date()), transfer_reason: '', request_date: toEthiopianDateStr(new Date()) });
  const [searchedCompany, setSearchedCompany] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');

  const fetchCompanyByRef = async (refNo) => {
    if (!refNo.trim()) return;
    setLookupLoading(true); setLookupError('');
    try {
      const res = await axios.get(`${API_URL}${lookupEndpoint}?reference_no=${refNo}`);
      if (res.data.length > 0) { setSearchedCompany(res.data[0]); }
      else { setLookupError('No record found with this reference number'); setSearchedCompany(null); }
    } catch (err) { setLookupError('Failed to lookup reference'); setSearchedCompany(null); }
    finally { setLookupLoading(false); }
  };

  const handleLookup = () => { fetchCompanyByRef(formData.reference_no); };
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.name === 'reference_no') { setSearchedCompany(null); setLookupError(''); }
  };

  const [additionalAttachments, setAdditionalAttachments] = useState([]);

  const handleAdditionalFileChange = (e, index) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF document.');
      e.target.value = '';
      return;
    }
    const newAttachments = [...additionalAttachments];
    newAttachments[index].file = selectedFile;
    setAdditionalAttachments(newAttachments);
  };

  const handleAdditionalFileNameChange = (e, index) => {
    const newAttachments = [...additionalAttachments];
    newAttachments[index].name = e.target.value;
    setAdditionalAttachments(newAttachments);
  };

  const addAdditionalAttachment = () => {
    if (additionalAttachments.length >= 10) {
      toast.warn('You can only add up to 10 attachments');
      return;
    }
    setAdditionalAttachments([...additionalAttachments, { name: '', file: null }]);
  };

  const removeAdditionalAttachment = (index) => {
    const newAttachments = additionalAttachments.filter((_, i) => i !== index);
    setAdditionalAttachments(newAttachments);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!searchedCompany) { toast.warning('Please lookup the reference number first'); return; }
    const data = new FormData();
    data.append('reference_no', formData.reference_no);
    data.append('transfer_from', formData.transfer_from);
    data.append('transfer_to', formData.transfer_to);
    data.append('payment_fee', formData.payment_fee);
    data.append('transfer_date', ethDateStrToGregStr(formData.transfer_date));
    data.append('transfer_reason', formData.transfer_reason);
    data.append('request_date', ethDateStrToGregStr(formData.request_date));
    if (isEdit && initialData) {
      data.append('status', initialData.status || 'pending');
      data.append('response_reason', initialData.response_reason || '');
    }
    additionalAttachments.forEach((att, index) => {
      if (att.file) {
        data.append(`additional_attachment_${index}_name`, att.name || '');
        data.append(`additional_attachment_${index}`, att.file);
      }
    });
    data.append('additional_attachments_count', additionalAttachments.filter(a => a.file).length);
    onSubmit(data, isEdit ? initialData.id : null);
  };

  const inputClass = "w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 shadow">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-[#006400]">{isEdit ? 'Edit Service Request' : title}</h2>
        {isEdit && <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm">Editing Request</span>}
      </div>
      <div className="mb-4 p-4 bg-yellow-50 rounded border border-yellow-200">
        <p className="text-sm text-yellow-800">ከዚህ ቀደም በሲስተም ውስጥ ካልተመዘገቡ ይመዝገቡ የሚለውን ቁልፍ ይጫኑ / If you are not previously registered in the system, click the Register button.</p>
      </div>
      <div className="mb-4 p-4 bg-blue-50 rounded border border-blue-200">
        <p className="text-sm text-blue-800">Enter the reference number to look up its details, then fill in the renewal information.</p>
      </div>
      <div className="mb-6">
        <label className={labelClass}>Reference No (የማመልከቻ ቁጥር) *</label>
        <div className="flex gap-2">
          <input type="text" name="reference_no" value={formData.reference_no} onChange={handleChange} className={inputClass + " flex-1"} placeholder="e.g. NFC-XXXXXXXX" required />
          <button type="button" onClick={handleLookup} disabled={lookupLoading || !formData.reference_no.trim()} className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 disabled:bg-gray-400 flex items-center gap-2">
            <FaSearch /> {lookupLoading ? 'Searching...' : 'Search'}
          </button>
        </div>
        {lookupError && <p className="text-red-600 text-sm mt-1">{lookupError}</p>}
      </div>
      {searchedCompany && (
        <div className="mb-6 p-4 bg-gray-50 rounded border">
          <h4 className="font-bold text-gray-700 mb-3">Details (ዝርዝር)</h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><strong>Applicant Name:</strong> {searchedCompany.applicant_name || searchedCompany.full_name || 'N/A'}</div>
            <div><strong>Nationality:</strong> {searchedCompany.nationality || 'N/A'}</div>
            <div><strong>Company Name:</strong> {searchedCompany.company_name || searchedCompany.organization_name || 'N/A'}</div>
            <div><strong>Region:</strong> {searchedCompany.region || 'N/A'}</div>
            <div><strong>Sub City:</strong> {searchedCompany.subcity || 'N/A'}</div>
            <div><strong>Manager Name:</strong> {searchedCompany.manager_name || 'N/A'}</div>
            <div><strong>Manager Mobile:</strong> {searchedCompany.manager_mobile || searchedCompany.manager_phone || searchedCompany.phone_no || 'N/A'}</div>
          </div>
        </div>
      )}
      <div className="border-t pt-4">
        <h4 className="font-bold text-gray-700 mb-3">Transfer Information (የዝውውር መረጃ)</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="mb-6"><label className={labelClass}>ከ / Transfer From *</label><input type="text" name="transfer_from" value={formData.transfer_from} onChange={handleChange} className={inputClass} placeholder="Transfer from" required /></div>
          <div className="mb-6"><label className={labelClass}>ወደ / Transfer To *</label><input type="text" name="transfer_to" value={formData.transfer_to} onChange={handleChange} className={inputClass} placeholder="Transfer to" required /></div>
          <div className="mb-6"><label className={labelClass}>የአገልግሎት ክፍያ / Payment Fee *</label><input type="text" name="payment_fee" value={formData.payment_fee} onChange={handleChange} className={inputClass} placeholder="Enter payment fee" required /></div>
          <div className="mb-6"><label className={labelClass}>የተዘዋወረበት ቀን / Transfer Date *</label><EthiopianDatePicker name="transfer_date" value={formData.transfer_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required /></div>
          <div className="col-span-2 mb-6"><label className={labelClass}>የሚዛወርበት ምክንያት / Reason for Transfer</label><textarea name="transfer_reason" value={formData.transfer_reason} onChange={handleChange} className={inputClass} rows="3" placeholder="Enter reason for transfer" /></div>
        </div>
      </div>
      <div className="col-span-2">
        <label className="block text-sm font-medium mb-1">ያመለከቱበት ቀን / Request date</label>
        <EthiopianDatePicker name="request_date" value={formData.request_date} onChange={handleChange} className="w-44 border rounded px-3 py-2" placeholder="dd/mm/yyyy" required />
      </div>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">Additional Attachments (ተያያዥ መረጃዎች)</h4>
        <button type="button" onClick={addAdditionalAttachment} className="text-[#006400] font-medium flex items-center gap-1 hover:underline">
          <FaPlus /> Add Attachment
        </button>
        {additionalAttachments.map((att, index) => (
          <div key={index} className="mt-3 p-3 border border-gray-200 rounded bg-gray-50">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Additional Attachment {index + 1}</span>
              <button type="button" onClick={() => removeAdditionalAttachment(index)} className="text-red-500 hover:text-red-700">
                <FaTrash />
              </button>
            </div>
            <input 
              type="text" 
              placeholder="Attachment name/description" 
              value={att.name} 
              onChange={(e) => handleAdditionalFileNameChange(e, index)} 
              className={inputClass} 
            />
            <input 
              type="file" 
              onChange={(e) => handleAdditionalFileChange(e, index)} 
              className="mt-2 text-sm" 
              accept=".pdf" 
            />
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2"><FaTimes /> Cancel</button>
        <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-400 flex items-center gap-2"><FaPaperPlane /> {isEdit ? 'Update Request' : 'Submit'}</button>
      </div>
    </form>
  );
}

const SERVICE_COLORS = [
  { bg: 'from-[#006400] to-[#008000]', light: 'bg-green-50', border: 'border-[#006400]', icon: 'text-[#006400]', badge: 'bg-[#006400]' },
  { bg: 'from-[#FF6B00] to-[#FF8C00]', light: 'bg-orange-50', border: 'border-[#FF6B00]', icon: 'text-[#FF6B00]', badge: 'bg-[#FF6B00]' },
  { bg: 'from-[#2563EB] to-[#3B82F6]', light: 'bg-blue-50', border: 'border-[#2563EB]', icon: 'text-[#2563EB]', badge: 'bg-[#2563EB]' },
  { bg: 'from-[#7C3AED] to-[#8B5CF6]', light: 'bg-purple-50', border: 'border-[#7C3AED]', icon: 'text-[#7C3AED]', badge: 'bg-[#7C3AED]' },
  { bg: 'from-[#DC2626] to-[#EF4444]', light: 'bg-red-50', border: 'border-[#DC2626]', icon: 'text-[#DC2626]', badge: 'bg-[#DC2626]' },
  { bg: 'from-[#0891B2] to-[#06B6D4]', light: 'bg-cyan-50', border: 'border-[#0891B2]', icon: 'text-[#0891B2]', badge: 'bg-[#0891B2]' },
  { bg: 'from-[#CA8A04] to-[#EAB308]', light: 'bg-amber-50', border: 'border-[#CA8A04]', icon: 'text-[#CA8A04]', badge: 'bg-[#CA8A04]' },
  { bg: 'from-[#059669] to-[#10B981]', light: 'bg-emerald-50', border: 'border-[#059669]', icon: 'text-[#059669]', badge: 'bg-[#059669]' },
];

const SERVICE_ICONS = [FaGasPump, FaOilCan, FaTruck, FaIndustry, FaFire, FaFlask, FaShip, FaBox, FaCubes, FaIndustry, FaGasPump, FaOilCan, FaTruck, FaShip, FaFlask, FaFire, FaBox, FaCubes, FaIndustry, FaGasPump];

const DISABLED_SERVICE_TYPES = [
  'new-fuel-company',
  'new-gas-station',
  'new-depot-fuel-cargo',
  'new-gas-station-retail',
];

function Home({ onServiceSelect }) {
  const [services, setServices] = useState([]);
  const [news, setNews] = useState([]);

  useEffect(() => {
    fetchServices();
    fetchNews();
  }, []);

  const fetchServices = async () => {
    try {
      console.log('FETCHING SERVICES FROM:', `${API_URL}/services`);
      const response = await axios.get(`${API_URL}/services`);
      if (Array.isArray(response.data)) {
        setServices(response.data.filter(s => s.status));
      } else {
        console.error('Services API returned non-array:', JSON.stringify(response.data).substring(0, 200));
        setServices([]);
      }
    } catch (error) {
      toast.error('Failed to fetch services');
    }
  };

  const fetchNews = async () => {
    try {
      console.log('FETCHING NEWS FROM:', `${API_URL}/news`);
      const response = await axios.get(`${API_URL}/news`);
      if (Array.isArray(response.data)) {
        setNews(response.data.filter(n => n.is_active));
      } else {
        console.error('News API returned non-array:', JSON.stringify(response.data).substring(0, 200));
        setNews([]);
      }
    } catch (error) {
      console.error('Failed to fetch news:', error.message, error.response?.status);
    }
  };

  return (
    <div className="container mx-auto p-6">
      <style>{`.news-content a { color: #0066cc; text-decoration: underline; }`}</style>
      {news.length > 0 && (
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2"><FaBell className="text-[#FF6B00]" /> ዜናዎች / Latest News</h2>
          <div className="space-y-4">
            {news.map((item) => (
              <div key={item.id} className="border-l-4 border-[#FF6B00] pl-4 hover:bg-orange-50 transition p-2 rounded-r-lg">
                <p className="text-sm text-gray-500">{item.created_at ? (() => { try { return formatEthDateEn(new Date(item.created_at)); } catch(e) { return ''; } })() : ''}</p>
                <p className="text-gray-700 font-bold">{item.title}</p>
                {item.title_amharic && <p className="text-gray-700">{item.title_amharic}</p>}
                {item.content && <div className="text-gray-600 mt-2 news-content" dangerouslySetInnerHTML={{ __html: item.content }} onClick={(e) => { const a = e.target.closest('a'); if (a) { e.preventDefault(); let href = a.getAttribute('href'); if (href && !href.startsWith('http://') && !href.startsWith('https://')) href = 'https://' + href; window.open(href, '_blank'); } }} />}
              </div>
            ))}
          </div>
        </div>
      )}
      <style>{`@keyframes marquee { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } } .animate-marquee { animation: marquee 8s linear infinite; }`}</style>
      <div className="mb-8 text-center overflow-hidden bg-white rounded-lg shadow-sm p-3">
        <div className="whitespace-nowrap animate-marquee inline-block">
          <span className="text-2xl md:text-3xl font-bold text-gray-800 mr-8">የነዳጅ እና ኢነርጂ ባለስልጣን</span>
          <span className="text-lg text-gray-600">Petroleum and Energy Authority — Online Services</span>
        </div>
      </div>
      {services.length === 0 ? (
        <p className="text-gray-500 text-center py-12 text-lg">No services available at the moment</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {services.map((service, index) => {
            const colors = SERVICE_COLORS[index % SERVICE_COLORS.length];
            const IconComponent = SERVICE_ICONS[index % SERVICE_ICONS.length];
            return (
              <button
                key={service.id}
                onClick={() => onServiceSelect(service)}
                className={`group relative bg-white rounded-xl shadow-md text-left overflow-hidden ${DISABLED_SERVICE_TYPES.includes(service.type) ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-xl transition-all duration-300 hover:-translate-y-1'}`}
              >
                <div className="h-2 w-full bg-gradient-to-r from-[#006400] to-[#008000]" />
                <div className="p-6">
                  <div className={`w-12 h-12 rounded-lg ${colors.light} flex items-center justify-center mb-4 ${DISABLED_SERVICE_TYPES.includes(service.type) ? '' : 'group-hover:scale-110 transition-transform duration-300'}`}>
                    <IconComponent className={`text-2xl ${colors.icon}`} />
                  </div>
                  {service.name_amharic && (
                    <p className="text-lg font-bold text-gray-800 mb-1 leading-tight">{service.name_amharic}</p>
                  )}
                  <p className="text-sm text-gray-500 mb-4 leading-snug min-h-[2.5em]">{service.name}</p>
                  {DISABLED_SERVICE_TYPES.includes(service.type) ? (
                    <span className="inline-flex items-center gap-2 bg-gray-400 text-white px-4 py-2 rounded-lg text-sm font-medium">
                      ለጊዜ ተቋርጧል / Temporarily Unavailable
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2 bg-[#FF6B00] text-white px-4 py-2 rounded-lg text-sm font-medium opacity-90 group-hover:opacity-100 transition-opacity">
                      Apply Now <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform" />
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Services whose approval letter / certificate must not be visible to the
// customer until the back-office officer sends it via "Send letter to client".
const LETTER_RELEASE_GATED_SERVICES = new Set([
  'direct-fuel-beneficiary',
  'gas-station-transfer',
  'diversion-license',
  'operator-change-license',
  'new-gas-station-retail',
  'new-fuel-distributor',
  'new-fuel-import',
  'new-gas-import',
  'new-oil-exporter',
  'new-fuel-refinery',
  'new-fuel-transportation',
  'Fuel_Station_Retail_Construction_Permit',
  'Fuel_Distributor_Construction_Permit',
  'Fuel_Importer_ Construction_Permit',
  'Gas_Importer_Construction_Permit',
  'Fuel_Exporter_Construction_permit',
  'Fuel_Refining_Construction_permit',
  'fuel-transportation-construction'
]);

const applyLetterReleaseGate = (items, serviceType) => {
  if (!Array.isArray(items) || !LETTER_RELEASE_GATED_SERVICES.has(serviceType)) return items;
  items.forEach((item) => {
    if (!item) return;
    const released = !!item.letter_released || !!item.letter_released_at || !!item.client_letter;
    if (!released) {
      item.approved_letter = null;
      item.certificate_path = null;
    }
  });
  return items;
};

function MyApplications({ user, onSessionExpired }) {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingApp, setEditingApp] = useState(null);

  const EDITABLE_SERVICES = ['direct-fuel-beneficiary', 'gas-station-transfer', 'diversion-license', 'operator-change-license', 'new-fuel-company', 'fuel-company-renewal', 'fuel-company-transfer', 'new-gas-station', 'gas-station-renewal', 'gas-station-connection-transfer', 'new-depot-fuel-cargo', 'depot-fuel-cargo-renewal', 'depot-fuel-cargo-transfer', 'new-gas-station-retail', 'new-fuel-distributor', 'new-fuel-import', 'new-gas-import', 'new-oil-exporter', 'new-fuel-refinery', 'new-fuel-transportation', 'gas-station-retail-renewal', 'fuel-distributor-renewal', 'fuel-import-renewal', 'gas-import-renewal', 'oil-exporter-renewal', 'fuel-refinery-renewal', 'fuel-transportation-renewal', 'fuel-order-cancellation', 'fuel-order-cancellation-service', 'vehicle-breakdown-reporting', 'fuel-delivery-extension', 'electric-vehicle-charging', 'Fuel_Station_Retail_Construction_Permit', 'Fuel_Distributor_Construction_Permit', 'Fuel_Importer_ Construction_Permit', 'Gas_Importer_Construction_Permit', 'Fuel_Exporter_Construction_permit', 'Fuel_Refining_Construction_permit', 'Fuel_Transportation_Construction_permit'];

  const getResubmitPath = (app) => {
    const overrides = {
      'Fuel_Station_Retail_Construction_Permit': 'gas-station-retail-construction',
      'Fuel_Distributor_Construction_Permit': 'fuel-distributor-construction',
      'Fuel_Importer_ Construction_Permit': 'fuel-importer-construction',
      'Gas_Importer_Construction_Permit': 'gas-importer-construction',
      'Fuel_Exporter_Construction_permit': 'oil-exporter-construction',
      'Fuel_Refining_Construction_permit': 'fuel-refinery-construction',
      'Fuel_Transportation_Construction_permit': 'fuel-transportation-construction'
    };
    return overrides[app.serviceType] || app.serviceType;
  };

  const handleResubmit = (formData, id) => {
    axios.put(`${API_URL}/${getResubmitPath(editingApp)}/${id}`, formData).then(() => {
      toast.success('Application corrected and resubmitted successfully!');
      setEditingApp(null);
      fetchApplications();
    }).catch(err => {
      toast.error('Failed to resubmit: ' + (err.response?.data?.error || err.message));
    });
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    if (!user || !user.id) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const [directFuel, gsTransfer, diversion, operator, newFuelCompany, fuelRenewal, fuelTransfer, newGasStation, gasStationRenewal, gasStationConnectionTransfer, newDepotFuelCargo, depotFuelCargoRenewal, depotFuelCargoTransfer, new_gas_station_retail, new_fuel_distributor, new_fuel_import, new_gas_import, new_oil_exporter, new_fuel_refinery, new_fuel_transportation, gas_station_retail_renewal, fuel_distributor_renewal, fuel_import_renewal, gas_import_renewal, oil_exporter_renewal, fuel_refinery_renewal, fuel_transportation_renewal, fuelOrderCancellation, fuelOrderCancellationService, vehicleBreakdownReporting, fuelDeliveryExtension, electricVehicleCharging, fuelTransportationConstruction, gasStationRetailConstruction, fuelDistributorConstruction, fuelImportConstruction, gasImportConstruction, oilExporterConstruction, fuelRefineryConstruction, customerResponses] = await Promise.all([
        axios.get(`${API_URL}/direct-fuel-beneficiary?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/gas-station-transfer?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/diversion-license?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/operator-change-license?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/new-fuel-company?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/fuel-company-renewal?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/fuel-company-transfer?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/new-gas-station?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/gas-station-renewal?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/gas-station-connection-transfer?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/new-depot-fuel-cargo?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/depot-fuel-cargo-renewal?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/depot-fuel-cargo-transfer?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/new-gas-station-retail?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/new-fuel-distributor?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/new-fuel-import?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/new-gas-import?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/new-oil-exporter?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/new-fuel-refinery?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/new-fuel-transportation?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/gas-station-retail-renewal?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/fuel-distributor-renewal?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/fuel-import-renewal?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/gas-import-renewal?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/oil-exporter-renewal?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/fuel-refinery-renewal?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/fuel-transportation-renewal?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/fuel-order-cancellation?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/fuel-order-cancellation-service?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/vehicle-breakdown-reporting?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/fuel-delivery-extension?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/electric-vehicle-charging?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/fuel-transportation-construction?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/gas-station-retail-construction?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/fuel-distributor-construction?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/fuel-import-construction?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/gas-import-construction?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/oil-exporter-construction?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/fuel-refinery-construction?user_id=${user.id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/request-assignments/customer-responses`).catch((error) => {
          console.error('Failed to load customer response history:', error.response?.data?.error || error.message);
          return { data: [] };
        })
      ]);

      const allApps = [];
      
      directFuel.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'direct-fuel-beneficiary',
          serviceName: 'Service for direct fuel beneficiary renewal'
        });
      });
      
      gsTransfer.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'gas-station-transfer',
          serviceName: 'Service for existing gas station transfer from on company to the other'
        });
      });
      
      diversion.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'diversion-license',
          serviceName: 'Service for diversion permit'
        });
      });
      
      operator.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'operator-change-license',
          serviceName: 'Service for operator/dealer change permit'
        });
      });

      newFuelCompany.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'new-fuel-company',
          serviceName: 'New Fuel Distribution Company Connection License Service'
        });
      });

      fuelRenewal.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'fuel-company-renewal',
          serviceName: 'Fuel Distribution Company Connection License Renewal Service'
        });
      });

      fuelTransfer.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'fuel-company-transfer',
          serviceName: 'Fuel Distribution Company Connection License Transfer Service'
        });
      });

      newGasStation.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'new-gas-station',
          serviceName: 'New Gas Station Connection License Service'
        });
      });

      gasStationRenewal.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'gas-station-renewal',
          serviceName: 'Service for the renewal of Gas Station Permit'
        });
      });

      gasStationConnectionTransfer.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'gas-station-connection-transfer',
          serviceName: 'Service for the transfer of Gas Station Permit'
        });
      });

      newDepotFuelCargo.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'new-depot-fuel-cargo',
          serviceName: 'Service for New Fuel Loading Connection Permit from Depot'
        });
      });

      depotFuelCargoRenewal.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'depot-fuel-cargo-renewal',
          serviceName: 'Service for the Renewal of Fuel Loading Connection Permit from Depot'
        });
      });

      depotFuelCargoTransfer.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'depot-fuel-cargo-transfer',
          serviceName: 'Service for the transfer of Fuel Loading Connection Permit from Depot'
        });
      });
      new_gas_station_retail.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'new-gas-station-retail',
          serviceName: 'New Fuel Station Retail Competency and Licensing Service'
        });
      });

      new_fuel_distributor.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'new-fuel-distributor',
          serviceName: 'New Fuel Distributor Competency and Licensing Service'
        });
      });

      new_fuel_import.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'new-fuel-import',
          serviceName: 'New Fuel Importer Competency and Licensing Service'
        });
      });

      new_gas_import.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'new-gas-import',
          serviceName: 'አዲስ የጋዝ አስመጪነት ብቃትና ፈቃድ አገልግሎት / New Gas Importer Competency and Licensing Service'
        });
      });

      new_oil_exporter.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'new-oil-exporter',
          serviceName: 'አዲስ የነዳጅ ላኪነት ብቃትና ፈቃድ አገልግሎት / New Fuel Exporter Competency and Licensing Service'
        });
      });

      new_fuel_refinery.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'new-fuel-refinery',
          serviceName: 'አዲስ የነዳጅ ማጣራት ብቃትና ፈቃድ አገልግሎት / New Fuel Refining Competency and Licensing Service'
        });
      });

      new_fuel_transportation.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'new-fuel-transportation',
          serviceName: 'አዲስ የነዳጅ ማጓጓዝ ብቃትና ፈቃድ አገልግሎት / New Fuel Transportation Competency and Licensing Service'
        });
      });

      gas_station_retail_renewal.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'gas-station-retail-renewal',
          serviceName: 'Fuel Station Retail License Renewal Service'
        });
      });

      fuel_distributor_renewal.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'fuel-distributor-renewal',
          serviceName: 'Fuel Distributor License Renewal Service'
        });
      });

      fuel_import_renewal.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'fuel-import-renewal',
          serviceName: 'Fuel Importer License Renewal Service'
        });
      });

      gas_import_renewal.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'gas-import-renewal',
          serviceName: 'Gas Importer License Renewal Service'
        });
      });

      oil_exporter_renewal.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'oil-exporter-renewal',
          serviceName: 'Fuel Exporter License Renewal Service'
        });
      });

      fuel_refinery_renewal.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'fuel-refinery-renewal',
          serviceName: 'Fuel Refining License Renewal Service'
        });
      });

      fuel_transportation_renewal.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'fuel-transportation-renewal',
          serviceName: 'Fuel Transportation License Renewal Service'
        });
      });

      fuelOrderCancellation.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'fuel-order-cancellation',
          serviceName: 'የነዳጅ ጭነት ትእዛዝ ማቋረጥ / Fuel Order Cancellation Service'
        });
      });

      fuelOrderCancellationService.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'fuel-order-cancellation-service',
          serviceName: 'የነዳጅ ጭነት ትእዛዝ አገልግሎት ማቋረጥ / Fuel delivery order cancellation service'
        });
      });

      vehicleBreakdownReporting.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'vehicle-breakdown-reporting',
          serviceName: 'ብልሽት የገጠመውን ተሸከርካሪ ማሳወቅ አገልግሎት / Vehicle Breakdown Reporting Service'
        });
      });

      fuelDeliveryExtension.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'fuel-delivery-extension',
          serviceName: 'የነዳጅ አቅርቦት ማራዘም ጥያቄ አገልግሎት / Fuel Delivery Extension Service'
        });
      });

      electricVehicleCharging.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'electric-vehicle-charging',
          serviceName: 'የኤሌክትሪክ ተሽከርካሪ ቻርጅ መሙሌት አገልግሎት / Electric Vehicle Charging Service License Request'
        });
      });

      fuelTransportationConstruction.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'fuel-transportation-construction',
          serviceName: 'Fuel Transportation Construction Permit Service (የነዳጅ ማጓጓዣ ኮንስትራክሽን ፈቃድ አገልግሎት)'
        });
      });

      gasStationRetailConstruction.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'Fuel_Station_Retail_Construction_Permit',
          serviceName: 'የነዳጅ ማደያ ችርቻሮ ግንባታ ፈቃድ አገልግሎት / Fuel Station Retail Construction Permit Service'
        });
      });

      fuelDistributorConstruction.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'Fuel_Distributor_Construction_Permit',
          serviceName: 'የነዳጅ አከፋፋይ ግንባታ ፈቃድ አገልግሎት / Fuel Distributor Construction Permit Service'
        });
      });

      fuelImportConstruction.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'Fuel_Importer_ Construction_Permit',
          serviceName: 'የነዳጅ አስመጪነት ግንባታ ፈቃድ አገልግሎት / Fuel Importer Construction Permit Service'
        });
      });

      gasImportConstruction.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'Gas_Importer_Construction_Permit',
          serviceName: 'የጋዝ አስመጪነት ግንባታ ፈቃድ አገልግሎት / Gas Importer Construction Permit Service'
        });
      });

      oilExporterConstruction.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'Fuel_Exporter_Construction_permit',
          serviceName: 'የነዳጅ ላኪነት ግንባታ ፈቃድ አገልግሎት / Fuel Exporter Construction Permit Service'
        });
      });

      fuelRefineryConstruction.data.forEach(app => {
        allApps.push({
          ...app,
          serviceType: 'Fuel_Refining_Construction_permit',
          serviceName: 'Fuel Refining Construction Permit Service (የነዳጅ ማጣሪያ ኮንስትራክሽን ፈቃድ አገልግሎት)'
        });
      });

      const responsesByRequest = new Map();
      customerResponses.data.forEach((response) => {
        const serviceType = response.service_type === 'direct-fuel' ? 'direct-fuel-beneficiary' : response.service_type;
        const key = `${serviceType}:${response.request_id}`;
        const match = /^\[(Payment|Correction) Request\]\s*/.exec(response.transfer_message || '');
        const responses = responsesByRequest.get(key) || [];
        responses.push({
          type: match ? match[1] : '',
          message: match ? response.transfer_message.slice(match[0].length) : response.transfer_message,
          byName: response.assigned_by_name,
          at: response.transferred_at
        });
        responsesByRequest.set(key, responses);
      });
      allApps.forEach((app) => {
        const responses = responsesByRequest.get(`${app.serviceType}:${app.id}`) || [];
        if (app.response_reason && (app.status === 'payment_pending' || app.status === 'returned')) {
          const type = app.status === 'payment_pending' ? 'Payment' : 'Correction';
          if (!responses.some(response => response.message === app.response_reason)) {
            responses.push({ type, message: app.response_reason });
          }
        }
        app.customerResponses = responses.map((response) => ({
          ...response,
          type: response.type || (app.status === 'payment_pending' ? 'Payment' : app.status === 'returned' ? 'Correction' : '')
        }));
      });
      allApps.forEach((app) => {
        if (!LETTER_RELEASE_GATED_SERVICES.has(app.serviceType)) return;
        const released = !!app.letter_released || !!app.letter_released_at || !!app.client_letter;
        if (!released) {
          app.approved_letter = null;
          app.certificate_path = null;
        }
      });
      allApps.sort((a, b) => new Date(b.created_at || b.request_date) - new Date(a.created_at || a.request_date));
      setApplications(allApps);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const [uploadingReceipt, setUploadingReceipt] = useState(null);
  const [receiptFile, setReceiptFile] = useState({});

  const handleUploadReceipt = async (app) => {
    const file = receiptFile[`${app.serviceType}-${app.id}`];
    if (!file) { toast.warning('Please select a receipt file'); return; }
    const formData = new FormData();
    formData.append('receipt', file);
    setUploadingReceipt(`${app.serviceType}-${app.id}`);
    try {
      await axios.post(`${API_URL}/upload-receipt/${app.serviceType}/${app.id}`, formData, {
        withCredentials: true,
        headers: { 'X-Portal': 'customer' }
      });
      toast.success('Receipt uploaded successfully! Waiting for backofficer approval.');
      fetchApplications();
    } catch (err) {
      const errorMessage = err.response?.data?.error;
      if (err.response?.status === 401 || (err.response?.status === 403 && errorMessage === 'Invalid or expired token')) {
        onSessionExpired();
        toast.error('Your session has expired. Please sign in again, then upload your receipt.');
      } else {
        toast.error('Failed to upload receipt: ' + (errorMessage || err.message));
      }
    } finally {
      setUploadingReceipt(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusClass = status === 'approved' ? 'bg-green-500 text-white' : status === 'rejected' ? 'bg-red-500 text-white' : status === 'returned' ? 'bg-amber-500 text-white' : status === 'payment_pending' ? 'bg-purple-500 text-white' : status === 'paid' ? 'bg-teal-500 text-white' : 'bg-yellow-500 text-black';
    const label = status === 'returned' ? 'RETURNED FOR CORRECTION' : (status || 'PENDING');
    return <span className={`px-3 py-1 rounded text-sm font-bold ${statusClass}`}>{label}</span>;
  };

  const downloadFile = (url, filename) => {
    if (url) {
      window.open(fileUrl(url), '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="container mx-auto p-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold mb-6">የእኔ ማመልከቻ / My Applications</h2>
        
        {loading ? (
          <p className="text-center py-8">Loading...</p>
        ) : applications.length === 0 ? (
          <p className="text-center py-8 text-gray-500">No applications found</p>
        ) : (
          <div className="space-y-4">
            {editingApp && (
              <div className="border-2 border-amber-400 rounded-lg p-4 bg-amber-50">
                <div className="flex justify-between items-center mb-3">
                  <p className="font-bold">Correct &amp; Resubmit: {editingApp.reference_no}</p>
                  <button onClick={() => setEditingApp(null)} className="px-3 py-1 border rounded hover:bg-gray-100 text-sm">Cancel</button>
                </div>
                {editingApp.serviceType === 'operator-change-license' && (
                  <OperatorChangeLicenseForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} />
                )}
                {editingApp.serviceType === 'diversion-license' && (
                  <DiversionLicenseForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} />
                )}
                {editingApp.serviceType === 'gas-station-transfer' && (
                  <GasStationTransferForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} />
                )}
                {editingApp.serviceType === 'new-fuel-company' && (
                  <NewFuelCompanyForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} />
                )}
                {editingApp.serviceType === 'new-gas-import' && (
                  <NewGasImportForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} title="አዲስ የጋዝ አስመጪነት ብቃትና ፈቃድ አገልግሎት / New Gas Import Competency and Licensing Service" />
                )}
                {editingApp.serviceType === 'new-oil-exporter' && (
                  <NewOilExporterForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} title="አዲስ የነዳጅ ላኪነት ብቃትና ፈቃድ አገልግሎት / New Fuel Exporter Competency and Licensing Service" />
                )}
                {editingApp.serviceType === 'new-fuel-refinery' && (
                  <NewFuelRefineryForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} title="አዲስ የነዳጅ ማጣራት ብቃትና ፈቃድ አገልግሎት / New Fuel Refining (Refinery) Competency and Licensing Service" />
                )}
                {editingApp.serviceType === 'new-fuel-transportation' && (
                  <NewFuelTransportationForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} title="አዲስ የነዳጅ ማጓጓዝ ብቃትና ፈቃድ አገልግሎት / New Fuel Transportation Competency and Licensing Service" />
                )}
                {editingApp.serviceType === 'gas-station-retail-renewal' && (
                  <GasStationRetailRenewalForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} />
                )}
                {editingApp.serviceType === 'fuel-distributor-renewal' && (
                  <FuelDistributorRenewalForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} />
                )}
                {editingApp.serviceType === 'fuel-import-renewal' && (
                  <FuelImportRenewalForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} />
                )}
                {editingApp.serviceType === 'gas-import-renewal' && (
                  <GasImportRenewalForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} />
                )}
                {editingApp.serviceType === 'oil-exporter-renewal' && (
                  <OilExporterRenewalForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} />
                )}
                {editingApp.serviceType === 'fuel-refinery-renewal' && (
                  <FuelRefineryRenewalForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} />
                )}
                {editingApp.serviceType === 'new-gas-station' && (
                  <NewGasStationConnectionForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} />
                )}
                {editingApp.serviceType === 'new-depot-fuel-cargo' && (
                  <NewDepotFuelCargoForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} />
                )}
                {editingApp.serviceType === 'new-gas-station-retail' && (
                  <NewGasStationRetailForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} title="አዲስ የነዳጅ ማደያ ችርቻሮ ብቃትና ፈቃድ አገልግሎት / New Fuel Station Retail Competency and Licensing Service" />
                )}
                {editingApp.serviceType === 'new-fuel-distributor' && (
                  <NewFuelDistributorForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} title="አዲስ የነዳጅ አከፋፋይ ብቃትና ፈቃድ አገልግሎት / New Fuel Distributor Competency and Licensing Service" />
                )}
                {editingApp.serviceType === 'new-fuel-import' && (
                  <NewFuelImportForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} title="አዲስ የነዳጅ አስመጪነት ብቃትና ፈቃድ አገልግሎት / New Fuel Import Competency and Licensing Service" />
                )}
                {editingApp.serviceType === 'fuel-transportation-renewal' && (
                  <FuelTransportationRenewalForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} />
                )}
                {editingApp.serviceType === 'fuel-company-renewal' && (
                  <RenewalServiceForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} title="የነዳጅ አከፋፋይ ኩባንያ ትስስር ፈቃድ እድሳት አገልግሎት / Service for the Renewal of Fuel Distributor Company Connection Permit" />
                )}
                {editingApp.serviceType === 'fuel-company-transfer' && (
                  <TransferServiceForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} title="የነዳጅ አከፋፋይ ኩባንያ ትስስር ፈቃድ ዝውውር አገልግሎት / Service for the Transfer of Fuel Distributor Company Connection permit" />
                )}
                {editingApp.serviceType === 'gas-station-renewal' && (
                  <RenewalServiceForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} title="የነዳጅ ማደያ ግንኙነት ፍቃድ እድሳት አገልግሎት / Service for the renewal of Gas Station Permit" />
                )}
                {editingApp.serviceType === 'gas-station-connection-transfer' && (
                  <TransferServiceForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} title="የነዳጅ ማደያ ግንኙነት ፍቃድ ዝውውር አገልግሎት / Service for the transfer of Gas Station Permit" />
                )}
                {editingApp.serviceType === 'depot-fuel-cargo-renewal' && (
                  <RenewalServiceForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} title="አዲስ ከዲፓ የነዳጅ ጭነት ትስስር ፈቃድ እድሳት አገልግሎት / Service for the Renewal of Fuel Loading Connection Permit from Depot" />
                )}
                {editingApp.serviceType === 'depot-fuel-cargo-transfer' && (
                  <TransferServiceForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} title="የነዳጅ ማጠራቀሚያ ግንኙነት ፍቃድ ዝውውር አገልግሎት / Service for the transfer of Fuel Loading Connection Permit from Depot" />
                )}
                {editingApp.serviceType === 'Fuel_Station_Retail_Construction_Permit' && (
                  <NewGasStationRetailForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} title="የነዳጅ ማደያ ችርቻሮ ግንባታ ፈቃድ አገልግሎት / Fuel Station Retail Construction Permit Licensing Service" />
                )}
                {editingApp.serviceType === 'Fuel_Distributor_Construction_Permit' && (
                  <NewFuelDistributorForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} title="የነዳጅ አከፋፋይ ግንባታ ፈቃድ አገልግሎት / Fuel Distributor Construction Permit Licensing Service" />
                )}
                {editingApp.serviceType === 'Fuel_Importer_ Construction_Permit' && (
                  <NewFuelImportForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} title="የነዳጅ አስመጪነት ግንባታ ፈቃድ አገልግሎት / Fuel Importer Construction Permit Licensing Service" />
                )}
                {editingApp.serviceType === 'Gas_Importer_Construction_Permit' && (
                  <NewGasImportForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} title="የጋዝ አስመጪነት ግንባታ ፈቃድ አገልግሎት / Gas Importer Construction Permit Licensing Service" />
                )}
                {editingApp.serviceType === 'Fuel_Exporter_Construction_permit' && (
                  <NewOilExporterForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} title="የነዳጅ ላኪነት ግንባታ ፈቃድ አገልግሎት / Fuel Exporter Construction Permit Licensing Service" />
                )}
                {editingApp.serviceType === 'Fuel_Refining_Construction_permit' && (
                  <NewFuelRefineryForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} title="Fuel Refining Construction Permit Licensing Service" />
                )}
                {editingApp.serviceType === 'fuel-transportation-construction' && (
                  <NewFuelTransportationForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} title="Fuel Transportation Construction Permit Licensing Service" />
                )}
                {editingApp.serviceType === 'direct-fuel-beneficiary' && (
                  <DirectFuelForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} />
                )}
                {editingApp.serviceType === 'fuel-order-cancellation' && (
                  <DirectFuelForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} title="የነዳጅ ጭነት ትእዛዝ ማቋረጥ / Fuel Order Cancellation Service" />
                )}
                {editingApp.serviceType === 'fuel-order-cancellation-service' && (
                  <FuelOrderCancellationServiceForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} title="የነዳጅ ጭነት ትእዛዝ አገልግሎት ማቋረጥ / Fuel delivery order cancellation service" />
                )}
                {editingApp.serviceType === 'vehicle-breakdown-reporting' && (
                  <VehicleBreakdownReportingForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} title="ብልሽት የገጠመውን ተሸከርካሪ ማሳወቅ አገልግሎት / Vehicle Breakdown Reporting Service" />
                )}
                {editingApp.serviceType === 'fuel-delivery-extension' && (
                  <FuelDeliveryExtensionForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} title="የነዳጅ አቅርቦት ማራዘም ጥያቄ አገልግሎት / Fuel Delivery Extension Service" />
                )}
                {editingApp.serviceType === 'electric-vehicle-charging' && (
                  <ElectricVehicleChargingForm onSubmit={handleResubmit} onCancel={() => setEditingApp(null)} initialData={editingApp} isEdit={true} title="የኤሌክትሪክ ተሽከርካሪ ቻርጅ መሙሌት አገልግሎት / Electric Vehicle Charging Service License Request" />
                )}
              </div>
            )}
            {applications.map((app) => (
              <div key={`${app.serviceType}-${app.id}`} className="border rounded-lg p-4 hover:shadow-md transition">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="font-bold text-lg">{app.serviceName}</p>
                    <p className="text-sm text-gray-500">Reference: {app.reference_no}</p>
                    <p className="text-sm text-gray-500">Submitted: {new Date(app.created_at || app.request_date).toLocaleDateString()}</p>
                    {app.serviceType === 'gas-station-transfer' && app.supplier_name && (
                      <p className="text-sm text-gray-500">የነዳጅ አቅራቢ ድርጅት ሥም / Supplier: {app.supplier_name}</p>
                    )}
                    {app.serviceType === 'gas-station-transfer' && app.fuel_retail_price_from && (
                      <p className="text-sm text-gray-500">የነዳጅ ችርቻሮ ዋጋ የሚጀምርበት ቀን / Fuel price from: {new Date(app.fuel_retail_price_from).toLocaleDateString()}</p>
                    )}
                    {app.serviceType === 'gas-station-transfer' && app.fuel_retail_price_to && (
                      <p className="text-sm text-gray-500">የነዳጅ ችርቻሮ ዋጋ የሚያልቅበት ቀን / Fuel price to: {new Date(app.fuel_retail_price_to).toLocaleDateString()}</p>
                    )}
                    {app.serviceType === 'operator-change-license' && app.station_type && (
                      <p className="text-sm text-gray-500">የማደያ አይነት / Station type: {app.station_type}</p>
                    )}
                    {app.serviceType === 'operator-change-license' && app.site_area && (
                      <p className="text-sm text-gray-500">የቦታው ስፋት / Site area: {app.site_area}</p>
                    )}
                  </div>
                  {getStatusBadge(app.status)}
                </div>
                
                {(((app.status === 'returned' || app.status === 'pending') && app.response_reason)
                  || (app.status === 'paid' && app.response_reason && app.customerResponses?.some(response => response.type === 'Correction' && response.message === app.response_reason))) && (
                  <div className="mt-2 p-3 bg-amber-50 rounded border border-amber-300">
                    <p className="text-sm font-bold text-amber-800 mb-1">⚠ Returned for Correction</p>
                    <p className="text-sm text-amber-800">{app.response_reason}</p>
                    {EDITABLE_SERVICES.includes(app.serviceType) ? (
                      <button
                        onClick={() => setEditingApp(app)}
                        className="mt-2 bg-amber-500 text-white px-4 py-2 rounded text-sm font-medium hover:bg-amber-600"
                      >
                        Edit &amp; Resubmit
                      </button>
                    ) : (
                      <p className="text-xs text-amber-700 mt-1">Please correct the information and resubmit your application.</p>
                    )}
                  </div>
                )}
                {app.response_reason && app.status !== 'payment_pending' && app.status !== 'returned' && app.status !== 'pending' && (
                  <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                    <p className="text-sm text-blue-700"><strong>Response:</strong> {app.response_reason}</p>
                  </div>
                )}
                {app.status === 'payment_pending' && app.response_reason && (
                  <div className="mt-2 p-2 bg-purple-50 rounded border border-purple-200">
                    <p className="text-sm text-purple-700"><strong>Payment Instructions:</strong> {app.response_reason}</p>
                  </div>
                )}
                {app.customerResponses?.filter(response => response.message !== app.response_reason).map((response, index) => (
                  <div key={`customer-response-${index}`} className={`mt-2 p-2 rounded border ${response.type === 'Payment' ? 'bg-purple-50 border-purple-200' : 'bg-amber-50 border-amber-200'}`}>
                    <p className={`text-sm ${response.type === 'Payment' ? 'text-purple-700' : 'text-amber-800'}`}>
                      <strong>{response.type === 'Payment' ? 'Payment Instructions' : response.type === 'Correction' ? 'Correction Request' : 'Message from PEA'}:</strong> {response.message}
                    </p>
                  </div>
                ))}
                {app.rejection_reason && (
                  <div className="mt-2 p-2 bg-red-50 rounded border border-red-200">
                    <p className="text-sm text-red-700"><strong>Rejection Reason:</strong> {app.rejection_reason}</p>
                  </div>
                )}
                
                {(app.status === 'payment_pending' || (app.status === 'returned' && app.customerResponses?.some(response => response.type === 'Payment'))) && !app.payment_receipt && (
                  <div className="mt-3 p-3 bg-purple-50 rounded border border-purple-200">
                    <p className="text-sm font-medium text-purple-800 mb-2">Upload Payment Receipt</p>
                    <div className="flex gap-2 items-center">
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={(e) => { const f = e.target.files[0]; if (f && f.size === 0) { toast.error('The selected file is empty. Please choose a valid PDF document.'); e.target.value = ''; return; } setReceiptFile({ ...receiptFile, [`${app.serviceType}-${app.id}`]: f }); }}
                        className="flex-1 border rounded px-3 py-2 text-sm"
                      />
                      <button
                        onClick={() => handleUploadReceipt(app)}
                        disabled={uploadingReceipt === `${app.serviceType}-${app.id}`}
                        className="bg-purple-600 text-white px-4 py-2 rounded text-sm hover:bg-purple-700 disabled:opacity-50"
                      >
                        {uploadingReceipt === `${app.serviceType}-${app.id}` ? 'Uploading...' : 'Upload Receipt'}
                      </button>
                    </div>
                  </div>
                )}
                
                <div className="mt-3 pt-3 border-t">
                  <p className="text-sm font-medium text-gray-600 mb-2">Attachments:</p>
                  <div className="flex flex-wrap gap-2">
                    {app.application_letter && (
                      <button onClick={() => downloadFile(app.application_letter, 'Application Letter')} className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1">
                        📄 Application Letter
                      </button>
                    )}
                    {app.support_letter && (
                      <button onClick={() => downloadFile(app.support_letter, 'Support Letter')} className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1">
                        📄 Support Letter
                      </button>
                    )}
                    {app.aggrement_letter && (
                      <button onClick={() => downloadFile(app.aggrement_letter, 'Agreement Letter')} className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1">
                        📄 Agreement Letter
                      </button>
                    )}
                    {app.site_plan_drawing && (
                      <button onClick={() => downloadFile(app.site_plan_drawing, 'Site Plan and Design Drawing')} className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1">
                        📄 Site Plan and Design Drawing
                      </button>
                    )}
                    {app.approved_letter && (
                      <button onClick={() => downloadFile(app.approved_letter, 'Letter')} className="text-green-500 hover:text-green-800 text-sm flex items-center gap-1">
                        📄 {app.status === 'rejected' ? 'Rejection Letter' : 'Approval Letter'}
                      </button>
                    )}
                    {app.client_letter && (
                      <button onClick={() => downloadFile(app.client_letter, app.client_letter_name || 'Letter from PEA')} className="text-blue-700 hover:text-blue-900 text-sm flex items-center gap-1 font-semibold">
                        📩 {app.client_letter_name || 'Letter from PEA'}
                      </button>
                    )}
                    {app.payment_receipt && (
                      <button onClick={() => downloadFile(app.payment_receipt, 'Payment Receipt')} className="text-purple-600 hover:text-purple-800 text-sm flex items-center gap-1">
                        🧾 Payment Receipt
                      </button>
                    )}
                    {app.certificate_path && (
                      <a href={fileUrl(app.certificate_path)} target="_blank" rel="noopener noreferrer" className="text-green-700 hover:text-green-900 text-sm flex items-center gap-1">
                        🏆 Certificate
                      </a>
                    )}
                    {app.additional_attachments && (
                      <>
                        {(() => {
                          try {
                            const attachments = typeof app.additional_attachments === 'string' 
                              ? JSON.parse(app.additional_attachments) 
                              : app.additional_attachments;
                            return attachments.map((att, idx) => (
                              <button key={idx} onClick={() => downloadFile(att.file, att.name)} className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1">
                                📎 {att.name || `Attachment ${idx + 1}`}
                              </button>
                            ));
                          } catch (e) {
                            return null;
                          }
                        })()}
                      </>
                    )}
                    {!app.application_letter && !app.support_letter && !app.aggrement_letter && !app.site_plan_drawing && !app.approved_letter && !app.client_letter && !app.payment_receipt && !app.certificate_path && !app.additional_attachments && (
                      <span className="text-sm text-gray-400">No attachments</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SearchStatus() {
  const [referenceNo, setReferenceNo] = useState('');
  const [results, setResults] = useState([]);
  const [gsTransferResults, setGsTransferResults] = useState([]);
  const [diversionResults, setDiversionResults] = useState([]);
  const [operatorResults, setOperatorResults] = useState([]);
  const [nfcResults, setNfcResults] = useState([]);
  const [renewalResults, setRenewalResults] = useState([]);
  const [transferResults, setTransferResults] = useState([]);
  const [newGasStationResults, setNewGasStationResults] = useState([]);
  const [gasStationRenewalResults, setGasStationRenewalResults] = useState([]);
  const [gasStationConnectionTransferResults, setGasStationConnectionTransferResults] = useState([]);
  const [newDepotFuelCargoResults, setNewDepotFuelCargoResults] = useState([]);
  const [depotFuelCargoRenewalResults, setDepotFuelCargoRenewalResults] = useState([]);
  const [depotFuelCargoTransferResults, setDepotFuelCargoTransferResults] = useState([]);
  const [new_gas_station_retailResults, setNewGasStationRetailResults] = useState([]);
  const [new_fuel_distributorResults, setNewFuelDistributorResults] = useState([]);
  const [new_fuel_importResults, setNewFuelImportResults] = useState([]);
  const [new_gas_importResults, setNewGasImportResults] = useState([]);
  const [new_oil_exporterResults, setNewOilExporterResults] = useState([]);
  const [new_fuel_refineryResults, setNewFuelRefineryResults] = useState([]);
  const [new_fuel_transportationResults, setNewFuelTransportationResults] = useState([]);
  const [fuel_order_cancellation_serviceResults, setFuelOrderCancellationServiceResults] = useState([]);
  const [vehicleBreakdownReportingResults, setVehicleBreakdownReportingResults] = useState([]);
  const [fuelDeliveryExtensionResults, setFuelDeliveryExtensionResults] = useState([]);
  const [electricVehicleChargingResults, setElectricVehicleChargingResults] = useState([]);
  const [gas_station_retail_renewalResults, setGasStationRetailRenewalResults] = useState([]);
  const [fuel_distributor_renewalResults, setFuelDistributorRenewalResults] = useState([]);
  const [fuel_import_renewalResults, setFuelImportRenewalResults] = useState([]);
  const [gas_import_renewalResults, setGasImportRenewalResults] = useState([]);
  const [oil_exporter_renewalResults, setOilExporterRenewalResults] = useState([]);
  const [fuel_refinery_renewalResults, setFuelRefineryRenewalResults] = useState([]);
  const [fuel_transportation_renewalResults, setFuelTransportationRenewalResults] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState(null);
  const [editingGsId, setEditingGsId] = useState(null);
  const [editingGsData, setEditingGsData] = useState(null);
  const [editingDivId, setEditingDivId] = useState(null);
  const [editingDivData, setEditingDivData] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
  }, []);

  const handleSearch = async () => {
    const currentUser = JSON.parse(localStorage.getItem('user'));
    
    if (!referenceNo.trim() && !currentUser) {
      toast.warning('Please enter a reference number or login to view your applications');
      return;
    }
    
    try {
      let response, gsResponse, divResponse, opResponse, nfcResponse, renewalResponse, transferResponse, newGasStationResponse, gasStationRenewalResponse, gasStationConnectionTransferResponse, newDepotFuelCargoResponse, depotFuelCargoRenewalResponse, depotFuelCargoTransferResponse, newGasStationRetailResponse, newFuelDistributorResponse, newFuelImportResponse, newGasImportResponse, newOilExporterResponse, newFuelRefineryResponse, newFuelTransportationResponse, gasStationRetailRenewalResponse, fuelDistributorRenewalResponse, fuelImportRenewalResponse, gasImportRenewalResponse, oilExporterRenewalResponse, fuelRefineryRenewalResponse, fuelTransportationRenewalResponse, fuelOrderCancellationServiceResponse, vehicleBreakdownReportingResponse, fuelDeliveryExtensionResponse, electricVehicleChargingResponse
      
      if (currentUser) {
        response = await axios.get(`${API_URL}/direct-fuel-beneficiary?user_id=${currentUser.id}`);
        gsResponse = await axios.get(`${API_URL}/gas-station-transfer?user_id=${currentUser.id}`);
        divResponse = await axios.get(`${API_URL}/diversion-license?user_id=${currentUser.id}`);
        opResponse = await axios.get(`${API_URL}/operator-change-license?user_id=${currentUser.id}`);
        nfcResponse = await axios.get(`${API_URL}/new-fuel-company?user_id=${currentUser.id}`);
        renewalResponse = await axios.get(`${API_URL}/fuel-company-renewal?user_id=${currentUser.id}`);
        transferResponse = await axios.get(`${API_URL}/fuel-company-transfer?user_id=${currentUser.id}`);
        newGasStationResponse = await axios.get(`${API_URL}/new-gas-station?user_id=${currentUser.id}`);
        gasStationRenewalResponse = await axios.get(`${API_URL}/gas-station-renewal?user_id=${currentUser.id}`);
        gasStationConnectionTransferResponse = await axios.get(`${API_URL}/gas-station-connection-transfer?user_id=${currentUser.id}`);
        newDepotFuelCargoResponse = await axios.get(`${API_URL}/new-depot-fuel-cargo?user_id=${currentUser.id}`);
        depotFuelCargoRenewalResponse = await axios.get(`${API_URL}/depot-fuel-cargo-renewal?user_id=${currentUser.id}`);
        depotFuelCargoTransferResponse = await axios.get(`${API_URL}/depot-fuel-cargo-transfer?user_id=${currentUser.id}`);
        newGasStationRetailResponse = await axios.get(`${API_URL}/new-gas-station-retail?user_id=${currentUser.id}`);
        newFuelDistributorResponse = await axios.get(`${API_URL}/new-fuel-distributor?user_id=${currentUser.id}`);
        newFuelImportResponse = await axios.get(`${API_URL}/new-fuel-import?user_id=${currentUser.id}`);
        newGasImportResponse = await axios.get(`${API_URL}/new-gas-import?user_id=${currentUser.id}`);
        newOilExporterResponse = await axios.get(`${API_URL}/new-oil-exporter?user_id=${currentUser.id}`);
        newFuelRefineryResponse = await axios.get(`${API_URL}/new-fuel-refinery?user_id=${currentUser.id}`);
        newFuelTransportationResponse = await axios.get(`${API_URL}/new-fuel-transportation?user_id=${currentUser.id}`);
        gasStationRetailRenewalResponse = await axios.get(`${API_URL}/gas-station-retail-renewal?user_id=${currentUser.id}`);
        fuelDistributorRenewalResponse = await axios.get(`${API_URL}/fuel-distributor-renewal?user_id=${currentUser.id}`);
        fuelImportRenewalResponse = await axios.get(`${API_URL}/fuel-import-renewal?user_id=${currentUser.id}`);
        gasImportRenewalResponse = await axios.get(`${API_URL}/gas-import-renewal?user_id=${currentUser.id}`);
        oilExporterRenewalResponse = await axios.get(`${API_URL}/oil-exporter-renewal?user_id=${currentUser.id}`);
        fuelRefineryRenewalResponse = await axios.get(`${API_URL}/fuel-refinery-renewal?user_id=${currentUser.id}`);
        fuelTransportationRenewalResponse = await axios.get(`${API_URL}/fuel-transportation-renewal?user_id=${currentUser.id}`);
        fuelOrderCancellationServiceResponse = await axios.get(`${API_URL}/fuel-order-cancellation-service?user_id=${currentUser.id}`);
        vehicleBreakdownReportingResponse = await axios.get(`${API_URL}/vehicle-breakdown-reporting?user_id=${currentUser.id}`);
        fuelDeliveryExtensionResponse = await axios.get(`${API_URL}/fuel-delivery-extension?user_id=${currentUser.id}`);
        electricVehicleChargingResponse = await axios.get(`${API_URL}/electric-vehicle-charging?user_id=${currentUser.id}`);
        
        if (referenceNo.trim()) {
          response.data = response.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          gsResponse.data = gsResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          divResponse.data = divResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          opResponse.data = opResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          nfcResponse.data = nfcResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          renewalResponse.data = renewalResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          transferResponse.data = transferResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          newGasStationResponse.data = newGasStationResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          gasStationRenewalResponse.data = gasStationRenewalResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          gasStationConnectionTransferResponse.data = gasStationConnectionTransferResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          newDepotFuelCargoResponse.data = newDepotFuelCargoResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          depotFuelCargoRenewalResponse.data = depotFuelCargoRenewalResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          depotFuelCargoTransferResponse.data = depotFuelCargoTransferResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          newGasStationRetailResponse.data = newGasStationRetailResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          newFuelDistributorResponse.data = newFuelDistributorResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          newFuelImportResponse.data = newFuelImportResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          newGasImportResponse.data = newGasImportResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          newOilExporterResponse.data = newOilExporterResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          newFuelRefineryResponse.data = newFuelRefineryResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          newFuelTransportationResponse.data = newFuelTransportationResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          gasStationRetailRenewalResponse.data = gasStationRetailRenewalResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          fuelDistributorRenewalResponse.data = fuelDistributorRenewalResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          fuelImportRenewalResponse.data = fuelImportRenewalResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          gasImportRenewalResponse.data = gasImportRenewalResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          oilExporterRenewalResponse.data = oilExporterRenewalResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          fuelRefineryRenewalResponse.data = fuelRefineryRenewalResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          fuelTransportationRenewalResponse.data = fuelTransportationRenewalResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          fuelOrderCancellationServiceResponse.data = fuelOrderCancellationServiceResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          vehicleBreakdownReportingResponse.data = vehicleBreakdownReportingResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          fuelDeliveryExtensionResponse.data = fuelDeliveryExtensionResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
          electricVehicleChargingResponse.data = electricVehicleChargingResponse.data.filter(r => r.reference_no.toLowerCase().includes(referenceNo.toLowerCase()));
        }
      } else {
        response = await axios.get(`${API_URL}/direct-fuel-beneficiary?reference_no=${referenceNo}`);
        gsResponse = await axios.get(`${API_URL}/gas-station-transfer?reference_no=${referenceNo}`);
        divResponse = await axios.get(`${API_URL}/diversion-license?reference_no=${referenceNo}`);
        opResponse = await axios.get(`${API_URL}/operator-change-license?reference_no=${referenceNo}`);
        nfcResponse = await axios.get(`${API_URL}/new-fuel-company?reference_no=${referenceNo}`);
        renewalResponse = await axios.get(`${API_URL}/fuel-company-renewal?reference_no=${referenceNo}`);
        transferResponse = await axios.get(`${API_URL}/fuel-company-transfer?reference_no=${referenceNo}`);
        newGasStationResponse = await axios.get(`${API_URL}/new-gas-station?reference_no=${referenceNo}`);
        gasStationRenewalResponse = await axios.get(`${API_URL}/gas-station-renewal?reference_no=${referenceNo}`);
        gasStationConnectionTransferResponse = await axios.get(`${API_URL}/gas-station-connection-transfer?reference_no=${referenceNo}`);
        newDepotFuelCargoResponse = await axios.get(`${API_URL}/new-depot-fuel-cargo?reference_no=${referenceNo}`);
        depotFuelCargoRenewalResponse = await axios.get(`${API_URL}/depot-fuel-cargo-renewal?reference_no=${referenceNo}`);
        depotFuelCargoTransferResponse = await axios.get(`${API_URL}/depot-fuel-cargo-transfer?reference_no=${referenceNo}`);
        newGasStationRetailResponse = await axios.get(`${API_URL}/new-gas-station-retail?reference_no=${referenceNo}`);
        newFuelDistributorResponse = await axios.get(`${API_URL}/new-fuel-distributor?reference_no=${referenceNo}`);
        newFuelImportResponse = await axios.get(`${API_URL}/new-fuel-import?reference_no=${referenceNo}`);
        newGasImportResponse = await axios.get(`${API_URL}/new-gas-import?reference_no=${referenceNo}`);
        newOilExporterResponse = await axios.get(`${API_URL}/new-oil-exporter?reference_no=${referenceNo}`);
        newFuelRefineryResponse = await axios.get(`${API_URL}/new-fuel-refinery?reference_no=${referenceNo}`);
        newFuelTransportationResponse = await axios.get(`${API_URL}/new-fuel-transportation?reference_no=${referenceNo}`);
        gasStationRetailRenewalResponse = await axios.get(`${API_URL}/gas-station-retail-renewal?reference_no=${referenceNo}`);
        fuelDistributorRenewalResponse = await axios.get(`${API_URL}/fuel-distributor-renewal?reference_no=${referenceNo}`);
        fuelImportRenewalResponse = await axios.get(`${API_URL}/fuel-import-renewal?reference_no=${referenceNo}`);
        gasImportRenewalResponse = await axios.get(`${API_URL}/gas-import-renewal?reference_no=${referenceNo}`);
        oilExporterRenewalResponse = await axios.get(`${API_URL}/oil-exporter-renewal?reference_no=${referenceNo}`);
        fuelRefineryRenewalResponse = await axios.get(`${API_URL}/fuel-refinery-renewal?reference_no=${referenceNo}`);
        fuelTransportationRenewalResponse = await axios.get(`${API_URL}/fuel-transportation-renewal?reference_no=${referenceNo}`);
        fuelOrderCancellationServiceResponse = await axios.get(`${API_URL}/fuel-order-cancellation-service?reference_no=${referenceNo}`);
        vehicleBreakdownReportingResponse = await axios.get(`${API_URL}/vehicle-breakdown-reporting?reference_no=${referenceNo}`);
        fuelDeliveryExtensionResponse = await axios.get(`${API_URL}/fuel-delivery-extension?reference_no=${referenceNo}`);
        electricVehicleChargingResponse = await axios.get(`${API_URL}/electric-vehicle-charging?reference_no=${referenceNo}`);
      }
      
      let assignMap = {};
      try {
        const assignRes = await axios.get(`${API_URL}/request-assignments`);
        assignRes.data.forEach(a => {
          assignMap[a.service_type + ':' + a.request_id] = a.assigned_to_name || '';
        });
      } catch (e) {}

      const enrich = (items, svcType, altSvcType) => {
        if (!items) return;
        items.forEach(item => {
          item.assignedToName = assignMap[svcType + ':' + item.id] || (altSvcType ? assignMap[altSvcType + ':' + item.id] : '') || '';
        });
        applyLetterReleaseGate(items, svcType);
      };

      enrich(response.data, 'direct-fuel-beneficiary', 'direct-fuel');
      enrich(gsResponse.data, 'gas-station-transfer');
      enrich(divResponse.data, 'diversion-license');
      enrich(opResponse.data, 'operator-change-license');
      enrich(nfcResponse.data, 'new-fuel-company');
      enrich(renewalResponse.data, 'fuel-company-renewal');
      enrich(transferResponse.data, 'fuel-company-transfer');
      enrich(newGasStationResponse.data, 'new-gas-station');
      enrich(gasStationRenewalResponse.data, 'gas-station-renewal');
      enrich(gasStationConnectionTransferResponse.data, 'gas-station-connection-transfer');
      enrich(newDepotFuelCargoResponse.data, 'new-depot-fuel-cargo');
      enrich(depotFuelCargoRenewalResponse.data, 'depot-fuel-cargo-renewal');
      enrich(depotFuelCargoTransferResponse.data, 'depot-fuel-cargo-transfer');
      enrich(newGasStationRetailResponse.data, 'new-gas-station-retail');
      enrich(newFuelDistributorResponse.data, 'new-fuel-distributor');
      enrich(newFuelImportResponse.data, 'new-fuel-import');
      enrich(newGasImportResponse.data, 'new-gas-import');
      enrich(newOilExporterResponse.data, 'new-oil-exporter');
      enrich(newFuelRefineryResponse.data, 'new-fuel-refinery');
      enrich(newFuelTransportationResponse.data, 'new-fuel-transportation');
      enrich(gasStationRetailRenewalResponse.data, 'gas-station-retail-renewal');
      enrich(fuelDistributorRenewalResponse.data, 'fuel-distributor-renewal');
      enrich(fuelImportRenewalResponse.data, 'fuel-import-renewal');
      enrich(gasImportRenewalResponse.data, 'gas-import-renewal');
      enrich(oilExporterRenewalResponse.data, 'oil-exporter-renewal');
      enrich(fuelRefineryRenewalResponse.data, 'fuel-refinery-renewal');
      enrich(fuelTransportationRenewalResponse.data, 'fuel-transportation-renewal');
      enrich(fuelOrderCancellationServiceResponse.data, 'fuel-order-cancellation-service');
      enrich(vehicleBreakdownReportingResponse.data, 'vehicle-breakdown-reporting');
      enrich(fuelDeliveryExtensionResponse.data, 'fuel-delivery-extension');
      enrich(electricVehicleChargingResponse.data, 'electric-vehicle-charging');

      setResults(response.data);
      setGsTransferResults(gsResponse.data);
      setDiversionResults(divResponse.data);
      setOperatorResults(opResponse.data);
      setNfcResults(nfcResponse.data);
      setRenewalResults(renewalResponse.data);
      setTransferResults(transferResponse.data);
      setNewGasStationResults(newGasStationResponse.data);
      setGasStationRenewalResults(gasStationRenewalResponse.data);
      setGasStationConnectionTransferResults(gasStationConnectionTransferResponse.data);
      setNewDepotFuelCargoResults(newDepotFuelCargoResponse.data);
      setDepotFuelCargoRenewalResults(depotFuelCargoRenewalResponse.data);
      setDepotFuelCargoTransferResults(depotFuelCargoTransferResponse.data);
      setNewGasStationRetailResults(newGasStationRetailResponse.data);
      setNewFuelDistributorResults(newFuelDistributorResponse.data);
      setNewFuelImportResults(newFuelImportResponse.data);
      setNewGasImportResults(newGasImportResponse.data);
      setNewOilExporterResults(newOilExporterResponse.data);
      setNewFuelRefineryResults(newFuelRefineryResponse.data);
      setNewFuelTransportationResults(newFuelTransportationResponse.data);
      setGasStationRetailRenewalResults(gasStationRetailRenewalResponse.data);
      setFuelDistributorRenewalResults(fuelDistributorRenewalResponse.data);
      setFuelImportRenewalResults(fuelImportRenewalResponse.data);
      setGasImportRenewalResults(gasImportRenewalResponse.data);
      setOilExporterRenewalResults(oilExporterRenewalResponse.data);
      setFuelRefineryRenewalResults(fuelRefineryRenewalResponse.data);
      setFuelTransportationRenewalResults(fuelTransportationRenewalResponse.data);
      setFuelOrderCancellationServiceResults(fuelOrderCancellationServiceResponse.data);
      setVehicleBreakdownReportingResults(vehicleBreakdownReportingResponse.data);
      setFuelDeliveryExtensionResults(fuelDeliveryExtensionResponse.data);
      setElectricVehicleChargingResults(electricVehicleChargingResponse.data);
      
      if (response.data.length === 0 && gsResponse.data.length === 0 && divResponse.data.length === 0 && opResponse.data.length === 0 && nfcResponse.data.length === 0 && renewalResponse.data.length === 0 && transferResponse.data.length === 0 && newGasStationResponse.data.length === 0 && gasStationRenewalResponse.data.length === 0 && gasStationConnectionTransferResponse.data.length === 0 && newDepotFuelCargoResponse.data.length === 0 && depotFuelCargoRenewalResponse.data.length === 0 && depotFuelCargoTransferResponse.data.length === 0 && newGasStationRetailResponse.data.length === 0 && newFuelDistributorResponse.data.length === 0 && newFuelImportResponse.data.length === 0 && newGasImportResponse.data.length === 0 && newOilExporterResponse.data.length === 0 && newFuelRefineryResponse.data.length === 0 && newFuelTransportationResponse.data.length === 0 && gasStationRetailRenewalResponse.data.length === 0 && fuelDistributorRenewalResponse.data.length === 0 && fuelImportRenewalResponse.data.length === 0 && gasImportRenewalResponse.data.length === 0 && oilExporterRenewalResponse.data.length === 0 && fuelRefineryRenewalResponse.data.length === 0 && fuelTransportationRenewalResponse.data.length === 0 && fuelOrderCancellationServiceResponse.data.length === 0 && vehicleBreakdownReportingResponse.data.length === 0 && fuelDeliveryExtensionResponse.data.length === 0 && electricVehicleChargingResponse.data.length === 0) {
        toast.info('No results found');
      }
    } catch (error) {
      toast.error('Failed to search');
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setEditingData(item);
  };

  const handleGsEdit = (item) => {
    setEditingGsId(item.id);
    setEditingGsData(item);
  };

  const handleDivEdit = (item) => {
    setEditingDivId(item.id);
    setEditingDivData(item);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingData(null);
    setEditingGsId(null);
    setEditingGsData(null);
    setEditingDivId(null);
    setEditingDivData(null);
  };

  const handleUpdateSuccess = (updatedItem) => {
    toast.success('Request updated successfully');
    setEditingId(null);
    setEditingData(null);
    handleSearch();
  };

  const handleGsUpdateSuccess = (updatedItem) => {
    toast.success('Request updated successfully');
    setEditingGsId(null);
    setEditingGsData(null);
    handleSearch();
  };

  const handleDivUpdateSuccess = (updatedItem) => {
    toast.success('Request updated successfully');
    setEditingDivId(null);
    setEditingDivData(null);
    handleSearch();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  const StatusCell = ({ item }) => (
    <td className="px-4 py-3">
      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(item.status || 'pending')}`}>
        {item.status}
      </span>
      {item.response_reason && (
        <div className="text-xs text-blue-600 mt-1">Response: {item.response_reason}</div>
      )}
      {item.status === 'rejected' && item.rejection_reason && (
        <div className="text-xs text-red-600 mt-1">Reason: {item.rejection_reason}</div>
      )}
      {item.assignedToName && (
        <div className="text-xs text-blue-600 mt-1">Handled by: {item.assignedToName}</div>
      )}
    </td>
  );

  const downloadFile = (url, filename) => {
    if (url) {
      window.open(fileUrl(url), '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="container mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6">የጠየቁትን አገልግሎት ይከታተሉ / Check Your Service Status</h2>
      
      {!user && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-blue-800">Please <strong>login</strong> to view all your applications. Without login, you can only search by reference number.</p>
        </div>
      )}
      
      <div className="flex gap-2 mb-6">
        <input
          type="text"
          value={referenceNo}
          onChange={(e) => setReferenceNo(e.target.value)}
          placeholder="Enter reference number (e.g., DFB-XXXXXXXX, GST-XXXXXXXX, DIV-XXXXXXXX)"
          className="flex-1 border rounded px-4 py-2"
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
        />
        <button onClick={handleSearch} className="bg-[#4CAF50] text-white px-6 py-2 rounded hover:bg-green-400 flex items-center gap-2">
          <FaSearch /> Search
        </button>
      </div>

      {editingData && editingId && (
        <div className="mb-6">
          <RenewalServiceForm 
            onSubmit={(formData, id) => {
              axios.put(`${API_URL}/direct-fuel-beneficiary/${id}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
              })
              .then(res => {
                handleUpdateSuccess(res.data);
              })
              .catch(err => {
                toast.error('Failed to update request');
              });
            }}
            onCancel={handleCancelEdit}
            initialData={editingData}
            isEdit={true}
            lookupEndpoint="/direct-fuel-beneficiary"
            title="Edit Direct Fuel Beneficiary Renewal"
          />
        </div>
      )}

      {editingGsData && editingGsId && (
        <div className="mb-6">
          <GasStationTransferForm 
            onSubmit={(formData, id) => {
              axios.put(`${API_URL}/gas-station-transfer/${id}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
              })
              .then(res => {
                handleGsUpdateSuccess(res.data);
              })
              .catch(err => {
                toast.error('Failed to update request');
              });
            }}
            onCancel={handleCancelEdit}
            initialData={editingGsData}
            isEdit={true}
          />
        </div>
      )}

      {editingDivData && editingDivId && (
        <div className="mb-6">
          <DiversionLicenseForm 
            onSubmit={(formData, id) => {
              axios.put(`${API_URL}/diversion-license/${id}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
              })
              .then(res => {
                handleDivUpdateSuccess(res.data);
              })
              .catch(err => {
                toast.error('Failed to update request');
              });
            }}
            onCancel={handleCancelEdit}
            initialData={editingDivData}
            isEdit={true}
          />
        </div>
      )}

      {results.length > 0 && !editingId && (
        <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">Direct Fuel Beneficiary Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organization</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Documents</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {results.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <td className="px-4 py-3">{item.service_type || 'Direct Fuel Beneficiary'}</td>
                  <td className="px-4 py-3">{item.organization_name}</td>
                  <td className="px-4 py-3">{item.project_name}</td>
                  <td className="px-4 py-3">{item.product_amount}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {item.application_letter && (
                        <button onClick={() => downloadFile(item.application_letter, 'application')} title="Download Application Letter" className="text-blue-600 hover:text-blue-800">
                          <FaFile />
                        </button>
                      )}
                      {item.support_letter && (
                        <button onClick={() => downloadFile(item.support_letter, 'support')} title="Download Support Letter" className="text-blue-600 hover:text-blue-800">
                          <FaFile />
                        </button>
                      )}
{item.aggrement_letter && (
                      <button onClick={() => downloadFile(item.aggrement_letter, 'agreement')} title="Download Agreement Letter" className="text-blue-600 hover:text-blue-800">
                        <FaFile />
                      </button>
                    )}
                    {item.site_plan_drawing && (
                      <button onClick={() => downloadFile(item.site_plan_drawing, 'site-plan-drawing')} title="Download Site Plan and Design Drawing" className="text-blue-600 hover:text-blue-800">
                        <FaFile />
                      </button>
                    )}
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800">
                        <FaDownload />
                      </button>
                    )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {item.status === 'pending' && (
                      <button onClick={() => handleEdit(item)} className="text-blue-600 hover:text-blue-800 flex items-center gap-1">
                        <FaEdit /> Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {gsTransferResults.length > 0 && !editingGsId && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">Gas Station Transfer Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applicant Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Existing Company</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transfer To</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fuel Price From</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fuel Price To</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {gsTransferResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <td className="px-4 py-3">{item.nameofapplicant}</td>
                  <td className="px-4 py-3">{item.fan_national_id || item.idno}</td>
                  <td className="px-4 py-3">{item.existingcompanyname}</td>
                  <td className="px-4 py-3">{item.transferto}</td>
                  <td className="px-4 py-3">{item.supplier_name}</td>
                  <td className="px-4 py-3">{item.fuel_retail_price_from ? new Date(item.fuel_retail_price_from).toLocaleDateString() : ''}</td>
                  <td className="px-4 py-3">{item.fuel_retail_price_to ? new Date(item.fuel_retail_price_to).toLocaleDateString() : ''}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.status === 'pending' && (
                      <button onClick={() => handleGsEdit(item)} className="text-blue-600 hover:text-blue-800 flex items-center gap-1">
                        <FaEdit /> Edit
                      </button>
                    )}
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800 ml-2">
                        <FaDownload />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {diversionResults.length > 0 && !editingDivId && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">Diversion License Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fuel Distributer</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Manager Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">First Destination</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {diversionResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <td className="px-4 py-3">{item.fuel_distributer_name}</td>
                  <td className="px-4 py-3">{item.manager_name}</td>
                  <td className="px-4 py-3">{item.first_destination_name}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.status === 'pending' && (
                      <button onClick={() => handleDivEdit(item)} className="text-blue-600 hover:text-blue-800 flex items-center gap-1">
                        <FaEdit /> Edit
                      </button>
                    )}
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800 ml-2">
                        <FaDownload />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {operatorResults.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">Operator Change License Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dealer Requester</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Manager Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {operatorResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <td className="px-4 py-3">{item.dealer_requester_name}</td>
                  <td className="px-4 py-3">{item.manager_name}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800">
                        <FaDownload />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {nfcResults.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">New Fuel Distribution Company Connection License Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applicant</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {nfcResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <td className="px-4 py-3">{item.applicant_name || item.company_name}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800">
                        <FaDownload />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {renewalResults.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">Fuel Distribution Company Connection License Renewal Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Renewal Year</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Fee</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Renewal Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {renewalResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <td className="px-4 py-3">{item.renewal_year}</td>
                  <td className="px-4 py-3">{item.payment_fee}</td>
                  <td className="px-4 py-3">{item.renewal_date ? new Date(item.renewal_date).toLocaleDateString() : ''}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800">
                        <FaDownload />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {transferResults.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">Fuel Distribution Company Connection License Transfer Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transfer From</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transfer To</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Fee</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transfer Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {transferResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <td className="px-4 py-3">{item.transfer_from}</td>
                  <td className="px-4 py-3">{item.transfer_to}</td>
                  <td className="px-4 py-3">{item.payment_fee}</td>
                  <td className="px-4 py-3">{item.transfer_date ? new Date(item.transfer_date).toLocaleDateString() : ''}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800">
                        <FaDownload />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {newGasStationResults.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">New Gas Station Connection License Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {newGasStationResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800">
                        <FaDownload />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {gasStationRenewalResults.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">Gas Station Connection License Renewal Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {gasStationRenewalResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800">
                        <FaDownload />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {gasStationConnectionTransferResults.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">Gas Station Connection License Transfer Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {gasStationConnectionTransferResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800">
                        <FaDownload />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {newDepotFuelCargoResults.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">New Depot Fuel Cargo Connection License Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {newDepotFuelCargoResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800">
                        <FaDownload />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {depotFuelCargoRenewalResults.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">Depot Fuel Cargo Connection License Renewal Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Existing Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Request Letter No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Request Letter Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Renewal Year</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Fee</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Renewal Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {depotFuelCargoRenewalResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <td className="px-4 py-3">{item.existing_ref_no}</td>
                  <td className="px-4 py-3">{item.request_letter_no}</td>
                  <td className="px-4 py-3">{item.request_letter_date}</td>
                  <td className="px-4 py-3">{item.renewal_year}</td>
                  <td className="px-4 py-3">{item.payment_fee}</td>
                  <td className="px-4 py-3">{item.renewal_date}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800">
                        <FaDownload />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {depotFuelCargoTransferResults.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">Depot Fuel Cargo Connection License Transfer Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {depotFuelCargoTransferResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800">
                        <FaDownload />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
      {new_gas_station_retailResults.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">New Fuel Station Retail Competency and Licensing Service Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {new_gas_station_retailResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800">
                        <FaDownload />
                      </button>
                    )}
                    {item.certificate_path && (
                      <a href={fileUrl(item.certificate_path)} target="_blank" rel="noopener noreferrer" className="text-green-700 hover:text-green-900 ml-2" title="Download Certificate">🏆</a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {new_fuel_distributorResults.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">New Fuel Distributor Competency and Licensing Service Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {new_fuel_distributorResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800">
                        <FaDownload />
                      </button>
                    )}
                    {item.certificate_path && (
                      <a href={fileUrl(item.certificate_path)} target="_blank" rel="noopener noreferrer" className="text-green-700 hover:text-green-900 ml-2" title="Download Certificate">🏆</a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {new_fuel_importResults.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">New Fuel Import Competency and Licensing Service Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {new_fuel_importResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800">
                        <FaDownload />
                      </button>
                    )}
                    {item.certificate_path && (
                      <a href={fileUrl(item.certificate_path)} target="_blank" rel="noopener noreferrer" className="text-green-700 hover:text-green-900 ml-2" title="Download Certificate">🏆</a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {new_gas_importResults.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">አዲስ የጋዝ አስመጪነት ብቃትና ፈቃድ አገልግሎት / New Gas Import Competency and Licensing Service Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {new_gas_importResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800">
                        <FaDownload />
                      </button>
                    )}
                    {item.certificate_path && (
                      <a href={fileUrl(item.certificate_path)} target="_blank" rel="noopener noreferrer" className="text-green-700 hover:text-green-900 ml-2" title="Download Certificate">🏆</a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {new_oil_exporterResults.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">New Fuel Exporter Competency and Licensing Service Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {new_oil_exporterResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800">
                        <FaDownload />
                      </button>
                    )}
                    {item.certificate_path && (
                      <a href={fileUrl(item.certificate_path)} target="_blank" rel="noopener noreferrer" className="text-green-700 hover:text-green-900 ml-2" title="Download Certificate">🏆</a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {new_fuel_refineryResults.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">New Fuel Refining Competency and Licensing Service Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {new_fuel_refineryResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800">
                        <FaDownload />
                      </button>
                    )}
                    {item.certificate_path && (
                      <a href={fileUrl(item.certificate_path)} target="_blank" rel="noopener noreferrer" className="text-green-700 hover:text-green-900 ml-2" title="Download Certificate">🏆</a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {new_fuel_transportationResults.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">New Fuel Transportation Competency and Licensing Service Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {new_fuel_transportationResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800">
                        <FaDownload />
                      </button>
                    )}
                    {item.certificate_path && (
                      <a href={fileUrl(item.certificate_path)} target="_blank" rel="noopener noreferrer" className="text-green-700 hover:text-green-900 ml-2" title="Download Certificate">🏆</a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {fuel_order_cancellation_serviceResults.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">የነዳጅ ጭነት ትእዛዝ አገልግሎት ማቋረጥ / Fuel Delivery Order Cancellation Service Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {fuel_order_cancellation_serviceResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800">
                        <FaDownload />
                      </button>
                    )}
                    {item.certificate_path && (
                      <a href={fileUrl(item.certificate_path)} target="_blank" rel="noopener noreferrer" className="text-green-700 hover:text-green-900 ml-2" title="Download Certificate">🏆</a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {fuelDeliveryExtensionResults.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">የነዳጅ አቅርቦት ማራዘም ጥያቄ አገልግሎት / Fuel Delivery Extension Service Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Existing Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Extension</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fuel Qty (L)</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {fuelDeliveryExtensionResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <td className="px-4 py-3 font-mono text-sm">{item.existing_ref_no}</td>
                  <td className="px-4 py-3 text-sm">{item.extension_start_date} → {item.extension_end_date}</td>
                  <td className="px-4 py-3 text-sm">{item.fuel_quantity}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800">
                        <FaDownload />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {electricVehicleChargingResults.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">የኤሌክትሪክ ተሽከርካሪ ቻርጅ መሙሌት አገልግሎት / Electric Vehicle Charging Service License Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">License Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {electricVehicleChargingResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <td className="px-4 py-3 text-sm">{item.license_type}</td>
                  <td className="px-4 py-3 text-sm">{item.license_category}</td>
                  <td className="px-4 py-3 text-sm">{item.company_name}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800">
                        <FaDownload />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {gas_station_retail_renewalResults.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">Fuel Station Retail License Renewal Service Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {gas_station_retail_renewalResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800">
                        <FaDownload />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {fuel_distributor_renewalResults.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">Fuel Distributor License Renewal Service Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {fuel_distributor_renewalResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800">
                        <FaDownload />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {fuel_import_renewalResults.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">Fuel Import License Renewal Service Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {fuel_import_renewalResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800">
                        <FaDownload />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {gas_import_renewalResults.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">Gas Import License Renewal Service Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {gas_import_renewalResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800">
                        <FaDownload />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {oil_exporter_renewalResults.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">Fuel Exporter License Renewal Service Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {oil_exporter_renewalResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800">
                        <FaDownload />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {fuel_refinery_renewalResults.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">Fuel Refining License Renewal Service Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {fuel_refinery_renewalResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800">
                        <FaDownload />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {fuel_transportation_renewalResults.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-bold text-gray-700">Fuel Transportation License Renewal Service Requests</h3>
          </div>
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {fuel_transportation_renewalResults.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 font-mono text-sm">{item.reference_no}</td>
                  <StatusCell item={item} />
                  <td className="px-4 py-3">
                    {item.approved_letter && (
                      <button onClick={() => downloadFile(item.approved_letter, 'letter')} title={item.status === 'rejected' ? 'Download Rejection Letter' : 'Download Approval Letter'} className="text-green-500 hover:text-green-800">
                        <FaDownload />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

    </div>
  );
}

function ResetPassword({ onSuccess }) {
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const resetToken = params.get('token');
    if (resetToken) {
      setToken(resetToken);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/reset-password`, {
        token,
        newPassword
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
      setTimeout(() => {
        onSuccess();
      }, 2000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to reset password');
      toast.error(error.response?.data?.error || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-green-500 text-3xl">&#10003;</span>
          </div>
          <h2 className="text-2xl font-bold text-[#006400] mb-4">Password Reset Successful!</h2>
          <p className="text-gray-600 mb-4">You can now login with your new password.</p>
          <button onClick={onSuccess} className="bg-[#4CAF50] text-white px-6 py-2 rounded hover:bg-green-400">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Invalid Reset Link</h2>
          <p className="text-gray-600 mb-4">This password reset link is invalid or has expired.</p>
          <button onClick={onSuccess} className="bg-[#4CAF50] text-white px-6 py-2 rounded hover:bg-green-400">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
        <h2 className="text-2xl font-bold text-center mb-6 text-[#006400]">Reset Password</h2>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]"
              required
              minLength={6}
            />
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium mb-1">Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-[#006400]"
              required
              minLength={6}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#4CAF50] text-white py-2 rounded hover:bg-green-400 disabled:opacity-50"
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  );
}

function DraftBanner({ serviceType, onDiscard }) {
  const [savedAt, setSavedAt] = useState(() => getDraftMeta(serviceType));

  useEffect(() => {
    setSavedAt(getDraftMeta(serviceType));
    const onSave = (e) => {
      if (e.detail && e.detail.serviceType === serviceType) setSavedAt(e.detail.savedAt);
    };
    window.addEventListener('pea-draft-saved', onSave);
    return () => window.removeEventListener('pea-draft-saved', onSave);
  }, [serviceType]);

  if (!savedAt) return null;
  return (
    <div className="bg-amber-50 border border-amber-300 text-amber-900 rounded-lg p-3 mb-4 flex flex-wrap items-center justify-between gap-2">
      <div className="min-w-0">
        <p className="text-sm font-semibold">Draft saved — {new Date(savedAt).toLocaleString()}</p>
        <p className="text-xs mt-0.5">Your incomplete form is saved on this device, ready to continue. Uploaded documents are not kept in the draft; please re-attach them before submitting.</p>
      </div>
      <button type="button" onClick={() => { clearDraft(serviceType); setSavedAt(getDraftMeta(serviceType)); if (onDiscard) onDiscard(); }} className="text-xs bg-amber-200 hover:bg-amber-300 text-amber-900 px-3 py-1.5 rounded font-medium">
        Discard draft
      </button>
    </div>
  );
}

function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [selectedService, setSelectedService] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submittedRefNo, setSubmittedRefNo] = useState('');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [user, setUser] = useState(null);
  const [forceAuth, setForceAuth] = useState(!localStorage.getItem('user'));
  const [forcePasswordChange, setForcePasswordChange] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [complaintForm, setComplaintForm] = useState({ name: '', email: '', phone: '', subject: '', complaint: '' });
  const [feedbackForm, setFeedbackForm] = useState({ name: '', email: '', phone: '', rating: 5, feedback: '' });
  const [services, setServices] = useState([]);
  const [draftVersion, setDraftVersion] = useState(0);

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      const parsedUser = JSON.parse(savedUser);
      setUser(parsedUser);
      if (parsedUser.must_change_password) {
        setForcePasswordChange(true);
      }
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get('token')) {
      setCurrentPage('reset-password');
    }
    axios.get(`${API_URL}/services`).then(r => setServices(r.data)).catch(() => {});
  }, []);

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    setShowLoginModal(false);
    setForceAuth(false);
    if (userData.must_change_password) {
      setForcePasswordChange(true);
    }
  };

  const handleSessionExpired = () => {
    localStorage.removeItem('user');
    setUser(null);
    setShowLoginModal(true);
  };

  const handlePasswordChanged = () => {
    setForcePasswordChange(false);
    setShowPasswordModal(false);
    const savedUser = JSON.parse(localStorage.getItem('user'));
    savedUser.must_change_password = false;
    localStorage.setItem('user', JSON.stringify(savedUser));
  };

  const handleLogout = () => {
    axios.post(`${API_URL}/auth/logout`).catch(() => {});
    localStorage.removeItem('user');
    setUser(null);
    setCurrentPage('home');
    toast.info('Logged out successfully');
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    setSubmittedRefNo('');
  };

  const handleServiceSelect = (service) => {
    if (!user) {
      toast.info('Please sign in to apply for services');
      setShowLoginModal(true);
      return;
    }
    if (DISABLED_SERVICE_TYPES.includes(service.type)) {
      toast.warning('ይህ አገልግሎት ለጊዜ ተቋርጧል / This service is temporarily unavailable');
      return;
    }
    window.scrollTo(0, 0);
    setSelectedService(service);
    setCurrentPage('apply');
  };

  const handleSubmit = async (formData, id = null) => {
    try {
      const serviceType = selectedService?.type;
      
      const currentUser = JSON.parse(localStorage.getItem('user'));
      if (!currentUser || !currentUser.id) {
        toast.error('Please login to submit application');
        setShowLoginModal(true);
        return;
      }
      
      formData.append('user_id', currentUser.id);
      console.log('Submitting with user_id:', currentUser.id, 'username:', currentUser.username);
      
      if (serviceType === 'operator-change-license') {
        if (id) {
          await axios.put(`${API_URL}/operator-change-license/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/operator-change-license`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'diversion-license') {
        if (id) {
          await axios.put(`${API_URL}/diversion-license/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/diversion-license`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'gas-station-transfer') {
        if (id) {
          await axios.put(`${API_URL}/gas-station-transfer/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/gas-station-transfer`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'new-fuel-company') {
        if (id) {
          await axios.put(`${API_URL}/new-fuel-company/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/new-fuel-company`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'fuel-company-renewal') {
        if (id) {
          await axios.put(`${API_URL}/fuel-company-renewal/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/fuel-company-renewal`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'fuel-company-transfer') {
        if (id) {
          await axios.put(`${API_URL}/fuel-company-transfer/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/fuel-company-transfer`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'new-gas-station') {
        if (id) {
          await axios.put(`${API_URL}/new-gas-station/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/new-gas-station`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'gas-station-renewal') {
        if (id) {
          await axios.put(`${API_URL}/gas-station-renewal/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/gas-station-renewal`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'gas-station-connection-transfer') {
        if (id) {
          await axios.put(`${API_URL}/gas-station-connection-transfer/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/gas-station-connection-transfer`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'new-depot-fuel-cargo') {
        if (id) {
          await axios.put(`${API_URL}/new-depot-fuel-cargo/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/new-depot-fuel-cargo`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'depot-fuel-cargo-renewal') {
        if (id) {
          await axios.put(`${API_URL}/depot-fuel-cargo-renewal/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/depot-fuel-cargo-renewal`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'depot-fuel-cargo-transfer') {
        if (id) {
          await axios.put(`${API_URL}/depot-fuel-cargo-transfer/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/depot-fuel-cargo-transfer`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'new-gas-station-retail') {
        if (id) {
          await axios.put(`${API_URL}/new-gas-station-retail/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/new-gas-station-retail`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'new-fuel-distributor') {
        if (id) {
          await axios.put(`${API_URL}/new-fuel-distributor/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/new-fuel-distributor`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'new-fuel-import') {
        if (id) {
          await axios.put(`${API_URL}/new-fuel-import/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/new-fuel-import`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'new-gas-import') {
        if (id) {
          await axios.put(`${API_URL}/new-gas-import/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/new-gas-import`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'new-oil-exporter') {
        if (id) {
          await axios.put(`${API_URL}/new-oil-exporter/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/new-oil-exporter`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'new-fuel-refinery') {
        if (id) {
          await axios.put(`${API_URL}/new-fuel-refinery/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/new-fuel-refinery`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'new-fuel-transportation') {
        if (id) {
          await axios.put(`${API_URL}/new-fuel-transportation/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/new-fuel-transportation`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'gas-station-retail-renewal') {
        if (id) {
          await axios.put(`${API_URL}/gas-station-retail-renewal/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/gas-station-retail-renewal`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'fuel-distributor-renewal') {
        if (id) {
          await axios.put(`${API_URL}/fuel-distributor-renewal/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/fuel-distributor-renewal`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'fuel-import-renewal') {
        if (id) {
          await axios.put(`${API_URL}/fuel-import-renewal/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/fuel-import-renewal`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'gas-import-renewal') {
        if (id) {
          await axios.put(`${API_URL}/gas-import-renewal/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/gas-import-renewal`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'oil-exporter-renewal') {
        if (id) {
          await axios.put(`${API_URL}/oil-exporter-renewal/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/oil-exporter-renewal`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'fuel-refinery-renewal') {
        if (id) {
          await axios.put(`${API_URL}/fuel-refinery-renewal/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/fuel-refinery-renewal`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'fuel-transportation-renewal') {
        if (id) {
          await axios.put(`${API_URL}/fuel-transportation-renewal/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/fuel-transportation-renewal`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'Fuel_Station_Retail_Construction_Permit') {
        if (id) {
          await axios.put(`${API_URL}/gas-station-retail-construction/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/gas-station-retail-construction`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'Fuel_Distributor_Construction_Permit') {
        if (id) {
          await axios.put(`${API_URL}/fuel-distributor-construction/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/fuel-distributor-construction`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'Fuel_Importer_ Construction_Permit') {
        if (id) {
          await axios.put(`${API_URL}/fuel-import-construction/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/fuel-import-construction`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'Gas_Importer_Construction_Permit') {
        if (id) {
          await axios.put(`${API_URL}/gas-import-construction/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/gas-import-construction`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'Fuel_Exporter_Construction_permit') {
        if (id) {
          await axios.put(`${API_URL}/oil-exporter-construction/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/oil-exporter-construction`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'Fuel_Refining_Construction_permit') {
        if (id) {
          await axios.put(`${API_URL}/fuel-refinery-construction/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/fuel-refinery-construction`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'Fuel_Transportation_Construction_permit') {
        if (id) {
          await axios.put(`${API_URL}/fuel-transportation-construction/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/fuel-transportation-construction`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'fuel-order-cancellation') {
        if (id) {
          await axios.put(`${API_URL}/fuel-order-cancellation/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/fuel-order-cancellation`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'fuel-order-cancellation-service') {
        if (id) {
          await axios.put(`${API_URL}/fuel-order-cancellation-service/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/fuel-order-cancellation-service`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'vehicle-breakdown-reporting') {
        if (id) {
          await axios.put(`${API_URL}/vehicle-breakdown-reporting/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/vehicle-breakdown-reporting`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'fuel-delivery-extension') {
        if (id) {
          await axios.put(`${API_URL}/fuel-delivery-extension/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/fuel-delivery-extension`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else if (serviceType === 'electric-vehicle-charging') {
        if (id) {
          await axios.put(`${API_URL}/electric-vehicle-charging/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/electric-vehicle-charging`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      } else {
        if (id) {
          await axios.put(`${API_URL}/direct-fuel-beneficiary/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Request updated successfully!');
        } else {
          const response = await axios.post(`${API_URL}/direct-fuel-beneficiary`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          setSubmittedRefNo(response.data.reference_no);
          setShowSuccess(true);
        }
      }
      clearDraft(serviceType);
      setSelectedService(null);
      setCurrentPage('home');
    } catch (error) {
      const errMsg = error.response?.data?.error || error.message || 'Unknown error';
      console.error('Form submission error:', errMsg, error.response?.data);
      toast.error(id ? 'Failed to update request: ' + errMsg : 'Failed to submit application: ' + errMsg);
    }
  };

  const handleComplaintSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/complaints`, complaintForm);
      toast.success('Complaint submitted successfully!');
      setComplaintForm({ name: '', email: '', phone: '', subject: '', complaint: '' });
    } catch (error) {
      toast.error('Failed to submit complaint');
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/feedback`, feedbackForm);
      toast.success('Feedback submitted successfully!');
      setFeedbackForm({ name: '', email: '', phone: '', rating: 5, feedback: '' });
    } catch (error) {
      toast.error('Failed to submit feedback');
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div style={{
        position: 'fixed',
        top: 0, left: 0, width: '100%', height: '100%',
        backgroundImage: `url(${watermarkImage})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: 'contain',
        opacity: 0.12,
        pointerEvents: 'none',
        zIndex: 0
      }} />
      <Header 
        user={user} 
        onLoginClick={() => { setAuthMode('login'); setShowLoginModal(true); }} 
        onLogout={handleLogout}
        onOpenPasswordChange={() => setShowPasswordModal(true)}
        onMyApplicationsClick={() => setCurrentPage('my-applications')}
        onSignupClick={() => { setAuthMode('register'); setShowLoginModal(true); }}
        onNavigate={(page) => { setCurrentPage(page); setSelectedService(null); }}
      />
      <main className="flex-grow bg-gray-50">
        {currentPage === 'reset-password' ? (
          <ResetPassword onSuccess={() => { setCurrentPage('home'); setShowLoginModal(true); }} />
        ) : (
          <>
            {currentPage === 'home' && !selectedService && <Home onServiceSelect={handleServiceSelect} />}
            {currentPage === 'apply' && selectedService && (
              <div className="container mx-auto p-4 md:p-6">
                <DraftContext.Provider value={selectedService.type} key={draftVersion}>
                  <DraftBanner serviceType={selectedService.type} onDiscard={() => setDraftVersion(v => v + 1)} />
                  <p className="mb-3 text-center text-xs text-gray-500">Tip: your progress is saved automatically on this device. You can stop anytime and continue this form later.</p>
            {selectedService.type === 'operator-change-license' ? (
              <OperatorChangeLicenseForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} />
            ) : selectedService.type === 'diversion-license' ? (
              <DiversionLicenseForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} />
            ) : selectedService.type === 'gas-station-transfer' ? (
              <GasStationTransferForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} />
            ) : selectedService.type === 'new-fuel-company' ? (
              <NewFuelCompanyForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} />
            ) : selectedService.type === 'new-gas-import' ? (
              <NewGasImportForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} serviceId={selectedService.id} title="አዲስ የጋዝ አስመጪነት ብቃትና ፈቃድ አገልግሎት / New Gas Import Competency and Licensing Service" />
            ) : selectedService.type === 'new-oil-exporter' ? (
              <NewOilExporterForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} serviceId={selectedService.id} title="አዲስ የነዳጅ ላኪነት ብቃትና ፈቃድ አገልግሎት / New Fuel Exporter Competency and Licensing Service" />
            ) : selectedService.type === 'new-fuel-refinery' ? (
              <NewFuelRefineryForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} serviceId={selectedService.id} title="አዲስ የነዳጅ ማጣራት ብቃትና ፈቃድ አገልግሎት / New Fuel Refining (Refinery) Competency and Licensing Service" />
            ) : selectedService.type === 'new-fuel-transportation' ? (
              <NewFuelTransportationForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} serviceId={selectedService.id} title="አዲስ የነዳጅ ማጓጓዝ ብቃትና ፈቃድ አገልግሎት / New Fuel Transportation Competency and Licensing Service" />
            ) : selectedService.type === 'gas-station-retail-renewal' ? (
              <GasStationRetailRenewalForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} onRegister={() => { const svc = { id: 14, type: 'new-gas-station-retail', name: 'New Fuel Station Retail Competency and Licensing Service', name_amharic: 'አዲስ የነዳጅ ማደያ ችርቻሮ ብቃትና ፈቃድ አገልግሎት' }; setSelectedService(svc); setCurrentPage('apply'); }} />
            ) : selectedService.type === 'fuel-distributor-renewal' ? (
              <FuelDistributorRenewalForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} onRegister={() => { const svc = { id: 15, type: 'new-fuel-distributor', name: 'New Fuel Distributor Competency and Licensing Service', name_amharic: 'አዲስ የነዳጅ አከፋፋይ ብቃትና ፈቃድ አገልግሎት' }; setSelectedService(svc); setCurrentPage('apply'); }} />
            ) : selectedService.type === 'fuel-import-renewal' ? (
              <FuelImportRenewalForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} onRegister={() => { const svc = { id: 16, type: 'new-fuel-import', name: 'New Fuel Importer Competency and Licensing Service', name_amharic: 'አዲስ የነዳጅ አስመጪነት ብቃትና ፈቃድ አገልግሎት' }; setSelectedService(svc); setCurrentPage('apply'); }} />
            ) : selectedService.type === 'gas-import-renewal' ? (
              <GasImportRenewalForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} onRegister={() => { const svc = { id: 17, type: 'new-gas-import', name: 'አዲስ የጋዝ አስመጪነት ብቃትና ፈቃድ አገልግሎት / New Gas Importer Competency and Licensing Service', name_amharic: 'አዲስ የጋዝ አስመጪነት ብቃትና ፈቃድ አገልግሎት' }; setSelectedService(svc); setCurrentPage('apply'); }} />
            ) : selectedService.type === 'oil-exporter-renewal' ? (
              <OilExporterRenewalForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} onRegister={() => { const svc = { id: 18, type: 'new-oil-exporter', name: 'New Fuel Exporter Competency and Licensing Service', name_amharic: 'አዲስ የነዳጅ ላኪነት ብቃትና ፈቃድ አገልግሎት' }; setSelectedService(svc); setCurrentPage('apply'); }} />
            ) : selectedService.type === 'fuel-refinery-renewal' ? (
              <FuelRefineryRenewalForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} onRegister={() => { const svc = { id: 19, type: 'new-fuel-refinery', name: 'New Fuel Refining Competency and Licensing Service', name_amharic: 'አዲስ የነዳጅ ማጣራት ብቃትና ፈቃድ አገልግሎት' }; setSelectedService(svc); setCurrentPage('apply'); }} />
             ) : selectedService.type === 'new-gas-station' ? (
              <NewGasStationConnectionForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} />
            ) : selectedService.type === 'new-depot-fuel-cargo' ? (
              <NewDepotFuelCargoForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} />
            ) : selectedService.type === 'new-gas-station-retail' ? (
              <NewGasStationRetailForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} serviceId={selectedService.id} title="አዲስ የነዳጅ ማደያ ችርቻሮ ብቃትና ፈቃድ አገልግሎት / New Fuel Station Retail Competency and Licensing Service" />
            ) : selectedService.type === 'new-fuel-distributor' ? (
              <NewFuelDistributorForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} serviceId={selectedService.id} title="አዲስ የነዳጅ አከፋፋይ ብቃትና ፈቃድ አገልግሎት / New Fuel Distributor Competency and Licensing Service" />
            ) : selectedService.type === 'new-fuel-import' ? (
              <NewFuelImportForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} serviceId={selectedService.id} title="አዲስ የነዳጅ አስመጪነት ብቃትና ፈቃድ አገልግሎት / New Fuel Import Competency and Licensing Service" />
             ) : selectedService.type === 'fuel-transportation-renewal' ? (
              <FuelTransportationRenewalForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} onRegister={() => { const svc = { id: 20, type: 'new-fuel-transportation', name: 'New Fuel Transportation Competency and Licensing Service', name_amharic: 'አዲስ የነዳጅ ማጓጓዝ ብቃትና ፈቃድ አገልግሎት' }; setSelectedService(svc); setCurrentPage('apply'); }} />
             ) : selectedService.type === 'fuel-company-renewal' ? (
              <RenewalServiceForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} lookupEndpoint="/new-fuel-company" title="የነዳጅ አከፋፋይ ኩባንያ ትስስር ፈቃድ እድሳት አገልግሎት / Service for the Renewal of Fuel Distributor Company Connection Permit" />
             ) : selectedService.type === 'fuel-company-transfer' ? (
              <TransferServiceForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} lookupEndpoint="/new-fuel-company" title="የነዳጅ አከፋፋይ ኩባንያ ትስስር ፈቃድ ዝውውር አገልግሎት / Service for the Transfer of Fuel Distributor Company Connection permit" />
             ) : selectedService.type === 'gas-station-renewal' ? (
              <RenewalServiceForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} lookupEndpoint="/new-gas-station" title="የነዳጅ ማደያ ግንኙነት ፍቃድ እድሳት አገልግሎት / Service for the renewal of Gas Station Permit" />
             ) : selectedService.type === 'gas-station-connection-transfer' ? (
              <TransferServiceForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} lookupEndpoint="/new-gas-station" title="የነዳጅ ማደያ ግንኙነት ፍቃድ ዝውውር አገልግሎት / Service for the transfer of Gas Station Permit" />
             ) : selectedService.type === 'depot-fuel-cargo-renewal' ? (
              <RenewalServiceForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} lookupEndpoint="/new-depot-fuel-cargo" title="አዲስ ከዲፓ የነዳጅ ጭነት ትስስር ፈቃድ እድሳት አገልግሎት / Service for the Renewal of Fuel Loading Connection Permit from Depot" />
             ) : selectedService.type === 'depot-fuel-cargo-transfer' ? (
               <TransferServiceForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} lookupEndpoint="/new-depot-fuel-cargo" title="የነዳጅ ማጠራቀሚያ ግንኙነት ፍቃድ ዝውውር አገልግሎት / Service for the transfer of Fuel Loading Connection Permit from Depot" />
               ) : selectedService.type === 'Fuel_Station_Retail_Construction_Permit' ? (
                <NewGasStationRetailForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} serviceId={selectedService.id} title="የነዳጅ ማደያ ችርቻሮ ግንባታ ፈቃድ አገልግሎት / Fuel Station Retail Construction Permit Licensing Service" />
              ) : selectedService.type === 'Fuel_Distributor_Construction_Permit' ? (
                <NewFuelDistributorForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} serviceId={selectedService.id} title="የነዳጅ አከፋፋይ ግንባታ ፈቃድ አገልግሎት / Fuel Distributor Construction Permit Licensing Service" />
              ) : selectedService.type === 'Fuel_Importer_ Construction_Permit' ? (
                <NewFuelImportForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} serviceId={selectedService.id} title="የነዳጅ አስመጪነት ግንባታ ፈቃድ አገልግሎት / Fuel Importer Construction Permit Licensing Service" />
               ) : selectedService.type === 'Gas_Importer_Construction_Permit' ? (
                <NewGasImportForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} serviceId={selectedService.id} title="የጋዝ አስመጪነት ግንባታ ፈቃድ አገልግሎት / Gas Importer Construction Permit Licensing Service" />
              ) : selectedService.type === 'Fuel_Exporter_Construction_permit' ? (
                <NewOilExporterForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} serviceId={selectedService.id} title="የነዳጅ ላኪነት ግንባታ ፈቃድ አገልግሎት / Fuel Exporter Construction Permit Licensing Service" />
               ) : selectedService.type === 'Fuel_Refining_Construction_permit' ? (
                <NewFuelRefineryForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} serviceId={selectedService.id} title="Fuel Refining Construction Permit Licensing Service" />
               ) : selectedService.type === 'Fuel_Transportation_Construction_permit' ? (
                <NewFuelTransportationForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} serviceId={selectedService.id} title="Fuel Transportation Construction Permit Licensing Service" />
               ) : selectedService.type === 'fuel-order-cancellation' ? (
                <DirectFuelForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} title="የነዳጅ ጭነት ትእዛዝ ማቋረጥ / Fuel Order Cancellation Service" />
               ) : selectedService.type === 'fuel-order-cancellation-service' ? (
                <FuelOrderCancellationServiceForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} title="የነዳጅ ጭነት ትእዛዝ አገልግሎት ማቋረጥ / Fuel delivery order cancellation service" />
               ) : selectedService.type === 'vehicle-breakdown-reporting' ? (
                <VehicleBreakdownReportingForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} title="ብልሽት የገጠመውን ተሸከርካሪ ማሳወቅ አገልግሎት / Vehicle Breakdown Reporting Service" />
              ) : selectedService.type === 'fuel-delivery-extension' ? (
                <FuelDeliveryExtensionForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} onRegister={() => { const svc = services.find(s => s.type === 'direct-fuel') || { id: 1, type: 'direct-fuel', name: 'Service for direct fuel beneficiary', name_amharic: 'የነዳጅ ቀጥታ ተጠቃሚ ተቋማት ትስስር ፈቃድ አገልግሎት' }; setSelectedService(svc); setCurrentPage('apply'); }} />
              ) : selectedService.type === 'electric-vehicle-charging' ? (
                <ElectricVehicleChargingForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} title="የኤሌክትሪክ ተሽከርካሪ ቻርጅ መሙሌት አገልግሎት / Electric Vehicle Charging Service License Request" />
              ) : (
                <DirectFuelForm onSubmit={handleSubmit} onCancel={() => { setSelectedService(null); setCurrentPage('home'); }} />
              )}
                </DraftContext.Provider>
          </div>
        )}
        {currentPage === 'search' && <SearchStatus />}
        {currentPage === 'manual' && (
          <div className="container mx-auto p-4 md:p-6">
            <div className="bg-white rounded-lg p-4 md:p-8 shadow">
              <h2 className="text-2xl font-bold mb-4">የተጠቃሚ መመሪያ / User Manual</h2>
              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-800 mb-1">Steps to Use the Online Services Platform</h3>
                <p className="text-sm text-gray-900 font-medium mb-4">Follow these simple steps to get started</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { title: 'Register an Account', desc: 'Create a new account by providing your personal information and setting a secure password.' },
                    { title: 'Login', desc: 'After registration, sign in with your username and password to access available services.' },
                    { title: 'Browse Available Services', desc: 'Explore the list of services offered.' },
                    { title: 'Submit a Request or Application', desc: 'Choose your desired service, complete the required information, and submit any necessary documents for processing.' },
                    { title: 'Track Your Application Status', desc: "After submitting, view application progress in 'My Workspace' and receive notifications at each processing stage." },
                    { title: 'Receive and Download Results or Certificates', desc: 'Once processed, download your certificates or documents easily.' }
                  ].map((step, index) => (
                    <div key={index} className="flex items-start gap-3 bg-gradient-to-br from-orange-50 to-green-50 border-l-4 border-[#FF6B00] rounded-lg p-4 shadow-sm">
                      <div className="flex items-center justify-center w-9 h-9 rounded-full bg-gradient-to-br from-[#FF6B00] to-[#cc5500] text-white font-bold flex-shrink-0 shadow">{index + 1}</div>
                      <div>
                        <p className="font-semibold text-gray-800 leading-snug">{step.title}</p>
                        <p className="text-sm text-gray-600 mt-1 leading-snug">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-gray-700 mb-4">Watch the video below for instructions on how to use the PEA Online Service portal.</p>
              <video controls className="w-full max-w-4xl mx-auto rounded-lg shadow" style={{ maxHeight: '70vh' }}>
                <source src={`${API_ORIGIN}/uploads/manual.mp4`} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        )}
        {currentPage === 'requirements' && (
          <div className="container mx-auto p-4 md:p-6">
            <div className="bg-white rounded-lg p-4 md:p-8 shadow">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
                <h2 className="text-2xl font-bold">የአገልግሎት መስፈርቶች / Service Requirements</h2>
                <a
                  href={`${API_ORIGIN}/uploads/${encodeURIComponent('All Service Delivery Criteria new.pdf')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#FF6B00] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#cc5500] transition"
                >
                  <FaDownload /> Download PDF
                </a>
              </div>
              <p className="text-gray-700 mb-4">Below is the complete service delivery criteria document. You can view it online or download it for reference.</p>
              <iframe
                src={`${API_ORIGIN}/uploads/${encodeURIComponent('All Service Delivery Criteria new.pdf')}`}
                title="Service Delivery Criteria"
                className="w-full rounded-lg shadow"
                style={{ height: '80vh', border: 'none' }}
              />
            </div>
          </div>
        )}
        {currentPage === 'my-applications' && user && <MyApplications user={user} onSessionExpired={handleSessionExpired} />}
        {currentPage === 'about' && (
          <div className="container mx-auto p-4 md:p-6">
            <div className="bg-white rounded-lg p-4 md:p-8 shadow">
              <h2 className="text-2xl font-bold mb-4">ስለ እኛ / About Us</h2>
              <p className="text-gray-700 mb-4">The Petroleum and Energy Authority (PEA) is dedicated to providing efficient and transparent fuel services to the public.</p>
              <p className="text-gray-700">Our mission is to ensure fair distribution of petroleum products and maintain quality standards in the energy sector.</p>
            </div>
          </div>
        )}
        {currentPage === 'services' && (
          <div className="container mx-auto p-4 md:p-6">
            <div className="bg-white rounded-lg p-4 md:p-8 shadow">
              <h2 className="text-2xl font-bold mb-6">የምንስጣቸው አገልግሎቶች / Our Services</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {services.filter(s => s.status).map((service, index) => {
                  const colors = SERVICE_COLORS[index % SERVICE_COLORS.length];
                  const IconComponent = SERVICE_ICONS[index % SERVICE_ICONS.length];
                  return (
                    <button
                      key={service.id}
                      onClick={() => handleServiceSelect(service)}
                      className={`group relative bg-white rounded-xl shadow-md text-left overflow-hidden ${DISABLED_SERVICE_TYPES.includes(service.type) ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-xl transition-all duration-300 hover:-translate-y-1'}`}
                    >
                      <div className={`h-2 w-full bg-gradient-to-r ${colors.bg}`} />
                      <div className="p-6">
                        <div className={`w-12 h-12 rounded-lg ${colors.light} flex items-center justify-center mb-4 ${DISABLED_SERVICE_TYPES.includes(service.type) ? '' : 'group-hover:scale-110 transition-transform duration-300'}`}>
                          <IconComponent className={`text-2xl ${colors.icon}`} />
                        </div>
                        {service.name_amharic && (
                          <p className="text-lg font-bold text-gray-800 mb-1 leading-tight">{service.name_amharic}</p>
                        )}
                        <p className="text-sm text-gray-500 mb-4 leading-snug min-h-[2.5em]">{service.name}</p>
                        {DISABLED_SERVICE_TYPES.includes(service.type) ? (
                          <span className="inline-flex items-center gap-2 bg-gray-400 text-white px-4 py-2 rounded-lg text-sm font-medium">
                            ለጊዜ ተቋርጧል / Temporarily Unavailable
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-2 bg-[#FF6B00] text-white px-4 py-2 rounded-lg text-sm font-medium opacity-90 group-hover:opacity-100 transition-opacity">
                            Apply Now <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform" />
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        {currentPage === 'complaints' && (
          <div className="container mx-auto p-4 md:p-6">
            <div className="bg-white rounded-lg p-4 md:p-8 shadow">
              <h2 className="text-2xl font-bold mb-6">ቅሬታዎን እናከብራለን / We respect your complaint</h2>
              <form onSubmit={handleComplaintSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <input type="text" placeholder="Your name" value={complaintForm.name} onChange={(e) => setComplaintForm({...complaintForm, name: e.target.value})} className="w-full md:w-44 border rounded px-3 py-2" required />
                    </div>
                    <div>
                      <input type="email" placeholder="Your email" value={complaintForm.email} onChange={(e) => setComplaintForm({...complaintForm, email: e.target.value})} className="w-full md:w-44 border rounded px-3 py-2" required />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <input type="text" placeholder="Your phone" value={complaintForm.phone} onChange={(e) => setComplaintForm({...complaintForm, phone: e.target.value})} className="w-full md:w-44 border rounded px-3 py-2" />
                    </div>
                    <div>
                      <input type="text" placeholder="Subject" value={complaintForm.subject} onChange={(e) => setComplaintForm({...complaintForm, subject: e.target.value})} className="w-full md:w-44 border rounded px-3 py-2" required />
                    </div>
                  </div>
                  <div>
                    <textarea placeholder="Share your complaints" value={complaintForm.complaint} onChange={(e) => setComplaintForm({...complaintForm, complaint: e.target.value})} className="w-full border rounded px-3 py-2" rows="4" required></textarea>
                </div>
                <button type="submit" className="bg-[#4CAF50] text-white px-6 py-2 rounded hover:bg-green-400">Submit</button>
              </form>
            </div>
          </div>
        )}
        {currentPage === 'feedback' && (
          <div className="container mx-auto p-4 md:p-6">
            <div className="bg-white rounded-lg p-4 md:p-8 shadow">
              <h2 className="text-xl md:text-2xl font-bold mb-6">የሚሰጡንን ገንቢ ሃሳብ እናከብራለን / We value your feedback</h2>
              <form onSubmit={handleFeedbackSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <input type="text" placeholder="Your name" value={feedbackForm.name} onChange={(e) => setFeedbackForm({...feedbackForm, name: e.target.value})} className="w-full md:w-44 border rounded px-3 py-2" required />
                  </div>
                  <div>
                    <input type="email" placeholder="Your email" value={feedbackForm.email} onChange={(e) => setFeedbackForm({...feedbackForm, email: e.target.value})} className="w-full md:w-44 border rounded px-3 py-2" required />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <input type="text" placeholder="Your phone" value={feedbackForm.phone} onChange={(e) => setFeedbackForm({...feedbackForm, phone: e.target.value})} className="w-full md:w-44 border rounded px-3 py-2" />
                  </div>
                  <div>
                    <select value={feedbackForm.rating} onChange={(e) => setFeedbackForm({...feedbackForm, rating: parseInt(e.target.value)})} className="w-full md:w-44 border rounded px-3 py-2">
                      <option value="5">5 - Excellent</option>
                      <option value="4">4 - Very Good</option>
                      <option value="3">3 - Good</option>
                      <option value="2">2 - Fair</option>
                      <option value="1">1 - Poor</option>
                    </select>
                  </div>
                </div>
                <div>
                  <textarea placeholder="Share your complaints" value={feedbackForm.feedback} onChange={(e) => setFeedbackForm({...feedbackForm, feedback: e.target.value})} className="w-full border rounded px-3 py-2" rows="4" required></textarea>
                </div>
                <button type="submit" className="bg-[#4CAF50] text-white px-6 py-2 rounded hover:bg-green-400">Submit Feedback</button>
              </form>
            </div>
          </div>
        )}
          </>
        )}
      </main>
      <Footer />
      {showSuccess && <SuccessModal referenceNo={submittedRefNo} onClose={handleCloseSuccess} />}
      {(forcePasswordChange || showPasswordModal) && (
        <ChangePasswordModal
          onPasswordChanged={handlePasswordChanged}
          onLogout={handleLogout}
          onCancel={forcePasswordChange ? undefined : () => setShowPasswordModal(false)}
        />
      )}
      {(showLoginModal || forceAuth) && (
          <AuthModal onClose={() => { setShowLoginModal(false); setForceAuth(false); }} onLoginSuccess={handleLoginSuccess} initialMode={authMode === 'register'} />
      )}
    </div>
  );
}

export default App;
